import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useDeviceType } from '../../hooks/useDeviceType.ts';

import { nanoid } from 'nanoid';
import debounce from '../../utils/debounce';

import { getStroke } from 'perfect-freehand';

// eslint-disable-next-line
import {
    changeCursor,
    disableBtnNewTableClick,
    disableBtnNewNoteClick,
    disableBtnExportImage,
    updateLiveElementsPositions,
    updateFocusedInsideElement,
    updateFocusedIds,
    updateScale,
    updateOriginXY,
    focusOnColumnBelow,
    focusOnColumnAbove,
    focusOnFirstColumn,
    setIsHistoryClicked,
    disableBtnNewArea,
    toggleBtnNewLine,
    enableCanvasPricingPlanLimit,
    disableExportImageClick,
} from '../../redux/diagram/diagram.actions';

// eslint-disable-next-line
import {
    selectIsBtnNewTableClickEnabled,
    selectIsBtnNewTableGrabEnabled,
    selectIsBtnNewNoteClickEnabled,
    selectIsBtnNewNoteGrabEnabled,
    selectIsBtnNewLineEnabled,
    selectIsBtnNewAreaEnabled,
    selectIsBtnPenEnabled,
    selectIsCursorHand,
    selectIsHistoryClicked,
    // selectElementsChanges,
    selectFocusedInsideElement,
    selectFocusedIds,
    selectSearchHoveredColumn,
    selectScale,
    selectOriginXY,
    selectIsBtnExportImageEnabled,
    selectIsBtnRedGreenViewEnabled,
    selectIsBtnSearchEnabled,
    selectViewMode,
    selectIsDiagramReadOnly,
    selectIsBtnMinimapEnabled,
    selectCanvasLimits,
    selectNewPenProps,
    selectIsBtnExportImageClickEnabled,
} from '../../redux/diagram/diagram.selectors';

import { selectResolvedTheme, selectDiagramPrefs, resolveInputMode } from '../../redux/preferences/preferences.selectors';

import { themes } from '../../utils/themes';
import { parseNoteHtml } from '../../lib/noteHtml/parseNoteHtml';
import { layoutRichBlocks, collectNoteLinkZones, LIST_INDENT, BULLET_DOT_RADIUS, BULLET_DOT_X } from '../../utils/canvas/richTextLayout';
import {
    noteLinkZoneEntered,
    noteLinkZoneLeft,
    hideNoteLinkPreviewImmediately,
} from '../NoteLinkPreview/noteLinkHoverStore';
import {
    publishCanvasScrollGeometry,
    registerCanvasScrollPanApi,
    unregisterCanvasScrollPanApi,
} from '../CanvasScrollbars/canvasScrollbarsStore';
// clampOriginAxis is pure (no store/subscription involved) — imported
// directly from the math module rather than routed through
// canvasScrollbarsStore.js, which only exists to cross the Canvas/
// CanvasScrollbars component boundary; this stays entirely inside Canvas.
import { clampOriginAxis } from '../CanvasScrollbars/scrollbarMath';
import {
    getTableEffectiveHeight,
    getVisibleColumnsCount,
    heightForRows,
    snapTableDisplayHeight,
    isTableClipped,
} from '../../utils/canvas/tableClipping';
import { noteBodyColor } from '../../utils/canvas/noteColors';
import { canvasBackgroundColor } from '../../utils/canvas/canvasBackgrounds';

import {
    getDiagramDoc,
    useRole,
    getDiagramAwareness,
    getAwarenessProducer,
    yElements,
    yLines,
    ORIGIN_LOCAL,
} from '../../lib/yjs';
import { yToJS, invalidateAlong } from '../../lib/yjs/hooks/_yToJS';
import {
    addElement,
    setElementName,
    resizeElements,
    moveElements,
    moveAndResizeElements,
    setNoteText,
} from '../../lib/yjs/mutators/elements';
import {
    addColumn,
    deleteColumn as yjsDeleteColumn,
    moveColumnAbove as yjsMoveColumnAbove,
    moveColumnBelow as yjsMoveColumnBelow,
    setColumnName,
    setColumnType,
    setTableDisplayHeight,
} from '../../lib/yjs/mutators/columns';
import {
    addLine,
} from '../../lib/yjs/mutators/lines';
import {
    addArea,
    moveAreasAndElements,
} from '../../lib/yjs/mutators/areas';

import ContextMenu from '../ContextMenu/ContextMenu';
import { _buildLinePath, _drawLinePath } from './CanvasLine';
import EditableTableColumn from '../EditableElements/EditableTableColumn/EditableTableColumn';
import EditableTableHeader from '../EditableElements/EditableTableHeader/EditableTableHeader';
import EditableNote from '../EditableElements/EditableNote/EditableNote';
import { RemoteCursors } from './RemoteCursors';
import { drawRemoteHalos } from './remoteHalos';

import LoaderBar from '../Loaders/LoaderBar/LoaderBar';

// import TableEditableText from './TableEditableText'; // with redrawed full table, can be used for 'note' element
// import TableEditableText2 from './TableEditableText2';

import './Canvas.scss';


let tables_limit_free = 40;
let columns_limit_free = 20;
let notes_limit_free = 20;
let pens_limit_free = 100;
let areas_limit_free = 10;

let tables_limit_max = 400;
let columns_limit_max = 80;
let notes_limit_max = 200;
let pens_limit_max = 400;
let areas_limit_max = 200;



function checkTables(elementsValue, addCount = 1, isP = false) {
    let tablesLimit = isP ? tables_limit_max : tables_limit_free;
    let tablesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'table': {
                tablesCount++;

                if ((tablesCount + addCount) > tablesLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkColumns(elementsValue, elementId, addCount = 1, isP = false) {
    let columnsLimit = isP ? columns_limit_max : columns_limit_free;
    if (elementId) {
        const element = elementsValue[elementId];
        const columns = element?.columns;

        if (columns && (Object.keys(columns)?.length + addCount) > columnsLimit) {
            return false;
        }
    }
    return true;
}

function checkNotes(elementsValue, addCount = 1, isP = false) {
    let notesLimit = isP ? notes_limit_max : notes_limit_free;
    let notesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'note': {
                notesCount++;

                if ((notesCount + addCount) > notesLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkPens(elementsValue, addCount = 1, isP = false) {
    let pensLimit = isP ? pens_limit_max : pens_limit_free;
    let pensCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'pen-segment': {
                pensCount++;

                if ((pensCount + addCount) > pensLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkAreas(areasValue, addCount = 1, isP = false) {
    let areasLimit = isP ? areas_limit_max : areas_limit_free;
    if (areasValue && (Object.keys(areasValue)?.length + addCount) > areasLimit) {
        return false;
    }
    return true;
}

const TABLE_HEADER_HEIGHT = 40;
const TABLE_ROW_HEIGHT = 36; // prev value 28 with font 13(not 16), and after 30
const TABLE_BORDER_RADIUS = 4;

const HEADER_BODY_MARGIN = 4;

const NEW_TABLE_WIDTH = 320;
const NEW_TABLE_HEIGHT = TABLE_HEADER_HEIGHT + HEADER_BODY_MARGIN + TABLE_BORDER_RADIUS;

const NEW_NOTE_WIDTH = 360;
const NEW_NOTE_HEIGHT = 190;
// const NEW_NOTE_WIDTH = 360;
// const NEW_NOTE_HEIGHT = 360;

const NOTE_PADDING_X = 24;
const NOTE_PADDING_Y = 20;
const NOTE_PADDING_Y_BOTTOM = 12;
const NOTE_LINE_HEIGHT = 30;
const NOTE_FONT_SIZE = 21;
const NOTE_FONT_MONO_SIZE = 21;

// Shared min-zoom threshold for BOTH overflow indicators (note text overflow
// and table column-clipping) — draw block and click/hover hit-test must all
// gate on this same constant so there's never an invisible click target.
// Distinct from (and unrelated to) the drawNoteHidden/drawTableHidden
// small-zoom switchover at scale <= 0.15 — that swaps the whole element to a
// simplified render and is untouched by this constant.
const OVERFLOW_INDICATOR_MIN_SCALE = 0.5;

const TABLE_NAME_WIDTH = 250;
const TABLE_NAME_HEIGHT = 100;
// eslint-disable-next-line
const LETTERS_MAP = {
    _: {
        width: 0,
    },
    1: {
        width: 0,
    },
    2: {
        width: 0,
    },
    3: {
        width: 0,
    },
    4: {
        width: 0,
    },
    5: {
        width: 0,
    },
    6: {
        width: 0,
    },
    7: {
        width: 0,
    },
    8: {
        width: 0,
    },
    9: {
        width: 0,
    },
    0: {
        width: 0,
    },
    a: {
        width: 9.3,
    },
    b: {
        width: 10.7,
    },
    c: {
        width: 10.0,
    },
    d: {
        width: 10.3, // 10.25
    },
    e: {
        width: 10.2,
    },
    f: {
        width: 6.8,
    },
    g: {
        width: 10.3, // 10.25
    },
    h: {
        width: 9.85,
    },
    i: {
        width: 3.5,
    },
    j: {
        width: 3.55,
    },
    k: {
        width: 10.15,
    },
    l: {
        width: 3.25,
    },
    m: {
        width: 15.25,
    },
    n: {
        width: 9.8,
    },
    o: {
        width: 10.5,
    },
    p: {
        width: 10.7,
    },
    q: {
        width: 10.3,
    },
    r: {
        width: 6.8,
    },
    s: {
        width: 9.1,
    },
    t: {
        width: 5.9,
    },
    u: {
        width: 9.9,
    },
    v: {
        width: 10.2,
    },
    w: {
        width: 15.0,
    },
    x: {
        width: 9.8,
    },
    y: {
        width: 10.2,
    },
    z: {
        width: 9.45,
    },
};

const TWO_PI = 2 * Math.PI;

// Cached Path2D objects — parsed once at module load, reused every draw call.
const _PATH2D_TABLE_ICON_HEADER = new Path2D('M45,0 L3,0 C1.343,0 0,1.344 0,3 L0,12 L48,12 L48,3 C48,1.344 46.657,0 45,0');
const _PATH2D_TABLE_ICON_CELLS = new Path2D('M0,45 C0,46.656 1.343,48 3,48 L12,48 L12,36 L0,36 L0,45 M0,36 L12,36 L12,24 L0,24 M12,48 L24,48 L24,36 L12,36 M24,48 L36,48 L36,36 L24,36 M12,36 L24,36 L24,24 L12,24 M24,36 L36,36 L36,24 L24,24 M36,36 L48,36 L48,24 L36,24 M0,24 L12,24 L12,12 L0,12 M12,24 L24,24 L24,12 L12,12 M24,24 L36,24 L36,12 L24,12 M36,24 L48,24 L48,12 L36,12 M36,36 L36,48 L45,48 C46.657,48 48,46.656 48,45 L48,36 L36,36 Z');
const _PATH2D_TABLE_ICON_LINES = new Path2D('M45,0 L3,0 C1.343,0 0,1.343 0,3 L0,45 C0,46.657 1.343,48 3,48 L45,48 C46.657,48 48,46.657 48,45 L48,3 C48,1.343 46.657,0 45,0 M0,12 L48,12M0,24 L48,24 M0,36 L48,36 M24,12 L24,48 M36,12 L36,48 M12,12 L12,48');
const _PATH2D_DESCRIPTION_ICON = new Path2D('M509.039,324.633a1.891,1.891,0,0,1,1.889,1.89V349.7a1.891,1.891,0,0,1-1.889,1.89H485.862a1.892,1.892,0,0,1-1.89-1.89V326.523a1.892,1.892,0,0,1,1.89-1.89h23.177m0-2H485.862a3.89,3.89,0,0,0-3.89,3.89V349.7a3.89,3.89,0,0,0,3.89,3.89h23.177a3.889,3.889,0,0,0,3.889-3.89V326.523a3.889,3.889,0,0,0-3.889-3.89Z M505.105,332.478H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,339.188H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,345.9H489.523a1,1,0,0,1,0-2h15.582a1,1,0,0,1,0,2Z');
const _PATH2D_KEY_ICON = new Path2D('M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z');

// Returns column keys in visual order: uses columnsOrder when present (Yjs),
// falls back to Object.keys insertion order.
function orderedColumnKeys(columns, columnsOrder) {
    if (Array.isArray(columnsOrder) && columnsOrder.length) {
        return columnsOrder.filter((id) => columns?.[id] !== undefined);
    }
    return Object.keys(columns ?? {});
}

function getIndexNumber(columns, columnId, columnsOrder) {
    const keys = orderedColumnKeys(columns, columnsOrder);
    const idx = keys.indexOf(columnId);
    return idx >= 0 ? idx : 0;
}

// 'keys' view shows only PK/FK/related columns, so a line connects at the column's
// index among those visible columns (not all columns) — matches drawTableKey's filter.
function getKeysViewIndexNumber(columns, columnId, columnsOrder) {
    const keys = orderedColumnKeys(columns, columnsOrder).filter(
        (k) => columns[k]?.isPK || columns[k]?.isFK || columns[k]?.isRelated
    );
    const idx = keys.indexOf(columnId);
    return idx >= 0 ? idx : 0;
}

function calculateScaleProgress(value) {
    const SCALE_MIN = 0.25;
    const SCALE_MAX = 5.0;
    return ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;
}
function calculateResizeHandleCoefValue(percents) {
    const COEF_MIN = 0.6;
    const COEF_MAX = 1.5;
    return (percents / 100) * (COEF_MAX - COEF_MIN) + COEF_MIN;
}
function calculateResizeOffsetValue(percents) {
    const COEF_MIN = 0.4;
    const COEF_MAX = 5;
    return (percents / 100) * (COEF_MAX - COEF_MIN) + COEF_MIN;
}
// resize coordinates format = { nw: { x, y }, ne: { x,y }, sw: { x, y }, se: { x, y } };
function calculateItemResizeCoordinates(item, resizeHandleSize, outlineOffset) {
    const itemFrameOffset = outlineOffset;
    const width = resizeHandleSize;

    const { x, y, width: itemWidth, height: itemHeight } = item;

    const itemFrameWidth = itemWidth + itemFrameOffset * 2;
    const itemFrameHeight = itemHeight + itemFrameOffset * 2;
    const resizeX = x - itemFrameOffset;
    const resizeY = y - itemFrameOffset;

    const resizeCoordinates = {
        width,
        points: {
            nw: {
                x: resizeX - width / 2,
                y: resizeY - width / 2,
            },
            ne: {
                x: resizeX + itemFrameWidth - width / 2,
                y: resizeY - width / 2,
            },
            sw: {
                x: resizeX - width / 2,
                y: resizeY + itemFrameHeight - width / 2,
            },
            se: {
                x: resizeX + itemFrameWidth - width / 2,
                y: resizeY + itemFrameHeight - width / 2,
            },
        },
        lines: {
            w: {
                x: { value: resizeX },
                y: {
                    min: resizeY + width / 2,
                    max: resizeY + itemFrameHeight - width / 2,
                },
            },
            e: {
                x: { value: resizeX + itemFrameWidth },
                y: {
                    min: resizeY + width / 2,
                    max: resizeY + itemFrameHeight - width / 2,
                },
            },
            n: {
                x: {
                    min: resizeX + width / 2,
                    max: resizeX + itemFrameWidth - width / 2,
                },
                y: { value: resizeY },
            },
            s: {
                x: {
                    min: resizeX + width / 2,
                    max: resizeX + itemFrameWidth - width / 2,
                },
                y: { value: resizeY + itemFrameHeight },
            },
        },
    };

    return resizeCoordinates;
}
function calculateOutlineWidth(scaleValue) {
    if (scaleValue > 4.5) {
        return 0.55 + 1;
    } else if (scaleValue > 3.5) {
        return 0.65 + 1;
    } else if (scaleValue > 2.5) {
        return 0.75 + 1;
    } else if (scaleValue > 1.5) {
        return 1 + 1;
    } else if (scaleValue > 1.2) {
        return 1.2 + 1;
    } else if (scaleValue > 1) {
        return 1.3 + 1;
    } else if (scaleValue > 0.75) {
        return 1.5 + 1;
    } else if (scaleValue > 0.6) {
        return 1.75 + 1;
    } else if (scaleValue > 0.5) {
        return 2 + 1;
    } else if (scaleValue > 0.35) {
        return 2.5;
    } else if (scaleValue > 0.25) {
        return 3;
    } else if (scaleValue > 0.15) {
        return 5;
    } else if (scaleValue > 0.12) {
        return 8;
    } else if (scaleValue > 0.1) {
        return 10;
    } else if (scaleValue > 0.08) {
        return 15;
    } else if (scaleValue > 0.07) {
        return 20;
    } else if (scaleValue > 0.06) {
        return 25;
    } else if (scaleValue > 0.04) {
        return 30;
    } else if (scaleValue > 0.01) {
        return 40;
    } else {
        return 50;
    }
}

function getSelectedElements(startXY, currentXY, elementsValue, viewMode) {
    if (viewMode === 'keys') {
        return getSelectedElementsForViewKeys(
            startXY,
            currentXY,
            elementsValue
        );
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const el = elementsValue[key];
        if (el.type === 'area') return false;
        const { x, y, width, height } = el;
        // clipping only affects full-view geometry — box-select should capture
        // a clipped table by its VISIBLE body only; keys/names stay natural
        const effectiveHeight = viewMode === 'full' ? getTableEffectiveHeight(el) : height;
        return x + width > minX && x < maxX && y + effectiveHeight > minY && y < maxY;
    });
}

function getSelectedElementsForViewKeys(startXY, currentXY, elementsValue) {
    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const element = elementsValue[key];

        if (element.type === 'table') {
            const { x, y, width, columns } = element;

            const columnsKeys = Object.keys(columns ?? {}).filter(
                (columnKey) =>
                    columns[columnKey]?.isPK ||
                    columns[columnKey]?.isFK ||
                    columns[columnKey]?.isRelated
            );
            const columnsCount = columnsKeys?.length || 0;
            const tableHeight =
                TABLE_HEADER_HEIGHT +
                HEADER_BODY_MARGIN +
                (columnsCount + 1) * TABLE_ROW_HEIGHT +
                TABLE_BORDER_RADIUS +
                1;

            return (
                x + width > minX &&
                x < maxX &&
                y + tableHeight > minY &&
                y < maxY
            );
        } else {
            const { x, y, width, height } = element;

            return (
                x + width > minX && x < maxX && y + height > minY && y < maxY
            );
        }
    });
}

function getSelectedAreas(startXY, currentXY, areasValue) {
    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);

    return Object.keys(areasValue).filter((key) => {
        const { x, y, width, height } = areasValue[key];
        return (x >= minX && y >= minY && x + width <= maxX && y + height <= maxY); // now full-containment
        // return (x + width > minX && x < maxX && y + height > minY && y < maxY);  // for intersection contains
    });
}

function getAreaSelectedElements(
    startXY,
    currentXY,
    elementsValue,
    areasValue,
    viewMode
) {
    if (viewMode === 'keys') {
        return getAreaSelectedElementsForViewKeys(
            startXY,
            currentXY,
            elementsValue,
            areasValue
        );
    }

    // Derive which elements are already in some area (to exclude them from selection)
    const otherAreasSelectedElementsIds = areasValue
        ? Object.values(elementsValue).filter(el => el.areaId && areasValue[el.areaId]).map(el => el.id)
        : [];

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const el = elementsValue[key];
        const { id, x, y, width, height } = el;
        // clipping only affects full-view geometry — an area should only claim
        // a clipped table's VISIBLE body; keys/names stay natural
        const effectiveHeight = viewMode === 'full' ? getTableEffectiveHeight(el) : height;

        return (
            x + width > minX &&
            x < maxX &&
            y + effectiveHeight > minY &&
            y < maxY &&
            !otherAreasSelectedElementsIds.includes(id)
        );
    });
}

function getAreaSelectedElementsForViewKeys(
    startXY,
    currentXY,
    elementsValue,
    areasValue
) {
    // Derive which elements are already in some area (to exclude them from selection)
    const otherAreasSelectedElementsIds = areasValue
        ? Object.values(elementsValue).filter(el => el.areaId && areasValue[el.areaId]).map(el => el.id)
        : [];

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const element = elementsValue[key];

        if (element.type === 'table') {
            const {
                id,
                x,
                y,
                width,
                columns,
            } = element;

            const columnsKeys = Object.keys(columns ?? {}).filter(
                (columnKey) =>
                    columns[columnKey]?.isPK ||
                    columns[columnKey]?.isFK ||
                    columns[columnKey]?.isRelated
            );
            const columnsCount = columnsKeys?.length || 0;
            const tableHeight =
                TABLE_HEADER_HEIGHT +
                HEADER_BODY_MARGIN +
                (columnsCount + 1) * TABLE_ROW_HEIGHT +
                TABLE_BORDER_RADIUS +
                1;

            return (
                x + width > minX &&
                x < maxX &&
                y + tableHeight > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        } else {
            const { id, x, y, width, height } = element;

            return (
                x + width > minX &&
                x < maxX &&
                y + height > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        }
    });
}






function getAreaSelectedElementsForResize(
    startXY,
    currentXY,
    elementsValue,
    areasValue,
    viewMode,
    areaId
) {
    if (viewMode === 'keys') {
        return getAreaSelectedElementsForViewKeysForResize(
            startXY,
            currentXY,
            elementsValue,
            areasValue,
            areaId
        );
    }

    const otherAreasSelectedElementsIds = [];

    if (areasValue) {
        for (const key in areasValue) {
            const area = areasValue[key];
            const nestedElementsIds = Object.values(elementsValue ?? {}).filter(el => el.areaId === area.id).map(el => el.id);

            if (area.id !== areaId) {
                otherAreasSelectedElementsIds.push(...nestedElementsIds);
            }
        }
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const el = elementsValue[key];
        const { id, x, y, width, height } = el;
        // clipping only affects full-view geometry — an area resize should only
        // claim a clipped table's VISIBLE body; keys/names stay natural
        const effectiveHeight = viewMode === 'full' ? getTableEffectiveHeight(el) : height;

        return (
            x + width > minX &&
            x < maxX &&
            y + effectiveHeight > minY &&
            y < maxY &&
            !otherAreasSelectedElementsIds.includes(id)
        );
    });
}
function getAreaSelectedElementsForViewKeysForResize(
    startXY,
    currentXY,
    elementsValue,
    areasValue,
    areaId
) {
    const otherAreasSelectedElementsIds = [];
    if (areasValue) {
        for (const key in areasValue) {
            const area = areasValue[key];
            const nestedElementsIds = Object.values(elementsValue ?? {}).filter(el => el.areaId === area.id).map(el => el.id);

            if (area.id !== areaId) {
                otherAreasSelectedElementsIds.push(...nestedElementsIds);
            }
        }
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const element = elementsValue[key];

        if (element.type === 'table') {
            const {
                id,
                x,
                y,
                width,
                columns,
            } = element;

            const columnsKeys = Object.keys(columns ?? {}).filter(
                (columnKey) =>
                    columns[columnKey]?.isPK ||
                    columns[columnKey]?.isFK ||
                    columns[columnKey]?.isRelated
            );
            const columnsCount = columnsKeys?.length || 0;
            const tableHeight =
                TABLE_HEADER_HEIGHT +
                HEADER_BODY_MARGIN +
                (columnsCount + 1) * TABLE_ROW_HEIGHT +
                TABLE_BORDER_RADIUS +
                1;

            return (
                x + width > minX &&
                x < maxX &&
                y + tableHeight > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        } else {
            const { id, x, y, width, height } = element;

            return (
                x + width > minX &&
                x < maxX &&
                y + height > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        }
    });
}




// function scrollIfNeeded(e) {
//     const margin = 50; // margin to start scrolling
//     if (e.clientX < margin) {
//       window.scrollBy(-10, 0);
//       offsetX = Math.max(0, offsetX - 10);
//     } else if (e.clientX > window.innerWidth - margin) {
//       window.scrollBy(10, 0);
//       offsetX = Math.min(canvas.width - window.innerWidth, offsetX + 10);
//     }
//     if (e.clientY < margin) {
//       window.scrollBy(0, -10);
//       offsetY = Math.max(0, offsetY - 10);
//     } else if (e.clientY > window.innerHeight - margin) {
//       window.scrollBy(0, 10);
//       offsetY = Math.min(canvas.height - window.innerHeight, offsetY + 10);
//     }
//   }

const setsAreEqual = (set1, set2) => {
    if (set1 === set2) return true;
    if (!set1 || !set2) return false;
    if (set1.size !== set2.size) return false;
    for (const v of set1) {
        if (!set2.has(v)) return false;
    }
    return true;
};

const arraysAreEqual = (array1, array2) => {
    if (typeof array1 !== 'object' || typeof array2 !== 'object') {
        return false;
    }

    if (array1.length !== array2.length) {
        return false;
    }

    const sortedArray1 = array1.slice().sort();
    const sortedArray2 = array2.slice().sort();

    for (let i = 0; i < sortedArray1.length; i++) {
        if (sortedArray1[i] !== sortedArray2[i]) {
            return false;
        }
    }

    return true;
};

function drawTextWithLetterSpacing(ctx, text, x, y, letterSpacing, textAlign) {
    // Зберігаємо початкові координати X
    let currentX = x;
    ctx.textAlign = textAlign;

    // Проходимо через кожен символ у тексті
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        // Малюємо символ
        ctx.fillText(char, currentX, y);

        // Розраховуємо ширину символу та додаємо інтервал між літерами
        const charWidth = ctxMeasure(ctx, char);
        currentX += charWidth + letterSpacing;
    }
}
function drawTextRight(ctx, text, x, y, letterSpacing) {
    let startX = x;
    let currentX = startX;
    ctx.textAlign = 'left';

    // Проходимо через кожен символ у тексті в зворотньому порядку
    for (let i = text.length - 1; i >= 0; i--) {
        const char = text[i];
        // Розраховуємо ширину символу та зменшуємо currentX
        const charWidth = ctxMeasure(ctx, char);
        currentX -= charWidth + letterSpacing;

        // Малюємо символ
        ctx.fillText(char, currentX, y);
    }
}
// function drawText(ctx, text, x, y, letterSpacing, textAlign = 'left') {
//     // Визначаємо ширину всього тексту з урахуванням інтервалів між літерами
//     const totalWidth = ctxMeasure(ctx, text) + (text.length - 1) * letterSpacing;

//     // Розраховуємо початкову координату X залежно від вирівнювання
//     let startX = x;
//     if (textAlign === 'center') {
//         startX = x - totalWidth / 2;
//     } else if (textAlign === 'right') {
//         startX = x - totalWidth;
//     }

//     // Зберігаємо початкові координати X
//     let currentX = startX;

//     // Проходимо через кожен символ у тексті
//     for (let i = 0; i < text.length; i++) {
//         const char = text[i];
//         // Малюємо символ
//         ctx.fillText(char, currentX, y);

//         // Розраховуємо ширину символу та додаємо інтервал між літерами
//         const charWidth = ctxMeasure(ctx, char);
//         currentX += charWidth + letterSpacing;
//     }
// }

const average = (a, b) => (a + b) / 2;

const getSvgPathFromStroke = (points) => {
    let p1 = points[0];
    let p2 = points[1];
    const p3 = points[2];

    let result = `M${p1[0].toFixed(2)}, ${p1[1].toFixed(2)} Q${p2[0].toFixed(
        2
    )}, ${p2[1].toFixed(2)} ${average(p2[0], p3[0]).toFixed(2)}, ${average(
        p2[1],
        p3[1]
    ).toFixed(2)} T`;

    for (let i = 2, max = points.length - 1; i < max; i++) {
        p1 = points[i];
        p2 = points[i + 1];
        result += `${average(p1[0], p2[0]).toFixed(2)},${average(
            p1[1],
            p2[1]
        ).toFixed(2)} `;
    }
    return result;
};

// Pen-segments persist only their `points` (Yjs is the source of truth); the renderer
// and hit-tests draw the stroke from `path`, so it's rebuilt from points whenever a
// pen-segment is loaded into elementsRef. Options must match buildNewPenSegment.
const PEN_STROKE_OPTIONS = { size: 20, smoothing: 0.5, thinning: 0.25, streamline: 0.7 };

// Build the SVG path and the stroke's natural top-left from a pen-segment's points.
// The origin equals the element's x/y at creation, so the renderer and hit-tests can
// position the stroke by (x - originX, y - originY) — i.e. the pen moves via x/y
// (translateX/translateY was a leftover from the persisted-path approach).
function buildPenStroke(points) {
    if (!Array.isArray(points) || !points.length) return null;
    const stroke = getStroke(points, PEN_STROKE_OPTIONS);
    // getSvgPathFromStroke indexes points[0..2]; needs at least 3 outline points.
    if (!stroke || stroke.length < 3) return null;
    let originX = Infinity, originY = Infinity;
    for (const p of stroke) {
        if (p[0] < originX) originX = p[0];
        if (p[1] < originY) originY = p[1];
    }
    return { path: getSvgPathFromStroke(stroke), originX, originY };
}

function ensurePenSegmentPath(el) {
    if (el && el.type === 'pen-segment' && !el.path) {
        const built = buildPenStroke(el.points);
        el.path = built ? built.path : '';
        el.pathOriginX = built ? built.originX : (el.x ?? 0);
        el.pathOriginY = built ? built.originY : (el.y ?? 0);
    }
    return el;
}

// Pattern-based grid renderer cache. Module-scoped so tile bitmaps survive
// across component re-renders. Keyed by ctx (WeakMap) -> Map(key -> entry).
const __gridPatternCache = new WeakMap();

function __modGrid(n, m) {
    const r = n % m;
    return r < 0 ? r + m : r;
}

function __getGridPatternCacheForCtx(ctx) {
    let cache = __gridPatternCache.get(ctx);
    if (!cache) {
        cache = new Map();
        __gridPatternCache.set(ctx, cache);
    }
    return cache;
}

function __buildGridDotPattern(ctx, spacingCssPx, radiusCssPx, color, dpr) {
    const cache = __getGridPatternCacheForCtx(ctx);
    const tileDevicePx = Math.max(2, Math.round(spacingCssPx * dpr));
    const radiusDevicePx = Math.max(0.5, radiusCssPx * dpr);

    const key = `${tileDevicePx}|${radiusDevicePx.toFixed(3)}|${color}`;
    const hit = cache.get(key);
    if (hit) return hit;

    const tile =
        typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(tileDevicePx, tileDevicePx)
            : Object.assign(document.createElement('canvas'), {
                width: tileDevicePx,
                height: tileDevicePx,
            });
    const tctx = tile.getContext('2d');
    tctx.clearRect(0, 0, tileDevicePx, tileDevicePx);
    tctx.fillStyle = color;
    tctx.beginPath();
    tctx.arc(tileDevicePx / 2, tileDevicePx / 2, radiusDevicePx, 0, Math.PI * 2);
    tctx.fill();

    const pattern = ctx.createPattern(tile, 'repeat');
    const entry = { pattern, tileDevicePx };
    cache.set(key, entry);
    return entry;
}

// ---------------------------------------------------------------------------
// Canvas draw helpers (module-level, shared across renders)
// ---------------------------------------------------------------------------

// measureText is expensive (~1–5µs/call). Cache width by "font\0text" since
// canvas text metrics are independent of transforms — same font+text always
// yields the same pixel width.
const _measureCache = new Map();
function ctxMeasure(ctx, text) {
    const key = ctx.font + '\x00' + text;
    let w = _measureCache.get(key);
    if (w === undefined) {
        w = ctx.measureText(text).width;
        _measureCache.set(key, w);
    }
    return w;
}

// Avoid redundant ctx.font= assignments — the browser re-parses the font
// string on every assignment even when it hasn't changed.
let _ctxCurrentFont = '';
function ctxSetFont(ctx, font) {
    if (font !== _ctxCurrentFont) {
        ctx.font = font;
        _ctxCurrentFont = font;
    }
}
// ctxSetFont's module-level dedup can't be trusted across a ctx swap — a
// fresh export canvas's 2D context starts at the browser default font while
// the tracker still holds the main canvas's last-set font string. If the
// export pass's first font happens to match that stale value, ctxSetFont
// skips the assignment and text renders at the browser default (10px sans-
// serif) instead of the intended font. Call this right after swapping to the
// export ctx so the very next ctxSetFont call always assigns.
function ctxResetFontTracker() {
    _ctxCurrentFont = '';
}

// ---------------------------------------------------------------------------

const Canvas = React.forwardRef(({ minimapRef }, ref) => {
    // dispatch
    const dispatch = useDispatch();
    const { isDesktop } = useDeviceType(); // mobile/tablet => false
    const { t } = useTranslation();

    // selectors
    const resolvedTheme = useSelector(selectResolvedTheme);
    // let theme = themes[resolvedTheme];
    let theme = themes['dark'];

    const clickTimesRef = useRef([]);

    const alignmentLinesRef = useRef(null);
    const visibleElementsIdsRef = useRef(new Set());
    const visibleLinesIdsRef = useRef(new Set());
    const visibleAreaIdsRef = useRef([]);
    const visibleViewportElementsIdsRef = useRef([]);
    const sumOriginDxyRef = useRef({ dx: 0, dy: 0 });


    // const exportImgCanvasRef = useRef(null);
    // const exportImgContextRef = useRef(null);

    // ===================== 0. NEW AREA | START XY / CURRENT XY =====================
    const newAreaPropsRef = useRef(null); // { startX: 0, startY: 0, endX: 0, endY: 0 }

    // ===================== 0. RESIZE AREA | START XY / CURRENT XY =====================
    const resizeAreaPropsRef = useRef(null); // { selectedElements }

    // ===================== 0. RESIZE AREA | START XY / CURRENT XY =====================
    const resizeElementPropsRef = useRef(null); // { elementId, areaId }

    // is selection started
    // const isSelectionClickedRef = useRef(false); // ?? try to use isBoardClickedRef & isCursorHand

    const canvasLimits = useSelector(selectCanvasLimits);
    const canvasLimitsRef = useRef(null);

    const minimapClickedXYRef = useRef(null);
    // const minimapZoomRef = useRef(null);
    const minimapZoomRef = useRef({
        zoom: 1,
        originX: 1,
        originY: 1,
    });
    // minimapZoomRef.current = {
    // 	zoom: newZoom,
    // 	originX: newOriginX,
    // 	originY: newOriginY,
    // };

    const canvasGridRef = useRef(null);
    const lastGridProps = useRef(null);

    const isExportImageClickedRef = useRef(false);

    const selectedElementsIdsRef = useRef([]);
    const selectedAreasIdsRef = useRef([]);

    // const isUnfocusElementsBeforeRef = useRef(false);
    const shouldUnfocusElementsOnClickOnAreaRef = useRef(false); // prev. name 'isUnfocusElementsBeforeRef'

    const scale = useSelector(selectScale);
    const originXY = useSelector(selectOriginXY);

    // const elementsChangesRedux = useSelector(selectElementsChanges);

    const { canEdit: canEditByRole } = useRole();
    const isReadOnly = useSelector(selectIsDiagramReadOnly);
    // Editing requires an edit role AND the diagram not being read-only (archived).
    const canEdit = canEditByRole && !isReadOnly;
    const canEditRef = useRef(canEdit);
    useEffect(() => { canEditRef.current = canEdit; }, [canEdit]);

    // "Mouse or Trackpad" preference (redux/preferences → diagram.input_mode,
    // 'trackpad' | 'mouse', default 'trackpad'). Same ref-mirror as canEditRef
    // just above: handleCanvasWheel is created once inside a []-dep effect
    // (see the big `useEffect(() => {...}, [])` around handleCanvasMouseDown
    // below), so a bare closure read of a `useSelector` value there would be
    // frozen at whatever it was on first mount — this ref keeps it current.
    const diagramPrefs = useSelector(selectDiagramPrefs);
    // 'auto' resolves through resolveInputMode (currently → 'trackpad'), so the
    // wheel-handler checks below keep comparing against concrete modes only.
    const inputModeRef = useRef(resolveInputMode(diagramPrefs?.input_mode));
    useEffect(() => { inputModeRef.current = resolveInputMode(diagramPrefs?.input_mode); }, [diagramPrefs?.input_mode]);
    // Same mirror shape for the 'Show align lines' settings toggle —
    // getAlignmentLines runs inside once-bound drag handlers.
    const showAlignLinesRef = useRef(diagramPrefs?.show_align_lines);
    useEffect(() => { showAlignLinesRef.current = diagramPrefs?.show_align_lines; }, [diagramPrefs?.show_align_lines]);

    const focusedIds = useSelector(selectFocusedIds, shallowEqual);

    const focusedInsideElementRedux = useSelector(selectFocusedInsideElement);

    const searchHoveredColumn = useSelector(selectSearchHoveredColumn, shallowEqual);

    const isCursorHand = useSelector(selectIsCursorHand);

    const isHistoryClicked = useSelector(selectIsHistoryClicked);

    const isBtnMinimapEnabled = useSelector(selectIsBtnMinimapEnabled);

    const isBtnNewTableClickEnabled = useSelector(
        selectIsBtnNewTableClickEnabled
    );
    const isBtnNewTableGrabEnabled = useSelector(
        selectIsBtnNewTableGrabEnabled
    );
    const isNewTableGrabStartedRef = useRef(false);

    const isBtnNewNoteClickEnabled = useSelector(
        selectIsBtnNewNoteClickEnabled
    );
    const isBtnNewNoteGrabEnabled = useSelector(selectIsBtnNewNoteGrabEnabled);
    const isNewNoteGrabStartedRef = useRef(false);

    const isBtnNewLineEnabled = useSelector(selectIsBtnNewLineEnabled);

    const isBtnNewAreaEnabled = useSelector(selectIsBtnNewAreaEnabled);

    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);
    const isBtnExportImageClickEnabled = useSelector(selectIsBtnExportImageClickEnabled);

    const isBtnPenEnabled = useSelector(selectIsBtnPenEnabled);
    const newPenProps = useSelector(selectNewPenProps);
    // Always-current copy of the pen menu props (color + session id). The mouse
    // handlers / draw closure capture state from when pen mode was enabled, so a
    // color picked afterwards would otherwise be ignored, and the session id is
    // needed to tag segments so "clear" can delete them.
    const newPenPropsRef = useRef(newPenProps);
    newPenPropsRef.current = newPenProps;

    const isBtnSearchEnabled = useSelector(selectIsBtnSearchEnabled);

    const isBtnRedGreenViewEnabled = useSelector(
        selectIsBtnRedGreenViewEnabled
    );

    // Grid lives only in preferences (editor.grid); null = 'none' keeps the
    // legacy shape the draw code and dep arrays below expect.
    const gridStyle = diagramPrefs?.grid === 'none' ? null : (diagramPrefs?.grid ?? null);

    const viewMode = useSelector(selectViewMode);
    // Same ref-mirror as canEditRef/inputModeRef above (review follow-up):
    // computeContentBoundsFromElements runs from handleElChange, which is
    // registered once inside the elements-observer's []-dep effect — a bare
    // closure read of the `viewMode` useSelector binding there would freeze
    // at whatever it was on the effect's FIRST run (i.e. mount-time) and
    // never see later toggles, silently applying stale keys/full clipping
    // semantics to every subsequent element/displayHeight mutation's bbox
    // recompute. This ref keeps it current; computeContentBoundsFromElements
    // reads viewModeRef.current instead of the outer `viewMode` closure.
    const viewModeRef = useRef(viewMode);
    useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);

    const [isHistoryClickedState, setIsHistoryClickedState] = useState(false);

    const exportImageBoxPropsRef = useRef({
        isPressed: false,
        x: 0,
        y: 0,
        boxWidth: 0,
        boxHeight: 0,
    });
    // TODO:
    // - if line is focused, then unfocus other elements on hover

    // ===================== 0. ELEMENTS HAS CHANGES | STATE | UPDATE LIVE ELEMENTS =====================
    // has changes from last previous value focusedIdsRef, if yes then update redis store

    const [columnNameState, setColumnNameState] = useState('');
    const [columnTypeState, setColumnTypeState] = useState('');
    const [columnState, setColumnState] = useState(null);

    // ===================== 0. ELEMENTS AND LINES | WITH REDIS =====================
    // elements, updates from/to redis store value
    const elementsRef = useRef({});
    // Previous Redux snapshot — used to diff which entries actually changed so
    // we only structuredClone the changed subset instead of the entire object.
    const prevElementsReduxRef = useRef(null);

    // elements changes, updates from/to redis store value
    const elementsChangesRef = useRef({});

    // lines, updates from/to redis store value
    const linesRef = useRef({});
    const prevLinesReduxRef = useRef(null);

    // areas, updates from/to redis store value
    const areasRef = useRef({});

    // ===================== 0. SELECTED IDS (UNIFIED) =====================
    // unified selection: array of ids of selected elements/areas/lines
    const focusedIdsRef = useRef([]);

    // ===================== 0. FOCUSED INSIDE ELEMENT =====================
    // focused inside element
    const [focusedInsideElementState, setFocusedInsideElementState] =
        useState(null);
    // Latest ref so the Yjs observeDeep callback (registered once per diagram)
    // can check which element is focused without being in its dep array.
    const focusedInsideElementStateRef = useRef(null);
    // Client-space {x, y} of the click that opened a note's text editor —
    // handleDocumentClick stashes it so EditableNote's mount effect can land
    // the caret exactly there instead of at end-of-text. A ref (not redux):
    // UPDATE_FOCUSED_INSIDE_ELEMENT's action shape has other consumers
    // (incl. a saga) this must not touch.
    //
    // Primary clear is consume-and-clear: EditableNote's mount effect calls
    // the onCaretPointConsumed prop (passed at the render site below) right
    // after using — or not using — this ref's value, exactly once per mount.
    // That's what makes a REMOUNT of the SAME note's editor safe: e.g. a
    // wheel-zoom's transient isScaling gate (part of EditableNote's own
    // render condition below) unmounts then remounts this component while
    // focusedInsideElementState never leaves 'note-text' — no state
    // transition for the effect below to observe — and without consume-
    // and-clear the remount would replay the ORIGINAL click's point instead
    // of end-of-text.
    //
    // The effect below is belt-and-braces on top of that: whenever
    // focusedInsideElementState isn't 'note-text' (the editor fully closed,
    // or a DIFFERENT note-text open never routed through this ref), null it
    // too — covers e.g. a future bug in the consume-and-clear wiring itself.
    // The two non-click 'note-text' producers (new-note click/grab-drop,
    // ~line 13408/14377) never touch this ref at all and don't need to:
    // both are only reachable after ENABLE_BTN_NEW_NOTE_CLICK/GRAB's reducer
    // (redux/diagram/index.js) has already called unfocusFocusedItems(),
    // which nulls focusedInsideElement — guaranteeing an intervening
    // non-'note-text' render (and hence a ref clear, belt-and-braces or
    // consume-and-clear) before their own dispatch(updateFocusedInsideElement
    // (..., 'note-text')) ever lands. If a future reducer edit drops that
    // unfocus call, this invariant breaks silently — grep for this comment
    // before changing ENABLE_BTN_NEW_NOTE_CLICK/GRAB's reducer branches.
    const noteOpenClickPointRef = useRef(null);
    useEffect(() => {
        focusedInsideElementStateRef.current = focusedInsideElementState;
        if (focusedInsideElementState?.insideElementType !== 'note-text') {
            noteOpenClickPointRef.current = null;
        }
    });
    // Bumped when the focused element's Yjs data changes so the column editor
    // re-renders with fresh flag values (isPK/isFK/isNN etc.) from elementsRef.
    const [focusedElementVersion, setFocusedElementVersion] = useState(0);

    // is focused inside element changed
    const [isFocusedInsideElementChanged, setIsFocusedInsideElementChanged] =
        useState(false);

    // ===================== 0. RELATED ELEMENTS FOR FOCUSED ELEMENTS AND LINES =====================
    // related or connected elements/lines to 'focusedIdsRef' (element-only ids)
    const focusedRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: new Set(),
        linesIds: [],
    });

    // related or connected elements to focused line (from focusedIdsRef)
    const focusedLineRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: new Set(),
    });

    // ===================== 0. HOVERED ELEMENT AND LINE =====================
    // hovered element
    const hoveredElementIdRef = useRef(null); // null | id

    // hovered insideElement
    const hoveredInsideElementRef = useRef(null); // null | { id, index }

    // hovered element header
    const hoveredElementIdHeaderRef = useRef(null); // null | id

    // hovered element column
    const hoveredColumnIdRef = useRef(null); // null | id
    const hoveredColumnIndexRef = useRef(null); // null | id

    // hovered line
    const hoveredLineIdRef = useRef(null); // null | id

    // hovered empty column
    const hoveredElementIdEmptyColumnRef = useRef(null); // null | id

    // hovered area
    const hoveredAreaIdRef = useRef(null); // null | id

    // search spotlight column hover (redux-driven, not mouse) — mirrors searchHoveredColumn
    const searchHoveredColumnRef = useRef(null); // null | { elementId, columnId }

    // canvas hover preview (spec 8b): identity of the currently-hovered note
    // link zone, so the mousemove handler only pokes noteLinkHoverStore on an
    // actual transition — same change-gating convention as every hoveredXRef
    // above, not a store write on every mousemove tick.
    const hoveredNoteLinkKeyRef = useRef(null); // null | string

    // ===================== 0. RELATED ELEMENTS FOR HOVERED ELEMENT AND LINE =====================
    // related or connected elements/lines to 'hoveredElementIdRef'
    const hoveredElementRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: new Set(),
        linesIds: [],
    });

    // related or connected elements to 'hoveredLineIdRef'
    const hoveredLineRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: new Set(),
    });

    // ===================== 0. NEW LINE 'FROM' / 'TO'  =====================
    const lineFromRef = useRef(null);
    const lineToRef = useRef(null);

    // ===================== 0. HOVERED CIRCLE OF ELEMENT  =====================
    // hovered circle for add new internal element in the end
    const hoveredCircleRef = useRef(null); // { elementId }

    // hovered circle area for show/hide 'circle' for add new internal element in the end
    const hoveredCircleAreaRef = useRef(null); // { elementsIds }

    // ===================== 0. HOVERED POINT OF ELEMENT  =====================
    // hovered point for add new line from element
    const hoveredPointRef = useRef(null); // { insideElementId, direction }

    // hovered point area for show/hide 'point' for add new line from element
    const hoveredPointAreaRef = useRef(null); // { insideElementsIds }

    // ===================== 0. CLICKED ELEMENT AND LINE =====================
    const alignmentLinePropsRef = useRef({});

    // clicked element by 'mousedown'. clear on 'click'
    const clickedElementIdRef = useRef(null);

    // clicked inside element inside main element by 'mousedown'. clear on 'click'
    const clickedInsideElementRef = useRef(null);

    // clicked line
    const clickedLineIdRef = useRef(null);

    // clicked area
    const clickedAreaIdRef = useRef(null);

    // ===================== 0. CLICKED CIRCLE OF ELEMENT =====================
    // clicked circle to add new row
    const clickedCircleElementIdRef = useRef(null);

    // ===================== 0. CLICKED POINT OF ELEMENT =====================
    // clicked column's point to add new line
    const clickedPointRef = useRef(null); // { elementId, insideElementId, insideElementIndex, direction });

    //??? ===================== 0.  =====================
    const [isStartedLineState, setIsStartedLineState] = useState(false);

    // ===================== 0. CANVAS =====================
    const canvasWrapRef = useRef(null); // don't need anymore
    const contextRef = useRef(null);

    // drawNote layout cache: id -> {text, width, isFontMonospace, blocks, lines}
    // — parsing + wrapping re-runs only when content/geometry/font change,
    // not every frame.
    const noteLayoutCacheRef = useRef(new Map());

    // drawNote's link-hover hit zones (spec 8b): id -> [{x, y, w, h, href}]
    // in WORLD coords, rebuilt every time that note's text pass runs (so it
    // always matches the just-painted glyphs). Same lifecycle as
    // noteLayoutCacheRef above and deliberately NOT pruned on note delete —
    // that cache isn't either (a deleted id's entry just goes stale and
    // unread, since the mousemove hit-test below only ever looks up ids
    // still present in elementsRef).
    const noteLinkZonesRef = useRef(new Map());

    const newPenCursorPointRef = useRef(null);

    const newPenSegmentRef = useRef({
        isPressed: false,
        points: [],
    });

    // ===================== 1. BOARD - ORIGIN XY / SCALE =====================
    // board origin {x, y} - offsets when you drag & move board | = (e.clientX)
    const originXYRef = useRef({ x: 0, y: 300 });

    // board scale
    const scaleRef = useRef(scale);

    // ===================== 1.1 COLLAB AWARENESS =====================
    // Geometry refs: written during the draw pass, read by drawRemoteHalos.
    const elementsGeomRef = useRef({});
    const areasGeomRef = useRef({});
    const linesGeomRef = useRef({});
    const columnsGeomRef = useRef({});

    // Held in state so RemoteCursors re-subscribes once the parent's effect
    // creates the awareness instance (which happens after Canvas's first render).
    const [diagramAwareness, setDiagramAwareness] = useState(() => getDiagramAwareness());

    // Always holds the latest draw callback so the awareness effect below can
    // call it without being in its dep array (avoids re-registering on every draw change).
    const drawRef = useRef(null);
    // Latest-ref wrappers for drawTable/drawSelectionBox so draw() stays stable
    // (plain functions recreated on every render can't go in draw's deps).
    const drawTableRef = useRef(null);
    const drawSelectionBoxRef = useRef(null);
    // Snapshot of all event-handler state, kept current on every render so
    // the two large event-listener effects can use [] dep arrays and never
    // re-register their listeners on state changes.
    const handlerStateRef = useRef({});

    // rAF handle for coalescing multiple draw() calls within the same frame.
    const rafIdRef = useRef(null);
    // Stable function (only closes over stable refs) — safe to use in any effect dep array.
    const scheduleDraw = useCallback(() => {
        if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            drawRef.current?.();
        });
    }, []);

    // is board scaling right now | timer for update isScaling state after pause 200ms
    const [isScaling, setIsScaling] = useState(false);
    const scalingTimerRef = useRef(null);

    const scalingRedrawTimerRef = useRef(null);
    const scalingRedrawCounterRef = useRef(0);

    // ===================== 2. SCALE NUMBERS REF =====================
    // saved to use without recalculating in render/distances/drawTable/drawNote
    const scaleNumbersRef = useRef({
        outlineOffset: 0,
        outlineWidth: 0,
        resizeHandleSize: 0,
        resizeHandleRadius: 0,
    });

    // ===================== 2. ELEMENTS MOVING - START XY =====================
    // start {x,y} of mouse on 'mousedown' event | = (e.clientX) | CHANGABLE ON MOUSEMOVE
    const startXYRef = useRef({ x: 0, y: 0 });

    // ===================== 3. MOUSE SELECTION BOX - START XY / CURRENT XY =====================
    // is selection started
    const isSelectionClickedRef = useRef(false); // ?? try to use isBoardClickedRef & isCursorHand

    // selection start {x,y} of mouse on 'mousedown' event | = ((e.clientX - originXY.x) / scale)
    const selectionStartXYRef = useRef({ x: 0, y: 0 });

    // selection current {x,y} of mouse on 'mousemove' event | = ((e.clientX - originXY.x) / scale)
    const selectionCurrentXYRef = useRef({ x: 0, y: 0 });

    // ===================== 4. BOARD MOVING =====================
    // is board clicked by 'mousedown'
    const isBoardClickedRef = useRef(false);

    // is element resizing
    const [isElementResizing, setIsElementResizing] = useState(false);

    // is board moving right now | timer for update isBoardMoving state after pause 200ms
    const [isBoardMoving, setIsBoardMoving] = useState(false);
    const boardMovingTimerRef = useRef(null);

    // is board started move by 'mousemove'
    const [isBoardMovingStarted, setIsBoardMovingStarted] = useState(false);

    // ===================== 7. ELEMENTS MOVING =====================
    // is element moving
    const [isMovingElementId, setIsMovingElementId] = useState(null);

    // ===================== 7. ITEM RESIZE  =====================
    // clicked item resize point by 'mousedown'. clear on 'click'
    const isItemResizeClickedRef = useRef(false);
    const resizeKeyRef = useRef(null);

    // ===================== =====================

    function calculateAndSetScaleNumbers(scale) {
        const scalePercents = calculateScaleProgress(scale);
        const resizeHandleCoef = calculateResizeHandleCoefValue(scalePercents);
        const resizeOffsetCoef = calculateResizeOffsetValue(scalePercents);

        // 24
        // 10
        const outlineOffset = (5 * resizeOffsetCoef) / scale;
        const outlineWidth = calculateOutlineWidth(scale);
        const relationLineWidth =
            scale < 0.1
                ? Math.min(outlineWidth, 15) * 0.7
                : Math.min(outlineWidth, 16);

        // 11
        const resizeHandleSize = (9 / scale) * resizeHandleCoef;
        // 3
        const resizeHandleRadius = (1.5 / scale) * resizeHandleCoef;

        scaleNumbersRef.current = {
            outlineOffset,
            outlineWidth,
            relationLineWidth,
            resizeHandleSize,
            resizeHandleRadius,
            selectionBoxLine: 1 / scale,
        };
    }

    // CanvasScrollbars geometry (spec: canvas scrollbars). World-space bbox
    // of every element (tables/notes/areas — see computeContentBoundsFromElements
    // below), cached here rather than recomputed per-draw-tick: it only
    // needs to change when element geometry actually changes (see the
    // elements-observer call sites in the Yjs subscription effect further
    // down), which is far rarer than draw() itself runs.
    const contentBoundsRef = useRef(null); // null | { minX, minY, maxX, maxY }

    // Mutable scratch rect computeViewportWorldRect writes into IN PLACE
    // every draw() tick, instead of allocating a fresh object every frame
    // (review minor (a)). Never handed to a store subscriber directly —
    // publishCanvasScrollGeometry clones its fields into a fresh object of
    // its own only when they've actually changed, so the store's published
    // snapshots stay immutable-by-replacement regardless of this reuse.
    const viewportScratchRef = useRef({ minX: 0, minY: 0, maxX: 0, maxY: 0 });

    function calculateVisibleElementsAndLines() {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const visibleElementsIds = new Set();
        const visibleAreaIds = [];
        const visibleLinesIds = new Set();
        // for alignment lines
        const visibleViewportElementsIds = [];

        // if elements / lines points (x1,y1  x2,y2) includes in (startXY, endXY) frame =>
        // => add to visibleElementsIds/visibleLinesIds

        const newOriginX = originXYRef.current.x;
        const newOriginY = originXYRef.current.y;

        const newWindowWidth = windowWidth;
        const newWindowHeight = windowHeight;

        // const startX = (0 - newOriginX) / scaleRef.current - 1000;
        // const startY = (0 - newOriginY) / scaleRef.current - 1000;
        // const endX = (newWindowWidth - newOriginX) / scaleRef.current + 1000;
        // const endY = (newWindowHeight - newOriginY) / scaleRef.current + 1000;

        // bigger viewport area for 'visible elements/lines'
        const frameStartX = (0 - newOriginX) / scaleRef.current - 2000;
        const frameStartY = (0 - newOriginY) / scaleRef.current - 2000;
        const frameEndX =
            (newWindowWidth - newOriginX) / scaleRef.current + 2000;
        const frameEndY =
            (newWindowHeight - newOriginY) / scaleRef.current + 2000;

        // viewport area for 'alignment lines'
        const viewportStartX = (0 - newOriginX) / scaleRef.current;
        const viewportStartY = (0 - newOriginY) / scaleRef.current;
        const viewportEndX = (newWindowWidth - newOriginX) / scaleRef.current;
        const viewportEndY = (newWindowHeight - newOriginY) / scaleRef.current;

        // elements
        for (const key in elementsRef.current) {
            const element = elementsRef.current[key];
            const { id, x, y, width, height } = element;

            const elementMinX = x;
            const elementMaxX = x + width;
            const elementMinY = y;
            const elementMaxY = y + height;

            // frameX < x + width &&
            // frameX + frameWidth > x &&
            // frameY < y + height &&
            // frameY + frameHeight > y

            // if (
            // 	(elementMinX >= startX && elementMinX <= endX || elementMaxX >= startX && elementMaxX <= endX) &&
            // 	(elementMinY >= startY && elementMinY <= endY || elementMaxY >= startY && elementMaxY <= endY)
            // ) {
            if (
                frameStartX <= elementMaxX &&
                frameEndX >= elementMinX &&
                frameStartY <= elementMaxY &&
                frameEndY >= elementMinY
            ) {
                if (element.type === 'area') {
                    visibleAreaIds.push(id);
                } else {
                    // visibleElementsIds.push(element.name);
                    visibleElementsIds.add(id);

                    if (
                        viewportStartX <= elementMaxX &&
                        viewportEndX >= elementMinX &&
                        viewportStartY <= elementMaxY &&
                        viewportEndY >= elementMinY
                    ) {
                        visibleViewportElementsIds.push(id);
                    }
                }
            }
        }

        // lines
        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            let { id } = lineObject;
            let path = lineObject.path;

            if (!path) {
                const lineData = buildLineData(lineObject);
                if (!lineData) {
                    continue;
                }
                path = _buildLinePath(lineData);
            }

            if (!Array.isArray(path)) {
                // Defensive: skip a line whose path can't be built (e.g. an orphan
                // line referencing a deleted element) instead of crashing the recalc.
                continue;
            }
            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines, points);

            const lineMinX = bounds.minX;
            const lineMaxX = bounds.maxX;
            const lineMinY = bounds.minY;
            const lineMaxY = bounds.maxY;

            if (
                frameStartX <= lineMaxX &&
                frameEndX >= lineMinX &&
                frameStartY <= lineMaxY &&
                frameEndY >= lineMinY
            ) {
                visibleLinesIds.add(id);
            }
        }

        // result
        visibleElementsIdsRef.current = visibleElementsIds;
        visibleAreaIdsRef.current = visibleAreaIds;
        visibleLinesIdsRef.current = visibleLinesIds;
        visibleViewportElementsIdsRef.current = visibleViewportElementsIds;
    }

    // CanvasScrollbars: world bbox of every element with a valid geometry
    // (tables/notes/areas all carry x/y/width/height directly on the
    // element, same fields calculateVisibleElementsAndLines reads above;
    // anything without finite width/height — e.g. a not-yet-built
    // pen-segment — is skipped rather than poisoning the bbox with NaN).
    //
    // Tables use getTableEffectiveHeight (review IMPORTANT 3), not the raw
    // `height` field: a clipped table's on-screen footprint is its
    // EFFECTIVE height, same condition every other getTableEffectiveHeight
    // call site in this file already uses — clipping is a 'full'-view-only
    // feature, 'keys' view always renders at the raw `height` regardless of
    // `displayHeight` (see e.g. the drawTable callers a few hundred lines
    // down: `viewMode === 'full' ? getTableEffectiveHeight(el) : height`).
    //
    // Called from the elements-observer effect below (see BBOX_FIELDS —
    // NOT the same gate as GEOM_FIELDS's viewport-culling recalc) and from
    // the [viewMode] effect (toggling keys/full changes clipped tables'
    // effective height with no element data changing at all), NOT from
    // draw(), so it stays O(elements) work done on structural/view-mode
    // change instead of every animation frame.
    function computeContentBoundsFromElements() {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const id in elementsRef.current) {
            const el = elementsRef.current[id];
            if (!Number.isFinite(el?.x) || !Number.isFinite(el?.y) || !Number.isFinite(el?.width)) {
                continue;
            }
            // viewModeRef, not the outer `viewMode` closure (review follow-up):
            // this function is called from handleElChange, registered once
            // inside the elements-observer's []-dep effect — a bare `viewMode`
            // read there would freeze at mount-time and never see later
            // keys/full toggles. See viewModeRef's declaration for the full
            // rationale (same ref-mirror shape as canEditRef/inputModeRef).
            const effectiveHeight = (el.type === 'table' && viewModeRef.current === 'full')
                ? getTableEffectiveHeight(el)
                : el.height;
            if (!Number.isFinite(effectiveHeight)) continue;
            if (el.x < minX) minX = el.x;
            if (el.y < minY) minY = el.y;
            if (el.x + el.width > maxX) maxX = el.x + el.width;
            if (el.y + effectiveHeight > maxY) maxY = el.y + effectiveHeight;
        }
        contentBoundsRef.current = minX === Infinity ? null : { minX, minY, maxX, maxY };
    }

    // CanvasScrollbars: world-space rect currently on screen, same formula
    // calculateVisibleElementsAndLines above uses for its own viewport
    // bounds (viewportStart = (0 - origin) / scale). Cheap O(1) — safe to
    // call every draw() tick so the published viewport (and hence the
    // scrollbar thumbs) tracks pan/zoom live. Writes into viewportScratchRef
    // IN PLACE (review minor (a)) instead of allocating a fresh object every
    // frame — publishCanvasScrollGeometry is responsible for cloning it into
    // an immutable snapshot on the (much rarer) frames where it actually
    // changed; see that function's own comment.
    function computeViewportWorldRect() {
        const scale = scaleRef.current || 1;
        const originX = originXYRef.current.x;
        const originY = originXYRef.current.y;
        const rect = viewportScratchRef.current;
        rect.minX = (0 - originX) / scale;
        rect.maxX = (window.innerWidth - originX) / scale;
        rect.minY = (0 - originY) / scale;
        rect.maxY = (window.innerHeight - originY) / scale;
        return rect;
    }

    // Sole publish call site — invoked at the end of draw() (see below).
    // Reads the cached contentBoundsRef (fresh as of the last structural
    // element/view-mode change) and a freshly computed viewport rect every
    // time.
    function publishScrollGeometry() {
        publishCanvasScrollGeometry({
            contentBounds: contentBoundsRef.current,
            viewport: computeViewportWorldRect(),
        });
    }

    // Hard pan limits: the viewport's world window may extend at most
    // PAN_LIMIT_FRACTION × viewportSizeWorld beyond content bounds on EACH
    // side — PER AXIS: the X clamp's slack is a fraction of the X
    // viewport's own world WIDTH, the Y clamp's slack is a fraction of the
    // Y viewport's own world HEIGHT (one full viewport width/height of
    // slack per side, per-product requirement — a single tunable number).
    // A "share one pad across both axes, based on whichever dimension is
    // larger" variant was considered and explicitly rejected in favor of
    // this per-axis form — see clampOriginAxis's own module comment
    // (scrollbarMath.js) for the derivation. Reads the SAME cached
    // contentBoundsRef every other scrollbar geometry call reads (no
    // recompute per mousemove — this is called from hot per-tick pan
    // paths). viewportSizeWorld is recomputed fresh
    // (window.inner{Width,Height}/scale) rather than pulled from
    // viewportScratchRef, so it's exactly current for THIS tick's scale
    // regardless of whether draw() has run yet this frame.
    const PAN_LIMIT_FRACTION = 1;
    function clampOriginForAxis(axis, desiredOrigin) {
        const bounds = contentBoundsRef.current;
        const scale = scaleRef.current || 1;
        const viewportSizeWorld = (axis === 'x' ? window.innerWidth : window.innerHeight) / scale;
        return clampOriginAxis({
            desiredOrigin,
            contentMin: bounds ? (axis === 'x' ? bounds.minX : bounds.minY) : null,
            contentMax: bounds ? (axis === 'x' ? bounds.maxX : bounds.maxY) : null,
            viewportSizeWorld,
            scale,
            padFactor: PAN_LIMIT_FRACTION,
        });
    }

    function deriveIsRelated() {
        const els = elementsRef.current;
        const lines = linesRef.current;
        for (const elId in els) {
            const el = els[elId];
            if (el.type !== 'table' || !el.columns) continue;
            for (const colId in el.columns) {
                el.columns[colId].isRelated = false;
            }
        }
        for (const lineId in lines) {
            const line = lines[lineId];
            const fromColId = line.sourceColumnId ?? line.fromColumnId ?? line.fromInsideElementId;
            const toColId = line.targetColumnId ?? line.toColumnId ?? line.toInsideElementId;
            const fromEl = els[line.sourceElementId ?? line.fromElementId];
            const toEl = els[line.targetElementId ?? line.toElementId];
            if (fromEl?.columns?.[fromColId]) fromEl.columns[fromColId].isRelated = true;
            if (toEl?.columns?.[toColId]) toEl.columns[toColId].isRelated = true;
        }
    }

    function drawTableName(element) {
        const isStartedNewLine =
            lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const ctx = contextRef.current;

        // let { id, x, y, width, height, value: { header: { name } } } = element;
        let {
            id,
            x,
            y,
            name,
        } = element;
        let width = TABLE_NAME_WIDTH;
        let height = TABLE_NAME_HEIGHT;

        // ===================== 0. FOCUSED TABLE | OUTLINE FRAME =====================
        if (
            // table is focused
            focusedIdsRef.current.includes(id) ||
            // table is hovered and its not new line drawing
            (hoveredElementIdRef.current === id &&
                !isStartedNewLine &&
                !isStartedNewPenSegment)
        ) {
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

            ctx.beginPath();
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset,
                width + 2 * scaleNumbersRef.current.outlineOffset,
                height + 2 * scaleNumbersRef.current.outlineOffset,
                0.75
            );

            ctx.stroke();

            // ctx.shadowColor = 'transparent';
            // ctx.fillStyle = '#39424c'; // canvas background
            // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
            // ctx.stroke();
        }

        // ===================== TABLE SHADOW =====================
        // Set shadow properties
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. TABLE BACKGROUND | RECTANGLE =====================
        // #f7e968
        // #e7db6d
        // ctx.fillStyle = 'rgba(27, 106, 242, 0.55)';
        ctx.fillStyle = 'lightgreen'; // : 'lightgreen';

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 0.5);
        ctx.fill();

        // Reset shadow after drawing the table
        ctx.shadowColor = 'transparent';

        // ===================== TABLE | NAME =====================
        ctx.fillStyle = 'white';
        ctx.textAlign = 'left';
        // ctxSetFont(ctx, `500 30px Roboto`);
        ctxSetFont(ctx, `500 30px Roboto, sans-serif`);
        ctx.fillText(name, x + 38, y + 60);
    }

    function drawTableKey(element, exportImgCanvas = null) {
        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        const scale = scaleRef.current;
        const scaleNums = scaleNumbersRef.current;

        // let { id, x, y, width: tableWidth, height: tableHeight, value: { header: { name }, columns } } = element;
        let {
            id,
            x,
            y,
            width: tableWidth,
            name,
            columns,
            columnsOrder: keysViewColumnsOrder,
            color,
            border,
        } = element;

        const columnsKeys = orderedColumnKeys(columns, keysViewColumnsOrder).filter(
            (columnKey) =>
                columns[columnKey]?.isPK ||
                columns[columnKey]?.isFK ||
                columns[columnKey]?.isRelated
        );
        const columnsCount = columnsKeys?.length || 0;

        const tableHeight =
            TABLE_HEADER_HEIGHT +
            HEADER_BODY_MARGIN +
            (columnsCount + 1) * TABLE_ROW_HEIGHT +
            TABLE_BORDER_RADIUS +
            1;

        // ===================== 0. FOCUSED TABLE | OUTLINE FRAME =====================
        if (
            !isBtnNewAreaEnabled &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // table is selected by mouse selection box
            (selectedElementsIdsRef.current.includes(id) ||
                // table is focused
                focusedIdsRef.current.includes(id) ||
                // table is hovered and its not new line drawing
                (hoveredElementIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment))
        ) {
            // light blue when the frame comes from the search spotlight preview
            ctx.strokeStyle = isBtnSearchEnabled && focusedIdsRef.current.includes(id)
                ? 'rgba(93, 158, 255, 0.4)'
                // : 'rgba(45, 52, 65, 1)';
                : '#262b33';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;

            if (
                hoveredElementIdRef.current === id &&
                !focusedIdsRef.current.includes(id) &&
                !selectedElementsIdsRef.current.includes(id)
            ) {
                // ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
                // ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1; // ASAP LAST
                ctx.lineWidth =
                    scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;
                // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';
            }

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                tableWidth + 2 * scaleNums.outlineOffset,
                tableHeight + 2 * scaleNums.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            // ctx.shadowColor = 'transparent';

            // ctx.fillRect(
            //     x - scaleNums.outlineOffset,
            //     y - scaleNums.outlineOffset - 0.5,
            //     tableWidth + 2 * scaleNums.outlineOffset,
            //     tableHeight + 2 * scaleNums.outlineOffset + 1
            // );
            ctx.strokeRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                tableWidth + 2 * scaleNums.outlineOffset,
                tableHeight + 2 * scaleNums.outlineOffset + 1
            );
            // ctx.stroke();
        }

        // ===================== 2. TABLE SHADOW =====================
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. TABLE BACKGROUND | RECTANGLE =====================
        ctx.beginPath();
        ctx.roundRect(x, y, tableWidth, tableHeight, TABLE_BORDER_RADIUS);

        ctx.fillStyle = theme.table.body.background;
        // -----------------------------------------------------------
        // let headerColor = 'rgba(14, 15, 15, 1)';
        // let headerColor = 'rgba(15, 15, 16, 1)';
        let headerColor = 'rgb(17, 18, 18)';

        if (isBtnExportImageEnabled && exportImgCanvas) {
            headerColor = 'rgb(9, 10, 11)';
        }

        if (color) {
            switch (color) {
                case 'blue': {
                    headerColor = '#0b2a50ff';
                    break;
                }
                case 'green': {
                    headerColor = '#033c33ff';
                    break;
                }
                case 'orange': {
                    headerColor = '#451d01';
                    break;
                }
                case 'yellow': {
                    headerColor = '#573900';
                    break;
                }
                case 'pink': {
                    headerColor = '#660928';
                    break;
                }
                case 'purple': {
                    headerColor = '#34054aff';
                    break;
                }
                case 'violet': {
                    headerColor = '#61044eff';
                    break;
                }
                case 'red': {
                    headerColor = '#6e002eff';
                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }

        // const bodyColor = 'rgb(19.5, 21, 23)';
        const bodyColor = 'rgb(20, 21, 22)';

        ctx.fillStyle = bodyColor;
        ctx.lineWidth = scaleNums.outlineWidth + 0.5;
        ctx.strokeStyle = 'rgb(34, 40, 47)';
        // -----------------------------------------------------------

        ctx.stroke();
        ctx.fill();
        ctx.strokeStyle = 'rgba(81, 92, 126, 0)';

        ctx.lineWidth = 1;

        // Reset shadow after drawing the table
        ctx.shadowColor = 'transparent';


        if (scale > 0.15) {

            // ===================== 4. TABLE HEADER =====================
            // ===================== 4.1. TABLE HEADER | BACKGROUND =====================
            // draw background rectangle
            ctx.fillStyle = headerColor;

            ctx.beginPath();
            ctx.moveTo(x + TABLE_BORDER_RADIUS, y); // Верхній лівий кут заголовка
            ctx.lineTo(x + tableWidth - TABLE_BORDER_RADIUS, y); // Верхній правий кут заголовка
            ctx.quadraticCurveTo(
                x + tableWidth,
                y,
                x + tableWidth,
                y + TABLE_BORDER_RADIUS
            ); // Верхня права дуга заголовка
            ctx.lineTo(x + tableWidth, y + TABLE_HEADER_HEIGHT); // Нижня права сторона заголовка
            ctx.lineTo(x, y + TABLE_HEADER_HEIGHT); // Нижня ліва сторона заголовка
            ctx.lineTo(x, y + TABLE_BORDER_RADIUS); // Верхня ліва сторона заголовка
            ctx.quadraticCurveTo(x, y, x + TABLE_BORDER_RADIUS, y); // Верхня ліва дуга заголовка
            ctx.fill();

            // -----------------------------------------------------------
            let headColor = '#333b44';
            let cellsColor = '#444e5a';
            let linesColor = '#262c31';

            if (color) {
                switch (color) {
                    case 'blue': {
                        headColor = '#0f325a';
                        cellsColor = '#15467d';
                        linesColor = '#071b31';
                        break;
                    }
                    case 'green': {
                        headColor = '#10533c';
                        cellsColor = '#14694c';
                        linesColor = '#0b3b2a';
                        break;
                    }
                    case 'orange': {
                        // headerColor = '#451d01'; // //562605 662e09
                        // bodyColor = '#6a2c03';
                        // headColor = '#40085a';
                        // cellsColor = '#540b76';
                        // linesColor = '#300643';
                        break;
                    }
                    case 'yellow': {
                        // headerColor = '#573900';
                        // bodyColor = '#6d4700'; //ad7000
                        // headColor = '#40085a';
                        // cellsColor = '#540b76';
                        // linesColor = '#300643';
                        break;
                    }
                    case 'pink': {
                        // headColor = '#760b2f';
                        // cellsColor = '#900e39';
                        // linesColor = '#660928';

                        break;
                    }
                    case 'purple': {
                        headColor = '#40085a';
                        cellsColor = '#540b76';
                        linesColor = '#300643';
                        break;
                    }
                    case 'violet': {
                        headColor = '#4d064d';
                        cellsColor = '#6c0c6c';
                        linesColor = '#350235';
                        break;
                    }
                    case 'red': {
                        headColor = '#661320';
                        cellsColor = '#821829';
                        linesColor = '#490e16';
                        break;
                    }
                    case 'default': {
                        break;
                    }
                    default:
                        break;
                }
            }
            // -----------------------------------------------------------

            if (scale > 0.35) {

                // ===================== 4.1. TABLE HEADER | ICON =====================
                ctx.save(); // Зберігаємо поточний стан канвасу
                // ctx.scale(0.36, 0.36); // Масштабування шляху для більшого розміру
                ctx.scale(0.38, 0.38); // Масштабування шляху для більшого розміру

                // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.36, (y + 11.5) / 0.36); // Переміщуємо початкову точку малювання
                // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.38, (y + 11.5) / 0.38); // Переміщуємо початкову точку малювання
                ctx.translate((x + TABLE_BORDER_RADIUS + 8) / 0.38, (y + 10.25) / 0.38); // Переміщуємо початкову точку малювання
                ctx.strokeStyle = theme.controls.background;
                ctx.lineWidth = 2; // Лінії мають бути тонкими після масштабування

                // ctx.fillStyle = '#333b44';
                ctx.fillStyle = headColor;
                ctx.beginPath();
                ctx.fill(_PATH2D_TABLE_ICON_HEADER);

                // ctx.fillStyle = '#444e5a';
                ctx.fillStyle = cellsColor;
                ctx.beginPath();
                ctx.fill(_PATH2D_TABLE_ICON_CELLS);

                // ctx.strokeStyle = '#262c31';
                ctx.strokeStyle = linesColor;
                ctx.beginPath();
                ctx.stroke(_PATH2D_TABLE_ICON_LINES);

                ctx.restore(); // Відновлюємо попередній стан канвасу


                // ===================== 4.1.2. TABLE HEADER | DESCRIPTION ICON =====================
                // if (element.description || element.value.isTableWithIndexes) {
                if (element.description) {

                    ctxSetFont(ctx, `600 21px Inter, sans-serif`);
                    const tableNameWidth = ctxMeasure(ctx, name);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.55, 0.55); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                    ctx.translate((x - 264 + tableWidth / 2 + tableNameWidth / 2 + 5.5 - 0.3) / 0.55, (y - 166 + 0.25) / 0.55); // Переміщуємо початкову точку малювання

                    // ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.lineWidth = 0; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.header.color;
                    ctx.strokeStyle = theme.table.header.color;

                    ctx.stroke(_PATH2D_DESCRIPTION_ICON);
                    ctx.fill(_PATH2D_DESCRIPTION_ICON);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

            }
            // ===================== 4.1. TABLE HEADER | TEXT =====================
            ctx.textAlign = 'center';
            // ctx.fillStyle = theme.table.header.color;
            // ctxSetFont(ctx, `600 21px Inter, sans-serif`);
            ctx.fillStyle = 'rgba(201, 217, 220, 1)';
            ctxSetFont(ctx, `600 20px Inter, sans-serif`);

            ctx.fillText(name, x + tableWidth / 2, y + 27);

            // ===================== 5. TABLE HEADER/BODY PADDING =====================
            ctx.fillStyle = bodyColor;
            ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 4);
        }

        if (scale > 0.15) {

            // ===================== 6. TABLE BODY =====================
            // ===================== 6. TABLE COLUMNS =====================
            for (let index = 0; index < columnsKeys.length; index++) {
                const key = columnsKeys[index];
                const column = columns[key];
                const columnY =
                    y +
                    TABLE_HEADER_HEIGHT +
                    index * TABLE_ROW_HEIGHT +
                    HEADER_BODY_MARGIN;

                // column's background is light grey
                const isHoveredBgStyle =
                    !isElementResizing &&
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment && // hovered table and hovered column
                    ((hoveredElementIdRef.current === id &&
                        hoveredInsideElementRef.current?.id === column.id) ||
                        // hovered column's point
                        hoveredPointRef.current?.insideElementId === column.id);

                // search spotlight column hover (redux-driven) — its own style,
                // distinct from the mouse hover; a hovered non-key column simply
                // has no visual in keys mode
                const isSearchHoveredBgStyle =
                    searchHoveredColumnRef.current?.elementId === id &&
                    searchHoveredColumnRef.current?.columnId === column.id;

                // column's background is black
                const isFocusedBgStyle =
                    focusedInsideElementStateRef.current?.elementId === id &&
                    focusedInsideElementStateRef.current?.insideElementId === column.id;

                // column's background is black
                const isRelatedToFocusedElementsBgStyle =
                    focusedRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredElementBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isRelatedToFocusedLineBgStyle =
                    focusedLineRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredLineBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isColumnsFromToNewLineBgStyle =
                    // new line's column 'from'
                    (lineFromRef.current?.elementId === id &&
                        lineFromRef.current?.insideElementId === column.id) ||
                    // new line's column 'to'
                    (hoveredElementIdRef.current === id &&
                        hoveredInsideElementRef.current?.id === column.id &&
                        lineFromRef.current?.insideElementId &&
                        lineFromRef.current?.insideElementId !==
                        hoveredInsideElementRef.current?.id);

                // ===================== 6.1. TABLE COLUMNS | START DRAWING =====================
                const columnColor = bodyColor;
                // const focusedColumnColor = 'rgb(19, 22, 25)';
                const focusedColumnColor = 'rgb(31, 33, 36)';
                // const hoveredColumnColor = 'rgba(26, 30, 34, 1)';
                // const hoveredColumnColor = 'rgb(29, 31, 34)';
                const hoveredColumnColor = 'rgb(22, 24, 27)';
                // light blue — search-spotlight column hover (distinct from mouse hover)
                const searchHoveredColumnColor = 'rgba(93, 158, 255, 0.28)';

                ctx.fillStyle =
                    // search-spotlight hover wins — explicit intent from the search list
                    isSearchHoveredBgStyle
                        ? searchHoveredColumnColor
                        // selection / focus / new-line styles win over hover
                        : (isFocusedBgStyle ||
                            isRelatedToFocusedElementsBgStyle ||
                            isRelatedToFocusedLineBgStyle ||
                            isColumnsFromToNewLineBgStyle) &&
                            !isBtnExportImageEnabled
                            ? focusedColumnColor
                            // the directly-hovered column shows the hover color even when it
                            // has connected lines — a column related to the hovered element/line
                            // must not mask the direct hover on that same column.
                            : (isRelatedToHoveredElementBgStyle ||
                                isRelatedToHoveredLineBgStyle) &&
                                !isStartedNewLine &&
                                !isBtnExportImageEnabled
                                ? focusedColumnColor
                                : isHoveredBgStyle
                                    ? hoveredColumnColor
                                    : columnColor;

                // : isHoveredBgStyle
                //     ? hoveredColumnColor
                //     // other columns merely related to the hovered element/line
                //     : (isRelatedToHoveredElementBgStyle ||
                //         isRelatedToHoveredLineBgStyle) &&
                //         !isBtnExportImageEnabled
                //         ? focusedColumnColor
                //         : columnColor;



                // ===================== 6.7. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON TABLE HOVER | (PURPLE) =====================
                let isColumnLineHovered = false;

                const isColumnRelatedToFocusedElements =
                    // column is related to focused elements
                    focusedRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    ) &&
                    // parent element is not focused
                    !focusedIdsRef.current.includes(id);

                const isColumnRelatedToFocusedLine =
                    // column is related to focused line
                    focusedLineRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    ) &&
                    // no selection (hover uses only when there is no selection)
                    !focusedIdsRef.current.length;

                const isColumnRelatedToHoveredElements =
                    // column is related to hovered element
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    ) &&
                    // parent element is not hovered
                    hoveredElementIdRef.current !== id &&
                    // no selection (hover uses only when there is no selection)
                    !focusedIdsRef.current.length;

                const isColumnRelatedToHoveredLine =
                    // column is related to focused line
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    ) &&
                    // no selection (hover uses only when there is no selection)
                    !focusedIdsRef.current.length &&
                    // no hovered element
                    hoveredElementIdRef.current !== id;

                if (
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment && // column is related to focused elements
                    (isColumnRelatedToFocusedElements ||
                        // column is related to hovered element
                        isColumnRelatedToHoveredElements ||
                        // column is related to focused line
                        isColumnRelatedToFocusedLine ||
                        // column is related to hovered line
                        isColumnRelatedToHoveredLine)
                ) {
                    isColumnLineHovered = true;

                    // const columnY =
                    //     y +
                    //     TABLE_HEADER_HEIGHT +
                    //     index * TABLE_ROW_HEIGHT +
                    //     HEADER_BODY_MARGIN;

                    // ctx.strokeStyle = '#8b5fff';
                    // // ctx.fillStyle = '#cc9ff8a9';
                    // ctx.fillStyle = '#a46ddc8e';
                    // ctx.fillStyle = '#a46ddc76';
                    // ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';

                    // // ctx.lineWidth = scaleNums.outlineWidth + 0.7;
                    // ctx.lineWidth = scaleNums.outlineWidth + 0.1;

                    // ctx.beginPath();
                    // ctx.roundRect(
                    //     x + 1,
                    //     columnY,
                    //     tableWidth - 2,
                    //     TABLE_ROW_HEIGHT,
                    //     3
                    // );
                    // // ctx.stroke();
                    // ctx.fill();

                    // // ---------------
                    // // const radius = 5;
                    // // const smallRadius = 2.5;

                    // // const radius = 5;
                    // // const smallRadius = 3.25;

                    // // const leftCenterX = x + 1;
                    // // const rightCenterX = x - 1 + tableWidth;
                    // // const centerY = columnY + TABLE_ROW_HEIGHT / 2;

                    // // // left circle, big one
                    // // ctx.beginPath();
                    // // ctx.fillStyle = '#8b5fff';
                    // // ctx.arc(leftCenterX, centerY, radius, 0, 2 * Math.PI); // Малюємо коло
                    // // ctx.fill();

                    // // // left circle, small one
                    // // ctx.beginPath();
                    // // ctx.fillStyle = '#e6e6e6';
                    // // ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI); // Малюємо коло
                    // // ctx.fill();

                    // // // right circle, big one
                    // // ctx.beginPath();
                    // // ctx.fillStyle = '#8b5fff';
                    // // ctx.arc(rightCenterX, centerY, radius, 0, 2 * Math.PI); // Малюємо коло
                    // // ctx.fill();

                    // // // right circle, small one
                    // // ctx.beginPath();
                    // // ctx.fillStyle = '#e6e6e6';
                    // // ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI); // Малюємо коло
                    // // ctx.fill();
                }


                if (isColumnLineHovered) {
                    // ctx.fillStyle = 'rgba(49, 60, 120, 0.7)';
                    ctx.fillStyle = 'rgb(31, 33, 36)';
                }

                ctx.beginPath();
                ctx.roundRect(x, columnY + 1, tableWidth, TABLE_ROW_HEIGHT - 2, 3);
                ctx.fill();

                let columnNameFontSize = 400;
                ctxSetFont(ctx, `${columnNameFontSize} 18.4px Inter, sans-serif`);

                // ===================== 6.2. TABLE COLUMNS | PRIMARY KEY & FOREIGN KEY =====================
                if (column.isPK || column.isFK) {
                    if (column.isPK) {
                        columnNameFontSize = 700;
                    }
                    ctxSetFont(ctx, `${columnNameFontSize} 18.4px Inter, sans-serif`);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x + 4, columnY + 8.5); // Переміщуємо початкову точку малювання
                    // ctx.translate(x + 5.5, columnY + 8.5); // Переміщуємо початкову точку малювання
                    ctx.translate(x + 4, columnY + 8.5 + 1.2); // Переміщуємо початкову точку малювання

                    ctx.scale(0.75, 0.75); // Масштабування шляху для більшого розміру
                    ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування


                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#bd7bff';
                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#8b76ff';  // b092ff'; // a784ff
                    ctx.fillStyle = column.isPK ? '#edb855' : '#7b8fff';  // b092ff'; // a784ff // 7b7bff

                    ctx.fill(_PATH2D_KEY_ICON);

                    ctx.stroke(_PATH2D_KEY_ICON);
                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

                // ===================== 6.2.1. TABLE COLUMNS | DESCRIPTION ICON =====================
                // if (column.description || column.defaultValue || column.enumValues || column.generatedValue || column.checkValue) {
                if (column.description) {
                    const columnNameWidth = ctxMeasure(ctx, column.name);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.525, 0.525); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                    // ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 4.5) / 0.55, (columnY - 168) / 0.55); // Переміщуємо початкову точку малювання
                    // ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 5) / 0.55, (columnY - 168) / 0.55); // Переміщуємо початкову точку малювання
                    ctx.translate((x - 264 + 25 + columnNameWidth + 5 + 11) / 0.525, (columnY - 168 + 9) / 0.525); // Переміщуємо початкову точку малювання

                    // ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.lineWidth = 0; // Лінії мають бути тонкими після масштабування

                    // ctx.fillStyle = theme.table.header.color;
                    // ctx.strokeStyle = theme.table.header.color;
                    ctx.fillStyle = theme.table.body.row.name.color;
                    ctx.strokeStyle = theme.table.body.row.name.color;

                    ctx.stroke(_PATH2D_DESCRIPTION_ICON);
                    ctx.fill(_PATH2D_DESCRIPTION_ICON);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

                // ===================== 6.3. TABLE COLUMNS | NAME =====================
                ctx.textAlign = 'left';
                ctx.fillStyle = theme.table.body.row.name.color;

                ctxSetFont(ctx, `${columnNameFontSize} 18.4px Inter, sans-serif`);

                // ctx.fillText(column.name, x + 28, columnY + 23);
                // ctx.fillText(column.name, x + 25, columnY + 23);
                ctx.fillText(column.name, x + 25, columnY + 23 + 1.8);

                Math.random() === 2 && drawTextWithLetterSpacing();
                Math.random() === 2 && drawTextRight();

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE  =====================
                // ===================== 6.4. TABLE COLUMNS | NULLABLE       =====================
                // ===================== 6.4. TABLE COLUMNS | ENUM           =====================
                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================

                let widthGenerated = 17 - 1;
                let widthDef = 17;
                let widthNN = 25;
                let widthEnum = 15.25;
                let widthAutoInc = 15.5;
                let distanceTextInternal = 4.35;
                let distanceBetween = 4.5; // or 8, like with index and nn

                let endX = x + tableWidth - 28; // = tableEnd - indexWidth with 2x distance
                let positionNext = endX;

                // ===================== 6.4. TABLE COLUMNS | GENERATED VALUE =====================
                if (column.generatedValue) {
                    ctx.fillStyle = '#299ec2';

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthGenerated, columnY + 23 - 14 + 1.7 + 0.75, widthGenerated, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';

                    ctx.fillText('G', positionNext - distanceTextInternal, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthGenerated + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE =====================
                if (column.defaultValue) {
                    ctx.fillStyle = '#b36525';

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthDef + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthDef - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';

                    ctx.fillText('D', positionNext - distanceTextInternal - 0.4, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthDef + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | NULLABLE =====================
                if (column.isNotNull) {
                    ctx.fillStyle = '#4254afff';

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthNN + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthNN - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';

                    ctx.fillText('NN', positionNext - distanceTextInternal - 0.8, columnY + 23 - 2 + 2 - 0.6);

                    positionNext -= widthNN + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | ENUM =====================
                if (column.isEnum) {
                    ctx.fillStyle = '#dca745ff';

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthEnum + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthEnum - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';

                    ctx.fillText('E', positionNext - distanceTextInternal - 0.4, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthEnum + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================
                if (column.isAutoIncrement) {
                    ctx.fillStyle = '#3eb372ff';

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthAutoInc + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthAutoInc - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 15px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';

                    ctx.fillText('+', positionNext - distanceTextInternal + 2 - 0.2, columnY + 23 - 2 + 2 + 0.4);

                    positionNext -= widthAutoInc + distanceBetween;
                }


                // ===================== 6.4.2. TABLE COLUMNS | TYPE =====================
                // if (
                //     isStartedLineState ||
                //     isBoardMovingStarted ||
                //     isBoardMoving ||
                //     isScaling ||
                //     isMovingElementId ||
                //     isElementResizing // ||

                //     // !focusedInsideElementState ||
                //     // focusedInsideElementState?.elementId !== id ||
                //     // focusedInsideElementState?.insideElementId !== column.id
                // ) {
                ctx.fillStyle = theme.table.body.row.type.color;

                // POINT 1
                if (color) {
                    ctx.fillStyle = '#7d8e9c';
                }

                ctx.textAlign = 'right';
                ctxSetFont(ctx, `400 17.8px Inter, sans-serif`);
                ctx.fillText(column.type, positionNext, columnY + 23 + 1.8);

                // if (scale <= 0.18) {
                // 	ctx.fillStyle = '#cbd6df25';
                // 	// ctx.fillRect(startColumnX + 25, columnY + 23 + 1.8, 100, 10);
                // 	ctx.beginPath();
                // 	ctx.roundRect(positionX - 75, columnY + (23 + 1.8) / 2, 75, 8, 3);
                // 	ctx.fill();
                // } else {
                // 	ctx.fillStyle = theme.table.body.row.type.color;

                // 	// POINT 1
                // 	if (color) {
                // 		ctx.fillStyle = '#7d8e9c';
                // 	}

                // 	ctx.textAlign = "right";
                // 	ctxSetFont(ctx, `400 17.8px Inter, sans-serif`);
                // 	ctx.fillText(column.type, positionX, columnY + 23 + 1.8);
                // }
                // }


                // ===================== 6.5. TABLE COLUMNS | INDEX =====================
                if (column.indexType) {
                    // const path = "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z";
                    // const pathCanvas = new Path2D(path);

                    // ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x+5, columnY+9); // Переміщуємо початкову точку малювання
                    // ctx.scale(0.7, 0.7); // Масштабування шляху для більшого розміру
                    // ctx.strokeStyle = '#ffffff';
                    // ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.stroke(pathCanvas); // Малюємо шлях
                    // ctx.restore(); // Відновлюємо попередній стан канвасу

                    // ctx.setTransform(10, 0, 0, 10, 0, 0); // Масштабування
                    // ctx.strokeStyle = '#ffffff';
                    // ctx.lineWidth = 1;
                    // ctx.stroke(pathCanvas);

                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.indexes[column.indexType] || null;
                    ctx.strokeStyle =
                        theme.table.indexes[column.indexType] || null;

                    const coords = {
                        top: {
                            x: x + tableWidth - 14,
                            y: columnY + 11.5,
                        },
                        left: {
                            x: x + tableWidth - 19.5,
                            y: columnY + 17,
                        },
                        right: {
                            x: x + tableWidth - 8.5,
                            y: columnY + 17,
                        },
                        bottom: {
                            x: x + tableWidth - 14,
                            y: columnY + 22.5,
                        },
                    };

                    ctx.beginPath();
                    ctx.moveTo(coords.top.x - 0.3, coords.top.y - 0.2 + 2); // Top point
                    ctx.lineTo(coords.right.x - 0.3, coords.right.y - 0.2 + 2); // Right point
                    ctx.lineTo(coords.bottom.x - 0.3, coords.bottom.y - 0.2 + 2); // Bottom point
                    ctx.lineTo(coords.left.x - 0.3, coords.left.y - 0.2 + 2); // Left point

                    ctx.closePath();
                    ctx.stroke();
                    ctx.fill();
                }

                // ===================== 6.6. TABLE COLUMNS | HIGHLIGHTED COLUMN | FOCUSED COLUMN | (BLUE) =====================
                if (
                    !isBtnExportImageEnabled &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment &&
                    focusedInsideElementStateRef.current?.elementId === id &&
                    focusedInsideElementStateRef.current?.insideElementId === column.id
                ) {
                    ctx.strokeStyle = '#5f88ff';
                    ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
                    // ctx.lineWidth = scaleNums.outlineWidth + 0.7;
                    ctx.lineWidth = scaleNums.outlineWidth + 0.1;

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 1,
                        columnY,
                        tableWidth - 2,
                        TABLE_ROW_HEIGHT,
                        3
                    );
                    ctx.stroke();
                }

                // // ===================== 6.8. COLUMNS POINTS (TO CREATE NEW LINES) =====================
                // if (
                // 	// is not export enabled
                // 	!isBtnExportImageEnabled &&
                // 	!isSelectionMoving &&
                // 	!isStartedNewLine &&
                // 	!isStartedNewPenSegment &&
                // 	(   // table is one focused element
                // 		(focusedIdsRef.current.includes(id) && focusedIdsRef.current.length === 1)
                // 		// table is not focused, but this column is hovered
                // 		|| ((hoveredElementIdRef.current === id) && (hoveredInsideElementRef.current?.id === column.id))
                // 		// table is not focused, but this column's 'point area' is hovered
                // 		|| hoveredPointAreaRef.current?.insideElementsIds.includes(column.id)
                // 	)
                // ) {
                // 	const POINT_RADIUS_BEFORE_HOVER = 2.5;
                // 	const POINT_RADIUS_ON_HOVER = 6;
                // 	const POINT_DISTANCE = 20;

                // 	let leftRadius = POINT_RADIUS_BEFORE_HOVER;
                // 	let rightRadius = POINT_RADIUS_BEFORE_HOVER;

                // 	let isHoveredColumn = hoveredPointRef.current?.insideElementId === column.id;
                // 	let direction = hoveredPointRef.current?.direction;

                // 	// ===================== 6.8.1. COLUMNS POINTS | LEFT POINT =====================

                // 	const centerXLeft = x - POINT_DISTANCE;
                // 	const centerYLeft = columnY + TABLE_ROW_HEIGHT / 2;

                // 	ctx.fillStyle = '#667688';
                // 	if (isHoveredColumn && direction === 'left') {
                // 		ctx.fillStyle = '#5f88ff';
                // 		leftRadius = POINT_RADIUS_ON_HOVER;
                // 	}

                // 	ctx.beginPath();
                // 	ctx.arc(centerXLeft, centerYLeft, leftRadius, 0, 2 * Math.PI); // Малюємо коло
                // 	ctx.fill();

                // 	// ===================== 6.8.1. COLUMNS POINTS | RIGHT POINT =====================

                // 	const centerXRight = x + tableWidth + POINT_DISTANCE;
                // 	const centerYRight = columnY + TABLE_ROW_HEIGHT / 2;

                // 	ctx.fillStyle = '#667688';
                // 	if (isHoveredColumn && direction === 'right') {
                // 		ctx.fillStyle = '#5f88ff';
                // 		rightRadius = POINT_RADIUS_ON_HOVER;
                // 	}

                // 	ctx.beginPath();
                // 	ctx.arc(centerXRight, centerYRight, rightRadius, 0, 2 * Math.PI); // Малюємо коло
                // 	ctx.fill();
                // }

                // ===================== 6.9. TABLE COLUMNS | END =====================
            }
        }
        // ===================== 7.1. HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | FROM (PURPLE) =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            isStartedNewLine &&
            lineFromRef.current?.elementId === id &&
            lineFromRef.current?.insideElementId
        ) {
            const isSideLeft = true;
            const isSideRight = true;

            const columnY =
                y +
                TABLE_HEADER_HEIGHT +
                lineFromRef.current.insideElementIndex * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const leftCenterX = x + 1;
            const rightCenterX = x - 1 + tableWidth;
            const centerY = columnY + TABLE_ROW_HEIGHT / 2;

            const bigRadius = 5.25;

            // let leftMidRadius = 2.75;
            // let rightMidRadius = 2.75;
            // const smallRadius = 1.95;

            let leftMidRadius = 2.75;
            let rightMidRadius = 2.75;
            const smallRadius = 2.05;

            // if (isSideLeft) { leftMidRadius = 3.35; }
            // if (isSideRight) { rightMidRadius = 3.35; }
            if (isSideLeft) {
                leftMidRadius = 3.75;
            }
            if (isSideRight) {
                rightMidRadius = 3.75;
            }

            // ===================== FOCUSED FRAME (PURPLE) =====================
            ctx.strokeStyle = '#8b5fff';
            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';

            // ctx.lineWidth = scaleNums.outlineWidth + 0.7;
            ctx.lineWidth = scaleNums.outlineWidth + 0.1;

            ctx.beginPath();
            ctx.roundRect(x + 1, columnY, tableWidth - 2, TABLE_ROW_HEIGHT, 3);
            // ctx.stroke();
            ctx.fill();
            // ===================== LEFT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(leftCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(leftCenterX, centerY, leftMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideLeft) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }

            // ===================== RIGHT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(rightCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(rightCenterX, centerY, rightMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideRight) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }
        }

        // ===================== 7.2. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | TO (PURPLE) =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            isStartedNewLine &&
            hoveredElementIdRef.current === id &&
            hoveredInsideElementRef.current?.id &&
            lineFromRef.current?.insideElementId !==
            hoveredInsideElementRef.current?.id
        ) {
            const isSideLeft = true;
            const isSideRight = true;

            const columnY =
                y +
                TABLE_HEADER_HEIGHT +
                hoveredInsideElementRef.current?.index * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const leftCenterX = x + 1;
            const rightCenterX = x - 1 + tableWidth;
            const centerY = columnY + TABLE_ROW_HEIGHT / 2;

            const bigRadius = 5.25;
            let leftMidRadius = 2.75;
            let rightMidRadius = 2.75;
            const smallRadius = 1.95;

            if (isSideLeft) {
                leftMidRadius = 3.35;
            }
            if (isSideRight) {
                rightMidRadius = 3.35;
            }

            // ===================== FOCUSED FRAME (PURPLE) =====================
            ctx.strokeStyle = '#8b5fff';
            // ctx.lineWidth = scaleNums.outlineWidth + 0.7;
            ctx.lineWidth = scaleNums.outlineWidth + 0.1;

            ctx.beginPath();
            ctx.roundRect(x + 1, columnY, tableWidth - 2, TABLE_ROW_HEIGHT, 3);
            ctx.stroke();

            // ===================== LEFT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(leftCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(leftCenterX, centerY, leftMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideLeft) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }

            // ===================== RIGHT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(rightCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(rightCenterX, centerY, rightMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideRight) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }
        }


        if (scale > 0.15) {
            // ===================== 8. TABLE HEADER/BODY LINE (BETWEEN HEADER AND BODY) =====================
            // ===================== 10. DASHES FRAME =====================
            ctx.strokeStyle = '#a9b3bdff';

            if (!border) {
                // ctx.fillStyle = theme.table.header.border;
                // ctx.fillStyle = '#4c5866';
                // ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);

                ctx.fillStyle = 'rgb(28 36 46)';
                ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);

            } else if (border === 'dashes') {
                ctx.lineCap = 'butt';
                ctx.setLineDash([12, 15]);

                const width =
                    scale < 1
                        ? Math.min((1.1 * 3) / scale, (1.1 * 3) / 0.65)
                        : 1 * 3;
                ctx.lineWidth = width;

                ctx.beginPath();
                ctx.moveTo(x + 6, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                // ctx.stroke();

                // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            } else if (border === 'dots') {
                ctx.lineCap = 'butt';
                ctx.setLineDash([4, 6]);

                const width =
                    scale < 1
                        ? Math.min((1.2 * 3) / scale, (1.2 * 3) / 0.65)
                        : 1 * 3;
                ctx.lineWidth = width;

                ctx.beginPath();
                ctx.moveTo(x + 2.4, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.stroke();

                // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            } else if (border === 'solid') {
                ctx.lineCap = 'square';
                const width =
                    scale < 1
                        ? Math.min((1 * 2.5) / scale, (1 * 2.75) / 0.65)
                        : 1 * 2.75;
                ctx.lineWidth = width;

                ctx.beginPath();
                ctx.moveTo(x, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.stroke();

                // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            }

            ctx.setLineDash([]);

        }
        // ctx.fillStyle = theme.table.header.border;
        // ctx.fillStyle = '#4c5866';

        // ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);


        if (scale > 0.15) {

            // ===================== 9. TABLE | EMPTY COLUMN =====================
            // background
            const emptyColumnY =
                y +
                TABLE_HEADER_HEIGHT +
                columnsCount * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const isHoveredBgStyle =
                !isElementResizing &&
                // is not export enabled
                !isBtnExportImageEnabled &&
                !isSelectionMoving &&
                !isStartedNewLine &&
                !isStartedNewPenSegment && // hovered table and hovered empty column
                hoveredElementIdRef.current === id &&
                hoveredElementIdEmptyColumnRef.current === id;
            // (hoveredElementIdRef.current === id && true)

            ctx.fillStyle = isHoveredBgStyle
                ? theme.table.body.row.hover.background
                : theme.table.body.background;

            // -----------------------------------------------------------
            // asas
            if (bodyColor) {
                ctx.fillStyle = bodyColor;
            }
            // -----------------------------------------------------------

            ctx.beginPath();
            ctx.roundRect(x, emptyColumnY + 1, tableWidth, TABLE_ROW_HEIGHT - 2, 3);
            ctx.fill();

            // points
            const radius = 2.5;
            const distance = 15;

            const pointY = emptyColumnY + TABLE_ROW_HEIGHT / 2; // + 4;
            const leftPointX = x + tableWidth / 2 - distance;
            const centerPointX = x + tableWidth / 2;
            const rightPointX = x + tableWidth / 2 + distance;

            ctx.fillStyle = '#667688';

            ctx.beginPath();
            ctx.arc(leftPointX, pointY, radius, 0, 2 * Math.PI);
            ctx.arc(centerPointX, pointY, radius, 0, 2 * Math.PI);
            ctx.arc(rightPointX, pointY, radius, 0, 2 * Math.PI);
            ctx.fill();
        }
    }

    // ===================== DRAWING TABLE HIDDEN (scale ≤ 0.15 or no-access) =====================
    function drawTableHidden(element) {
        const ctx = contextRef.current;
        const scaleNums = scaleNumbersRef.current;
        const { id, x, y, width: tableWidth, color } = element;
        const tableHeight = getTableEffectiveHeight(element);

        const isTableFocused = focusedIdsRef.current.includes(id);
        const isTableSelected = selectedElementsIdsRef.current.includes(id);
        const isResizingArea = resizeAreaPropsRef.current?.areaId;

        const hoveredElId = hoveredElementIdRef.current;
        const lineFrom = lineFromRef.current;
        const isStartedNewLine = lineFrom?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        // 0. Outline frame (focused / selected only — no hover at this scale)
        if (
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            !isBtnExportImageEnabled &&
            // (isTableSelected || isTableFocused)
            (isTableSelected || isTableFocused || (hoveredElId === id && !isStartedNewLine && !isStartedNewPenSegment))
        ) {
            // light blue when the frame comes from the search spotlight preview
            ctx.strokeStyle = isBtnSearchEnabled && isTableFocused
                ? 'rgba(93, 158, 255, 0.4)'
                // : 'rgba(45, 52, 65, 1)';
                : '#262b33';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;
            ctx.strokeRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                tableWidth + 2 * scaleNums.outlineOffset,
                tableHeight + 2 * scaleNums.outlineOffset + 1
            );
        }

        // 2+3. Shadow + body background
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;

        ctx.beginPath();
        ctx.roundRect(x, y, tableWidth, tableHeight, TABLE_BORDER_RADIUS);

        // ctx.fillStyle = 'rgb(19.5, 21.2, 23)';
        ctx.fillStyle = 'rgb(20, 21, 22)';

        ctx.lineWidth = scaleNums.outlineWidth + 0.5;
        ctx.strokeStyle = 'rgb(34, 40, 47)';
        ctx.stroke();
        ctx.fill();

        ctx.strokeStyle = 'rgba(81, 92, 126, 0)';
        ctx.lineWidth = 1;
        ctx.shadowColor = 'transparent';

        // 10. Area selection outline
        if (
            (!isBtnExportImageEnabled &&
                isBtnNewAreaEnabled &&
                newAreaPropsRef?.current?.selectedElements?.includes(id))
            ||
            (!isBtnExportImageEnabled &&
                resizeAreaPropsRef.current?.selectedElements?.includes(id))
        ) {
            const ox = x - scaleNums.outlineOffset;
            const oy = y - scaleNums.outlineOffset - 0.5;
            const ow = tableWidth + 2 * scaleNums.outlineOffset;
            const oh = tableHeight + 2 * scaleNums.outlineOffset + 1;
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';
            ctx.fillStyle = 'rgba(111, 190, 255, 0.07)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
            ctx.beginPath();
            ctx.roundRect(ox, oy, ow, oh, 0.75);
            ctx.stroke();
            ctx.fill();
        }
    }

    // ===================== DRAWING TABLE =====================
    function drawTable(element, exportImgCanvas = null) {
        if (viewMode === 'names') {
            drawTableName(element);
            return;
        } else if (viewMode === 'keys') {
            drawTableKey(element, exportImgCanvas);
            return;
        }
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        const scale = scaleRef.current;
        if (scale <= 0.15) { drawTableHidden(element); return; }
        const scaleNums = scaleNumbersRef.current;
        const focusedInsideEl = focusedInsideElementStateRef.current;
        const hoveredElId = hoveredElementIdRef.current;
        const hoveredInsideEl = hoveredInsideElementRef.current;
        const lineFrom = lineFromRef.current;
        const hoveredPoint = hoveredPointRef.current;
        const searchHoveredCol = searchHoveredColumnRef.current;

        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFrom?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;
        const isStartedNewArea = isBtnNewAreaEnabled;
        const isResizingArea = resizeAreaPropsRef.current?.areaId;

        let {
            id,
            x,
            y,
            width: initTableWidth,
            headers,
            name,
            columns,
            columnsOrder,
            border,
            color,
            areaId,
        } = element;

        let startColumnX = x;
        let tableWidth = initTableWidth;

        // let tableHeight = Object.keys(columns).length > 25 ? normalTableHeight : tableHeightInit;
        let tableHeight = getTableEffectiveHeight(element);

        const isTableFocused = focusedIdsRef.current.includes(id);
        const isTableSelected = selectedElementsIdsRef.current.includes(id);

        // ===================== 0. FOCUSED TABLE | OUTLINE FRAME =====================
        if (
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // table is selected by mouse selection box
            (isTableSelected ||
                // table is focused
                isTableFocused ||
                // table is hovered and its not new line drawing
                (hoveredElId === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment))
        ) {
            // light blue when the frame comes from the search spotlight preview
            ctx.strokeStyle = isBtnSearchEnabled && isTableFocused
                ? 'rgba(93, 158, 255, 0.4)'
                // : 'rgba(45, 52, 65, 0.5)';
                // : '#343F4E';
                // : '#2d3540';
                : '#262b33';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                tableWidth + 2 * scaleNums.outlineOffset,
                tableHeight + 2 * scaleNums.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            ctx.strokeRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                tableWidth + 2 * scaleNums.outlineOffset,
                tableHeight + 2 * scaleNums.outlineOffset + 1
            );
        }

        // ===================== 1. FOCUSED TABLE | RESIZE POINTS =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            // is not mouse selection
            !isSelectionMoving &&
            // table is focused
            isTableFocused &&
            // focused only one table
            focusedIdsRef.current.length === 1
        ) {
            const resizeHandleSize = Math.max(scaleNums.resizeHandleSize > 4 ? scaleNums.resizeHandleSize - 3 : scaleNums.resizeHandleSize, 3);
            const resizeHandleRadius = Math.max(scaleNums.resizeHandleRadius > 1 ? scaleNums.resizeHandleRadius - 1 : scaleNums.resizeHandleRadius, 1.75);
            const resizeX = x - scaleNums.outlineOffset;
            const resizeY = y - scaleNums.outlineOffset;
            const hw = resizeHandleSize + 2;
            const hs = resizeHandleSize / 2 + 1;
            const leftX = resizeX - hs;
            const topY = resizeY - hs;
            const rightX = resizeX + tableWidth + scaleNums.outlineOffset * 2 - hs;
            const bottomY = resizeY + tableHeight + scaleNums.outlineOffset * 2 - hs;

            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.lineWidth = scaleNums.outlineWidth + 1;
            // ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
            ctx.strokeStyle = '#262b33';
            ctx.roundRect(leftX, topY, hw, hw, resizeHandleRadius);
            ctx.roundRect(rightX, topY, hw, hw, resizeHandleRadius);
            ctx.roundRect(leftX, bottomY, hw, hw, resizeHandleRadius);
            ctx.roundRect(rightX, bottomY, hw, hw, resizeHandleRadius);
            ctx.stroke();
            ctx.fill();
        }

        // ===================== 2. TABLE SHADOW =====================
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. TABLE BACKGROUND | RECTANGLE =====================
        ctx.beginPath();
        ctx.roundRect(x, y, tableWidth, tableHeight, TABLE_BORDER_RADIUS);

        // -----------------------------------------------------------
        // let headerColor = 'rgba(14, 15, 15, 1)';
        // let headerColor = 'rgba(15, 15, 16, 1)';
        let headerColor = 'rgb(17, 18, 18)';

        if (isBtnExportImageEnabled && exportImgCanvas) {
            headerColor = 'rgb(9, 10, 11)';
        }


        if (color) {
            switch (color) {
                case 'blue': {
                    headerColor = '#0b2a50ff';
                    break;
                }
                case 'green': {
                    headerColor = '#033c33ff';
                    break;
                }
                case 'orange': {
                    headerColor = '#451d01';
                    break;
                }
                case 'yellow': {
                    headerColor = '#573900';
                    break;
                }
                case 'pink': {
                    headerColor = '#660928';
                    break;
                }
                case 'purple': {
                    headerColor = '#34054aff';
                    break;
                }
                case 'violet': {
                    headerColor = '#61044eff';
                    break;
                }
                case 'red': {
                    headerColor = '#6e002eff';
                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }


        // const bodyColor = 'rgb(19.5, 21.2, 23)';
        const bodyColor = 'rgb(20, 21, 22)';

        ctx.fillStyle = bodyColor;
        ctx.lineWidth = scaleNums.outlineWidth + 0.5;
        ctx.strokeStyle = 'rgb(34, 40, 47)';
        // -----------------------------------------------------------

        ctx.stroke();
        ctx.fill();
        ctx.strokeStyle = 'rgba(81, 92, 126, 0)';

        ctx.lineWidth = 1;

        // Reset shadow after drawing the table
        ctx.shadowColor = 'transparent';

        const isRenderingStaticMode = isStartedLineState || isBoardMovingStarted || isBoardMoving || isScaling || isMovingElementId || isElementResizing;
        const isFocusedInsideThisTable = focusedInsideEl?.elementId === id;
        const isColumnStateForThisTable = columnState?.elementId === id;

        // ===================== 4. TABLE HEADER =====================
        {
            // ===================== 4.1. TABLE HEADER | BACKGROUND =====================
            // -----------------------------------------------------------
            let headColor = '#333b44';
            let cellsColor = '#444e5a';
            let linesColor = '#262c31';

            // if (color) {
            //     switch (color) {
            //         case 'blue': {
            //             headColor = '#0f325a';
            //             cellsColor = '#15467d';
            //             linesColor = '#071b31';
            //             break;
            //         }
            //         case 'green': {
            //             headColor = '#10533c';
            //             cellsColor = '#14694c';
            //             linesColor = '#0b3b2a';
            //             break;
            //         }
            //         case 'orange': {
            //             // headerColor = '#451d01'; // //562605 662e09
            //             // bodyColor = '#6a2c03';
            //             // headColor = '#40085a';
            //             // cellsColor = '#540b76';
            //             // linesColor = '#300643';
            //             break;
            //         }
            //         case 'yellow': {
            //             // headerColor = '#573900';
            //             // bodyColor = '#6d4700'; //ad7000
            //             // headColor = '#40085a';
            //             // cellsColor = '#540b76';
            //             // linesColor = '#300643';
            //             break;
            //         }
            //         case 'pink': {
            //             // headColor = '#760b2f';
            //             // cellsColor = '#900e39';
            //             // linesColor = '#660928';

            //             break;
            //         }
            //         case 'purple': {
            //             headColor = '#40085a';
            //             cellsColor = '#540b76';
            //             linesColor = '#300643';
            //             break;
            //         }
            //         case 'violet': {
            //             headColor = '#4d064d';
            //             cellsColor = '#6c0c6c';
            //             linesColor = '#350235';
            //             break;
            //         }
            //         case 'red': {
            //             headColor = '#661320';
            //             cellsColor = '#821829';
            //             linesColor = '#490e16';
            //             break;
            //         }
            //         case 'default': {
            //             break;
            //         }
            //         default:
            //             break;
            //     }
            // }
            ctx.fillStyle = headerColor;
            ctx.beginPath();
            ctx.roundRect(x, y, tableWidth, TABLE_HEADER_HEIGHT, [TABLE_BORDER_RADIUS, TABLE_BORDER_RADIUS, 0, 0]);
            ctx.fill();

            if (scale > 0.35) {

                // ===================== 4.1. TABLE HEADER | ICON =====================
                ctx.save(); // Зберігаємо поточний стан канвасу
                ctx.scale(0.38, 0.38); // Масштабування шляху для більшого розміру

                ctx.translate(
                    (x + TABLE_BORDER_RADIUS + 8) / 0.38,
                    (y + 10.25) / 0.38
                ); // Переміщуємо початкову точку малювання
                ctx.strokeStyle = theme.controls.background;
                ctx.lineWidth = 2; // Лінії мають бути тонкими після масштабування

                ctx.fillStyle = headColor;
                ctx.fill(_PATH2D_TABLE_ICON_HEADER);

                ctx.fillStyle = cellsColor;
                ctx.fill(_PATH2D_TABLE_ICON_CELLS);

                ctx.strokeStyle = linesColor;
                ctx.stroke(_PATH2D_TABLE_ICON_LINES);

                ctx.restore(); // Відновлюємо попередній стан канвасу


                // ===================== 4.1.2. TABLE HEADER | DESCRIPTION ICON =====================
                // if (element.description || element.value.isTableWithIndexes) {
                if (element.description) {
                    const isHeaderEditedAndChanged =
                        isColumnStateForThisTable &&
                        columnState.insideElementType === 'header' &&
                        columnNameState !== name;

                    // POINT 1
                    let nameValue = name;
                    if (isHeaderEditedAndChanged) {
                        // show local state value
                        nameValue = columnNameState;
                    }

                    if (nameValue === '') {
                        nameValue = 'untitled';
                    }


                    ctxSetFont(ctx, `600 21px Inter, sans-serif`);
                    const tableNameWidth = ctxMeasure(ctx, nameValue);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.55, 0.55); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                    ctx.translate((x - 264 + tableWidth / 2 + tableNameWidth / 2 + 5.5 - 0.3) / 0.55, (y - 166 + 0.25) / 0.55); // Переміщуємо початкову точку малювання

                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.header.color;
                    ctx.strokeStyle = theme.table.header.color;

                    ctx.stroke(_PATH2D_DESCRIPTION_ICON);
                    ctx.fill(_PATH2D_DESCRIPTION_ICON);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

            }
            // ===================== 4.1. TABLE HEADER | TEXT =====================
            const isHeaderFocused =
                isFocusedInsideThisTable &&
                focusedInsideEl.insideElementType === 'header';

            if (isRenderingStaticMode || !isHeaderFocused) {
                const isHeaderEditedAndChanged =
                    isColumnStateForThisTable &&
                    columnState.insideElementType === 'header' &&
                    columnNameState !== name;

                // POINT 1
                let nameValue = name;
                if (isHeaderEditedAndChanged) {
                    // show local state value
                    nameValue = columnNameState;
                }

                if (isElementResizing) {
                    nameValue = name;
                }

                if (nameValue === '') {
                    nameValue = 'untitled';
                }

                // if (
                // 	!focusedInsideElementState ||
                // 	focusedInsideElementState.elementId !== id ||
                // 	focusedInsideElementState.insideElementType !== 'header'
                // ) {
                ctx.textAlign = 'center';
                ctx.fillStyle = 'rgba(201, 217, 220, 1)';
                ctxSetFont(ctx, `600 20px Inter, sans-serif`);
                ctx.fillText(nameValue, x + tableWidth / 2, y + 27);
            } else {
                ctx.strokeStyle = 'rgba(124, 160, 237, 1)';
                ctx.lineWidth = scaleNums.outlineWidth + 0.1;

                ctx.beginPath();
                ctx.roundRect(
                    x + 45,
                    y + 5,
                    tableWidth - 90,
                    TABLE_HEADER_HEIGHT - 11,
                    3
                );
                ctx.stroke();
            }
            // ===================== 5. TABLE HEADER/BODY PADDING =====================
            ctx.fillStyle = theme.table.body.background; // Колір заголовка
        }
        // ===================== 6. TABLE BODY =====================
        // ===================== 6. TABLE COLUMNS =====================


        const headersCount = headers ? Object.keys(headers).length : 1;
        const keys = orderedColumnKeys(columns, columnsOrder);
        const visibleColumnsCount = getVisibleColumnsCount(element, keys.length);
        const focusedCount = focusedIdsRef.current.length;

        {
            const endX = x + tableWidth - 28;
            const ixRight = x + tableWidth;
            const BADGE_W_GENERATED = 16;
            const BADGE_W_DEF = 17;
            const BADGE_W_NN = 25;
            const BADGE_W_ENUM = 15.25;
            const BADGE_W_AUTO_INC = 15.5;
            const BADGE_TEXT_INTERNAL = 4.35;
            const BADGE_GAP = 4.5;
            const POINT_RADIUS_DEFAULT = 2.5;
            const POINT_RADIUS_HOVERED = 6;
            const POINT_DISTANCE = 20;

            for (let index = 0; index < Math.min(keys.length, visibleColumnsCount); index++) {
                const key = keys[index];
                const column = columns[key];
                const columnY =
                    y +
                    headersCount * TABLE_HEADER_HEIGHT +
                    index * TABLE_ROW_HEIGHT +
                    HEADER_BODY_MARGIN;

                // if (column.isChanged && column.isDeleted && column.name !== 'column_new') {
                // 	// ctx.fillStyle = '#566473'; // Колір заголовка
                // 	// ctx.fillStyle = '#8b5fff'; // Колір заголовка
                // 	ctx.fillStyle = null; // Колір заголовка

                // 	// ctx.fillRect(x, columnY - 1.35, tableWidth, 1.35);
                // 	ctx.fillRect(x, columnY - 2, tableWidth, 2);
                // }

                // column's background is light grey
                const isHoveredBgStyle =
                    !isElementResizing &&
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    !isStartedNewPenSegment && // hovered table and hovered column
                    ((hoveredElId === id &&
                        hoveredInsideEl?.id === column.id) ||
                        // hovered column's point
                        hoveredPoint?.insideElementId === column.id);

                // search spotlight column hover (redux-driven)
                const isSearchHoveredBgStyle =
                    searchHoveredCol?.elementId === id &&
                    searchHoveredCol?.columnId === column.id;

                // column's background is black
                const isFocusedBgStyle =
                    isFocusedInsideThisTable &&
                    focusedInsideEl.insideElementId === column.id;

                // column's background is black
                const isRelatedToFocusedElementsBgStyle =
                    focusedRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredElementBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isRelatedToFocusedLineBgStyle =
                    focusedLineRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredLineBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.has(
                        column.id
                    );

                // column's background is black
                const isColumnsFromToNewLineBgStyle =
                    // new line's column 'from'
                    (lineFrom?.elementId === id &&
                        lineFrom?.insideElementId === column.id) ||
                    // new line's column 'to'
                    (hoveredElId === id &&
                        hoveredInsideEl?.id === column.id &&
                        lineFrom?.insideElementId &&
                        lineFrom?.insideElementId !==
                        hoveredInsideEl?.id);

                // ===================== 6.1. TABLE COLUMNS | START DRAWING =====================
                const columnColor = bodyColor;
                // const focusedColumnColor = 'rgb(19, 22, 25)';
                // const focusedColumnColor = 'rgb(29, 31, 34)';
                const focusedColumnColor = 'rgb(31, 33, 36)';
                // const hoveredColumnColor = 'rgba(26, 30, 34, 1)';
                // const hoveredColumnColor = 'rgb(29, 31, 34)';
                const hoveredColumnColor = 'rgb(22, 24, 27)';
                const searchHoveredColumnColor = 'rgba(93, 158, 255, 0.28)';

                ctx.fillStyle =
                    // search-spotlight hover wins — explicit intent from the search list
                    isSearchHoveredBgStyle
                        ? searchHoveredColumnColor
                        // selection / focus / new-line styles win over hover
                        // (isFocusedBgStyle ||
                        : (isRelatedToFocusedElementsBgStyle ||
                            isRelatedToFocusedLineBgStyle ||
                            isColumnsFromToNewLineBgStyle) &&
                            !isBtnExportImageEnabled &&
                            !isBtnNewAreaEnabled &&
                            !isResizingArea
                            ? focusedColumnColor
                            // the directly-hovered column shows the hover color even when it
                            // has connected lines — a column related to the hovered element/line
                            // must not mask the direct hover on that same column.
                            : (isRelatedToHoveredElementBgStyle ||
                                isRelatedToHoveredLineBgStyle) &&
                                !isStartedNewLine &&
                                !isBtnExportImageEnabled &&
                                !isBtnNewAreaEnabled &&
                                !isResizingArea
                                ? focusedColumnColor
                                : isHoveredBgStyle
                                    ? hoveredColumnColor
                                    : columnColor;


                // : isHoveredBgStyle
                //     ? hoveredColumnColor
                //     // other columns merely related to the hovered element/line
                //     : (isRelatedToHoveredElementBgStyle ||
                //         isRelatedToHoveredLineBgStyle) &&
                //         !isBtnExportImageEnabled &&
                //         !isBtnNewAreaEnabled &&
                //         !isResizingArea
                //         ? focusedColumnColor
                //         : columnColor;


                // ===================== 6.7. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON TABLE HOVER | (PURPLE) =====================

                let isColumnLineHovered = false;
                const isColumnRelatedToFocusedElements =
                    isRelatedToFocusedElementsBgStyle &&
                    !isTableFocused;

                const isColumnRelatedToFocusedLine =
                    isRelatedToFocusedLineBgStyle &&
                    !focusedCount;

                const isColumnRelatedToHoveredElements =
                    isRelatedToHoveredElementBgStyle &&
                    hoveredElId !== id &&
                    !focusedCount;

                const isColumnRelatedToHoveredLine =
                    isRelatedToHoveredLineBgStyle &&
                    !focusedCount &&
                    hoveredElId !== id;

                if (
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    !isStartedNewPenSegment && // column is related to focused elements
                    (isColumnRelatedToFocusedElements ||
                        // column is related to hovered element
                        isColumnRelatedToHoveredElements ||
                        // column is related to focused line
                        isColumnRelatedToFocusedLine ||
                        // column is related to hovered line
                        isColumnRelatedToHoveredLine)
                ) {

                    isColumnLineHovered = true;
                }

                // ===================== 7.1. HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | FROM (PURPLE) =====================
                const isColumnNewLineLeftSelected =
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    isStartedNewLine &&
                    lineFrom?.elementId === id &&
                    lineFrom?.insideElementId === column.id;



                // ===================== 7.2. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | TO (PURPLE) =====================
                const isColumnNewLineRightSelected =
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    isStartedNewLine &&
                    lineToRef.current?.elementId === id &&
                    lineToRef.current?.insideElementId === column.id;



                if (isColumnLineHovered || isColumnNewLineLeftSelected || isColumnNewLineRightSelected) {
                    // ctx.fillStyle = 'rgba(49, 60, 120, 0.7)';
                    ctx.fillStyle = 'rgb(31, 33, 36)';
                }


                ctx.beginPath();

                ctx.roundRect(x, columnY + 1, tableWidth, TABLE_ROW_HEIGHT - 2, 3);
                ctx.fill();

                ctxSetFont(ctx, column.isPK ? '700 19px Inter, sans-serif' : '400 19px Inter, sans-serif');

                // ===================== 6.2. TABLE COLUMNS | PRIMARY KEY & FOREIGN KEY =====================
                if (scale > 0.35 && (column.isPK || column.isFK)) {

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x + 4, columnY + 8.5); // Переміщуємо початкову точку малювання
                    // ctx.translate(startColumnX + 5.5, columnY + 8.5); // Переміщуємо початкову точку малювання
                    // ctx.translate(startColumnX + 4, columnY + 8.5); // Переміщуємо початкову точку малювання
                    ctx.translate(startColumnX + 4, columnY + 8.5 + 1.2); // Переміщуємо початкову точку малювання
                    ctx.scale(0.75, 0.75); // Масштабування шляху для більшого розміру
                    ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#bd7bff';
                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#8b76ff';  // b092ff'; // a784ff // 9987ff // 6b63ff
                    ctx.fillStyle = column.isPK ? '#edb855' : '#7b8fff';  // b092ff'; // a784ff // 9987ff // 6b63ff // 7b7bff
                    // ctx.strokeStyle = 'white';

                    ctx.fill(_PATH2D_KEY_ICON);

                    ctx.stroke(_PATH2D_KEY_ICON);
                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

                // ===================== 6.2.1. TABLE COLUMNS | DESCRIPTION ICON =====================
                // if (column.description || column.defaultValue || column.enumValues || column.generatedValue || column.checkValue) {
                if (scale > 0.35 && column.description) {
                    const isColumnEditedAndChanged =
                        isColumnStateForThisTable &&
                        columnState.insideElementId === column.id &&
                        columnNameState !== column.name;

                    // POINT 1
                    let nameValue = column.name;
                    if (isColumnEditedAndChanged) {
                        // show local state value
                        nameValue = columnNameState;
                    }

                    if (nameValue === '') {
                        nameValue = 'unnamed';
                    }

                    const columnNameWidth = ctxMeasure(ctx, nameValue);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.525, 0.525); // Масштабування шляху для більшого розміру

                    ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 5 + 11) / 0.525, (columnY - 168 + 9) / 0.525); // Переміщуємо початкову точку малювання

                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.body.row.name.color;
                    ctx.strokeStyle = theme.table.body.row.name.color;

                    ctx.stroke(_PATH2D_DESCRIPTION_ICON);
                    ctx.fill(_PATH2D_DESCRIPTION_ICON);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }


                // ===================== 6.3. TABLE COLUMNS | NAME =====================
                const isColumnFocused =
                    isFocusedInsideThisTable &&
                    focusedInsideEl.insideElementId === column.id &&
                    focusedInsideEl.insideElementType === 'column';

                if (isRenderingStaticMode || !isColumnFocused) {
                    const isColumnEditedAndChanged =
                        isColumnStateForThisTable &&
                        columnState.insideElementId === column.id &&
                        columnNameState !== column.name;

                    // POINT 1
                    let nameValue = column.name;
                    if (isColumnEditedAndChanged) {
                        // show local state value
                        nameValue = columnNameState;
                    }

                    if (isElementResizing) {
                        nameValue = column.name;
                    }

                    if (nameValue === '') {
                        nameValue = 'unnamed';
                    }


                    ctx.textAlign = 'left';
                    ctxSetFont(ctx, column.isPK ? '700 18.4px Inter, sans-serif' : '400 18.4px Inter, sans-serif');
                    ctx.fillStyle = theme.table.body.row.name.color;
                    ctx.fillText(
                        nameValue,
                        startColumnX + 25,
                        columnY + 23 + 1.8
                    );

                }

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE  =====================
                // ===================== 6.4. TABLE COLUMNS | NULLABLE       =====================
                // ===================== 6.4. TABLE COLUMNS | ENUM           =====================
                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================

                const badgeY = columnY + 11.45;
                const badgeTextY = columnY + 22.6;
                let positionNext = endX;

                // ===================== 6.4. TABLE COLUMNS | GENERATED VALUE =====================
                if (column.generatedValue) {
                    ctx.fillStyle = '#299ec2';
                    ctx.beginPath();
                    ctx.roundRect(positionNext - BADGE_W_GENERATED, badgeY, BADGE_W_GENERATED, 15, 2.7);
                    ctx.fill();
                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';
                    ctx.fillText('G', positionNext - BADGE_TEXT_INTERNAL, badgeTextY);
                    positionNext -= BADGE_W_GENERATED + BADGE_GAP;
                }

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE =====================
                if (column.defaultValue) {
                    ctx.fillStyle = '#b36525';
                    ctx.beginPath();
                    ctx.roundRect(positionNext - BADGE_W_DEF + 0.5, badgeY, BADGE_W_DEF - 1, 15, 2.7);
                    ctx.fill();
                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';
                    ctx.fillText('D', positionNext - BADGE_TEXT_INTERNAL - 0.4, badgeTextY);
                    positionNext -= BADGE_W_DEF + BADGE_GAP;
                }

                // ===================== 6.4. TABLE COLUMNS | NULLABLE =====================
                if (column.isNotNull) {
                    ctx.fillStyle = '#4254afff';
                    ctx.beginPath();
                    ctx.roundRect(positionNext - BADGE_W_NN + 0.5, badgeY, BADGE_W_NN - 1, 15, 2.7);
                    ctx.fill();
                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';
                    ctx.fillText('NN', positionNext - BADGE_TEXT_INTERNAL - 0.8, badgeTextY - 0.2);
                    positionNext -= BADGE_W_NN + BADGE_GAP;
                }

                // ===================== 6.4. TABLE COLUMNS | ENUM =====================
                if (column.isEnum) {
                    ctx.fillStyle = '#dca745ff';
                    ctx.beginPath();
                    ctx.roundRect(positionNext - BADGE_W_ENUM + 0.5, badgeY, BADGE_W_ENUM - 1, 15, 2.7);
                    ctx.fill();
                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 9.7px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';
                    ctx.fillText('E', positionNext - BADGE_TEXT_INTERNAL - 0.4, badgeTextY);
                    positionNext -= BADGE_W_ENUM + BADGE_GAP;
                }

                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================
                if (column.isAutoIncrement) {
                    ctx.fillStyle = '#3eb372ff';
                    ctx.beginPath();
                    ctx.roundRect(positionNext - BADGE_W_AUTO_INC + 0.5, badgeY, BADGE_W_AUTO_INC - 1, 15, 2.7);
                    ctx.fill();
                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `600 15px Inter, sans-serif`);
                    ctx.fillStyle = '#fff';
                    ctx.fillText('+', positionNext - BADGE_TEXT_INTERNAL + 2 - 0.2, badgeTextY + 0.8);
                    positionNext -= BADGE_W_AUTO_INC + BADGE_GAP;
                }


                // ===================== 6.4.2. TABLE COLUMNS | TYPE =====================
                if (isRenderingStaticMode || !isFocusedInsideThisTable || focusedInsideEl.insideElementId !== column.id) {
                    ctx.fillStyle = theme.table.body.row.type.color;

                    // POINT 1
                    if (color) {
                        ctx.fillStyle = '#7d8e9c';
                    }

                    let typeValue = column.type;
                    if (typeValue === '') {
                        typeValue = 'null';
                    }



                    ctx.textAlign = 'right';
                    ctxSetFont(ctx, `400 17.8px Inter, sans-serif`);
                    ctx.fillText(typeValue, positionNext, columnY + 23 + 1.8);
                }

                // ===================== 6.5. TABLE COLUMNS | INDEX =====================
                if (column.indexType) {
                    ctx.lineWidth = 1;
                    ctx.fillStyle = theme.table.indexes[column.indexType] || null;
                    ctx.strokeStyle = theme.table.indexes[column.indexType] || null;

                    ctx.beginPath();
                    ctx.moveTo(ixRight - 14.3, columnY + 13.3); // top
                    ctx.lineTo(ixRight - 8.8, columnY + 18.8); // right
                    ctx.lineTo(ixRight - 14.3, columnY + 24.3); // bottom
                    ctx.lineTo(ixRight - 19.8, columnY + 18.8); // left

                    ctx.closePath();
                    ctx.stroke();
                    ctx.fill();
                }

                // ===================== 6.6. TABLE COLUMNS | HIGHLIGHTED COLUMN | FOCUSED COLUMN | (BLUE) =====================
                if (
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isStartedNewPenSegment &&
                    isFocusedInsideThisTable &&
                    focusedInsideEl.insideElementId === column.id
                ) {
                    ctx.lineWidth = scaleNums.outlineWidth + 0.1;
                    ctx.strokeStyle = 'rgba(124, 160, 237, 1)';

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 1,
                        columnY,
                        tableWidth - 2,
                        TABLE_ROW_HEIGHT,
                        3
                    );
                    ctx.stroke();
                }

                // ===================== 6.8. COLUMNS POINTS (TO CREATE NEW LINES) =====================
                if (
                    !isCursorHand &&
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    !isStartedNewPenSegment && // table is one focused element
                    ((isTableFocused &&
                        focusedCount === 1) ||
                        // table is not focused, but this column is hovered
                        (hoveredElId === id &&
                            hoveredInsideEl?.id === column.id) ||
                        // table is not focused, but this column's 'point area' is hovered
                        hoveredPointAreaRef.current?.insideElementsIds.has(
                            column.id
                        ))
                ) {
                    const isHoveredColumn = hoveredPoint?.insideElementId === column.id;
                    const direction = hoveredPoint?.direction;
                    const centerY = columnY + TABLE_ROW_HEIGHT / 2;
                    const leftHovered = isHoveredColumn && direction === 'left';
                    const rightHovered = isHoveredColumn && direction === 'right';

                    // ===================== 6.8.1. COLUMNS POINTS | LEFT POINT =====================
                    ctx.fillStyle = leftHovered ? 'rgba(105, 143, 232, 1)' : '#667688';
                    ctx.beginPath();
                    ctx.arc(x - POINT_DISTANCE, centerY, leftHovered ? POINT_RADIUS_HOVERED : POINT_RADIUS_DEFAULT, 0, TWO_PI);
                    ctx.fill();

                    // ===================== 6.8.2. COLUMNS POINTS | RIGHT POINT =====================
                    ctx.fillStyle = rightHovered ? 'rgba(105, 143, 232, 1)' : '#667688';
                    ctx.beginPath();
                    ctx.arc(ixRight + POINT_DISTANCE, centerY, rightHovered ? POINT_RADIUS_HOVERED : POINT_RADIUS_DEFAULT, 0, TWO_PI);
                    ctx.fill();
                }
                // ===================== 6.9. TABLE COLUMNS | END =====================
            }
        }


        {
            // ===================== 8. TABLE HEADER/BODY LINE (BETWEEN HEADER AND BODY) =====================
            // ===================== 10. DASHES FRAME =====================

            // if (name === 'bo.landing_pages' || name === 'bo.customers_id_blocks') {

            ctx.strokeStyle = '#a9b3bdff';

            if (!border) {
                ctx.fillStyle = 'rgb(28 36 46)';

                ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);
            } else if (border === 'dashes') {
                ctx.lineCap = 'butt';
                ctx.setLineDash([12, 15]);
                ctx.lineWidth = scale < 1 ? Math.min(3.3 / scale, 5.077) : 3;
                ctx.beginPath();
                ctx.moveTo(x + 6, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);

                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            } else if (border === 'dots') {
                ctx.lineCap = 'butt';
                ctx.setLineDash([4, 6]);
                ctx.lineWidth = scale < 1 ? Math.min(3.6 / scale, 5.538) : 3;
                ctx.beginPath();
                ctx.moveTo(x + 2.4, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.stroke();

                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            } else if (border === 'solid') {
                ctx.lineCap = 'square';
                ctx.lineWidth = scale < 1 ? Math.min(2.5 / scale, 4.231) : 2.75;

                ctx.beginPath();
                ctx.moveTo(x, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.stroke();

                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            }

            ctx.setLineDash([]);
        }
        // ===================== 9. TABLE CIRCLE | ADD NEW ROW =====================
        if (
            !isCursorHand &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            !isStartedNewLine &&
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            !isStartedNewPenSegment &&
            hoveredCircleAreaRef.current?.elementsIds.includes(id)
        ) {
            // ctx.fillStyle = '#4c5967';
            // ctx.fillStyle = 'rgb(76, 89, 103)';
            ctx.fillStyle = 'rgb(76, 85, 93)';
            ctx.fillStyle = 'rgb(70, 80, 87)';
            ctx.beginPath();

            if (hoveredCircleRef.current?.elementId === id) {
                // ctx.fillStyle = 'rgba(98, 136, 224, 1)';
                // ctx.fillStyle = 'rgba(80, 120, 200, 1)';
                // ctx.fillStyle = 'rgba(80, 120, 180, 1)';
                ctx.fillStyle = 'rgba(100, 140, 210, 1)';
            }

            const radius = 18;
            const distance = 36;
            const centerX = x + tableWidth / 2;
            const centerY = y + tableHeight + distance;
            ctx.arc(x + tableWidth / 2, y + tableHeight + distance, radius, 0, TWO_PI); // Малюємо коло
            ctx.fill();

            const lineLength = radius / 2 - 2;

            // Горизонтальна лінія
            ctx.beginPath();
            ctx.moveTo(centerX - lineLength, centerY);
            ctx.lineTo(centerX + lineLength, centerY);
            ctx.strokeStyle = '#e6e6e6'; // Встановлюємо колір обведення для плюса
            ctx.lineWidth = 2.3; // Встановлюємо ширину лінії
            ctx.lineCap = 'round';
            ctx.stroke();

            // Вертикальна лінія
            ctx.beginPath();
            ctx.moveTo(centerX, centerY - lineLength);
            ctx.lineTo(centerX, centerY + lineLength);
            ctx.strokeStyle = '#e6e6e6'; // Встановлюємо колір обведення для плюса
            ctx.lineWidth = 2.3; // Встановлюємо ширину лінії
            ctx.lineCap = 'round';
            ctx.stroke();
        }

        // ===================== TABLE OVERFLOW INDICATOR =====================
        // chevron circle + "N more columns" when bottom columns are clipped.
        // Same inverse-scaled circle/chevron style as the note overflow
        // indicator (drawNote's own "NOTE OVERFLOW INDICATOR" block) — radius,
        // stroke width and chevron geometry are copied verbatim from there so
        // the two indicators are visually identical; only the position
        // semantics differ (table: effective/clipped height, note: natural
        // height). The "N more columns" LABEL is the one deliberate
        // difference from the note (which has no label) — it uses a fixed
        // WORLD-size font or scales with zoom like normal table text; see
        // its own comment below. click-to-expand is hit-tested in the
        // mousedown handler (and hover-cursor in the mousemove handler)
        // against this exact circle geometry — keep chevronR in lockstep
        // with both of those (click/hover stay live-canvas-only — no
        // interactivity in an exported image, so they're untouched).
        //
        // DRAWN in image exports (an exported clipped table should always
        // show its clipped state) — OVERFLOW_INDICATOR_MIN_SCALE is a
        // live-canvas concern (avoids visual noise / an unclickable target at
        // very low zoom); it doesn't apply once exporting, so an export
        // bypasses it entirely rather than being subject to whatever zoom the
        // user happened to be at when they dragged the export box.
        const isExportingTableIndicator = isBtnExportImageEnabled && exportImgCanvas;
        if (
            (isExportingTableIndicator || scale > OVERFLOW_INDICATOR_MIN_SCALE) &&
            keys.length > visibleColumnsCount
        ) {
            const hiddenCount = keys.length - visibleColumnsCount;
            const chevronCx = x + tableWidth / 2;
            const chevronCy = y + tableHeight;
            const chevronR = 13 / scale;

            ctx.beginPath();
            ctx.arc(chevronCx, chevronCy, chevronR, 0, Math.PI * 2);
            ctx.fillStyle = 'rgb(32, 37, 44)';
            ctx.fill();

            ctx.beginPath();
            ctx.strokeStyle = 'rgb(200, 210, 214)';
            ctx.lineWidth = 2.5 / scale;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.moveTo(chevronCx - 4 / scale, chevronCy - 2 / scale);
            ctx.lineTo(chevronCx, chevronCy + 2.5 / scale);
            ctx.lineTo(chevronCx + 4 / scale, chevronCy - 2 / scale);
            ctx.stroke();
            ctx.lineCap = 'butt';

            // Label scales WITH the diagram like normal table text (world-size
            // font), unlike the inverse-scaled chevron circle above — fixed at
            // its zoom-1 equivalent (was `14 / scale`, `8 / scale`, `5 / scale`
            // inverse-scaled-for-constant-screen-size; at scale === 1 those
            // equal 14/8/5, so this looks identical to before at zoom 1, and
            // now grows/shrinks with zoom everywhere else). Offsets are world
            // units too, so the label keeps hugging the chevron at every zoom.
            const label = `${hiddenCount} ${hiddenCount === 1 ? t('canvas_table.more_column') : t('canvas_table.more_columns')}`;
            ctxSetFont(ctx, '500 14px Inter, sans-serif');
            ctx.fillStyle = 'rgb(134, 145, 148)';
            ctx.textAlign = 'left';
            ctx.fillText(label, chevronCx + chevronR + 8, chevronCy + 5);
        }

        // ===================== 10. SELECTED TABLE | OUTLINE FRAME (IN AREA SELECTED FRAME) =====================
        if (
            // create new area: selection table
            (!isBtnExportImageEnabled &&
                isBtnNewAreaEnabled &&
                newAreaPropsRef?.current?.selectedElements?.includes(id))
            ||
            // resize/move area: selection table
            (!isBtnExportImageEnabled &&
                // isBtnNewAreaEnabled &&
                resizeAreaPropsRef.current?.selectedElements?.includes(id))
        ) {
            const ox = x - scaleNums.outlineOffset;
            const oy = y - scaleNums.outlineOffset - 0.5;
            const ow = tableWidth + 2 * scaleNums.outlineOffset;
            const oh = tableHeight + 2 * scaleNums.outlineOffset + 1;
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';
            ctx.fillStyle = 'rgba(111, 190, 255, 0.07)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
            ctx.beginPath();
            ctx.roundRect(ox, oy, ow, oh, 0.75);
            ctx.stroke();
            ctx.fill();
        }

        // ===================== 10. TABLE | END =====================
    }
    // ===================== DRAWING NOTE | HIDDEN (small zoom) =====================
    // Mirror of drawTableHidden: at far zoom-out only the note body/frame is
    // drawn — no text layout, no wrap measuring.
    function drawNoteHidden(note) {
        const ctx = contextRef.current;
        const scaleNums = scaleNumbersRef.current;
        const { id, x, y, width, height, color } = note;

        const isNoteFocused = focusedIdsRef.current.includes(id);
        const isNoteSelected = selectedElementsIdsRef.current.includes(id);
        const isResizingArea = resizeAreaPropsRef.current?.areaId;

        const hoveredElId = hoveredElementIdRef.current;
        const lineFrom = lineFromRef.current;
        const isStartedNewLine = lineFrom?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        // 0. Outline frame (focused / selected / hovered)
        if (
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            !isBtnExportImageEnabled &&
            (isNoteSelected || isNoteFocused || (hoveredElId === id && !isStartedNewLine && !isStartedNewPenSegment))
        ) {
            // light blue when the frame comes from the search spotlight preview
            ctx.strokeStyle = isBtnSearchEnabled && isNoteFocused
                ? 'rgba(93, 158, 255, 0.4)'
                : '#262b33';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;
            ctx.strokeRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                width + 2 * scaleNums.outlineOffset,
                height + 2 * scaleNums.outlineOffset + 1
            );
        }

        // Shadow + body background (drawNote's dark body, no text at this zoom)
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 5);
        ctx.fillStyle = noteBodyColor(color);
        ctx.lineWidth = scaleNums.outlineWidth + 0.5;
        ctx.strokeStyle = 'rgb(34, 40, 47)';
        ctx.stroke();
        ctx.fill();

        ctx.shadowColor = 'transparent';

        // Area selection outline
        if (
            (!isBtnExportImageEnabled &&
                isBtnNewAreaEnabled &&
                newAreaPropsRef?.current?.selectedElements?.includes(id))
            ||
            (!isBtnExportImageEnabled &&
                resizeAreaPropsRef.current?.selectedElements?.includes(id))
        ) {
            const ox = x - scaleNums.outlineOffset;
            const oy = y - scaleNums.outlineOffset - 0.5;
            const ow = width + 2 * scaleNums.outlineOffset;
            const oh = height + 2 * scaleNums.outlineOffset + 1;
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';
            ctx.fillStyle = 'rgba(111, 190, 255, 0.07)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
            ctx.beginPath();
            ctx.roundRect(ox, oy, ow, oh, 0.75);
            ctx.stroke();
            ctx.fill();
        }
    }

    function drawNote(note, exportImgCanvas = null) {
        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine =
            lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        let ctx = contextRef.current;
        if (isBtnExportImageEnabled && exportImgCanvas) {
            ctx = exportImgCanvas?.getContext('2d');
            // See ctxResetFontTracker's comment: without this, a matching
            // first font on this fresh export ctx would skip ctx.font= and
            // notes would export in the browser-default font.
            ctxResetFontTracker();
        }
        const scaleNums = scaleNumbersRef.current;
        const scale = scaleRef.current;

        // same small-zoom cutoff as drawTable
        if (scale <= 0.15) { drawNoteHidden(note); return; }

        let {
            id, x, y, width, height, text,
            color, border,
            alignmentHorizontal, alignmentVertical,
            isFontMonospace,
        } = note;

        const isTableFocused = focusedIdsRef.current.includes(id);
        const isTableSelected = selectedElementsIdsRef.current.includes(id);

        ctx.shadowColor = 'transparent';

        // ===================== 0. FOCUSED NOTE | OUTLINE FRAME =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            (
                // note is focused
                isTableFocused ||
                // note is selected by mouse selection box
                isTableSelected ||
                // note is hovered and its not new line drawing
                (hoveredElementIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment)
            )
        ) {
            // same frame style as drawTable's section 0 (incl. the light-blue
            // search-preview variant)
            ctx.strokeStyle = isBtnSearchEnabled && isTableFocused
                ? 'rgba(93, 158, 255, 0.4)'
                : '#262b33';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                width + 2 * scaleNums.outlineOffset,
                height + 2 * scaleNums.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            ctx.strokeRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                width + 2 * scaleNums.outlineOffset,
                height + 2 * scaleNums.outlineOffset + 1
            );
        }

        // ===================== 1. FOCUSED NOTE | RESIZE POINTS =====================
        if (
            scale > 0.15 &&
            !isCursorHand &&
            !isBtnRedGreenViewEnabled &&
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            isTableFocused &&
            focusedIdsRef.current.length === 1
        ) {
            // same handle style as drawTable's section 1
            const resizeHandleSize = Math.max(scaleNums.resizeHandleSize > 4 ? scaleNums.resizeHandleSize - 3 : scaleNums.resizeHandleSize, 3);
            const resizeHandleRadius = Math.max(scaleNums.resizeHandleRadius > 1 ? scaleNums.resizeHandleRadius - 1 : scaleNums.resizeHandleRadius, 1.75);
            const resizeX = x - scaleNums.outlineOffset;
            const resizeY = y - scaleNums.outlineOffset;
            const hw = resizeHandleSize + 2;
            const hs = resizeHandleSize / 2 + 1;
            const leftX = resizeX - hs;
            const topY = resizeY - hs;
            const rightX = resizeX + width + scaleNums.outlineOffset * 2 - hs;
            const bottomY = resizeY + height + scaleNums.outlineOffset * 2 - hs;

            ctx.beginPath();
            ctx.strokeStyle = '#262b33';
            ctx.fillStyle = '#e6e6e6';
            ctx.lineWidth = scaleNums.outlineWidth + 1;
            ctx.roundRect(leftX, topY, hw, hw, resizeHandleRadius);
            ctx.roundRect(rightX, topY, hw, hw, resizeHandleRadius);
            ctx.roundRect(leftX, bottomY, hw, hw, resizeHandleRadius);
            ctx.roundRect(rightX, bottomY, hw, hw, resizeHandleRadius);
            ctx.stroke();
            ctx.fill();
        }

        // ===================== NOTE SHADOW =====================
        // Set shadow properties
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. NOTE BACKGROUND | RECTANGLE =====================
        ctx.fillStyle = noteBodyColor(color);

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 5);
        ctx.fill();
        // ctx.shadowColor = 'transparent';
        ctx.shadowColor = "rgba(0,0,0,0.35)";
        ctx.shadowBlur = 30;   // замість 24
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 8;
        // border style: solid (default) / dashes / dots — same dash pattern family as drawArea
        ctx.save();
        // ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
        // ctx.lineWidth = 1.5;

        // ctx.strokeStyle = 'rgba(171, 171, 184, 0.15)';
        // // ctx.lineWidth = scaleNums.outlineWidth + 1;
        // ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;
        ctx.lineWidth = 1;
        // ctx.strokeStyle = 'rgb(34, 40, 47)';
        // ctx.strokeStyle = 'rgb(33, 38, 45)';
        ctx.strokeStyle = 'rgb(32, 37, 44)';
        // ctx.strokeStyle = 'rgb(31, 36, 42)';

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 5);
        ctx.stroke();

        ctx.restore();

        // ===================== NOTE TEXT =====================
        const focusedInsideEl = focusedInsideElementStateRef.current;
        const isNoteEditing =
            focusedInsideEl?.elementId === id &&
            focusedInsideEl?.insideElementType === 'note-text';
        const isRenderingStaticMode =
            isStartedLineState || isBoardMovingStarted || isBoardMoving ||
            isScaling || isMovingElementId || isElementResizing;

        // While resizing, the editor overlay unmounts and isRenderingStaticMode
        // (which includes isElementResizing) keeps the canvas text VISIBLE —
        // doc values, live re-wrap to the changing width — matching how table
        // column text stays drawn during a resize.
        // if ((isBtnExportImageEnabled && exportImgCanvas) || isRenderingStaticMode || !isNoteEditing) {
        //
        // Every OTHER draw* function in this file (drawTable, headers, etc.)
        // assigns ctx.font DIRECTLY, bypassing ctxSetFont/_ctxCurrentFont —
        // so the tracker can walk in here still holding a PREVIOUS note's
        // font string while the real ctx.font is whatever a table last set.
        // If this note's first (plain) run happens to build that same font
        // string, ctxSetFont's dedup skips the assignment and that run
        // paints at the stale (usually smaller, table) font — until the
        // first bold/italic run's DIFFERENT string forces a real assignment
        // and everything after it looks right. Reset unconditionally here,
        // before both the layout/measure pass below (cache-miss path calls
        // ctxSetFont via measureRun too) and the paint loop further down, so
        // neither pass can trust a tracker value from anything outside this
        // note's own draw. The export-ctx swap above already resets for its
        // own reason (fresh ctx); resetting again here is a harmless extra
        // ctx.font= assignment on top.
        ctxResetFontTracker();
        const family = isFontMonospace ? 'Roboto Mono, sans-serif' : 'Inter, sans-serif';
        const fontSize = isFontMonospace ? NOTE_FONT_MONO_SIZE : NOTE_FONT_SIZE;
        // Shared by the measurer (layout pass) and the paint loop below, so
        // the two can never drift apart — a run measured with one font
        // string must be painted with that exact same string.
        const runFont = (bold, italic) => `${italic ? 'italic ' : ''}${bold ? 700 : 400} ${fontSize}px ${family}`;
        // Per-run measurer: bold/italic vary per run, size/family are whole-note.
        // Goes through the same ctxSetFont/_ctxCurrentFont dedup every other
        // draw* function uses, so redundant ctx.font= assignments are skipped
        // even as runs alternate styles within one note.
        const measureRun = (runText, { bold, italic }) => {
            ctxSetFont(ctx, runFont(bold, italic));
            return ctx.measureText(runText).width;
        };

        const maxTextWidth = width - 2 * NOTE_PADDING_X;
        const cached = noteLayoutCacheRef.current.get(id);
        let lines;
        if (cached && cached.text === text && cached.width === maxTextWidth && cached.isFontMonospace === isFontMonospace) {
            lines = cached.lines;
        } else {
            const blocks = parseNoteHtml(text);
            lines = layoutRichBlocks(blocks, { maxWidth: Math.max(maxTextWidth, 10), measureRun });
            noteLayoutCacheRef.current.set(id, { text, width: maxTextWidth, isFontMonospace, blocks, lines });
        }

        // link-hover hit zones (spec 8b): default to none, overwritten below
        // once firstBaseline/innerWidth are known — keeps a stale zone list
        // from lingering if this note's lines ever come back empty.
        noteLinkZonesRef.current.set(id, []);

        if (lines.length) {
            ctx.save();
            ctx.beginPath();
            // clip ends at the bottom padding — a partially visible last row
            // is allowed (cut at the boundary), matching the EditableNote
            // overlay whose scroll viewport ends at the same padded edge
            ctx.rect(x, y, width, height - NOTE_PADDING_Y);
            ctx.clip();

            const textColor = 'rgb(227, 235, 236)';
            // const linkColor = 'rgb(93, 158, 255)';
            // const linkColor = 'rgb(174, 182, 194)';
            // const linkColor = 'rgb(133, 182, 255)';
            // const linkColor = 'rgb(149, 191, 255)';
            const linkColor = 'rgb(138, 176, 233)';
            ctx.textAlign = 'left';

            const innerWidth = maxTextWidth;
            const blockHeight = lines.length * NOTE_LINE_HEIGHT;
            let firstBaseline = y + NOTE_PADDING_Y + fontSize;
            if (alignmentVertical === 'middle') {
                firstBaseline = y + Math.max((height - blockHeight) / 2, NOTE_PADDING_Y) + fontSize;
            } else if (alignmentVertical === 'bottom') {
                firstBaseline = y + Math.max(height - NOTE_PADDING_Y - blockHeight, NOTE_PADDING_Y) + fontSize;
            }

            for (let i = 0; i < lines.length; i++) {
                const lineY = firstBaseline + i * NOTE_LINE_HEIGHT;
                // stop only once a line falls fully below the padded clip —
                // the boundary row stays partially visible, like the editor
                if (lineY - fontSize > y + height - NOTE_PADDING_Y) break;
                const line = lines[i];

                // whole-note horizontal alignment offsets the line as a unit
                // (indent + text together) inside the note's inner width
                let lineOffsetX = 0;
                if (alignmentHorizontal === 'right') lineOffsetX = innerWidth - line.width;
                else if (alignmentHorizontal === 'center') lineOffsetX = (innerWidth - line.width) / 2;
                const lineX = x + NOTE_PADDING_X + lineOffsetX;

                for (const run of line.runs) {
                    if (run.text === '') continue;
                    // Same font string the layout pass measured this run
                    // with (via runFont) — set directly rather than calling
                    // measureRun again just to discard its width.
                    ctxSetFont(ctx, runFont(run.bold, run.italic));
                    ctx.fillStyle = run.href ? linkColor : textColor;
                    const runX = lineX + run.x;
                    ctx.fillText(run.text, runX, lineY);

                    if (run.underline || run.strike || run.href) {
                        ctx.strokeStyle = run.href ? linkColor : textColor;
                        ctx.lineWidth = 1.5;
                        ctx.setLineDash([]);
                        // butt caps, not inherited 'round' from earlier paths —
                        // CSS text-decoration in EditableNote has square ends
                        ctx.lineCap = 'butt';
                        if (run.underline || run.href) {
                            ctx.beginPath(); ctx.moveTo(runX, lineY + 3); ctx.lineTo(runX + run.width, lineY + 3); ctx.stroke();
                        }
                        if (run.strike) {
                            ctx.beginPath(); ctx.moveTo(runX, lineY - fontSize * 0.32); ctx.lineTo(runX + run.width, lineY - fontSize * 0.32); ctx.stroke();
                        }
                    }
                }

                // list markers: drawn once per line, only on a block's first
                // visual line (continuations stay indented but unmarked)
                if (line.isBlockStart && line.blockType === 'bullet') {
                    ctx.beginPath();
                    ctx.fillStyle = textColor;
                    const dotY = lineY - fontSize + NOTE_LINE_HEIGHT / 2 - 1.5;
                    ctx.arc(lineX + BULLET_DOT_X, dotY, BULLET_DOT_RADIUS, 0, Math.PI * 2);
                    ctx.fill();
                } else if (line.isBlockStart && line.blockType === 'numbered' && line.listIndex != null) {
                    ctxSetFont(ctx, `${fontSize}px ${family}`);
                    ctx.fillStyle = textColor;
                    ctx.textAlign = 'right';
                    ctx.fillText(`${line.listIndex}.`, lineX + LIST_INDENT - 6, lineY);
                    ctx.textAlign = 'left';
                }
            }

            // Same firstBaseline/innerWidth/alignment/clip-boundary math the
            // paint loop above just ran, replayed by the pure helper to
            // collect this note's VISIBLE link-run rects in world coords —
            // see collectNoteLinkZones' own doc comment (richTextLayout.js)
            // for the exact "fully visible line" rule.
            noteLinkZonesRef.current.set(id, collectNoteLinkZones(lines, {
                x: x + NOTE_PADDING_X,
                firstBaseline,
                lineHeight: NOTE_LINE_HEIGHT,
                fontSize,
                innerWidth,
                alignmentHorizontal,
                clipBottom: y + height - NOTE_PADDING_Y,
            }));

            ctx.textAlign = 'left';
            ctx.restore();

        }
        // }

        // ===================== NOTE OVERFLOW INDICATOR =====================
        // circle + chevron-down at the bottom center when the text has more
        // rows than the note can show. Driven by the layout cache so it also
        // renders while the editor overlay is open (the overlay leaves the
        // bottom strip uncovered) — and, for the same reason, while
        // exporting: the layout/measure pass above that populates this cache
        // runs unconditionally regardless of `isNoteEditing` (the canvas text
        // itself always draws; only the live DOM overlay, absent in an
        // export, ever visually covers it — see the "NOTE TEXT" comment
        // above), so this composes correctly with no changes needed there.
        //
        // DRAWN in image exports (an exported overflowing note should always
        // show its overflow state) — OVERFLOW_INDICATOR_MIN_SCALE is a
        // live-canvas-only concern (see the table indicator's identical
        // reasoning above); an export bypasses it entirely.
        {
            const cachedLayout = noteLayoutCacheRef.current.get(id);
            const innerHeight = height - NOTE_PADDING_Y - NOTE_PADDING_Y_BOTTOM;
            const isExportingNoteIndicator = isBtnExportImageEnabled && exportImgCanvas;
            if (
                (isExportingNoteIndicator || scale > OVERFLOW_INDICATOR_MIN_SCALE) &&
                cachedLayout &&
                cachedLayout.lines.length * NOTE_LINE_HEIGHT > innerHeight
            ) {
                const chevronCx = x + width / 2;
                const chevronCy = y + height;
                // inverse-scale so the indicator keeps a constant on-screen
                // size at any zoom (same idea as the resize-handle sizing)
                const chevronR = 13 / scale;

                ctx.beginPath();
                ctx.arc(chevronCx, chevronCy, chevronR, 0, Math.PI * 2);
                ctx.fillStyle = 'rgb(32, 37, 44)';
                ctx.fill();

                ctx.beginPath();
                ctx.strokeStyle = 'rgb(200, 210, 214)';
                ctx.lineWidth = 2.5 / scale;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.moveTo(chevronCx - 4 / scale, chevronCy - 2 / scale);
                ctx.lineTo(chevronCx, chevronCy + 2.5 / scale);
                ctx.lineTo(chevronCx + 4 / scale, chevronCy - 2 / scale);
                ctx.stroke();
                ctx.lineCap = 'butt';
            }
        }

        // ===================== SELECTED NOTE | OUTLINE FRAME (IN AREA SELECTED FRAME) =====================
        // same style as drawTable's section 10
        if (
            // create new area: selection note
            (!isBtnExportImageEnabled &&
                isBtnNewAreaEnabled &&
                newAreaPropsRef?.current?.selectedElements?.includes(id))
            ||
            // resize/move area: selection note
            (!isBtnExportImageEnabled &&
                resizeAreaPropsRef.current?.selectedElements?.includes(id))
        ) {
            const ox = x - scaleNums.outlineOffset;
            const oy = y - scaleNums.outlineOffset - 0.5;
            const ow = width + 2 * scaleNums.outlineOffset;
            const oh = height + 2 * scaleNums.outlineOffset + 1;
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';
            ctx.fillStyle = 'rgba(111, 190, 255, 0.07)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
            ctx.beginPath();
            ctx.roundRect(ox, oy, ow, oh, 0.75);
            ctx.stroke();
            ctx.fill();
        }
    }
    function drawSelectionBox() {
        const ctx = contextRef.current;
        const sx = selectionStartXYRef.current.x;
        const sy = selectionStartXYRef.current.y;
        const sw = selectionCurrentXYRef.current.x - sx;
        const sh = selectionCurrentXYRef.current.y - sy;

        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(129, 144, 173, 0.11)';
        ctx.fillRect(sx, sy, sw, sh);

        ctx.strokeStyle = 'rgba(171, 171, 184, 0.16)';
        ctx.lineWidth = scaleNumbersRef.current.selectionBoxLine;
        ctx.strokeRect(sx, sy, sw, sh);
    }

    function drawArea(area, exportImgCanvas = null) {
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        const {
            id,
            x,
            y,
            width,
            height,
            border,
            color,
        } = area;
        // let { id, x, y, width, height, value: { name, color, nestedItems } } = area;

        const isTableFocused = focusedIdsRef.current.includes(id);
        const scaleNums = scaleNumbersRef.current;
        const scale = scaleRef.current;

        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine =
            lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        // ===================== 0. FOCUSED AREA | OUTLINE FRAME =====================
        const isAreaHovered =
            hoveredAreaIdRef.current === id &&
            !hoveredElementIdRef.current &&
            !hoveredLineIdRef.current;
        if (
            // area is focused / selected
            isTableFocused ||
            // area is hovered and its not new line drawing
            (isAreaHovered && !isStartedNewLine && !isStartedNewPenSegment) ||
            selectedAreasIdsRef.current.includes(id)
        ) {
            ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;
            ctx.setLineDash([]);


            ctx.beginPath();
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);

            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset - 5, y - scaleNumbersRef.current.outlineOffset - 5, width + 2 * scaleNumbersRef.current.outlineOffset + 10, height + 2 * scaleNumbersRef.current.outlineOffset + 10, 0.75); // ASAP LAST


            const xOffset = width < 0 ? -scaleNums.outlineOffset : scaleNums.outlineOffset;
            const yOffset = height < 0 ? -scaleNums.outlineOffset : scaleNums.outlineOffset;
            ctx.roundRect(
                x - xOffset,
                y - yOffset,
                width + 2 * xOffset,
                height + 2 * yOffset,
                0.75
            );

            ctx.stroke();

            // ctx.shadowColor = 'transparent';
            // ctx.fillStyle = '#39424c'; // canvas background
            // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
            // ctx.stroke();
        }

        // ===================== 1. FOCUSED AREA | RESIZE POINTS =====================
        if (
            scale > 0.15 &&
            !isCursorHand &&
            !isBtnRedGreenViewEnabled &&
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            focusedIdsRef.current.includes(area.id) &&
            focusedIdsRef.current.length === 1
        ) {
            const resizeHandleSize = Math.max(scaleNums.resizeHandleSize > 4 ? scaleNums.resizeHandleSize - 3 : scaleNums.resizeHandleSize, 3);
            const resizeHandleRadius = Math.max(scaleNums.resizeHandleRadius > 1 ? scaleNums.resizeHandleRadius - 1 : scaleNums.resizeHandleRadius, 1.75);
            const rxOff = width < 0 ? -scaleNums.outlineOffset : scaleNums.outlineOffset;
            const ryOff = height < 0 ? -scaleNums.outlineOffset : scaleNums.outlineOffset;
            const resizeX = x - rxOff;
            const resizeY = y - ryOff;
            const hw = resizeHandleSize + 2;
            const hs = resizeHandleSize / 2 + 1;
            const leftX = resizeX - hs;
            const topY = resizeY - hs;
            const rightX = resizeX + width + rxOff * 2 - hs;
            const bottomY = resizeY + height + ryOff * 2 - hs;

            ctx.beginPath();
            ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
            ctx.fillStyle = '#e6e6e6';
            ctx.lineWidth = scaleNums.outlineWidth + 1;
            ctx.roundRect(leftX, topY, hw, hw, resizeHandleRadius);
            ctx.roundRect(rightX, topY, hw, hw, resizeHandleRadius);
            ctx.roundRect(leftX, bottomY, hw, hw, resizeHandleRadius);
            ctx.roundRect(rightX, bottomY, hw, hw, resizeHandleRadius);
            ctx.stroke();
            ctx.fill();
        }

        // // ===================== AREA SHADOW =====================
        // // Set shadow properties
        // ctx.shadowColor = theme.table.shadow.color;
        // ctx.shadowBlur = 8; // Розмиття тіні
        // ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        // ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== AREA FRAME LINE =====================
        // ===================== AREA BACKGROUND =====================
        // ctx.lineWidth = scaleRef.current > 1 ? 2.8 : 3;
        // ctx.lineWidth = scaleRef.current > 1 ? 2 / scaleRef.current : 2;
        ctx.lineWidth = 2 / scale;
        const borderRadius = 28;

        // // orange
        // ctx.fillStyle = 'rgba(255, 129, 88, 0.11)'; // 0.07 // 0.12
        // ctx.strokeStyle = '#ff8158';

        // // grey
        // ctx.fillStyle = 'rgba(168, 170, 174, 0.1)';
        // ctx.strokeStyle = '#a8aaae';

        // // blue
        // ctx.fillStyle = 'rgba(28, 134, 255, 0.1)';
        // ctx.strokeStyle = '#1c86ff';

        // // green
        // ctx.fillStyle = 'rgba(59, 173, 132, 0.1)';
        // ctx.strokeStyle = '#3bad84';

        // // red
        // ctx.fillStyle = 'rgba(255, 0, 0, 0.09)'; // 0.06 // 0.08
        // ctx.strokeStyle = '#e80362';

        // // purple
        // ctx.fillStyle = 'rgba(139, 125, 235, 0.1)';
        // ctx.strokeStyle = '#8b7deb';

        // // violet
        // ctx.fillStyle = 'rgba(235, 3, 235, 0.075)';
        // ctx.strokeStyle = '#eb03eb';

        // COLOR

        ctx.fillStyle = 'rgba(175, 197, 240, 0.05)';
        ctx.strokeStyle = 'rgba(173, 187, 218, 1)';

        if (color) {
            switch (color) {
                case 'blue': {
                    ctx.fillStyle = 'rgba(98, 153, 255, 0.05)';
                    ctx.strokeStyle = 'rgba(108, 146, 195, 1)';
                    break;
                }
                case 'green': {
                    ctx.fillStyle = 'rgba(98, 255, 158, 0.04)';
                    ctx.strokeStyle = 'rgba(55, 167, 126, 1)';
                    break;
                }
                case 'orange': {
                    ctx.fillStyle = 'rgba(255, 129, 88, 0.11)'; // 0.07 // 0.12
                    ctx.strokeStyle = '#ff8158';
                    break;
                }
                case 'yellow': {
                    ctx.fillStyle = 'rgba(225, 179, 93, 0.11)';
                    ctx.strokeStyle = '#e1b35d';
                    break;
                }
                case 'pink': {
                    ctx.fillStyle = 'rgba(225, 93, 137, 0.11)';
                    ctx.strokeStyle = '#e15d89';
                    break;
                }
                case 'purple': {
                    ctx.fillStyle = 'rgba(116, 84, 232, 0.04)';
                    ctx.strokeStyle = 'rgba(151, 145, 243, 1)';
                    break;
                }
                case 'violet': {
                    ctx.fillStyle = 'rgba(220, 97, 220, 0.05)';
                    ctx.strokeStyle = '#dd78ddff';
                    break;
                }
                case 'red': {
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.04)';
                    ctx.strokeStyle = '#e80362';
                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }

        // BORDER

        ctx.setLineDash([]);

        if (!border || border === 'solid') {
            ctx.lineCap = 'square';
        } else if (border === 'dashes') {
            const dashLineInterval = scale > 1 ? 12 : 12 / scale;
            const dashSpaceInterval = scale > 1 ? 15 : 15 / scale;
            ctx.lineCap = 'butt';
            ctx.setLineDash([dashLineInterval, dashSpaceInterval]);
        } else if (border === 'dots') {
            const dashLineInterval = scale > 1 ? 4 : 4 / scale;
            const dashSpaceInterval = scale > 1 ? 6 : 6 / scale;
            ctx.lineCap = 'butt';
            ctx.setLineDash([dashLineInterval, dashSpaceInterval]);
        }

        // ===================== FOCUS ON AREA | WHEN MOVED ELEMENT IS IN/OUT AREA =====================
        // if (
        //     isMovingElementId &&
        //     elementsRef.current[isMovingElementId]?.value?.areaId === id
        // ) {
        //     ctx.lineWidth = scaleRef.current > 1 ? 2.8 : 2.8 / scaleRef.current;

        //     ctx.lineCap = 'square';
        //     ctx.setLineDash([]);
        //     ctx.strokeStyle = '#5f88ff';
        //     ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
        //     // ctx.strokeStyle = '#e6e6e6';

        //     ctx.strokeStyle = '#e15d89';
        //     ctx.strokeStyle = '#e2f2ffff';
        //     ctx.strokeStyle = '#d2ebffff';

        // }

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, borderRadius);
        ctx.stroke();
        ctx.fill();

        ctx.setLineDash([]);












        // ===================== 10. SELECTED TABLE | OUTLINE FRAME (IN AREA SELECTED FRAME) =====================
        // if (!isBtnExportImageEnabled &&
        //     // resizeAreaPropsRef.current?.selectedElements?.includes(id)
        //     isMovingElementId
        // ) {

        if (
            // isMovingElementId &&
            // elementsRef.current[isMovingElementId]?.value?.areaId === id &&
            // elementsRef.current[isMovingElementId]?.value?.isChangedAreaId
            resizeElementPropsRef.current?.areaId === id
            // resizeElementPropsRef.current = { elementId, areaId: newAreaIdForElement }
        ) {
            // ctx.strokeStyle = 'rgba(250, 250, 250, 0.2)';
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
            // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                width + 2 * scaleNums.outlineOffset,
                height + 2 * scaleNums.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            // ctx.shadowColor = 'transparent';

            ctx.fillStyle = 'rgba(111, 190, 255, 0.05)';
            ctx.fillRect(
                x - scaleNums.outlineOffset,
                y - scaleNums.outlineOffset - 0.5,
                width + 2 * scaleNums.outlineOffset,
                height + 2 * scaleNums.outlineOffset + 1
            );
            // ctx.stroke();
        }





        // ===================== TEXT HEADER =====================

        // const radius = 18 / scaleRef.current;
        // // const borderRadius = Math.min((Math.min(Math.abs(width), Math.abs(height))) / 10, maxBorderRadius)

        // ctx.beginPath();
        // ctx.fillStyle = theme.table.body.background;
        // ctx.roundRect(x + 10, y + 10, 300, 50, radius);
        // ctx.fill();

        // ===================== AREA END =====================
    }

    // function getTextByWidthLimit(ctx, text, maxWidth) {
    //     let currentLine = "";

    //     const symbols = text.split('');
    //     for (let i = 0; i < symbols.length; i++) {
    //         const symbol = symbols[i];

    //         const tempLine = currentLine + symbol;
    //         const tempLineWidth = ctxMeasure(ctx, tempLine);

    //         if (tempLineWidth > maxWidth) {
    //             return currentLine + '...';
    //         } else {
    //             currentLine = tempLine;
    //         }
    //     }

    //     return currentLine;
    // }

    function getDescriptionMaxWidth(ctx, text) {
        const lines = [];
        let linesMaxWidth = 0;

        const paragraphs = text.split("\n");  // Розбиваємо текст на абзаци по \n (новий рядок)

        paragraphs.forEach((paragraph, index) => {
            const words = paragraph.split(" ");
            let currentLine = "";
            let currentLineWidth = 0;

            words.forEach(word => {
                // Якщо слово містить табуляцію, заміняємо її на пробіли
                word = word.replace(/\t/g, "    ");  // Заміна табуляції на 4 пробіли

                currentLine = currentLine + word + " ";
                currentLineWidth = ctxMeasure(ctx, currentLine);
            });

            // Додаємо останній рядок абзацу
            if (currentLine.length > 0) {
                lines.push(currentLine);
                linesMaxWidth = Math.max(linesMaxWidth, currentLineWidth);
            }
        });

        return linesMaxWidth;
    }

    function getTableNameByWidthLimit(ctx, text, firstLineMaxWidth, lineMaxWidth) {
        let lines = [];
        let currentLine = "";
        let textMaxWidth = firstLineMaxWidth;

        const symbols = text.split('');
        for (let i = 0; i < symbols.length; i++) {
            const symbol = symbols[i];

            const tempLine = currentLine + symbol;
            const tempLineWidth = ctxMeasure(ctx, tempLine);

            if (tempLineWidth > textMaxWidth) {
                lines.push(currentLine);
                currentLine = symbol;

                if (lines.length === 1 || textMaxWidth !== lineMaxWidth) {
                    textMaxWidth = lineMaxWidth;
                }
            } else {
                currentLine = tempLine;
            }
        }

        // Додаємо останній рядок абзацу
        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        if (lines.length === 0) {
            lines.push('');
        }

        return lines;
    }

    function getEnumValuesByWidthLimit(ctx, text, lineMaxWidth) {
        let lines = [];
        let currentLine = "";

        const symbols = text.split('');
        for (let i = 0; i < symbols.length; i++) {
            const symbol = symbols[i];

            const tempLine = currentLine + symbol;
            const tempLineWidth = ctxMeasure(ctx, tempLine);

            if (tempLineWidth > lineMaxWidth) {
                lines.push(currentLine);
                currentLine = symbol;
            } else {
                currentLine = tempLine;
            }
        }

        // Додаємо останній рядок абзацу
        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        if (lines.length === 0) {
            lines.push('');
        }

        return lines;
    }


    // // Функція для розбиття тексту на кілька рядків
    // function wrapTableName(ctx, text, maxWidth) {
    //     const lines = [];
    //     const paragraphs = text.split("\n");  // Розбиваємо текст на абзаци по \n (новий рядок)

    //     paragraphs.forEach(paragraph => {
    //         const words = paragraph.split(" ");
    //         let currentLine = "";

    //         words.forEach(word => {
    //             // Якщо слово містить табуляцію, заміняємо її на пробіли
    //             word = word.replace(/\t/g, "    ");  // Заміна табуляції на 4 пробіли

    //             // Якщо слово занадто довге, розбиваємо його на частини
    //             if (ctxMeasure(ctx, word) > maxWidth) {
    //                 const splitWord = breakLongWord(ctx, word, maxWidth);
    //                 splitWord.forEach(part => {
    //                     const testLine = currentLine + part + " ";
    //                     const testWidth = ctxMeasure(ctx, testLine);

    //                     if (testWidth > maxWidth && currentLine !== "") {
    //                         lines.push(currentLine);
    //                         currentLine = part + " ";
    //                     } else {
    //                         currentLine = testLine;
    //                     }
    //                 });
    //             } else {
    //                 const testLine = currentLine + word + " ";
    //                 const testWidth = ctxMeasure(ctx, testLine);

    //                 if (testWidth > maxWidth && currentLine !== "") {
    //                     lines.push(currentLine);
    //                     currentLine = word + " ";
    //                 } else {
    //                     currentLine = testLine;
    //                 }
    //             }
    //         });

    //         // Додаємо останній рядок абзацу
    //         if (currentLine.length > 0) {
    //             lines.push(currentLine);
    //         }
    //     });

    //     return lines;
    // }

    // Функція для розбиття тексту на кілька рядків
    function wrapText(ctx, text, maxWidth) {
        const lines = [];
        const paragraphs = text.split("\n");  // Розбиваємо текст на абзаци по \n (новий рядок)

        paragraphs.forEach(paragraph => {
            const words = paragraph.split(" ");
            let currentLine = "";

            words.forEach(word => {
                // Якщо слово містить табуляцію, заміняємо її на пробіли
                word = word.replace(/\t/g, "    ");  // Заміна табуляції на 4 пробіли

                // Якщо слово занадто довге, розбиваємо його на частини
                if (ctxMeasure(ctx, word) > maxWidth) {
                    const splitWord = breakLongWord(ctx, word, maxWidth);
                    splitWord.forEach(part => {
                        const testLine = currentLine + part + " ";
                        const testWidth = ctxMeasure(ctx, testLine);

                        if (testWidth > maxWidth && currentLine !== "") {
                            lines.push(currentLine);
                            currentLine = part + " ";
                        } else {
                            currentLine = testLine;
                        }
                    });
                } else {
                    const testLine = currentLine + word + " ";
                    const testWidth = ctxMeasure(ctx, testLine);

                    if (testWidth > maxWidth && currentLine !== "") {
                        lines.push(currentLine);
                        currentLine = word + " ";
                    } else {
                        currentLine = testLine;
                    }
                }
            });

            // Додаємо останній рядок абзацу
            if (currentLine.length > 0) {
                lines.push(currentLine);
            }
        });

        return lines;
    }

    // Функція для розбиття дуже довгого слова на частини
    function breakLongWord(ctx, word, maxWidth) {
        const parts = [];
        let part = "";

        // Розбиваємо слово на частини, поки кожна частина не буде вміщуватися в maxWidth
        for (let i = 0; i < word.length; i++) {
            part += word[i];
            if (ctxMeasure(ctx, part) > maxWidth) {
                parts.push(part.slice(0, -1));  // Додаємо частину слова, що вміщується
                part = word[i];  // Починаємо нову частину
            }
        }

        // Додаємо останню частину слова
        if (part) {
            parts.push(part);
        }

        return parts;
    }

    function drawTableTooltip(table) {
        const isSelectionMoving = isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !== selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !== selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        if (isBoardMoving || isSelectionMoving || isStartedNewLine || isStartedNewPenSegment) {
            return;
        }

        const ctx = contextRef.current;
        const { x: tableX, y: tableY, width: tableWidth, name, description: tableDescription, columns } = table;

        const description = tableDescription || '';
        const indexes = [];

        const columnsKeys = Object.keys(columns);
        columnsKeys.forEach(columnKey => {
            const column = columns[columnKey];
            if (column.indexType) {
                indexes.push({ columns: [column.name] });
            }
        });

        // indexes = [
        //     { columns: ['id'] },
        //     { columns: ['id', 'created_at'] },
        //     { columns: ['status', 'registration_date'] },
        // ]
        const scale = 1 / scaleRef.current;

        // ===================================================================
        // ============ 0. TOOLTIP (BASIC) ===================================
        // ===================================================================

        // ============ TOOLTIP (BASIC) ====================
        // tooltip (x, y) with distance to table on 'x' and align by table focused frame on 'y'
        const tooltipX = tableX + tableWidth + 21 * scale;
        const tooltipY = tableY - 6 * scale;

        // TODO: tooltip left to table / tooltip right to table

        const minTooltipWidth = 260;
        const maxTooltipWidth = 420;

        let tooltipWidth = minTooltipWidth;
        // const tooltipHeight = 160 * scale;

        const fontSize = 20;

        // ===================================================================
        // ============ 1. TOOLTIP (MEASURE TEXT FIELDS) =====================
        // ===================================================================

        // ============ TOOLTIP (MEASURE NAME) ====================
        // name styles
        ctxSetFont(ctx, `500 ${20}px Inter, sans-serif`);
        ctx.textAlign = 'left';

        const nameWidth = ctxMeasure(ctx, name);
        const oneLineHeaderWidth = nameWidth + 21 + 74 + 21 + 2;

        // ============ TOOLTIP (MEASURE DESCRIPTION) ====================
        // description styles
        ctxSetFont(ctx, `${20}px Inter, sans-serif`);
        ctx.textBaseline = "top";
        ctx.textAlign = 'left';

        // description width without 'wrapping' text (without splitting by smaller parts and move to the next line)
        const oneLineDescriptionWidth = 18 + 18 + getDescriptionMaxWidth(ctx, description); // descriptionMaxWidth

        // ============ TOOLTIP (MEASURE INDEXES) ========================
        let oneLineIndexMaxWidth = 0;
        const spaceSymbolWidth = 2.25;

        indexes.forEach(idx => {
            const indexName = idx.columns.join(', ');

            // measure 'left bracket' / '('
            ctxSetFont(ctx, `500 ${(fontSize)}px Inter, sans-serif`);
            const leftBracketWidth = ctxMeasure(ctx, '(');

            // measure 'index name'
            ctxSetFont(ctx, `400 ${(fontSize)}px Roboto, sans-serif`);
            const indexWidth = ctxMeasure(ctx, indexName);

            const oneLineIndexWidth = 18 + indexWidth + leftBracketWidth * 2 + spaceSymbolWidth * 2.15 + 21;
            oneLineIndexMaxWidth = Math.max(oneLineIndexMaxWidth, oneLineIndexWidth);
        })

        // ===================================================================
        // ============ 2. TOOLTIP (CALCULATE TOOLTIP WIDTH) =================
        // ===================================================================
        // let textMaxWidth = Math.max(oneLineHeaderWidth, oneLineDescriptionWidth, pkWidth, indexesWidth);
        let textMaxWidth = Math.max(oneLineHeaderWidth, oneLineDescriptionWidth, oneLineIndexMaxWidth);

        if (textMaxWidth > maxTooltipWidth) {
            tooltipWidth = maxTooltipWidth;
        } else if (textMaxWidth < minTooltipWidth) {
            tooltipWidth = minTooltipWidth;
        } else {
            tooltipWidth = textMaxWidth;
        }

        if (oneLineIndexMaxWidth > maxTooltipWidth) {
            tooltipWidth = oneLineIndexMaxWidth;
        }

        // ===================================================================
        // ============ 3. TOOLTIP (CALCULATE TEXT HEIGHT) ===================
        // ===================================================================

        // ============ TOOLTIP (CALCULATE NAME HEIGHT) ======================
        // name styles
        ctxSetFont(ctx, `500 ${20 * scale}px Inter, sans-serif`);
        ctx.textAlign = 'left';

        const nameFirstLineMaxWidth = (tooltipWidth - (21 + 70 - 6 + 21)) * scale;
        const nameLineMaxWidth = (tooltipWidth - (21 + 21)) * scale;

        const nameLines = getTableNameByWidthLimit(ctx, name, nameFirstLineMaxWidth, nameLineMaxWidth);
        const nameHeight = (27 + 6 - 18) * scale + nameLines.length * fontSize * 1.4 * scale + 21 * scale;

        // ============ TOOLTIP (CALCULATE DESCRIPTION HEIGHT) ===============
        // description styles
        ctxSetFont(ctx, `${fontSize}px Inter, sans-serif`);
        ctx.textBaseline = "top";

        const maxTextWidth = tooltipWidth - 38;

        const descriptionLines = wrapText(ctx, description, maxTextWidth);
        const descriptionHeight = description
            ? descriptionLines.length * fontSize * 1.4
            : 0;

        // FIX: if descriptionLines.length > 7 => then maxTooltipWidth from 420 to 460/480 or hide 'description next after 7 lines'

        // ============ TOOLTIP (CALCULATE INDEXES HEIGHT) ===============
        let indexesHeight = 22 * 1.4 + indexes.length * (20 * 1.6);

        if (description) {
            indexesHeight += 7;
        }

        if (!indexes.length) {
            indexesHeight = 0;
        }
        // ===================================================================
        // ============ 4. TOOLTIP BACKGROUND (CONNECTOR) ====================
        // ===================================================================

        // ============ TOOLTIP CONNECTOR (LINE) ===========
        const lineX = tableX + tableWidth - 15;
        const lineY = tableY + (TABLE_HEADER_HEIGHT / 2);

        ctx.lineWidth = 2.5 * scale;
        ctx.strokeStyle = 'rgba(45, 52, 65, 1)';

        ctx.beginPath();
        ctx.moveTo(lineX, lineY);
        ctx.lineTo(lineX + 15 + 25 * scale, lineY);
        ctx.stroke();

        // ============ TOOLTIP CONNECTOR (CIRCLE) =========
        const pointRadius = 5.5 * scale;
        ctx.fillStyle = 'rgba(45, 52, 65, 1)';

        ctx.beginPath();
        ctx.arc(lineX, lineY, pointRadius, 0, 2 * Math.PI);
        ctx.fill();

        // ===================================================================
        // ============ 5. TOOLTIP BACKGROUND (FRAME) ========================
        // ===================================================================

        // ============ TOOLTIP BACKGROUND (RECTANGLE + BORDER LINE)
        ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
        ctx.fillStyle = 'rgba(14, 15, 16, 1)';

        ctx.lineWidth = 1.6 * scale;
        ctx.beginPath();
        ctx.roundRect(tooltipX, tooltipY, tooltipWidth * scale, nameHeight + (descriptionHeight + indexesHeight) * scale, 6 * scale);
        ctx.fill();
        ctx.stroke();

        // ============ TOOLTIP BACKGROUND (SHADOW) ========
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================================================================
        // ============ 6. TOOLTIP HEADER (TABLE NAME) =======================
        // ===================================================================

        // ============ TOOLTIP HEADER (NAME TEXT) =========
        ctxSetFont(ctx, `500 ${20 * scale}px Inter, sans-serif`);
        ctx.fillStyle = '#eeeeee';
        ctx.textAlign = 'left';

        let headerX = tooltipX + 21 * scale;
        let headerY = tooltipY + (27 + 6 - 18) * scale + fontSize * 1.4 * scale;

        for (let i = 0; i < nameLines.length; i++) {
            if (i === 0) {
                ctxSetFont(ctx, `500 ${19 * scale}px Inter, sans-serif`);
                ctx.fillStyle = '#ffc65e';

                // ctx.fillText(t('canvas_table.tooltip_table'), tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
                ctx.fillText('Table', tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
                ctxSetFont(ctx, `500 ${19 * scale}px Inter, sans-serif`);
                ctx.fillStyle = '#eeeeee';
                ctx.fillText(nameLines[i], tooltipX + (21 + 70 - 6) * scale, tooltipY + (27 + 6 - 18) * scale);
            } else {
                ctx.fillText(nameLines[i], headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            }
        }
        // ctx.fillText('Table:', tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
        // ctx.fillText(name, tooltipX + (21 + 74) * scale, tooltipY + (27 + 6 - 18) * scale);

        // ============ TOOLTIP HEADER (LINE AFTER) ========
        const headerLineY = headerY + 3 * scale;
        ctx.fillStyle = 'rgba(45, 52, 65, 1)';

        ctx.fillRect(tooltipX + 16 * scale, headerLineY, tooltipWidth * scale - 32 * scale, 1.7 * scale);

        let nextSectionY = headerLineY;
        // ===================================================================
        // ============ 7. TOOLTIP BODY (DESCRIPTION) ========================
        // ===================================================================

        // ============ TOOLTIP BODY (DESCRIPTION TEXT) ====
        if (description) {
            ctxSetFont(ctx, `${fontSize * scale}px Inter, sans-serif`);
            ctx.fillStyle = '#efefef';

            // ctx.fillStyle = 'rgba(178, 191, 201, 1)';

            let descriptionX = tooltipX + 21 * scale;
            let descriptionY = nextSectionY + fontSize * 1.4 * 0.5 * scale;

            for (let i = 0; i < descriptionLines.length; i++) {
                ctx.fillText(descriptionLines[i], descriptionX, descriptionY);
                descriptionY += fontSize * 1.4 * scale;
            }

            nextSectionY = descriptionY;

            // ============ TOOLTIP BODY (LINE AFTER) ==========
            // const descriptionLineY = descriptionY + 4 * scale;
            // ctx.fillStyle = '#e6e8e9';
            // ctx.fillRect(tooltipX + 16 * scale, descriptionLineY, tooltipWidth * scale - 32 * scale, 1.7 * scale);
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (INDEXES) ============================
        // ===================================================================

        // ============ TOOLTIP BODY (INDEXES) =========
        if (indexes.length) {
            const newLineDiff = (32) * scale;
            const spaceSymbolWidth = 2.25;

            const indexesX = tooltipX + 18 * scale;

            const indexesY = description
                ? nextSectionY + (27 - 18 - 2) * scale
                : nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Indexes'
            ctxSetFont(ctx, `500 ${19 * scale}px Inter, sans-serif`);
            // ctx.fillStyle = '#d6384d';
            // ctx.fillStyle = '#ff5c5c';
            // ctx.fillStyle = '#ff6969';

            // ctx.fillStyle = '#ff7070';
            ctx.fillStyle = '#ffc65e';

            ctx.textAlign = 'left';
            // ctx.fillText(t('canvas_table.tooltip_indexes'), indexesX, indexesY);
            ctx.fillText('Indexes', indexesX, indexesY);

            const indexX = tooltipX + 23 * scale;
            let indexY = indexesY + (33) * scale;

            indexes.forEach(idx => {
                const indexName = idx.columns.join(', ');

                // measure 'index name'
                ctxSetFont(ctx, `400 ${(fontSize) * scale}px Roboto, sans-serif`);
                const indexWidth = ctxMeasure(ctx, indexName);

                // measure 'left bracket' / '('
                ctxSetFont(ctx, `500 ${(fontSize) * scale}px Inter, sans-serif`);
                const leftBracketWidth = ctxMeasure(ctx, '(');

                // fill 'left bracket' / '('
                ctxSetFont(ctx, `500 ${(fontSize) * scale}px Inter, sans-serif`);
                ctx.fillStyle = '#eeeeee';
                ctx.fillText('(', indexX, indexY);

                // fill 'index name'
                ctxSetFont(ctx, `400 ${(fontSize) * scale}px Roboto, sans-serif`);
                ctx.fillStyle = '#efefef';
                ctx.fillText(indexName, indexX + leftBracketWidth + (spaceSymbolWidth + 0.5) * scale, indexY);

                // fill 'right bracket' / ')'
                ctx.fillStyle = '#eeeeee';
                ctxSetFont(ctx, `500 ${(fontSize) * scale}px Inter, sans-serif`);
                ctx.fillText(')', indexX + indexWidth + leftBracketWidth + spaceSymbolWidth * 2.15 * scale, indexY);

                indexY = indexY + newLineDiff;
            })
        }
    }

    function drawColumnTooltip(table, column, columnIndex) {
        const isSelectionMoving = isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !== selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !== selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        if (isBoardMoving || isSelectionMoving || isStartedNewLine || isStartedNewPenSegment) {
            return;
        }

        const ctx = contextRef.current;
        const {
            x: tableX,
            y: tableY,
            width: tableWidth,
        } = table;
        const {
            name: columnName,
            type,
            description: columnDescription,
            enumValues: columnEnumValues,
            defaultValue: columnDefaultValue,
            generatedValue: columnGeneratedValue,
            checkValue: columnCheckValue,
        } = column;

        const description = columnDescription || '';
        const enumValues = columnEnumValues || [];
        const defaultValue = columnDefaultValue || '';
        const generatedValue = columnGeneratedValue || '';
        const checkValue = columnCheckValue || '';

        const scale = 1 / scaleRef.current;

        // ===================================================================
        // ============ 0. TOOLTIP (BASIC) ===================================
        // ===================================================================

        // ============ TOOLTIP (BASIC) ====================
        // tooltip (x, y) with distance to table on 'x' and align by table focused frame on 'y'
        const tooltipX = tableX + tableWidth + 21 * scale;

        // const tooltipY = tableY - 6 * scale;
        const columnY = tableY + TABLE_HEADER_HEIGHT + columnIndex * TABLE_ROW_HEIGHT + HEADER_BODY_MARGIN;

        const tooltipY = columnY - 6 * scale;
        // TODO: tooltip left to table / tooltip right to table

        const minTooltipWidth = 240;
        const maxTooltipWidth = 480;

        let tooltipWidth = minTooltipWidth;
        // const tooltipHeight = 160 * scale;

        const fontSize = 19;
        const spaceSymbolWidth = 2.25;

        // ===================================================================
        // ============ 1. TOOLTIP (MEASURE TEXT FIELDS) =====================
        // ===================================================================

        // ============ TOOLTIP (MEASURE NAME) ====================
        // name styles
        ctxSetFont(ctx, `500 ${20}px Inter, sans-serif`);
        ctx.textAlign = 'left';

        const nameText = `${columnName}  `;
        const nameTypeText = `${columnName}  ${type}`;

        const nameWidth = ctxMeasure(ctx, nameTypeText);
        const oneLineHeaderWidth = 21 + nameWidth + 21 + 2;

        // ============ TOOLTIP (MEASURE DESCRIPTION) ====================
        // description styles
        ctxSetFont(ctx, `${20}px Inter, sans-serif`);
        ctx.textBaseline = "top";
        ctx.textAlign = 'left';

        // description width without 'wrapping' text (without splitting by smaller parts and move to the next line)
        const oneLineDescriptionWidth = 18 + 18 + getDescriptionMaxWidth(ctx, description); // descriptionMaxWidth

        // ============ TOOLTIP (MEASURE 'ENUM') ========================
        const enumString = '( ' + enumValues.join(', ') + ' )';

        // measure 'enum'
        ctxSetFont(ctx, `400 ${(fontSize - 1)}px Inter, sans-serif`);
        const enumWidth = ctxMeasure(ctx, enumString);
        let oneLineEnumMaxWidth = 18 + enumWidth + spaceSymbolWidth * 2.15 + 21;

        // ============ TOOLTIP (MEASURE 'DEFAULT VALUE') ========================
        const defaultValueString = '( ' + defaultValue + ' )';

        // measure 'default value'
        ctxSetFont(ctx, `400 ${(fontSize - 1)}px Inter, sans-serif`);
        const defaultValueWidth = ctxMeasure(ctx, defaultValueString);
        let oneLineDefaultValueMaxWidth = 18 + defaultValueWidth + spaceSymbolWidth * 2.15 + 21;

        // ============ TOOLTIP (MEASURE 'GENERATED VALUE') ========================
        const generatedValueString = '( ' + generatedValue + ' )';

        // measure 'generated value'
        ctxSetFont(ctx, `400 ${(fontSize - 1)}px Inter, sans-serif`);
        const generatedValueWidth = ctxMeasure(ctx, generatedValueString);
        let oneLineGeneratedValueMaxWidth = 18 + generatedValueWidth + spaceSymbolWidth * 2.15 + 21;

        // ============ TOOLTIP (MEASURE 'CHECK VALUE') ========================
        const checkValueString = '( ' + checkValue + ' )';

        // measure 'check value'
        ctxSetFont(ctx, `400 ${(fontSize - 1)}px Inter, sans-serif`);
        const checkValueWidth = ctxMeasure(ctx, checkValueString);
        let oneLineCheckValueMaxWidth = 18 + checkValueWidth + spaceSymbolWidth * 2.15 + 21;

        // ===================================================================
        // ============ 2. TOOLTIP (CALCULATE TOOLTIP WIDTH) =================
        // ===================================================================
        let textMaxWidth = Math.max(
            oneLineHeaderWidth,
            oneLineDescriptionWidth,
            oneLineEnumMaxWidth,
            oneLineGeneratedValueMaxWidth,
            oneLineCheckValueMaxWidth,
            oneLineDefaultValueMaxWidth,
        );

        if (textMaxWidth > maxTooltipWidth) {
            tooltipWidth = maxTooltipWidth;
        } else if (textMaxWidth < minTooltipWidth) {
            tooltipWidth = minTooltipWidth;
        } else {
            tooltipWidth = textMaxWidth;
        }

        // if (oneLineEnumMaxWidth > maxTooltipWidth) {
        //     tooltipWidth = oneLineEnumMaxWidth;
        // }

        // ===================================================================
        // ============ 3. TOOLTIP (CALCULATE TEXT HEIGHT) ===================
        // ===================================================================

        // ============ TOOLTIP (CALCULATE NAME HEIGHT) ======================
        // name styles
        ctxSetFont(ctx, `500 ${20 * scale}px Inter, sans-serif`);
        ctx.textAlign = 'left';

        const nameLineMaxWidth = (tooltipWidth - (21 + 21)) * scale;

        const nameTypeLines = getTableNameByWidthLimit(ctx, nameTypeText, nameLineMaxWidth, nameLineMaxWidth);
        const nameHeight = (27 + 6 - 18) * scale + nameTypeLines.length * fontSize * 1.4 * scale + 21 * scale;
        const nameLines = getTableNameByWidthLimit(ctx, nameText, nameLineMaxWidth, nameLineMaxWidth);

        // ============ TOOLTIP (CALCULATE DESCRIPTION HEIGHT) ===============
        // description styles
        ctxSetFont(ctx, `${fontSize}px Inter, sans-serif`);
        ctx.textBaseline = "top";

        const maxTextWidth = tooltipWidth - 38;

        const descriptionLines = wrapText(ctx, description, maxTextWidth);
        const descriptionHeight = description
            ? descriptionLines.length * fontSize * 1.4
            : 0;

        // FIX: if descriptionLines.length > 7 => then maxTooltipWidth from 420 to 460/480 or hide 'description next after 7 lines'

        // ============ TOOLTIP (CALCULATE 'ENUM' HEIGHT) ===============
        // enum styles
        ctxSetFont(ctx, `${fontSize - 1}px Inter, sans-serif`);
        ctx.textBaseline = "top";

        const maxEnumTextWidth = tooltipWidth - 38;
        const enumLines = getEnumValuesByWidthLimit(ctx, enumString, maxEnumTextWidth);

        const enumTitleHeight = 21 * 1.4;
        let enumHeight = enumValues.length
            ? enumTitleHeight + enumLines.length * fontSize * 1.4
            : 0;

        if (description) {
            enumHeight += 4;
        }

        if (!enumValues.length) {
            enumHeight = 0;
        }

        // ============ TOOLTIP (CALCULATE 'DEFAULT VALUE' HEIGHT) ===============
        // default value styles
        ctxSetFont(ctx, `${fontSize - 1}px Inter, sans-serif`);
        ctx.textBaseline = "top";

        const maxDefaultValueTextWidth = tooltipWidth - 38;
        const defaultValueLines = getEnumValuesByWidthLimit(ctx, defaultValueString, maxDefaultValueTextWidth);

        const defaultValueTitleHeight = 21 * 1.4;
        let defaultValueHeight = defaultValue.length
            ? defaultValueTitleHeight + defaultValueLines.length * fontSize * 1.4
            : 0;

        if (description && !enumValues.length) {
            defaultValueHeight += 4;
        }

        if (enumValues.length) {
            defaultValueHeight += 5;
        }

        if (!defaultValue.length) {
            defaultValueHeight = 0;
        }

        // ============ TOOLTIP (CALCULATE 'GENERATED VALUE' HEIGHT) ===============
        // generated value styles
        ctxSetFont(ctx, `${fontSize - 1}px Inter, sans-serif`);
        ctx.textBaseline = "top";

        const maxGeneratedValueTextWidth = tooltipWidth - 38;
        const generatedValueLines = getEnumValuesByWidthLimit(ctx, generatedValueString, maxGeneratedValueTextWidth);

        const generatedValueTitleHeight = 21 * 1.4;
        let generatedValueHeight = generatedValue
            ? generatedValueTitleHeight + generatedValueLines.length * fontSize * 1.4
            : 0;

        if (description && !enumValues.length && !defaultValue) {
            generatedValueHeight += 4;
        }
        if (enumValues.length && !defaultValue) {
            generatedValueHeight += 5;
        }
        if (defaultValue) {
            generatedValueHeight += 5;
        }

        if (!generatedValue) {
            generatedValueHeight = 0;
        }

        // ============ TOOLTIP (CALCULATE 'CHECK VALUE' HEIGHT) ===============
        // check value styles
        ctxSetFont(ctx, `${fontSize - 1}px Inter, sans-serif`);
        ctx.textBaseline = "top";

        const maxCheckValueTextWidth = tooltipWidth - 38;
        const checkValueLines = getEnumValuesByWidthLimit(ctx, checkValueString, maxCheckValueTextWidth);

        const checkValueTitleHeight = 21 * 1.4;
        let checkValueHeight = checkValue
            ? checkValueTitleHeight + checkValueLines.length * fontSize * 1.4
            : 0;

        if (description && !enumValues.length && !defaultValue && !generatedValue) {
            checkValueHeight += 4;
        }
        if (enumValues.length && !defaultValue) {
            checkValueHeight += 5;
        }
        if (defaultValue && !generatedValue) {
            checkValueHeight += 5;
        }
        if (generatedValue) {
            checkValueHeight += 5;
        }

        if (!checkValue) {
            checkValueHeight = 0;
        }

        // ===================================================================
        // ============ 4. TOOLTIP BACKGROUND (CONNECTOR) ====================
        // ===================================================================

        // ============ TOOLTIP CONNECTOR (LINE) ===========
        const lineX = tableX + tableWidth - 15 + 0.75;
        const lineY = columnY + (TABLE_ROW_HEIGHT / 2) + 0.75;

        ctx.lineWidth = 2.5 * scale;
        ctx.strokeStyle = 'rgba(45, 52, 65, 1)';

        ctx.beginPath();
        ctx.moveTo(lineX, lineY);
        ctx.lineTo(lineX + 15 + 25 * scale, lineY);
        ctx.stroke();

        // ============ TOOLTIP CONNECTOR (CIRCLE) =========
        const pointRadius = 5.5 * scale;
        ctx.fillStyle = 'rgba(45, 52, 65, 1)';

        ctx.beginPath();
        ctx.arc(lineX, lineY, pointRadius, 0, 2 * Math.PI);
        ctx.fill();

        // ===================================================================
        // ============ 5. TOOLTIP BACKGROUND (FRAME) ========================
        // ===================================================================

        // ============ TOOLTIP BACKGROUND (RECTANGLE + BORDER LINE)
        ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
        ctx.fillStyle = 'rgba(14, 15, 16, 1)';

        ctx.lineWidth = 1.6 * scale;
        ctx.beginPath();
        ctx.roundRect(tooltipX, tooltipY, tooltipWidth * scale, nameHeight + (descriptionHeight + enumHeight + defaultValueHeight + generatedValueHeight + checkValueHeight) * scale, 6 * scale);
        ctx.fill();
        ctx.stroke();

        // ============ TOOLTIP BACKGROUND (SHADOW) ========
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================================================================
        // ============ 6. TOOLTIP HEADER (NAME + TYPE) ======================
        // ===================================================================

        // ============ TOOLTIP HEADER (NAME TEXT) =========
        ctxSetFont(ctx, `500 ${19 * scale}px Inter, sans-serif`);
        ctx.fillStyle = '#eeeeee';
        ctx.textAlign = 'left';

        let headerX = tooltipX + 21 * scale;
        let headerY = tooltipY + (27 + 6 - 18) * scale + fontSize * 1.4 * scale;

        for (let i = 0; i < nameLines.length; i++) {
            if (i === 0) {
                ctx.fillText(nameLines[i], tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
            } else {
                ctx.fillText(nameLines[i], headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            }
        }

        ctx.fillStyle = '#ffc65e';

        const lastNameLine = nameLines.length - 1;
        const lastNameLineText = nameLines[lastNameLine];
        const lastNameLineTextLength = lastNameLineText.length;

        for (let i = lastNameLine; i < nameTypeLines.length; i++) {
            if (i === lastNameLine) {
                const nameWidth = ctxMeasure(ctx, lastNameLineText);
                const typeText = nameTypeLines[i].slice(lastNameLineTextLength);

                headerY -= fontSize * 1.4 * scale;
                if (typeText) {
                    headerX += nameWidth;
                }

                ctx.fillText(typeText, headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            } else {
                ctx.fillText(nameTypeLines[i], headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            }
        }

        // ============ TOOLTIP HEADER (LINE AFTER) ========
        const headerLineY = headerY + 3 * scale;
        ctx.fillStyle = 'rgba(45, 52, 65, 1)';

        ctx.fillRect(tooltipX + 16 * scale, headerLineY, tooltipWidth * scale - 32 * scale, 1.7 * scale);

        let nextSectionY = headerLineY;
        // ===================================================================
        // ============ 7. TOOLTIP BODY (DESCRIPTION) ========================
        // ===================================================================

        // ============ TOOLTIP BODY (DESCRIPTION TEXT) ====
        if (description) {
            ctxSetFont(ctx, `${fontSize * scale}px Inter, sans-serif`);
            ctx.fillStyle = '#efefef';

            let descriptionX = tooltipX + 21 * scale;
            let descriptionY = nextSectionY + fontSize * 1.4 * 0.5 * scale;

            for (let i = 0; i < descriptionLines.length; i++) {
                ctx.fillText(descriptionLines[i], descriptionX, descriptionY);
                descriptionY += fontSize * 1.4 * scale;
            }

            nextSectionY = descriptionY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (ENUM VALUES) ========================
        // ===================================================================
        if (enumValues.length && enumLines.length) {
            const enumTitleX = tooltipX + 18 * scale;
            const enumTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Enum Title'
            ctxSetFont(ctx, `500 ${18 * scale}px Inter, sans-serif`);
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            // ctx.fillText(t('canvas_table.tooltip_enum'), enumTitleX, enumTitleY);
            ctx.fillText('Enum', enumTitleX, enumTitleY);

            const enumX = tooltipX + 23 * scale;
            let enumY = enumTitleY + (31) * scale;

            // fill text 'Enum Values'
            ctx.fillStyle = '#eeeeee';
            ctxSetFont(ctx, `400 ${(fontSize - 1) * scale}px Inter, sans-serif`);

            for (let i = 0; i < enumLines.length; i++) {
                ctx.fillText(enumLines[i], enumX, enumY);
                enumY += fontSize * 1.4 * scale;
            }

            nextSectionY = enumY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (DEFAULT VALUE) ======================
        // ===================================================================
        if (defaultValue && defaultValueLines.length) {
            const defaultValueTitleX = tooltipX + 18 * scale;

            const defaultValueTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Default Value Title'
            ctxSetFont(ctx, `500 ${18 * scale}px Inter, sans-serif`);
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            // ctx.fillText(t('canvas_table.tooltip_default'), defaultValueTitleX, defaultValueTitleY);
            ctx.fillText('Default', defaultValueTitleX, defaultValueTitleY);

            const defaultValueX = tooltipX + 23 * scale;
            let defaultValueY = defaultValueTitleY + (31) * scale;

            // fill text 'Default Value'
            ctx.fillStyle = '#eeeeee';
            ctxSetFont(ctx, `400 ${(fontSize - 1) * scale}px Inter, sans-serif`);

            for (let i = 0; i < defaultValueLines.length; i++) {
                ctx.fillText(defaultValueLines[i], defaultValueX, defaultValueY);
                defaultValueY += fontSize * 1.4 * scale;
            }

            nextSectionY = defaultValueY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (GENERATED VALUE) ====================
        // ===================================================================
        if (generatedValue && generatedValueLines.length) {
            const generatedValueTitleX = tooltipX + 18 * scale;
            const generatedValueTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Generated Value Title'
            ctxSetFont(ctx, `500 ${18 * scale}px Inter, sans-serif`);
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            // ctx.fillText(t('canvas_table.tooltip_generated'), generatedValueTitleX, generatedValueTitleY);
            ctx.fillText('Generated', generatedValueTitleX, generatedValueTitleY);

            const generatedValueX = tooltipX + 23 * scale;
            let generatedValueY = generatedValueTitleY + (31) * scale;

            // fill text 'Generated Value'
            ctx.fillStyle = '#eeeeee';
            ctxSetFont(ctx, `400 ${(fontSize - 1) * scale}px Inter, sans-serif`);

            for (let i = 0; i < generatedValueLines.length; i++) {
                ctx.fillText(generatedValueLines[i], generatedValueX, generatedValueY);
                generatedValueY += fontSize * 1.4 * scale;
            }

            nextSectionY = generatedValueY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (CHECK VALUE) ========================
        // ===================================================================
        if (checkValue && checkValueLines.length) {
            const checkValueTitleX = tooltipX + 18 * scale;
            const checkValueTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Check Value Title'
            ctxSetFont(ctx, `500 ${18 * scale}px Inter, sans-serif`);
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            // ctx.fillText(t('canvas_table.tooltip_check'), checkValueTitleX, checkValueTitleY);
            ctx.fillText('Check', checkValueTitleX, checkValueTitleY);

            const checkValueX = tooltipX + 23 * scale;
            let checkValueY = checkValueTitleY + (31) * scale;

            // fill text 'Check Value'
            ctx.fillStyle = '#eeeeee';
            ctxSetFont(ctx, `400 ${(fontSize - 1) * scale}px Inter, sans-serif`);

            for (let i = 0; i < checkValueLines.length; i++) {
                ctx.fillText(checkValueLines[i], checkValueX, checkValueY);
                checkValueY += fontSize * 1.4 * scale;
            }

            nextSectionY = checkValueY - 14 * scale;
        }
    }

    function setInitialValuesForExportImageBox() {
        const initialClientX = 460;
        const initialClientY = 150;

        // const initialClientBoxWidth = 1000;
        // const initialClientBoxHeight = 600;
        const initialClientBoxWidth = 900;
        const initialClientBoxHeight = 500;

        exportImageBoxPropsRef.current = {
            clientX: initialClientX,
            clientY: initialClientY,
            clientBoxWidth: initialClientBoxWidth,
            clientBoxHeight: initialClientBoxHeight,
        };
    }

    // function changePropsForExportImageBox(scaleValue) {

    // 	const initialClientX = 460;
    // 	const initialClientY = 150;
    // 	const initialX = (initialClientX - originXYRef.current.x) / scaleRef.current;
    // 	const initialY = (initialClientY - originXYRef.current.x) / scaleRef.current;

    // 	// const width = ref.current.width / scaleRef.current;
    // 	// const height = ref.current.height / scaleRef.current;

    // 	// const zeroX = (0 - originXYRef.current.x) / scaleRef.current;
    // 	// const zeroY = (0 - originXYRef.current.y) / scaleRef.current;

    // 	exportImageBoxPropsRef.current.x = initialX  / scaleRef.current,
    // 		y: initialY,
    // 		boxWidth: 1000 / scaleRef.current,
    // 		boxHeight: 600 / scaleRef.current,
    // 	}

    // }

    function drawExportImageBox() {
        // const canvas = ref.current;
        // const context = contextRef.current;

        // const exportImgCanvas = exportImgCanvasRef.current;
        // const exportImgContext = exportImgContextRef.current;

        // if (!canvas || !context || !exportImgCanvas || !exportImgContext) {
        //     return;
        // }

        // exportImgCanvas.style.width = canvas.style.width;
        // exportImgCanvas.style.height = canvas.style.height;

        // exportImgCanvas.width = canvas.width;
        // exportImgCanvas.height = canvas.height;

        // exportImgContext.imageSmoothingEnabled = true;
        // exportImgContext.imageSmoothingQuality = 'high';

        // // =========================================

        // const dpr = window.devicePixelRatio || 1;

        // exportImgContext.clearRect(0, 0, canvas.width, canvas.height);
        // exportImgContext.save();
        // exportImgContext.setTransform(
        //     scaleRef.current * dpr,
        //     0,
        //     0,
        //     scaleRef.current * dpr,
        //     originXYRef.current.x * dpr,
        //     originXYRef.current.y * dpr
        // );




        // const ctx = exportImgContext;
        const ctx = contextRef.current;

        const width = ref.current.width / scaleRef.current;
        const height = ref.current.height / scaleRef.current;

        const zeroX = (0 - originXYRef.current.x) / scaleRef.current;
        const zeroY = (0 - originXYRef.current.y) / scaleRef.current;

        let { clientX, clientY, clientBoxWidth, clientBoxHeight } =
            exportImageBoxPropsRef.current;

        const x = (clientX - originXYRef.current.x) / scaleRef.current;
        const y = (clientY - originXYRef.current.y) / scaleRef.current;
        const boxWidth = clientBoxWidth / scaleRef.current;
        const boxHeight = clientBoxHeight / scaleRef.current;

        const distanceTop = y - zeroY;
        const distanceBottom = height - boxHeight - distanceTop;
        const distanceLeft = x - zeroX;

        const distanceRight = width - boxWidth - distanceLeft;

        // Затемнення зверху
        // ctx.fillStyle = 'rgba(0, 0, 0, 0.56)';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        // ctx.fillRect(zeroX, zeroY, width, distanceY);
        ctx.fillRect(zeroX, zeroY, width, distanceTop);

        // Затемнення знизу
        // ctx.fillRect(zeroX, zeroY + height - distanceY, width, distanceY);
        ctx.fillRect(
            zeroX,
            zeroY + height - distanceBottom,
            width,
            distanceBottom
        );

        // Затемнення зліва
        // ctx.fillRect(zeroX, zeroY + distanceY, distanceX, height - 2 * distanceY);
        ctx.fillRect(
            zeroX,
            zeroY + distanceTop,
            distanceLeft,
            height - distanceTop - distanceBottom
        );

        // Затемнення справа
        // ctx.fillRect(zeroX + width - distanceX, zeroY + distanceY, distanceX, height - 2 * distanceY);
        ctx.fillRect(
            zeroX + width - distanceRight,
            zeroY + distanceTop,
            distanceRight,
            height - distanceTop - distanceBottom
        );

        const lineLength = 26 / scaleRef.current;
        const lineRadius = 3 / scaleRef.current;
        const distance = 9 / scaleRef.current;
        drawExportImageBoxCorners(
            ctx,
            x,
            y,
            boxWidth,
            boxHeight,
            lineLength,
            lineRadius,
            distance
        );
    }

    function drawExportImageBoxCorners(
        ctx,
        x,
        y,
        width,
        height,
        length,
        radius,
        distance
    ) {
        ctx.lineWidth = 6 / scaleRef.current;
        ctx.lineCap = 'round';

        // top left
        const pointNW = { x: x - distance, y: y - distance };
        drawExportImageBoxCornerNW(ctx, pointNW, false, radius, length);

        // top right
        const pointNE = { x: x + width + distance, y: y - distance };
        drawExportImageBoxCornerNE(ctx, pointNE, false, radius, length);

        // bottom left
        const pointSW = { x: x - distance, y: y + height + distance };
        drawExportImageBoxCornerSW(ctx, pointSW, false, radius, length);

        // bottom right
        const pointSE = { x: x + width + distance, y: y + height + distance };
        drawExportImageBoxCornerSE(ctx, pointSE, false, radius, length);

        // one more corner - selected corner
        switch (exportImageBoxPropsRef.current.clickedResizeKey) {
            case 'nw':
                drawExportImageBoxCornerNW(ctx, pointNW, true, radius, length);
                break;
            case 'ne':
                drawExportImageBoxCornerNE(ctx, pointNE, true, radius, length);
                break;
            case 'sw':
                drawExportImageBoxCornerSW(ctx, pointSW, true, radius, length);
                break;
            case 'se':
                drawExportImageBoxCornerSE(ctx, pointSE, true, radius, length);
                break;
            default:
                break;
        }
    }

    function drawExportImageBoxCornerNW(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x, p.y + radius + length);
        ctx.lineTo(p.x, p.y + radius);
        ctx.arcTo(p.x, p.y, p.x + radius, p.y, radius);
        ctx.lineTo(p.x + radius + length, p.y);
        ctx.stroke();
    }
    function drawExportImageBoxCornerNE(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x - radius - length, p.y);
        ctx.lineTo(p.x - radius, p.y);
        ctx.arcTo(p.x, p.y, p.x, p.y + radius, radius);
        ctx.lineTo(p.x, p.y + radius + length);
        ctx.stroke();
    }
    function drawExportImageBoxCornerSW(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x, p.y - radius - length);
        ctx.lineTo(p.x, p.y - radius);
        ctx.arcTo(p.x, p.y, p.x + radius, p.y, radius);
        ctx.lineTo(p.x + radius + length, p.y);
        ctx.stroke();
    }
    function drawExportImageBoxCornerSE(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x - radius - length, p.y);
        ctx.lineTo(p.x - radius, p.y);
        ctx.arcTo(p.x, p.y, p.x, p.y - radius, radius);
        ctx.lineTo(p.x, p.y - radius - length);
        ctx.stroke();
    }

    function drawPenSegment(segment, exportImgCanvas = null) {
        // pen-segment
        // const isSelectionMoving =
        //     isSelectionClickedRef.current &&
        //     (selectionStartXYRef.current.x !==
        //         selectionCurrentXYRef.current.x ||
        //         selectionStartXYRef.current.y !==
        //             selectionCurrentXYRef.current.y);
        // const isStartedNewLine =
        //     lineFromRef.current?.elementId || isBtnNewLineEnabled;
        // const isStartedNewPenSegment =
        //     newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        // const ctx = contextRef.current;
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        // let {
        //     id,
        //     x,
        //     y,
        //     translateX,
        //     translateY,
        //     width,
        //     height,
        //     value: { path, scale, origin, color },
        // } = segment;
        let {
            id,
            x,
            y,
            width,
            height,
            pathOriginX,
            pathOriginY,
            path,
            color,
        } = segment;

        const isPenSegmentFocused =
            !isBtnNewAreaEnabled &&
            !isBtnExportImageEnabled &&
            (selectedElementsIdsRef.current.includes(id) ||
                // table is focused
                focusedIdsRef.current.includes(id)); // ||
        // table is hovered and its not new line drawing
        // (hoveredElementIdRef.current === id && !isStartedNewLine && !isStartedNewPenSegment)

        // const isPenSegmentHovered =
        //     !isBtnNewAreaEnabled &&
        //     !isBtnExportImageEnabled &&
        //     (selectedElementsIdsRef.current.includes(id) ||
        //         // table is focused
        //         focusedIdsRef.current.includes(id) ||
        //         // table is hovered and its not new line drawing
        //         (hoveredElementIdRef.current === id &&
        //             !isStartedNewLine &&
        //             !isStartedNewPenSegment));

        // // ===================== 0. FOCUSED SEGMENT | OUTLINE FRAME =====================
        // if (
        // 	// segment is focused
        // 	focusedIdsRef.current.includes(id)
        // 	// focusedIdsRef.current.includes(id) ||
        // 	// segment is hovered and its not new line drawing
        // 	// (hoveredElementIdRef.current === id && !isStartedNewLine && !isStartedNewPenSegment)
        // ) {

        // 	ctx.strokeStyle = '#5f88ff';
        // 	// ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
        // 	// ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
        // 	ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

        // 	ctx.beginPath();
        // 	// ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);
        // 	ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset, width + 2 * scaleNumbersRef.current.outlineOffset, height + 2 * scaleNumbersRef.current.outlineOffset, 0.75);

        // 	ctx.stroke();

        // 	// ctx.shadowColor = 'transparent';
        // 	// ctx.fillStyle = '#39424c'; // canvas background
        // 	// ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
        // 	// ctx.stroke();
        // }

        // // ===================== 1. FOCUSED SEGMENT | RESIZE POINTS =====================
        // if (
        // 	!isBtnRedGreenViewEnabled &&
        // 	// is not export enabled
        // 	!isBtnExportImageEnabled &&
        // 	// is not mouse selection
        // 	!isSelectionMoving &&
        // 	// segment is focused
        // 	focusedIdsRef.current.includes(id) &&
        // 	// focused only one segment
        // 	focusedIdsRef.current.length === 1
        // ) {
        // 	let resizeHandleSize = scaleNumbersRef.current.resizeHandleSize - 4 > 0 ? scaleNumbersRef.current.resizeHandleSize - 3 : scaleNumbersRef.current.resizeHandleSize;
        // 	resizeHandleSize = Math.max(resizeHandleSize, 4)
        // 	let resizeHandleRadius = scaleNumbersRef.current.resizeHandleRadius - 1 > 0 ? scaleNumbersRef.current.resizeHandleRadius - 1 : scaleNumbersRef.current.resizeHandleRadius;
        // 	resizeHandleRadius = Math.max(resizeHandleRadius, 1)

        // 	const resizeOutlineWidth = width + scaleNumbersRef.current.outlineOffset * 2;
        // 	const resizeOutlineHeight = height + scaleNumbersRef.current.outlineOffset * 2;
        // 	const resizeX = x - scaleNumbersRef.current.outlineOffset;
        // 	const resizeY = y - scaleNumbersRef.current.outlineOffset;

        // 	ctx.beginPath();
        // 	ctx.strokeStyle = '#5f88ff';
        // 	ctx.fillStyle = '#e6e6e6';
        // 	// ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.5;
        // 	ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 1;

        // 	ctx.roundRect(resizeX - resizeHandleSize / 2 - 1, resizeY - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.roundRect(resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1, resizeY - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.roundRect(resizeX - resizeHandleSize / 2 - 1, resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.roundRect(resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1, resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.stroke();
        // 	ctx.fill();
        // }

        // -----------------------------
        const handPathCanvas = new Path2D(path);
        handPathCanvas.closePath();
        // ctx.save();
        // ctx.setTransform(scaleRef.current, 0, 0, scaleRef.current, originXYRef.current.x, originXYRef.current.y);

        // ctx.save(); // Зберігаємо поточний стан канвасу
        // ctx.translate(translateX, translateY); // Переміщуємо початкову точку малювання
        // ctx.translate(0, 0); // Переміщуємо початкову точку малювання

        // if (scale) {
        // 	ctx.scale(scale, scale); // Масштабування шляху для більшого розміру
        // }

        // ctx.lineWidth = scaleNumbersRef.current.outlineWidth;
        // ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

        // ctx.fillStyle = '#ffc65e'// : '#b955eb';
        ctx.fillStyle =
            color === 'yellow' ? '#ffc65e' : color ? color : '#ffc65e'; // : '#b955eb';

        // ==================================
        // // Перевіряємо, чи миша над лінією
        // const mouseX = (newPenCursorPointRef.current.x) * scaleRef.current + originXYRef.current.x;
        // const mouseY = (newPenCursorPointRef.current.y) * scaleRef.current + originXYRef.current.y;
        // if (ctx.isPointInPath(handPathCanvas, mouseX, mouseY)) {
        // 	ctx.fillStyle = 'red'// : '#b955eb';
        // } else {
        // 	ctx.fillStyle = '#ffc65e'// : '#b955eb';
        // }

        ctx.save(); // Зберігаємо поточний стан канвасу
        // Position the stroke by the element's x/y (offset from the path's natural
        // origin) so moving x/y — via direct drag or area move — moves the pen.
        ctx.translate((x ?? 0) - (pathOriginX ?? (x ?? 0)), (y ?? 0) - (pathOriginY ?? (y ?? 0)));

        // ctx.save(); // Зберігаємо поточний стан канвасу
        // ctx.translate(0, 0); // Переміщуємо початкову точку малювання
        // ctx.scale(0.9, 0.9); // Переміщуємо початкову точку малювання
        // if (scale && origin) {
        // 	ctx.setTransform(scale, 0, 0, scale, origin.x, origin.y);
        // }

        ctx.beginPath();
        ctx.fill(handPathCanvas);

        if (isPenSegmentFocused) {
            const scaleNums = scaleNumbersRef.current;
            // ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            // ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
            ctx.lineWidth = scaleNums.outlineWidth + 0.4;

            if (
                hoveredElementIdRef.current === id &&
                !focusedIdsRef.current.includes(id) &&
                !selectedElementsIdsRef.current.includes(id)
            ) {
                ctx.lineWidth = scaleNums.outlineWidth;
            }

            ctx.stroke(handPathCanvas);
        }

        ctx.restore(); // Відновлюємо попередній стан канвасу

        // Area selection outline — highlight the pen when an area being drawn, moved,
        // or resized would capture it (same frame tables get).
        if (
            !isBtnExportImageEnabled &&
            ((isBtnNewAreaEnabled && newAreaPropsRef?.current?.selectedElements?.includes(id))
                || resizeAreaPropsRef.current?.selectedElements?.includes(id))
        ) {
            const scaleNums = scaleNumbersRef.current;
            const ox = x - scaleNums.outlineOffset;
            const oy = y - scaleNums.outlineOffset - 0.5;
            const ow = width + 2 * scaleNums.outlineOffset;
            const oh = height + 2 * scaleNums.outlineOffset + 1;
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';
            // ctx.fillStyle = 'rgba(111, 190, 255, 0.07)';
            ctx.fillStyle = 'rgba(111, 190, 255, 0.05)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7;
            ctx.beginPath();
            ctx.roundRect(ox, oy, ow, oh, 0.75);
            ctx.stroke();
            ctx.fill();
        }

        // Focused/selected pen — show a bounding-box frame so the whole box (not just
        // the stroke) is the click/drag target.
        if (isPenSegmentFocused) {
            const scaleNums = scaleNumbersRef.current;
            const ox = x - scaleNums.outlineOffset;
            const oy = y - scaleNums.outlineOffset - 0.5;
            const ow = width + 2 * scaleNums.outlineOffset;
            const oh = height + 2 * scaleNums.outlineOffset + 1;
            ctx.strokeStyle = 'rgba(45, 52, 65, 1)';
            ctx.lineWidth = scaleNums.outlineWidth - 0.3 - 0.7 - 0.1;
            ctx.beginPath();
            ctx.roundRect(ox, oy, ow, oh, 0.75);
            ctx.stroke();
            ctx.strokeRect(ox, oy, ow, oh);
        }

        // // ctx.strokeStyle = 'red'; //theme.controls.background;
        // ctx.fillStyle = '#ffc65e'// : '#b955eb';

        // // ctx.stroke(handPathCanvas);
        // ctx.fill(handPathCanvas);

        // ctx.restore(); // Відновлюємо попередній стан канвасу
        // -----------------------------
    }
    function drawNewPenSegment() {
        const ctx = contextRef.current;

        const { points } = newPenSegmentRef.current;

        // const options = {
        // 	size: 15,
        // 	smoothing: 0.5,
        // 	thinning: 0.25,
        // 	streamline: 0.7,
        // };
        const options = {
            size: 20,
            smoothing: 0.5,
            thinning: 0.25,
            streamline: 0.7,
        };

        ctx.fillStyle =
            newPenPropsRef.current.color === 'yellow'
                ? '#ffc65e'
                : newPenPropsRef.current.color
                    ? newPenPropsRef.current.color
                    : '#ffc65e'; // : '#b955eb';

        ctx.lineWidth = 1;

        // const stroke = getStroke([...points, points[points.length -2], points[points.length -1]], options);
        const stroke = getStroke(points, options);
        const path = getSvgPathFromStroke(stroke);
        const myPath = new Path2D(path);

        ctx.save(); // Зберігаємо поточний стан канвасу
        ctx.translate(0, 0); // Переміщуємо початкову точку малювання
        ctx.beginPath();
        ctx.fill(myPath);
        ctx.restore(); // Відновлюємо попередній стан канвасу
    }

    function drawNewPenCursor() {
        const ctx = contextRef.current;

        ctx.fillStyle = '#ffc65e'; // : '#b955eb';
        ctx.lineWidth = 1 / scaleRef.current;

        // DRAW CURSOR POINTER FOR PEN

        const cursorPoint = newPenCursorPointRef.current;

        ctx.beginPath();
        ctx.strokeStyle = 'grey';
        ctx.arc(cursorPoint.x, cursorPoint.y, 9, 0, 2 * Math.PI); // Малюємо коло
        ctx.stroke();
    }

    function getBoundsByPoints(points) {
        if (!Array.isArray(points) || !points.length) {
            return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
        }

        let bounds = {};
        points.forEach(([x, y], index) => {
            if (index === 0) {
                bounds = { minX: x, maxX: x, minY: y, maxY: y };
            } else {
                bounds = {
                    minX: Math.min(bounds.minX, x),
                    maxX: Math.max(bounds.maxX, x),
                    minY: Math.min(bounds.minY, y),
                    maxY: Math.max(bounds.maxY, y),
                };
            }
        });

        bounds.width = bounds.maxX - bounds.minX;
        bounds.height = bounds.maxY - bounds.minY;

        return bounds;
    }

    function buildNewPenSegment(points) {
        // const { points } = newPenSegmentRef.current;

        // const options = {
        // 	size: 15,
        // 	smoothing: 0.5,
        // 	thinning: 0.25,
        // 	streamline: 0.7,
        // };
        const options = {
            size: 20,
            smoothing: 0.5,
            thinning: 0.25,
            streamline: 0.7,
        };


        // Store the raw points the live preview drew from (rounded to 2dp). The path
        // is rebuilt from these points when the segment loads, so they must match what
        // was drawn — previously they were simplified, which reshaped the stroke after
        // mouse-up.
        const pointsFinal = points.map(([x, y]) => ([parseFloat(x.toFixed(2)), parseFloat(y.toFixed(2))]));

        const stroke = getStroke(points, options);

        const path = getSvgPathFromStroke(stroke);

        const bounds = getBoundsByPoints(stroke);

        const newPenSegmentId = nanoid();
        const newPenSegment = {
            id: newPenSegmentId,
            type: 'pen-segment',
            x: bounds.minX,
            y: bounds.minY,
            width: bounds.width,
            height: bounds.height,

            translateX: 0,
            translateY: 0,
            // path stored locally for immediate use; Yjs stores points
            path,
            color: newPenPropsRef.current.color || 'yellow',
            // Tag with the current pen-session id so "clear" (onClearNewPen) can find
            // and delete the segments drawn in this session.
            newPenId: newPenPropsRef.current.id,
            points: pointsFinal,
        };

        return newPenSegment;
    }

    function buildLineData(lineObject) {
        const sourceElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
        const targetElementId = lineObject.targetElementId ?? lineObject.toElementId;
        const sourceCardinality = lineObject.sourceCardinality ?? lineObject.fromRelationType;
        const targetCardinality = lineObject.targetCardinality ?? lineObject.toRelationType;

        const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
        const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;

        const fromElement = elementsRef.current[sourceElementId];
        const toElement = elementsRef.current[targetElementId];

        if (!fromElement || !toElement) {
            return;
        }
        const fromInsideElement = fromElement.columns[fromInsideElementId];
        const toInsideElement = toElement.columns[toInsideElementId];

        if (!fromInsideElement || !toInsideElement) {
            return;
        }

        const lineData = {
            mouseX: null,
            mouseY: null,

            fromElementType: fromElement.type,
            fromElementWidth: fromElement.width,
            fromElementX: fromElement.x,
            fromElementY: fromElement.y,
            fromInsideElementIndex: (viewMode === 'keys' ? getKeysViewIndexNumber : getIndexNumber)(
                fromElement.columns,
                fromInsideElementId,
                fromElement.columnsOrder
            ),
            fromRelationType: sourceCardinality,
            // clipping only affects full-view geometry — keys/names view keep their
            // own index spaces, so pass Infinity there to leave buildTableConnectors'
            // clamp inert and keep those views byte-identical
            fromVisibleColumnsCount: viewMode === 'full'
                ? getVisibleColumnsCount(fromElement, orderedColumnKeys(fromElement.columns, fromElement.columnsOrder).length)
                : Infinity,
            fromElementEffectiveHeight: getTableEffectiveHeight(fromElement),

            toElementType: toElement.type,
            toElementWidth: toElement.width,
            toElementX: toElement.x,
            toElementY: toElement.y,
            toInsideElementIndex: (viewMode === 'keys' ? getKeysViewIndexNumber : getIndexNumber)(
                toElement.columns,
                toInsideElementId,
                toElement.columnsOrder
            ),
            toRelationType: targetCardinality,
            toVisibleColumnsCount: viewMode === 'full'
                ? getVisibleColumnsCount(toElement, orderedColumnKeys(toElement.columns, toElement.columnsOrder).length)
                : Infinity,
            toElementEffectiveHeight: getTableEffectiveHeight(toElement),
        };

        return lineData;
    }

    function drawCreatedLine(lineObject, exportImgCanvas = null) {
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        if (viewMode === 'keys') {
            return drawLineForViewKeys(lineObject, exportImgCanvas);
        }

        let { id, path, color } = lineObject;
        const solidType = lineObject.strokeStyle ?? lineObject.solidType ?? 'solid';
        const isStartedNewLine = lineFromRef.current?.elementId;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const isLineFocused =
            focusedIdsRef.current.includes(id) && !isBtnExportImageEnabled;
        const isLineHighlighted =
            !isBtnExportImageEnabled &&
            (focusedRelatedPropsRef.current?.linesIds.includes(id) ||
                (hoveredElementRelatedPropsRef.current?.linesIds.includes(id) &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment) ||
                (hoveredLineIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment));
        const isNewLine = false;

        if (path) {
            _drawLinePath(
                ctx,
                path,
                isLineHighlighted,
                isLineFocused,
                isNewLine,
                scaleNumbersRef.current.relationLineWidth,
                solidType,
                color
            );
        } else {
            const lineData = buildLineData(lineObject);
            if (!lineData) return;
            path = _buildLinePath(lineData);
            _drawLinePath(
                ctx,
                path,
                isLineHighlighted,
                isLineFocused,
                isNewLine,
                scaleNumbersRef.current.outlineWidth,
                solidType,
                color
            );
        }
    }
    function drawLineForViewKeys(lineObject, exportImgCanvas = null) {
        const { id, color } = lineObject;
        const solidType = lineObject.strokeStyle ?? lineObject.solidType ?? 'solid';

        const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
        const toElementId = lineObject.targetElementId ?? lineObject.toElementId;
        const fromRelationType = lineObject.sourceCardinality ?? lineObject.fromRelationType;
        const toRelationType = lineObject.targetCardinality ?? lineObject.toRelationType;

        const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
        const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;

        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }


        const isStartedNewLine = lineFromRef.current?.elementId;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const isLineFocused =
            focusedIdsRef.current.includes(id) && !isBtnExportImageEnabled;
        const isLineHighlighted =
            !isBtnExportImageEnabled &&
            (focusedRelatedPropsRef.current?.linesIds.includes(id) ||
                (hoveredElementRelatedPropsRef.current?.linesIds.includes(id) &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment) ||
                (hoveredLineIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment));
        const isNewLine = false;

        const fromElement = elementsRef.current[fromElementId];
        const toElement = elementsRef.current[toElementId];

        if (!fromElement || !toElement) {
            return;
        }
        const fromInsideElement =
            fromElement.columns[fromInsideElementId];
        const toInsideElement = toElement.columns[toInsideElementId];

        if (!fromInsideElement || !toInsideElement) {
            return;
        }

        const fromElementColumnsKeys = orderedColumnKeys(
            fromElement.columns,
            fromElement.columnsOrder
        ).filter(
            (columnKey) =>
                fromElement.columns[columnKey]?.isPK ||
                fromElement.columns[columnKey]?.isFK ||
                fromElement.columns[columnKey]?.isRelated
        );
        let fromInsideElementIndex = -1;
        fromElementColumnsKeys.forEach((key, index) => {
            if (fromInsideElementId === key) {
                fromInsideElementIndex = index;
            }
        });

        const toElementColumnsKeys = orderedColumnKeys(
            toElement.columns,
            toElement.columnsOrder
        ).filter(
            (columnKey) =>
                toElement.columns[columnKey]?.isPK ||
                toElement.columns[columnKey]?.isFK ||
                toElement.columns[columnKey]?.isRelated
        );
        let toInsideElementIndex = -1;
        toElementColumnsKeys.forEach((key, index) => {
            if (toInsideElementId === key) {
                toInsideElementIndex = index;
            }
        });

        const lineData = {
            mouseX: null,
            mouseY: null,

            fromElementType: fromElement.type,
            fromElementWidth: fromElement.width,
            fromElementX: fromElement.x,
            fromElementY: fromElement.y,
            fromInsideElementIndex,
            fromRelationType,

            toElementType: toElement.type,
            toElementWidth: toElement.width,
            toElementX: toElement.x,
            toElementY: toElement.y,
            toInsideElementIndex,
            toRelationType,
        };

        const path = _buildLinePath(lineData);
        _drawLinePath(
            ctx,
            path,
            isLineHighlighted,
            isLineFocused,
            isNewLine,
            scaleNumbersRef.current.outlineWidth,
            solidType,
            color
        );
    }
    function drawNewLine() {
        const lineData = {
            mouseX: lineToRef.current.x,
            mouseY: lineToRef.current.y,

            fromElementType: lineFromRef.current.elementType,
            fromElementWidth: lineFromRef.current.elementWidth,
            fromElementX: lineFromRef.current.elementX,
            fromElementY: lineFromRef.current.elementY,
            fromInsideElementIndex: lineFromRef.current.insideElementIndex,
            fromRelationType: 'many',

            toElementType: lineToRef.current.elementType,
            toElementWidth: lineToRef.current.elementWidth,
            toElementX: lineToRef.current.elementX,
            toElementY: lineToRef.current.elementY,
            toInsideElementIndex: lineToRef.current.insideElementIndex,
            toRelationType: 'one',
        };

        const isLineHighlighted = false;
        const isLineFocused = false;
        const isNewLine = true;

        const path = _buildLinePath(lineData);
        _drawLinePath(
            contextRef.current,
            path,
            isLineHighlighted,
            isLineFocused,
            isNewLine,
            scaleNumbersRef.current.outlineWidth,
            'solid'
        );
    }

    function buildNewTable(mouseX, mouseY) {
        const newTableId = nanoid();

        const newTable = {
            id: newTableId,
            type: 'table',
            x: mouseX,
            y: mouseY,
            width: NEW_TABLE_WIDTH,
            height: NEW_TABLE_HEIGHT,
            name: 'table_new',
            areaId: null,
        };

        const firstColumn = {
            id: nanoid(),
            name: 'id',
            type: 'int',
            isPK: true,
        };

        return { newTable, firstColumn };
    }
    function buildNewNote(mouseX, mouseY) {
        const newNoteId = nanoid();

        const newNote = {
            id: newNoteId,
            type: 'note',
            x: mouseX,
            y: mouseY,
            width: NEW_NOTE_WIDTH,
            height: NEW_NOTE_HEIGHT,
            // text: 'Tables represent core entities in our project: e.g., Customers, Orders, Products.',
            text: '',
            areaId: null,
            alignmentHorizontal: 'left',
            alignmentVertical: 'top',
            isFontMonospace: false,
        };

        return newNote;
    }
    function buildNewLine() {
        const newLineId = nanoid();

        const newLine = {
            id: newLineId,
            sourceElementId: lineFromRef.current.elementId,
            sourceColumnId: lineFromRef.current.insideElementId,
            sourceCardinality: 'many',
            targetElementId: lineToRef.current.elementId,
            targetColumnId: lineToRef.current.insideElementId,
            targetCardinality: 'one',
            strokeStyle: 'solid',
            lineType: 'reference',
            waypoints: [],
        };

        return newLine;
    }

    function buildNewArea() {
        const newAreaId = nanoid();

        const x1 = newAreaPropsRef.current.startX;
        const y1 = newAreaPropsRef.current.startY;
        const x2 = newAreaPropsRef.current.endX;
        const y2 = newAreaPropsRef.current.endY;

        const minX = Math.min(x1, x2);
        const maxX = Math.max(x1, x2);
        const minY = Math.min(y1, y2);
        const maxY = Math.max(y1, y2);

        const newArea = {
            id: newAreaId,
            // border: 'dashes',
            border: 'solid',
            color: 'blue',
            name: 'area_new',
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        };

        return newArea;
    }

    function getNewClickedElements(
        elementsValue,
        mouseX,
        mouseY,
        eventX,
        eventY
    ) {
        const ctx = contextRef.current;

        if (viewMode === 'keys') {
            return getNewClickedElementsForViewKeys(
                elementsValue,
                mouseX,
                mouseY
            );
        }

        let clickedElementId = null;
        let clickedInsideElement = null;

        const radius = 18;
        const distance = 36;
        let clickedCircleElementId = null;

        // 1. check inside clicks
        for (const key in elementsValue) {
            const element = elementsValue[key];
            if (element.type === 'area') continue;
            const {
                id,
                x,
                y,
                width,
                height,
                columns,
                columnsOrder: elColumnsOrder,
                name: header,
            } = element;

            if (element.type === 'pen-segment') {
                // A focused/selected pen can be grabbed anywhere inside its bounding
                // box (the focus frame); an unfocused one only on the stroke itself.
                if (
                    focusedIdsRef.current.includes(id) ||
                    selectedElementsIdsRef.current.includes(id)
                ) {
                    if (
                        mouseX >= x && mouseX <= x + width &&
                        mouseY >= y && mouseY <= y + height
                    ) {
                        clickedElementId = id;
                    }
                } else {
                    ctx.save();
                    ctx.setTransform(
                        scaleRef.current,
                        0,
                        0,
                        scaleRef.current,
                        originXYRef.current.x,
                        originXYRef.current.y
                    );

                    ctx.translate(
                        (element.x ?? 0) - (element.pathOriginX ?? (element.x ?? 0)),
                        (element.y ?? 0) - (element.pathOriginY ?? (element.y ?? 0))
                    );
                    const penPath = new Path2D(element.path);

                    if (ctx.isPointInPath(penPath, eventX, eventY)) {
                        clickedElementId = id;
                    }

                    ctx.restore(); // Переміщуємо початкову точку малювання
                }
            }

            // if (element.type === 'pen-segment') {
            // 	ctx.save();
            // 	ctx.setTransform(scaleRef.current, 0, 0, scaleRef.current, originXYRef.current.x, originXYRef.current.y);
            // 	const penPath = new Path2D(element.value.path);
            // 	penPath.closePath();

            // 	// ctx.fill(penPath);

            // 	const mousePenX = (mouseX) * scaleRef.current + originXYRef.current.x;
            // 	const mousePenY = (mouseY) * scaleRef.current + originXYRef.current.y;

            // 	if (ctx.isPointInPath(scaleNumbersRef.current.test, eventX, eventY)) {
            // 		clickedElementId = id;

            // 	} else {
            // 		clickedElementId = null;
            // 	}
            // 	ctx.restore(); // Переміщуємо початкову точку малювання

            // }

            // 1.1. check clicks in element x, y area, so click inside one of the elements
            // clipping is full-view only — keys/names view keep the natural
            // height so their hit-testing stays byte-identical
            else if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + (viewMode === 'full' ? getTableEffectiveHeight(element) : height)
            ) {
                clickedElementId = id;

                if (element.type === 'table') {
                    // CHECK CLICK ON HEADER
                    const relativeHeaderY = mouseY - y;
                    if (
                        relativeHeaderY > 0 &&
                        relativeHeaderY < TABLE_HEADER_HEIGHT
                    ) {
                        clickedInsideElement = {
                            elementId: element.id,
                            insideElement: { name: header },
                            insideElementType: 'header',
                        };
                    }

                    const columnsCount = Object.keys(columns).length;
                    // cap the bound at the visible count so a click can never
                    // resolve to a column hidden below the clip (full view only)
                    const hitTestColumnsCount = viewMode === 'full'
                        ? getVisibleColumnsCount(element, columnsCount)
                        : columnsCount;

                    if (focusedIdsRef.current.includes(id)) {
                        // CHECK CLICK ON COLUMNS
                        const relativeY =
                            mouseY -
                            y -
                            TABLE_HEADER_HEIGHT -
                            HEADER_BODY_MARGIN;
                        if (
                            relativeY > 0 &&
                            relativeY < hitTestColumnsCount * TABLE_ROW_HEIGHT
                        ) {
                            const columnIndex = Math.floor(
                                relativeY / TABLE_ROW_HEIGHT
                            );
                            const columnId = orderedColumnKeys(columns, elColumnsOrder)[columnIndex];
                            const column = columns[columnId];

                            clickedInsideElement = {
                                insideElementType: 'column',
                                elementId: element.id,
                                insideElementId: column.id,
                                insideElement: column,
                                insideElementIndex: columnIndex,
                                index: columnIndex,
                            };
                        }
                    }
                } else if (element.type === 'note' && focusedIdsRef.current.includes(id)) {
                    // second click inside an already-focused note opens its text editor.
                    // elementId is required here (unlike the brief's literal snippet) —
                    // UPDATE_FOCUSED_INSIDE_ELEMENT nulls out focusedInsideElement when
                    // tableId is falsy (redux/diagram/index.js) and the deleted-element
                    // observer / EditableNote mount guard both key off elementId too.
                    clickedInsideElement = {
                        elementId: element.id,
                        insideElementId: null,
                        insideElementIndex: null,
                        insideElementType: 'note-text',
                    };
                }
            }

            // 1.2. check clicks on circle to add new row
            if (element.type === 'table') {
                // drawTable's own add-row circle is drawn at the effective/
                // clipped height (tableHeight = getTableEffectiveHeight(element))
                // — mirror it here so the visible circle on a clipped table
                // responds and the invisible natural-height spot doesn't.
                // clipping is full-view only, same idiom as 1.1 above; keys/
                // names views keep the natural height, byte-identical to before.
                //
                // Adjacency with the overflow chevron (r=13/scale at effH,
                // this circle at effH+36): their world-space zones CAN overlap
                // once the chevron's inverse-scaled radius exceeds ~18 (below
                // roughly scale≈0.72, down to the chevron's own OVERFLOW_
                // INDICATOR_MIN_SCALE=0.5 floor). Not a bug: `getNewClickedElements`
                // only computes `clickedCircleElementId` here — it's a table
                // chevron click block (in handleCanvasMouseDown, running after
                // this function returns) that unconditionally `return`s on a
                // chevron hit, before the `else if (newClickedCircleElementId)`
                // branch further down is ever reached — so the chevron wins the
                // overlap exactly like it now wins over the resize handle.
                const circleBaseHeight = viewMode === 'full' ? getTableEffectiveHeight(element) : height;
                if (
                    mouseX >= x + width / 2 - radius &&
                    mouseX <= x + width / 2 + radius &&
                    mouseY >= y + circleBaseHeight + distance - radius &&
                    mouseY <= y + circleBaseHeight + distance + radius
                ) {
                    clickedCircleElementId = id;
                }
            }
        }

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;

        let clickedPointElementId = null;
        let clickedPointInsideElementId = null;
        let clickedPointInsideElementIndex = null;
        let clickedPointDirection = null; // 'left' / 'right'

        // 2. check outside clicks, so clicks on relation points
        for (const key in elementsValue) {
            const element = elementsValue[key];
            const {
                id,
                x,
                y,
                width,
                columns,
                columnsOrder: elColumnsOrder2,
            } = element;

            if (element.type === 'table') {
                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    // clipping is full-view only — keys/names view keep their own
                    // index space, so this must stay Infinity (inert) there
                    const orderedColumnKeysForPoints = orderedColumnKeys(columns, elColumnsOrder2);
                    const pointVisibleCount = viewMode === 'full'
                        ? getVisibleColumnsCount(element, orderedColumnKeysForPoints.length)
                        : Infinity;
                    orderedColumnKeysForPoints.forEach((key, index) => {
                        if (index >= pointVisibleCount) return;
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'left';

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'right';
                        }
                    });
                }
            }
        }

        // 3. check lines
        let clickedLineId = null;

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const { id } = lineObject;
            let path = lineObject.path;

            if (!path) {
                const lineData = buildLineData(lineObject);
                if (!lineData) {
                    continue;
                }
                path = _buildLinePath(lineData);
            }

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        20
                    );
                    if (isNear) {
                        clickedLineId = id;
                        break;
                    }
                }
            }
        }

        let isClickedInsideExportImageBox = null;

        // 4. check clicks in export image box
        if (isBtnExportImageEnabled) {
            const { clientX, clientY, clientBoxWidth, clientBoxHeight } =
                exportImageBoxPropsRef.current;

            const x = (clientX - originXYRef.current.x) / scaleRef.current;
            const y = (clientY - originXYRef.current.y) / scaleRef.current;
            const boxWidth = clientBoxWidth / scaleRef.current;
            const boxHeight = clientBoxHeight / scaleRef.current;

            if (
                mouseX >= x &&
                mouseX <= x + boxWidth &&
                mouseY >= y &&
                mouseY <= y + boxHeight
            ) {
                isClickedInsideExportImageBox = true;
            }
        }

        // 5. check areas
        let clickedAreaId = null;

        for (const key in areasRef.current) {
            const area = areasRef.current[key];
            const { id, x, y, width, height } = area;

            if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + height
            ) {
                clickedAreaId = id;
            }
        }

        return [
            clickedElementId,
            clickedInsideElement,
            clickedCircleElementId,
            clickedPointElementId,
            clickedPointInsideElementId,
            clickedPointInsideElementIndex,
            clickedPointDirection,
            clickedLineId,
            isClickedInsideExportImageBox,
            clickedAreaId,
        ];
    }

    function getNewClickedElementsForViewKeys(elementsValue, mouseX, mouseY) {
        let clickedElementId = null;
        let clickedInsideElement = null;

        // let clickedCircleElementId = null;

        // 1. check inside clicks
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    id,
                    x,
                    y,
                    width,
                    columns,
                    columnsOrder: elColumnsOrderKeys,
                } = element;

                const columnsKeys = orderedColumnKeys(columns ?? {}, elColumnsOrderKeys).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );
                const columnsCount = columnsKeys?.length || 0;
                const tableHeight =
                    TABLE_HEADER_HEIGHT +
                    HEADER_BODY_MARGIN +
                    (columnsCount + 1) * TABLE_ROW_HEIGHT +
                    TABLE_BORDER_RADIUS +
                    1;

                // 1.1. check clicks in element x, y area, so click inside one of the elements
                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + tableHeight
                ) {
                    clickedElementId = id;

                    if (focusedIdsRef.current.includes(id)) {
                        const relativeY =
                            mouseY -
                            y -
                            TABLE_HEADER_HEIGHT -
                            HEADER_BODY_MARGIN;
                        if (
                            relativeY > 0 &&
                            relativeY < columnsCount * TABLE_ROW_HEIGHT
                        ) {
                            const columnIndex = Math.floor(
                                relativeY / TABLE_ROW_HEIGHT
                            );
                            const columnId = columnsKeys[columnIndex];
                            const column = columns[columnId];

                            clickedInsideElement = {
                                elementId: element.id,
                                insideElementId: column.id,
                                index: columnIndex,
                            };
                        }
                    }
                }
            } else {
                const { id, x, y, width, height } = element;

                // 1.1. check clicks in element x, y area, so click inside one of the elements
                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + height
                ) {
                    clickedElementId = id;
                }

                // if (element.type === 'pen-segment') {
                // }
            }
        }

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;

        let clickedPointElementId = null;
        let clickedPointInsideElementId = null;
        let clickedPointInsideElementIndex = null;
        let clickedPointDirection = null; // 'left' / 'right'

        // 2. check outside clicks, so clicks on relation points
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    id,
                    x,
                    y,
                    width,
                    columns,
                    columnsOrder: clickRelColumnsOrder,
                } = element;

                const columnsKeys = orderedColumnKeys(columns ?? {}, clickRelColumnsOrder).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );

                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    columnsKeys.forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'left';

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'right';
                        }
                    });
                }
            }
        }

        // 3. check lines
        let clickedLineId = null;

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const { id } = lineObject;
            const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
            const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
            const fromRelationType = lineObject.sourceCardinality ?? lineObject.fromRelationType;
            const toElementId = lineObject.targetElementId ?? lineObject.toElementId;
            const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;
            const toRelationType = lineObject.targetCardinality ?? lineObject.toRelationType;

            const fromElement = elementsRef.current[fromElementId];
            const toElement = elementsRef.current[toElementId];

            if (!fromElement || !toElement) {
                continue;
            }
            const fromInsideElement =
                fromElement.columns[fromInsideElementId];
            const toInsideElement = toElement.columns[toInsideElementId];

            if (!fromInsideElement || !toInsideElement) {
                continue;
            }

            const fromElementColumnsKeys = orderedColumnKeys(
                fromElement.columns,
                fromElement.columnsOrder
            ).filter(
                (columnKey) =>
                    fromElement.columns[columnKey]?.isPK ||
                    fromElement.columns[columnKey]?.isFK ||
                    fromElement.columns[columnKey]?.isRelated
            );
            let fromInsideElementIndex = -1;
            fromElementColumnsKeys.forEach((key, index) => {
                if (fromInsideElementId === key) {
                    fromInsideElementIndex = index;
                }
            });

            const toElementColumnsKeys = orderedColumnKeys(
                toElement.columns,
                toElement.columnsOrder
            ).filter(
                (columnKey) =>
                    toElement.columns[columnKey]?.isPK ||
                    toElement.columns[columnKey]?.isFK ||
                    toElement.columns[columnKey]?.isRelated
            );
            let toInsideElementIndex = -1;
            toElementColumnsKeys.forEach((key, index) => {
                if (toInsideElementId === key) {
                    toInsideElementIndex = index;
                }
            });

            const lineData = {
                mouseX: null,
                mouseY: null,

                fromElementType: fromElement.type,
                fromElementWidth: fromElement.width,
                fromElementX: fromElement.x,
                fromElementY: fromElement.y,
                fromInsideElementIndex,
                fromRelationType,

                toElementType: toElement.type,
                toElementWidth: toElement.width,
                toElementX: toElement.x,
                toElementY: toElement.y,
                toInsideElementIndex,
                toRelationType,
            };

            const path = _buildLinePath(lineData);

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        20
                    );
                    if (isNear) {
                        clickedLineId = id;
                        break;
                    }
                }
            }
        }

        let isClickedInsideExportImageBox = null;

        // 4. check clicks in export image box
        if (isBtnExportImageEnabled) {
            const { clientX, clientY, clientBoxWidth, clientBoxHeight } =
                exportImageBoxPropsRef.current;

            const x = (clientX - originXYRef.current.x) / scaleRef.current;
            const y = (clientY - originXYRef.current.y) / scaleRef.current;
            const boxWidth = clientBoxWidth / scaleRef.current;
            const boxHeight = clientBoxHeight / scaleRef.current;

            if (
                mouseX >= x &&
                mouseX <= x + boxWidth &&
                mouseY >= y &&
                mouseY <= y + boxHeight
            ) {
                isClickedInsideExportImageBox = true;
            }
        }

        // 5. check areas
        let clickedAreaId = null;

        for (const key in areasRef.current) {
            const area = areasRef.current[key];
            const { id, x, y, width, height } = area;

            if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + height
            ) {
                clickedAreaId = id;
            }
        }

        return [
            clickedElementId,
            clickedInsideElement,
            null, // clickedCircleElementId,
            null, // clickedPointElementId,
            null, // clickedPointInsideElementId,
            null, // clickedPointInsideElementIndex,
            null, // clickedPointDirection,
            clickedLineId,
            isClickedInsideExportImageBox,
            clickedAreaId,
        ];
    }

    function isPointNearLine(x, y, x1, y1, x2, y2, distance) {
        const deltaX = x2 - x1;
        const deltaY = y2 - y1;
        const dx = x - x1;
        const dy = y - y1;

        // Параметр t для проекції точки на відрізок
        const t = Math.max(
            0,
            Math.min(
                1,
                (dx * deltaX + dy * deltaY) /
                (deltaX * deltaX + deltaY * deltaY)
            )
        );

        // Найближча точка на відрізку
        const nearestX = x1 + t * deltaX;
        const nearestY = y1 + t * deltaY;

        // Квадрат відстані від точки до найближчої точки на відрізку
        const distSquared = (x - nearestX) ** 2 + (y - nearestY) ** 2;
        const distanceSquared = distance ** 2;

        // Перевірка, чи відстань менше або дорівнює порогу
        return distSquared <= distanceSquared;
    }

    function getNewHoveredElements(
        elementsValue,
        mouseX,
        mouseY,
        eventX,
        eventY
    ) {
        const ctx = contextRef.current;

        if (viewMode === 'keys') {
            return getNewHoveredElementsForViewKeys(
                elementsValue,
                mouseX,
                mouseY
            );
        }

        let hoveredElementId = null;
        let hoveredInsideElementId = null;
        let hoveredInsideElementIndex = null;
        let hoveredElementIdEmptyColumn = null;
        let hoveredElementIdHeader = null;

        // 1. check elements and columns (hover on table body columns)
        for (const key in elementsValue) {
            const element = elementsValue[key];
            // Areas live in the same element map but are background containers, not
            // hoverable foreground elements. Skip them so an enclosing area can't
            // overwrite the hovered table/column (which would hide the table frame
            // and the columns' relation points). Area hover is handled separately
            // via hoveredAreaIdRef.
            if (element.type === 'area') continue;
            const {
                id,
                x,
                y,
                width,
                height,
                columns,
                columnsOrder: hoverColumnsOrder,
            } = element;

            if (element.type === 'pen-segment') {
                ctx.save();
                ctx.setTransform(
                    scaleRef.current,
                    0,
                    0,
                    scaleRef.current,
                    originXYRef.current.x,
                    originXYRef.current.y
                );

                ctx.translate(
                    (element.x ?? 0) - (element.pathOriginX ?? (element.x ?? 0)),
                    (element.y ?? 0) - (element.pathOriginY ?? (element.y ?? 0))
                );
                const penPath = new Path2D(element.path);

                if (ctx.isPointInPath(penPath, eventX, eventY)) {
                    hoveredElementId = id;
                    hoveredInsideElementId = null;
                    hoveredInsideElementIndex = null;
                }

                ctx.restore(); // Переміщуємо початкову точку малювання
            } else if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                // clipping is full-view only — keys/names view keep the natural
                // height so their hit-testing stays byte-identical
                mouseY <= y + (viewMode === 'full' ? getTableEffectiveHeight(element) : height) &&
                element.type !== 'pen-segment'
            ) {
                hoveredElementId = id;
                hoveredInsideElementId = null;
                hoveredInsideElementIndex = null;

                if (element.type === 'table') {
                    if (mouseY <= y + TABLE_HEADER_HEIGHT) {
                        // hover on the table's header
                        hoveredElementIdHeader = id;
                    }

                    // cap the loop at the visible count so hover can never
                    // resolve to a column hidden below the clip (full view only)
                    const hoverVisibleColumnsCount = viewMode === 'full'
                        ? getVisibleColumnsCount(element, orderedColumnKeys(columns, hoverColumnsOrder).length)
                        : Infinity;
                    orderedColumnKeys(columns, hoverColumnsOrder).forEach((key, index) => {
                        if (index >= hoverVisibleColumnsCount) return;
                        const column = columns[key];
                        const columnY = y + TABLE_HEADER_HEIGHT + index * TABLE_ROW_HEIGHT + HEADER_BODY_MARGIN;

                        if (mouseY >= columnY && mouseY <= columnY + TABLE_ROW_HEIGHT) {
                            hoveredInsideElementId = column.id;
                            hoveredInsideElementIndex = index;
                        }
                    });
                }
            }
        }

        const radius = 18;
        const distance = 36;
        const SHOW_CIRCLE_DISTANCE = 40;
        let hoveredCircleAreaElementsIds = [];
        let hoveredCircleElementId = null;

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;
        let hoveredPointAreaInsideElementsIds = new Set();
        let hoveredPointInsideElementId = null;
        let hoveredPointDirection = null; // 'left' / 'right'

        // 2. check relation points (hover on column's relation points)
        for (const key in elementsValue) {
            const element = elementsValue[key];
            const {
                id,
                x,
                y,
                width,
                height,
                columns,
                columnsOrder: hoverRelColumnsOrder,
            } = element;

            if (element.type === 'table') {
                // 2.1. check hover on column's relation points
                // if ((mouseX >= x - POINT_DISTANCE - POINT_RADIUS && mouseX <= x - POINT_DISTANCE + POINT_RADIUS) ||
                // 	(mouseX >= x + width + POINT_DISTANCE - POINT_RADIUS && mouseX <= x + width + POINT_DISTANCE + POINT_RADIUS)

                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    // clipping is full-view only — keys/names view keep their own
                    // index space, so this must stay Infinity (inert) there
                    const orderedColumnKeysForPoints = orderedColumnKeys(columns, hoverRelColumnsOrder);
                    const pointVisibleCount = viewMode === 'full'
                        ? getVisibleColumnsCount(element, orderedColumnKeysForPoints.length)
                        : Infinity;
                    orderedColumnKeysForPoints.forEach((key, index) => {
                        if (index >= pointVisibleCount) return;
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.add(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'left';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.add(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'right';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;
                        }

                        // 2.1.3. check relation points areas
                        else if (
                            mouseY >= columnY &&
                            mouseY <= columnY + TABLE_ROW_HEIGHT
                        ) {
                            hoveredPointAreaInsideElementsIds.add(column.id);
                        }
                    });
                }

                // 2.2. check hover on table's circle to add new row
                // Same effective-height fix as 1.2 in getNewClickedElements —
                // drawTable draws this circle off the clipped height, so both
                // the coarse "show area" box and the precise circle below must
                // use it too, or the visible circle stays cold while a ghost
                // spot at the natural-height line lights up instead.
                const circleBaseHeight = viewMode === 'full' ? getTableEffectiveHeight(element) : height;
                if (
                    mouseX >= x - SHOW_CIRCLE_DISTANCE &&
                    mouseX <= x + width + SHOW_CIRCLE_DISTANCE &&
                    mouseY >= y - SHOW_CIRCLE_DISTANCE &&
                    mouseY <= y + circleBaseHeight + SHOW_CIRCLE_DISTANCE * 2
                ) {
                    hoveredCircleAreaElementsIds.push(element.id);

                    if (
                        mouseX >= x + width / 2 - radius &&
                        mouseX <= x + width / 2 + radius &&
                        mouseY >= y + circleBaseHeight + distance - radius &&
                        mouseY <= y + circleBaseHeight + distance + radius
                    ) {
                        hoveredCircleElementId = id;
                    }
                }
            }
        }

        let hoveredLineId = null;

        // 3. check lines
        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            let { id } = lineObject;
            let path = lineObject.path;

            if (!path) {
                const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
                const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
                const fromRelationType = lineObject.sourceCardinality ?? lineObject.fromRelationType;
                const toElementId = lineObject.targetElementId ?? lineObject.toElementId;
                const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;
                const toRelationType = lineObject.targetCardinality ?? lineObject.toRelationType;

                const fromElement = elementsRef.current[fromElementId];
                const toElement = elementsRef.current[toElementId];

                if (!fromElement || !toElement) {
                    // return;
                    continue;
                }

                const fromInsideElement =
                    fromElement.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    // return;
                    continue;
                }

                const fromElementColumnsKeys = orderedColumnKeys(
                    fromElement.columns,
                    fromElement.columnsOrder
                );
                let fromInsideElementIndex = -1;
                fromElementColumnsKeys.forEach((key, index) => {
                    if (fromInsideElementId === key) {
                        fromInsideElementIndex = index;
                    }
                });

                const toElementColumnsKeys = orderedColumnKeys(
                    toElement.columns,
                    toElement.columnsOrder
                );
                let toInsideElementIndex = -1;
                toElementColumnsKeys.forEach((key, index) => {
                    if (toInsideElementId === key) {
                        toInsideElementIndex = index;
                    }
                });

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex,
                    fromRelationType,
                    // clipping is full-view only — keys/names view keep their own
                    // index space, so this must stay Infinity (inert) there
                    fromVisibleColumnsCount: viewMode === 'full'
                        ? getVisibleColumnsCount(fromElement, fromElementColumnsKeys.length)
                        : Infinity,
                    fromElementEffectiveHeight: getTableEffectiveHeight(fromElement),

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex,
                    toRelationType,
                    toVisibleColumnsCount: viewMode === 'full'
                        ? getVisibleColumnsCount(toElement, toElementColumnsKeys.length)
                        : Infinity,
                    toElementEffectiveHeight: getTableEffectiveHeight(toElement),
                };

                path = _buildLinePath(lineData);
            }
            if (!Array.isArray(path)) {
                // Defensive: a cached/derived path may be missing or non-array for a
                // line that can't currently be built; skip it instead of crashing the
                // hover hit-test (consistent with the !fromElement / !column guards above).
                continue;
            }
            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        10
                    );
                    if (isNear) {
                        hoveredLineId = id;
                        break;
                    }
                }
            }
        }

        let hoveredAreaId = null;

        // // 4. check areas
        // for (const key in areasRef.current) {
        // 	const area = areasRef.current[key];
        // 	const { id, x, y, width, height } = area;

        // 	if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
        // 		hoveredAreaId = id;
        // 	}
        // }

        return [
            !isElementResizing && hoveredElementId || null,
            !isElementResizing && hoveredInsideElementId || null,
            hoveredInsideElementIndex,
            !isElementResizing && hoveredCircleAreaElementsIds || [],
            !isElementResizing && hoveredCircleElementId || null,
            !isElementResizing && hoveredPointInsideElementId || null,
            hoveredPointDirection,
            !isElementResizing && hoveredPointAreaInsideElementsIds || new Set(),
            !isElementResizing && hoveredLineId || null,
            hoveredElementIdEmptyColumn,
            hoveredAreaId,
            !isElementResizing && hoveredElementIdHeader || null,
        ];
    }

    function getNewHoveredElementsForViewKeys(elementsValue, mouseX, mouseY) {
        let hoveredElementId = null;
        let hoveredInsideElementId = null;
        let hoveredInsideElementIndex = null;
        let hoveredElementIdEmptyColumn = null;
        let hoveredElementIdHeader = null;

        // 1. check hover on table body columns
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    id,
                    x,
                    y,
                    width,
                    columns,
                    columnsOrder: hoverKeysColumnsOrder,
                } = element;

                const columnsKeys = orderedColumnKeys(columns ?? {}, hoverKeysColumnsOrder).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );

                const columnsCount = columnsKeys?.length || 0;
                const tableHeight =
                    TABLE_HEADER_HEIGHT +
                    HEADER_BODY_MARGIN +
                    (columnsCount + 1) * TABLE_ROW_HEIGHT +
                    TABLE_BORDER_RADIUS +
                    1;

                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + tableHeight
                ) {
                    hoveredElementId = id;
                    hoveredInsideElementId = null;
                    hoveredInsideElementIndex = null;
                    hoveredElementIdEmptyColumn = null;

                    if (mouseY <= y + TABLE_HEADER_HEIGHT) {
                        // hover on the table's header
                        hoveredElementIdHeader = id;
                    }

                    // columns
                    columnsKeys.forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            HEADER_BODY_MARGIN +
                            index * TABLE_ROW_HEIGHT;

                        if (
                            mouseY >= columnY &&
                            mouseY <= columnY + TABLE_ROW_HEIGHT
                        ) {
                            hoveredInsideElementId = column.id;
                            hoveredInsideElementIndex = index;
                        }
                    });

                    // empty column
                    const emptyColumnY =
                        y +
                        TABLE_HEADER_HEIGHT +
                        HEADER_BODY_MARGIN +
                        columnsCount * TABLE_ROW_HEIGHT;
                    if (
                        mouseY >= emptyColumnY &&
                        mouseY <= emptyColumnY + TABLE_ROW_HEIGHT
                    ) {
                        hoveredElementIdEmptyColumn = id;
                    }
                }
            } else {
                const { id, x, y, width, height } = element;
                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + height
                ) {
                    hoveredElementId = id;
                    hoveredInsideElementId = null;
                    hoveredInsideElementIndex = null;
                }
            }
        }

        let hoveredCircleAreaElementsIds = [];
        let hoveredCircleElementId = null;

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;
        let hoveredPointAreaInsideElementsIds = new Set();
        let hoveredPointInsideElementId = null;
        let hoveredPointDirection = null; // 'left' / 'right'

        // 2. check hover on column's relation points
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    x,
                    y,
                    width,
                    columns,
                    columnsOrder: hoverRelPointsColumnsOrder,
                } = element;
                const columnsKeys = orderedColumnKeys(columns ?? {}, hoverRelPointsColumnsOrder).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );

                // 2.1. check hover on column's relation points
                // if ((mouseX >= x - POINT_DISTANCE - POINT_RADIUS && mouseX <= x - POINT_DISTANCE + POINT_RADIUS) ||
                // 	(mouseX >= x + width + POINT_DISTANCE - POINT_RADIUS && mouseX <= x + width + POINT_DISTANCE + POINT_RADIUS)

                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    columnsKeys.forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.add(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'left';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.add(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'right';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;
                        }

                        // 2.1.3. check relation points areas
                        else if (
                            mouseY >= columnY &&
                            mouseY <= columnY + TABLE_ROW_HEIGHT
                        ) {
                            hoveredPointAreaInsideElementsIds.add(column.id);
                        }
                    });
                }
            }
        }

        // 3. check lines
        let hoveredLineId = null;

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const { id } = lineObject;
            const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
            const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
            const fromRelationType = lineObject.sourceCardinality ?? lineObject.fromRelationType;
            const toElementId = lineObject.targetElementId ?? lineObject.toElementId;
            const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;
            const toRelationType = lineObject.targetCardinality ?? lineObject.toRelationType;

            const fromElement = elementsRef.current[fromElementId];
            const toElement = elementsRef.current[toElementId];

            if (!fromElement || !toElement) {
                // return;
                continue;
            }

            const fromInsideElement =
                fromElement.columns[fromInsideElementId];
            const toInsideElement = toElement.columns[toInsideElementId];

            if (!fromInsideElement || !toInsideElement) {
                // return;
                continue;
            }

            const fromElementColumnsKeys = orderedColumnKeys(
                fromElement.columns,
                fromElement.columnsOrder
            ).filter(
                (columnKey) =>
                    fromElement.columns[columnKey]?.isPK ||
                    fromElement.columns[columnKey]?.isFK ||
                    fromElement.columns[columnKey]?.isRelated
            );
            let fromInsideElementIndex = -1;
            fromElementColumnsKeys.forEach((key, index) => {
                if (fromInsideElementId === key) {
                    fromInsideElementIndex = index;
                }
            });

            const toElementColumnsKeys = orderedColumnKeys(
                toElement.columns,
                toElement.columnsOrder
            ).filter(
                (columnKey) =>
                    toElement.columns[columnKey]?.isPK ||
                    toElement.columns[columnKey]?.isFK ||
                    toElement.columns[columnKey]?.isRelated
            );
            let toInsideElementIndex = -1;
            toElementColumnsKeys.forEach((key, index) => {
                if (toInsideElementId === key) {
                    toInsideElementIndex = index;
                }
            });

            const lineData = {
                mouseX: null,
                mouseY: null,

                fromElementType: fromElement.type,
                fromElementWidth: fromElement.width,
                fromElementX: fromElement.x,
                fromElementY: fromElement.y,
                fromInsideElementIndex,
                fromRelationType,

                toElementType: toElement.type,
                toElementWidth: toElement.width,
                toElementX: toElement.x,
                toElementY: toElement.y,
                toInsideElementIndex,
                toRelationType,
            };

            const path = _buildLinePath(lineData);

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        10
                    );
                    if (isNear) {
                        hoveredLineId = id;
                        break;
                    }
                }
            }
        }

        // 4. check areas
        let hoveredAreaId = null;

        // for (const key in areasRef.current) {
        // 	const area = areasRef.current[key];
        // 	const { id, x, y, width, height } = area;

        // 	if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
        // 		hoveredAreaId = id;
        // 	}
        // }

        return [
            !isElementResizing && hoveredElementId || null,
            !isElementResizing && hoveredInsideElementId || null,
            hoveredInsideElementIndex,
            [], // hoveredCircleAreaElementsIds,
            null, // hoveredCircleElementId,
            null, // hoveredPointInsideElementId,
            null, // hoveredPointDirection,
            new Set(), // hoveredPointAreaInsideElementsIds,
            !isElementResizing && hoveredLineId || null,
            hoveredElementIdEmptyColumn,
            hoveredAreaId,
            !isElementResizing && hoveredElementIdHeader || null,
        ];
    }

    function getNewAreaIdForElement(areasValue, elementId) {
        const element = elementsRef.current[elementId];
        const {
            x: elementX,
            y: elementY,
            width: elementWidth,
            height: elementHeight,
        } = element;
        // clipping only affects full-view geometry — area membership should be
        // decided by a clipped table's VISIBLE body only; keys/names stay natural
        const elementEffectiveHeight = viewMode === 'full' ? getTableEffectiveHeight(element) : elementHeight;

        let newAreaId = null;

        for (const key in areasValue) {
            const area = areasValue[key];
            if (area.id === elementId) continue;
            const {
                id,
                x: areaX,
                y: areaY,
                width: areaWidth,
                height: areaHeight,
            } = area;

            const minX = areaX;
            const minY = areaY;
            const maxX = areaX + areaWidth;
            const maxY = areaY + areaHeight;

            if (
                elementX + elementWidth > minX &&
                elementX < maxX &&
                elementY + elementEffectiveHeight > minY &&
                elementY < maxY
            ) {
                newAreaId = id;
            }
        }

        return newAreaId;
    }

    // After an area moves or resizes, check every element (except those already
    // confirmed as nested via skipIds) and update areaId if containment changed.
    function recomputeElementAreaMembership() {
        const movedIds = focusedIdsRef.current || [];
        for (const elementId in elementsRef.current) {
            const element = elementsRef.current[elementId];
            if (element.type === 'area') continue;

            // An element that wasn't itself dragged keeps its current area as long as
            // that area still contains it — a different area moving (or resizing) over
            // it must NOT capture it. Only the dragged element(s), or elements whose
            // area no longer contains them, are (re)assigned.
            if (!movedIds.includes(elementId)) {
                const currentArea = element.areaId ? areasRef.current[element.areaId] : null;
                // clipping only affects full-view geometry — keys/names stay natural
                const elementEffectiveHeight = viewMode === 'full' ? getTableEffectiveHeight(element) : element.height;
                if (
                    currentArea &&
                    element.x + element.width > currentArea.x &&
                    element.x < currentArea.x + currentArea.width &&
                    element.y + elementEffectiveHeight > currentArea.y &&
                    element.y < currentArea.y + currentArea.height
                ) {
                    continue;
                }
            }

            const newAreaId = getNewAreaIdForElement(areasRef.current, elementId);
            if (newAreaId !== element.areaId) {
                elementsRef.current[elementId].areaId = newAreaId;
                elementsRef.current[elementId].isChangedAreaId = true;
            }
        }
    }

    function isHoverOnResizeHandle(mouseX, mouseY) {
        if (focusedIdsRef.current.length !== 1) {
            return [false, null];
        }

        const onlyId = focusedIdsRef.current[0];
        const focusedItem =
            elementsRef.current[onlyId] || areasRef.current[onlyId];

        // Selection ref may transiently outlive the underlying element/area
        // (e.g. between deleteElements firing and focusedIds being
        // cleared). The auto-cleanup observer in lib/yjs/doc.js catches
        // this on the next tick, but we still need to bail out cleanly
        // for any mouse event that races in between.
        if (!focusedItem) return [false, null];

        // Pen-segments can't be resized (for now) — no resize handles or cursor.
        if (focusedItem.type === 'pen-segment') return [false, null];

        const outlineOffset = scaleNumbersRef.current.outlineOffset;

        const resizeHandleSize = scaleNumbersRef.current.resizeHandleSize;

        const width = resizeHandleSize;
        let resizeKey = null;
        let isOnResize = false;

        // Clipped tables are DRAWN at their effective (visible/clipped)
        // height in full view (drawTable uses getTableEffectiveHeight) — the
        // resize handles must be hit-tested at that same height, or the
        // bottom-family zones (s/se/sw, and the y-range of the e/w edge
        // lines) sit at the table's natural, unclipped height instead: no
        // resize cursor over the visible clip edge, a ghost resize cursor
        // over the empty canvas below it. Same `viewMode === 'full' ?
        // getTableEffectiveHeight(el) : height` idiom used everywhere else
        // in this file (e.g. getSelectedElements, getNewAreaIdForElement);
        // getTableEffectiveHeight is itself a no-op for non-tables (returns
        // element.height unchanged), so areas/notes and keys/names views are
        // untouched here without an extra type check. Top handles (nw/ne/n)
        // are unaffected too — calculateItemResizeCoordinates derives them
        // purely from the item's `y` (top edge), never from `height`.
        const resizeGeometryItem = viewMode === 'full'
            ? { ...focusedItem, height: getTableEffectiveHeight(focusedItem) }
            : focusedItem;

        const focusedItemResizes = calculateItemResizeCoordinates(
            resizeGeometryItem,
            resizeHandleSize,
            outlineOffset
        );

        for (const key in focusedItemResizes.points) {
            const { x, y } = focusedItemResizes.points[key];
            if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + width
            ) {
                resizeKey = key;
                isOnResize = true;
            }
        }

        for (const key in focusedItemResizes.lines) {
            const { x, y } = focusedItemResizes.lines[key];
            const { value: xValue, min: xMin, max: xMax } = x;
            const { value: yValue, min: yMin, max: yMax } = y;

            let isHoverByX = false;
            let isHoverByY = false;

            // eslint-disable-next-line no-prototype-builtins
            if (
                (x.hasOwnProperty('value') && Math.abs(mouseX - xValue) < 10) ||
                (mouseX >= xMin && mouseX <= xMax)
            ) {
                isHoverByX = true;
            }
            // eslint-disable-next-line no-prototype-builtins
            if (
                (y.hasOwnProperty('value') && Math.abs(mouseY - yValue) < 10) ||
                (mouseY >= yMin && mouseY <= yMax)
            ) {
                isHoverByY = true;
            }

            if (isHoverByX && isHoverByY) {
                resizeKey = key;
                isOnResize = true;
            }
        }

        return [isOnResize, resizeKey];
    }

    function getHoveredResizeKeyOnExportImageBox(mouseX, mouseY) {
        const RESIZE_HANDLE_CLIENT_DISTANCE = 40;
        const RESIZE_HANDLE_DISTANCE =
            RESIZE_HANDLE_CLIENT_DISTANCE / scaleRef.current;

        let hoveredResizeKey = null;

        const { clientX, clientY, clientBoxWidth, clientBoxHeight } =
            exportImageBoxPropsRef.current;

        const x = (clientX - originXYRef.current.x) / scaleRef.current;
        const y = (clientY - originXYRef.current.y) / scaleRef.current;
        const boxWidth = clientBoxWidth / scaleRef.current;
        const boxHeight = clientBoxHeight / scaleRef.current;

        const resizeProps = {
            // 2 * distance, couse distance inside the box and distance outside the box
            distance: 2 * RESIZE_HANDLE_DISTANCE,
            points: {
                nw: {
                    x: x - RESIZE_HANDLE_DISTANCE,
                    y: y - RESIZE_HANDLE_DISTANCE,
                },
                ne: {
                    x: x + boxWidth - RESIZE_HANDLE_DISTANCE,
                    y: y - RESIZE_HANDLE_DISTANCE,
                },
                sw: {
                    x: x - RESIZE_HANDLE_DISTANCE,
                    y: y + boxHeight - RESIZE_HANDLE_DISTANCE,
                },
                se: {
                    x: x + boxWidth - RESIZE_HANDLE_DISTANCE,
                    y: y + boxHeight - RESIZE_HANDLE_DISTANCE,
                },
            },
        };

        for (const key in resizeProps.points) {
            const { x, y } = resizeProps.points[key];
            if (
                mouseX >= x &&
                mouseX <= x + resizeProps.distance &&
                mouseY >= y &&
                mouseY <= y + resizeProps.distance
            ) {
                hoveredResizeKey = key;
            }
        }

        return hoveredResizeKey;
    }

    function handleResize(resizeKey, deltaX, deltaY) {
        let x = 0;
        let y = 0;
        let width = 0;
        let height = 0;

        switch (resizeKey) {
            // points
            case 'nw':
                x = deltaX;
                y = deltaY;
                width = -deltaX;
                height = -deltaY;
                break;
            case 'ne':
                y = deltaY;
                width = deltaX;
                height = -deltaY;
                break;
            case 'sw':
                x = deltaX;
                width = -deltaX;
                height = deltaY;
                break;
            case 'se':
                width = deltaX;
                height = deltaY;
                break;
            // lines
            case 'n':
                y = deltaY;
                height = -deltaY;
                break;
            case 's':
                height = deltaY;
                break;
            case 'w':
                x = deltaX;
                width = -deltaX;
                break;
            case 'e':
                width = deltaX;
                break;
            default:
                break;
        }

        return { x, y, width, height };
    }


    function updateElementColumnsTextsOnResize(elementId) {
        if (!elementId || !elementsRef.current[elementId] || elementsRef.current[elementId].type !== 'table') {
            return;
        }

        const element = elementsRef.current[elementId];
        const { columns } = element;
        const keys = Object.keys(columns ?? {});

        keys.forEach((key, index) => {
            const column = columns[key];
            const oldName = column.name;

            if (oldName.length >= 4 && oldName)
                elementsRef.current[elementId].columns[key].name = oldName + '...';
        });

        // for (let i = 0; i < keys.length; i++) {
        //     if (keys[i] === columnId) {
        //         newInsideElementIndex = i;
        //         break;
        //     }
        // }

        // columns
        // foreach
        // get column measured width
        // compare measured width with table width
        // update column text (name ? type ?) (name => 'n..', then type => 't..', => 'n..' 't..')




        // const updatedLinesIds = [];

        // if (!Object.keys(linesRef.current).length) {
        //     return;
        // }

        // Object.keys(linesRef.current).forEach((key) => {
        //     const lineObject = linesRef.current[key];

        //     if (elementId === lineObject.fromElementId || elementId === lineObject.toElementId) {
        //         const lineData = buildLineData(lineObject.value);
        //         const path = lineData ? _buildLinePath(lineData) : {};
        //         linesRef.current[key].value.path = path;
        //         updatedLinesIds.push(lineObject.id);
        //     }
        // });

        // return updatedLinesIds;
    }


    function updateLinesByElementId(elementId) {
        const updatedLinesIds = [];

        if (!Object.keys(linesRef.current).length) {
            return;
        }

        Object.keys(linesRef.current).forEach((key) => {
            const lineObject = linesRef.current[key];

            if (elementId === (lineObject.sourceElementId ?? lineObject.fromElementId) || elementId === (lineObject.targetElementId ?? lineObject.toElementId)) {
                const lineData = buildLineData(lineObject);
                // Cache a falsy path (not {}) when the line can't be built, so the
                // readers' `if (!path)` checks rebuild/skip it instead of trying to
                // destructure or index a non-array and crashing.
                const path = lineData ? _buildLinePath(lineData) : null;
                linesRef.current[key].path = path;
                updatedLinesIds.push(lineObject.id);
            }
        });

        return updatedLinesIds;
    }

    function updateLinesByElementsIds(updatedElementsIds) {
        if (!Object.keys(linesRef.current).length) {
            return;
        }

        Object.keys(linesRef.current).forEach((key) => {
            const lineObject = linesRef.current[key];

            if (updatedElementsIds.includes(lineObject.sourceElementId ?? lineObject.fromElementId) ||
                updatedElementsIds.includes(lineObject.targetElementId ?? lineObject.toElementId)
            ) {
                const lineData = buildLineData(lineObject);
                const path = lineData ? _buildLinePath(lineData) : null;
                linesRef.current[key].path = path;
            }
        });
    }
    function getUpdatedLinesByElementsIds(updatedElementsIds) {
        const updatedLines = {};

        if (!Object.keys(linesRef.current).length) {
            return {};
        }

        Object.keys(linesRef.current).forEach((key) => {
            const lineObject = linesRef.current[key];

            if (
                updatedElementsIds.includes(lineObject.sourceElementId ?? lineObject.fromElementId) ||
                updatedElementsIds.includes(lineObject.targetElementId ?? lineObject.toElementId)
            ) {
                updatedLines[key] = linesRef.current[key];
            }
        });

        return updatedLines;
    }

    function getElementsRelatedProps(elementsIds = []) {
        let props = {
            linesIds: new Set(),
            elementsIds: new Set(),
            insideElementsIds: new Set(),
        };

        const elementsSet = new Set(elementsIds);

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const { id } = lineObject;
            const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
            const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
            const toElementId = lineObject.targetElementId ?? lineObject.toElementId;
            const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;

            if (
                elementsSet.has(fromElementId) ||
                elementsSet.has(toElementId)
            ) {
                props.linesIds.add(id);

                props.elementsIds.add(fromElementId);
                props.elementsIds.add(toElementId);

                props.insideElementsIds.add(fromInsideElementId);
                props.insideElementsIds.add(toInsideElementId);
            }
        }

        const updatedProps = {
            linesIds: Array.from(props.linesIds),
            elementsIds: Array.from(props.elementsIds),
            insideElementsIds: props.insideElementsIds,
        };

        return updatedProps;
    }
    function getLineRelatedProps(lineId) {
        let props = {
            elementsIds: [],
            insideElementsIds: new Set(),
        };

        if (!lineId) {
            return null;
        }

        const lineObject = linesRef.current[lineId];

        const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
        const fromInsideElementId = lineObject.sourceColumnId ?? lineObject.fromColumnId ?? lineObject.fromInsideElementId;
        const toElementId = lineObject.targetElementId ?? lineObject.toElementId;
        const toInsideElementId = lineObject.targetColumnId ?? lineObject.toColumnId ?? lineObject.toInsideElementId;

        props.elementsIds.push(fromElementId);
        props.elementsIds.push(toElementId);

        props.insideElementsIds.add(fromInsideElementId);
        props.insideElementsIds.add(toInsideElementId);

        return props;
    }

    // function calculateContextMenuXY() {
    // 	let x = 0;
    // 	let y = 0;
    // 	let width = 0;
    // 	let height = 0;

    // 	switch (resizeKeyRef.current) {
    // 		// points
    // 		case 'nw':
    // 			x = deltaX;
    // 			y = deltaY;
    // 			width = -deltaX;
    // 			height = -deltaY;
    // 			break;
    // 		case 'ne':
    // 			y = deltaY;
    // 			width = deltaX;
    // 			height = -deltaY;
    // 			break;
    // 		case 'sw':
    // 			x = deltaX;
    // 			width = -deltaX;
    // 			height = deltaY;
    // 			break;
    // 		case 'se':
    // 			width = deltaX;
    // 			height = deltaY;
    // 			break;
    // 		// lines
    // 		case 'n':
    // 			y = deltaY;
    // 			height = -deltaY;
    // 			break;
    // 		case 's':
    // 			height = deltaY;
    // 			break;
    // 		case 'w':
    // 			x = deltaX;
    // 			width = -deltaX;
    // 			break;
    // 		case 'e':
    // 			width = deltaX;
    // 			break;
    // 		default:
    // 			break;
    // 	}

    // }

    function getAlignmentLines(movedElementId) {
        // TODO: build moving when you draw line
        // - line = x1, y1 => x2, y2
        // if movingElement is left => line = x2, y2 => x1, y1
        // if movingElement is right => line = x1, y1 => x2, y2
        const alignmentLines = [];
        const threshold = 2;

        // 'Show align lines' settings toggle (editor.show_align_lines): off
        // disables the whole feature — no guide lines AND no snapping (they
        // are one feature; snapping without visible guides would feel like
        // random stickiness). Ref-mirrored — this runs from once-bound drag
        // handlers.
        if (showAlignLinesRef.current === false) return [alignmentLines, null];

        let movedElementNewXYPoints = [];

        const movedElement = elementsRef.current[movedElementId];

        // Pen-segments don't participate in alignment guides — not as the moved
        // element, and (below) not as a target to align against.
        if (movedElement?.type === 'pen-segment') return [alignmentLines, null];

        for (const key in elementsRef.current) {
            let movedElementNewXY = {};

            const element = elementsRef.current[key];

            if (element.type === 'pen-segment') continue;
            if (element.id === movedElement.id) continue;
            if (!visibleViewportElementsIdsRef.current.includes(element.id))
                continue;
            // if (element?.value?.header?.name !== 'bo.phones') continue;
            // if (element.type !== 'table') return;

            let line;

            // OUTLINE FRAME TABLE
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 0.75);

            // Вирівнювання по лівій стороні
            if (Math.abs(movedElement.x - element.x) < threshold) {
                movedElementNewXY.x = element.x;
                movedElementNewXY.distanceX = Math.abs(
                    movedElement.x - element.x
                );

                line = {
                    x1: element.x - scaleNumbersRef.current.outlineOffset - 2.5,
                    // y1: Math.min(movedElement.y, element.y) - 27,
                    y1: Math.min(movedElement.y, element.y) - 7,
                    x2: element.x - scaleNumbersRef.current.outlineOffset - 2.5,
                    y2:
                        Math.max(
                            movedElement.y + movedElement.height,
                            element.y + element.height
                            // ) + 30,
                        ) + 7,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по правій стороні
            if (
                Math.abs(
                    movedElement.x +
                    movedElement.width -
                    (element.x + element.width)
                ) < threshold
            ) {
                // movedElementNewXY.x = element.x + element.width - movedElement.width;
                movedElementNewXY.x =
                    element.x + element.width - movedElement.width;
                movedElementNewXY.distanceX = Math.abs(
                    movedElement.x +
                    movedElement.width -
                    (element.x + element.width)
                );

                line = {
                    x1:
                        element.x +
                        element.width +
                        scaleNumbersRef.current.outlineOffset +
                        2.5,
                    // y1: Math.min(movedElement.y, element.y) - 27,
                    y1: Math.min(movedElement.y, element.y) - 7,
                    x2:
                        element.x +
                        element.width +
                        scaleNumbersRef.current.outlineOffset +
                        2.5,
                    y2:
                        Math.max(
                            movedElement.y + movedElement.height,
                            element.y + element.height
                            // ) + 30,
                        ) + 7,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по верхній стороні
            if (Math.abs(movedElement.y - element.y) < threshold) {
                movedElementNewXY.y = element.y;
                movedElementNewXY.distanceY = Math.abs(
                    movedElement.y - element.y
                );

                line = {
                    // x1: Math.min(movedElement.x, element.x) - 27,
                    x1: Math.min(movedElement.x, element.x) - 7,
                    y1:
                        element.y -
                        scaleNumbersRef.current.outlineOffset -
                        0.5 -
                        2.5,
                    x2:
                        Math.max(
                            movedElement.x + movedElement.width,
                            element.x + element.width
                            // ) + 30,
                        ) + 7,
                    y2:
                        element.y -
                        scaleNumbersRef.current.outlineOffset -
                        0.5 -
                        2.5,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по нижній стороні
            if (
                Math.abs(
                    movedElement.y +
                    movedElement.height -
                    (element.y + element.height)
                ) < threshold
            ) {
                // movedElementNewXY.y = element.y + element.height - movedElement.height;
                movedElementNewXY.y =
                    element.y + element.height - movedElement.height;
                movedElementNewXY.distanceY = Math.abs(
                    movedElement.y +
                    movedElement.height -
                    (element.y + element.height)
                );

                line = {
                    // x1: Math.min(movedElement.x, element.x) - 27,
                    x1: Math.min(movedElement.x, element.x) - 7,
                    y1:
                        element.y +
                        element.height +
                        scaleNumbersRef.current.outlineOffset +
                        0.5 +
                        2.5 +
                        0.5,
                    x2:
                        Math.max(
                            movedElement.x + movedElement.width,
                            element.x + element.width
                            // ) + 30,
                        ) + 7,
                    y2:
                        element.y +
                        element.height +
                        scaleNumbersRef.current.outlineOffset +
                        0.5 +
                        2.5 +
                        0.5,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по центру по горизонталі
            const movedCenterX = movedElement.x + movedElement.width / 2;
            const elementCenterX = element.x + element.width / 2;
            if (Math.abs(movedCenterX - elementCenterX) < threshold) {
                movedElementNewXY.x = elementCenterX - movedElement.width / 2;
                movedElementNewXY.distanceX = Math.abs(
                    movedCenterX - elementCenterX
                );

                line = {
                    x1: elementCenterX,
                    // y1: Math.min(movedElement.y, element.y) - 25,
                    y1: Math.min(movedElement.y, element.y) - 7,
                    x2: elementCenterX,
                    y2:
                        Math.max(
                            movedElement.y + movedElement.height,
                            element.y + element.height
                            // ) + 28,
                        ) + 7,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по центру по вертикалі
            const movedCenterY = movedElement.y + movedElement.height / 2;
            const elementCenterY = element.y + element.height / 2;
            if (Math.abs(movedCenterY - elementCenterY) < threshold) {
                movedElementNewXY.y = elementCenterY - movedElement.height / 2;
                movedElementNewXY.distanceY = Math.abs(
                    movedCenterY - elementCenterY
                );

                line = {
                    // x1: Math.min(movedElement.x, element.x) - 25,
                    x1: Math.min(movedElement.x, element.x) - 7,
                    y1: elementCenterY,
                    x2:
                        Math.max(
                            movedElement.x + movedElement.width,
                            element.x + element.width
                            // ) + 28,
                        ) + 7,
                    y2: elementCenterY,
                };
                alignmentLines.push(line);
            }

            if (
                typeof movedElementNewXY.x === 'number' ||
                typeof movedElementNewXY.y === 'number'
            ) {
                movedElementNewXYPoints.push(movedElementNewXY);
            }
        }

        // get closer alignment line by 'x' and line by 'y' to the element
        if (movedElementNewXYPoints.length) {
            let firstPoint = movedElementNewXYPoints[0];

            // initial just bigger than threshold
            let closerNewXYPoint = {
                x: firstPoint.x,
                y: firstPoint.y,
                distanceX: firstPoint.distanceX,
                distanceY: firstPoint.distanceY,
            };
            movedElementNewXYPoints.forEach((point) => {
                if (typeof point.distanceX === 'number') {
                    if (
                        typeof closerNewXYPoint.distanceX !== 'number' ||
                        point.distanceX < closerNewXYPoint.distanceX
                    ) {
                        closerNewXYPoint.x = point.x;
                    }
                }
                if (typeof point.distanceY === 'number') {
                    if (
                        typeof closerNewXYPoint.distanceY !== 'number' ||
                        point.distanceY < closerNewXYPoint.distanceY
                    ) {
                        closerNewXYPoint.y = point.y;
                    }
                }
            });

            return [alignmentLines, closerNewXYPoint];
        }

        return [alignmentLines, null];
    }

    const syncScaleToReduxRef = useRef(
        debounce((scaleValue, originXValue, originYValue) => {
            dispatch(updateScale(scaleValue, originXValue, originYValue));
        }, 100)
    );
    const syncOriginXYToReduxRef = useRef(
        debounce((originXValue, originYValue) => {
            calculateVisibleElementsAndLines();
            dispatch(updateOriginXY(originXValue, originYValue));
        }, 100)
    );
    const syncFocusedInsideElementToReduxRef = useRef(
        debounce(
            (
                elementId,
                insideElementId,
                insideElementIndex,
                insideElementType
            ) => {
                // if (focusedInsideElementStateValue?.elementId !== focusedInsideElementRedux?.elementId ||
                // 	focusedInsideElementStateValue?.insideElementId !== focusedInsideElementRedux?.insideElementId ||
                // 	focusedInsideElementStateValue?.insideElement?.name !== focusedInsideElementRedux?.insideElement?.name ||
                // 	focusedInsideElementStateValue?.insideElement?.type !== focusedInsideElementRedux?.insideElement?.type
                // ) {

                // if (scaleRef.current > 0.15) {
                dispatch(
                    updateFocusedInsideElement(
                        elementId,
                        insideElementId,
                        insideElementIndex,
                        insideElementType
                    )
                );
                // } else {
                //     dispatch(
                //         updateFocusedInsideElement(
                //             null,
                //             null,
                //             null,
                //             null
                //         )
                //     );
                // }
                // }
            },
            1
        )
    );

    const syncEditableTableColumnNameToReduxRef = useRef(
        debounce((tableId, columnId, value, newWidth) => {
            if (!canEditRef.current) return;
            const doc = getDiagramDoc();
            setColumnName(doc, tableId, columnId, value);
            resizeElements(doc, [{ id: tableId, width: newWidth }]);
        }, 100)
    );
    const syncEditableTableColumnTypeToReduxRef = useRef(
        debounce((tableId, columnId, value, newWidth) => {
            if (!canEditRef.current) return;
            const doc = getDiagramDoc();
            setColumnType(doc, tableId, columnId, value);
            resizeElements(doc, [{ id: tableId, width: newWidth }]);
        }, 100)
    );
    const syncEditableTableHeaderNameToReduxRef = useRef(
        debounce((tableId, value) => {
            if (!canEditRef.current) return;
            setElementName(getDiagramDoc(), tableId, value);
        }, 100)
    );
    const syncNoteTextToYjsRef = useRef(
        debounce((noteId, value) => {
            if (!canEditRef.current) return;
            setNoteText(getDiagramDoc(), noteId, value);
        }, 100)
    );

    function onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
    }

    function handleCursorClick() {
        clickTimesRef.current = [
            ...(clickTimesRef.current || []),
            new Date().getTime(),
        ];

        let isDoubleClick = false;
        if (clickTimesRef.current.length >= 2) {
            const firstTime =
                clickTimesRef.current[clickTimesRef.current.length - 2];
            const lastTime =
                clickTimesRef.current[clickTimesRef.current.length - 1];
            const diffTime = lastTime - firstTime;

            clickTimesRef.current = clickTimesRef.current.slice(
                1,
                clickTimesRef.current.length
            );
            // isDoubleClick = diffTime <= 250;
            isDoubleClick = diffTime <= 300;
        }

        // double click
        if (isDoubleClick) {
            dispatch(changeCursor());
        }
    }


    const drawUpdateZoom = useCallback((newScale, newOriginX, newOriginY) => {
        console.log('drawUpdateZoom', newScale, newOriginX, newOriginY)

        const canvas = ref.current;
        const context = contextRef.current;

        if (!canvas || !context) {
            return;
        }

        // context.save();
        // context.clearRect(0, 0, canvas.width / 2, canvas.height / 2);

        context.setTransform(newScale, 0, 0, newScale, newOriginX, newOriginY);
        // context.restore();


    }, [])






    // const SPACING = 20;
    // const SPACING = 21;             // base spacing for dots renderer (CSS px @ scale 1)
    // const SPACING_LINES = 21;       // base spacing for lines renderer (CSS px @ scale 1)
    // const SPACING = 17;             // base spacing for dots renderer (CSS px @ scale 1)
    // const SPACING_LINES = 17;       // base spacing for lines renderer (CSS px @ scale 1)
    const SPACING = 19;             // base spacing for dots renderer (CSS px @ scale 1)
    const SPACING_LINES = 19;       // base spacing for lines renderer (CSS px @ scale 1)

    const GRID_RADIUS_CSS = 0.8;    // constant CSS-px dot radius
    // const GRID_RADIUS_CSS = 0.7;    // constant CSS-px dot radius

    // const GRID_LINE_COLOR_MINOR = 'rgba(240,240,255,0.025)';
    const GRID_LINE_COLOR_MINOR = 'rgba(240,240,255,0.02)';
    // const GRID_LINE_COLOR_MAJOR = 'rgba(240,240,255,0.1)';
    const GRID_LINE_COLOR_MAJOR = 'rgba(240,240,255,0.05)';
    const GRID_MAJOR_STEP = 4;   // every Nth line is a major
    const GRID_LINE_WIDTH_DEVICE = 1;   // device-px stroke width

    // Symmetric, formula-based spacing. World spacing snaps to baseSpacing * 2^k
    // for an integer k chosen so on-screen spacing stays near baseSpacing.
    // Each zoom step exactly halves or doubles the world spacing — new dots appear
    // strictly between existing ones, and the behavior is symmetric for zoom in/out.
    // baseSpacing defaults to SPACING (dots); pass SPACING_LINES for the lines grid.
    function getGridSpacing(scaleValue, baseSpacing = SPACING) {
        const safeScale = scaleValue > 0 ? scaleValue : 1;
        const k = Math.round(Math.log2(1 / safeScale));
        const space = baseSpacing * Math.pow(2, k);
        return [GRID_RADIUS_CSS / safeScale, space];
    }

    // Legacy table-driven spacing — kept for reference / A/B comparison.
    // Was active before the symmetric version above; rename and uncomment to restore.
    /*
    function getGridSpacingLegacy(scaleValue) {
        const scale = scaleValue / 0.9;
        let radius = 0.75;
        let space = SPACING;

        if (scale >= 4) {
            radius = 1.75;
            space = SPACING;
        } else if (scale >= 3.5) {
            radius = 1.5;
            space = SPACING;
        } else if (scale >= 3) {
            radius = 1.3;
            space = SPACING;
        } else if (scale >= 2.5) {
            radius = 1.15;
            space = SPACING;
        } else if (scale >= 2) {
            radius = 1;
            space = SPACING;
        } else if (scale >= 1.5) {
            radius = 0.9;
            space = SPACING;
        } else if (scale >= 1) {
            radius = 0.8;
            space = SPACING;
        } else if (scale <= 0.075) {
            radius = 0.6;
            space = SPACING * 16;
        } else if (scale <= 0.11) {
            radius = 0.5;
            space = SPACING * 8;
        } else if (scale <= 0.18) {
            radius = 0.6;
            space = SPACING * 8;
        } else if (scale <= 0.35) {
            radius = 0.5;
            space = SPACING * 4;
        } else if (scale <= 0.5) {
            radius = 0.6;
            space = SPACING * 2;
        } else if (scale <= 0.8) {
            radius = 0.7;
            space = SPACING * 2;
        } else if (scale <= 1) {
            radius = 0.7;
            space = SPACING;
        }

        return [radius / scale, space];
    }
    */


    function lowerNumber(value, divisor) {
        return Math.floor(value / divisor) * divisor;
    }


    function drawGrid(ctx, scale, exportImgCanvas = null) {
        const [radius, spacing] = getGridSpacing(scale);
        // ctx.fillStyle = `rgba(0,0,0,${0.15 * opacity})`;
        // ctx.fillStyle = `rgba(255,255,255,${0.9 * opacity})`;
        // ctx.fillStyle = `rgba(252,252,252,0.35)`;
        // ctx.fillStyle = `rgba(252,252,252,0.3)`;
        ctx.fillStyle = `rgba(240,240,255,0.28)`;



        const startX = (0 - originXYRef.current.x) / scale;
        const startY = (0 - originXYRef.current.y) / scale;
        const newX = lowerNumber(startX, spacing);
        const newY = lowerNumber(startY, spacing);

        // const mainCanvas = ref.current;
        // let viewportWidth = mainCanvas.width * scaleRef.current;
        // let viewportHeight = mainCanvas.height * scaleRef.current;



        let windowWidth = window.innerWidth;
        let windowHeight = window.innerHeight;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            windowWidth = exportImgCanvas.width;
            windowHeight = exportImgCanvas.height;
        }

        // bigger viewport area for 'visible elements/lines'
        const frameStartX = (0 - originXYRef.current.x) / scaleRef.current;
        const frameStartY = (0 - originXYRef.current.y) / scaleRef.current;
        // const frameEndX = (windowWidth - originXYRef.current.x) / scaleRef.current + 200;
        // const frameEndY = (windowHeight - originXYRef.current.y) / scaleRef.current + 200;
        const frameEndX = (windowWidth - originXYRef.current.x) / scaleRef.current + 200;
        const frameEndY = (windowHeight - originXYRef.current.y) / scaleRef.current + 200;





        const Pi2 = Math.PI * 2;

        // console.log('CANVAS', viewportWidth, mainCanvas.width, scaleRef.current)
        // console.log('PANAS', windowWidth, mainCanvas.width, scaleRef.current)
        for (let x = newX; x < frameEndX; x += spacing) {
            for (let y = newY; y < frameEndY; y += spacing) {
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Pi2);
                ctx.fill();
            }
        }
    }


    // Pattern's pre-rasterized tile gets bilinear-spread when sampled to the
    // canvas, which makes dots read heavier than the legacy per-dot fill at
    // the same alpha. Compensate with a slightly lower alpha here.
    const GRID_DOT_COLOR = 'rgba(240,240,255,0.3)';

    function drawGridPattern(ctx, scale, exportImgCanvas = null) {
        const [worldRadius, spacingWorld] = getGridSpacing(scale);
        // getGridSpacing returns radius pre-divided by scale. Recover screen-CSS px.
        const radiusCssPx = worldRadius * scale;
        const spacingCssPx = spacingWorld * scale;
        if (!(spacingCssPx > 0)) return;

        const dpr = window.devicePixelRatio || 1;
        const entry = __buildGridDotPattern(
            ctx,
            spacingCssPx,
            radiusCssPx,
            GRID_DOT_COLOR,
            dpr
        );
        if (!entry || !entry.pattern) return;
        const { pattern, tileDevicePx } = entry;

        // Tile is rounded to integer device px, but the true world-space spacing in
        // CSS px is fractional. Use the exact spacingCssPx for both the alignment
        // offset and the pattern's user-space scale so the repeat period is exact —
        // otherwise dots far from world (0,0) drift visibly during zoom.
        const tileScale = spacingCssPx / tileDevicePx;

        let viewportCssWidth = window.innerWidth;
        let viewportCssHeight = window.innerHeight;
        if (isBtnExportImageEnabled && exportImgCanvas) {
            viewportCssWidth = exportImgCanvas.width / dpr;
            viewportCssHeight = exportImgCanvas.height / dpr;
        }

        const originX = originXYRef.current.x;
        const originY = originXYRef.current.y;

        // Pattern places the dot at user (tileDevicePx*tileScale/2 + ox, ...) =
        // (spacingCssPx/2 + ox, ...). For a dot at world (0,0) -> CSS (originX, originY):
        const ox = __modGrid(originX - spacingCssPx / 2, spacingCssPx);
        const oy = __modGrid(originY - spacingCssPx / 2, spacingCssPx);

        ctx.save();
        // Render in screen-CSS px space — caller's world transform doesn't apply here.
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (typeof pattern.setTransform === 'function') {
            pattern.setTransform(
                new DOMMatrix().translate(ox, oy).scale(tileScale, tileScale)
            );
            ctx.fillStyle = pattern;
            ctx.fillRect(0, 0, viewportCssWidth, viewportCssHeight);
            ctx.restore();
            return;
        }

        // Fallback for engines without CanvasPattern.setTransform: defer to legacy.
        // (Path here would otherwise reintroduce drift; correctness > perf.)
        ctx.restore();
        drawGrid(ctx, scale, exportImgCanvas);
    }

    function drawGridLines(ctx, scale, exportImgCanvas = null) {
        const [, spacingWorld] = getGridSpacing(scale, SPACING_LINES);
        if (!(spacingWorld > 0)) return;

        const dpr = window.devicePixelRatio || 1;
        const stepCss = spacingWorld * scale;
        const stepDev = stepCss * dpr;

        // Bail out if the grid would render as a solid wash.
        if (stepDev < 2) return;

        let viewportDevW;
        let viewportDevH;
        if (isBtnExportImageEnabled && exportImgCanvas) {
            viewportDevW = exportImgCanvas.width;
            viewportDevH = exportImgCanvas.height;
        } else {
            viewportDevW = window.innerWidth * dpr;
            viewportDevH = window.innerHeight * dpr;
        }

        const originX = originXYRef.current.x;
        const originY = originXYRef.current.y;

        // First/last visible line indices along each axis.
        const kFirstX = Math.ceil((-originX) / stepCss);
        const kLastX = Math.floor((viewportDevW / dpr - originX) / stepCss);
        const kFirstY = Math.ceil((-originY) / stepCss);
        const kLastY = Math.floor((viewportDevH / dpr - originY) / stepCss);

        ctx.save();
        // Render in device-px space (vs drawGridPattern's CSS-px space) so the
        // +0.5 trick can pixel-snap a 1 device-px stroke to a single column.
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        const minorPath = new Path2D();
        const majorPath = new Path2D();

        for (let k = kFirstX; k <= kLastX; k++) {
            const xDev = Math.round((k * stepCss + originX) * dpr) + 0.5;
            const target = k % GRID_MAJOR_STEP === 0 ? majorPath : minorPath;
            target.moveTo(xDev, 0);
            target.lineTo(xDev, viewportDevH);
        }
        for (let k = kFirstY; k <= kLastY; k++) {
            const yDev = Math.round((k * stepCss + originY) * dpr) + 0.5;
            const target = k % GRID_MAJOR_STEP === 0 ? majorPath : minorPath;
            target.moveTo(0, yDev);
            target.lineTo(viewportDevW, yDev);
        }

        ctx.lineWidth = GRID_LINE_WIDTH_DEVICE;
        ctx.strokeStyle = GRID_LINE_COLOR_MINOR;
        ctx.stroke(minorPath);
        ctx.strokeStyle = GRID_LINE_COLOR_MAJOR;
        ctx.stroke(majorPath);

        ctx.restore();
    }









    const _gridPatternCache = new WeakMap(); // ctx -> Map(key -> pattern)

    function _mod(n, m) {
        return ((n % m) + m) % m;
    }

    function _getCtxCache(ctx) {
        let map = _gridPatternCache.get(ctx);
        if (!map) {
            map = new Map();
            _gridPatternCache.set(ctx, map);
        }
        return map;
    }

    function getGridPattern(ctx, radiusPx, spacing, colorRgba) {
        const dpr = window.devicePixelRatio || 1;
        const cache = _getCtxCache(ctx);

        // невеликий clamp, щоб не було нульових тайлів
        // const spacing = Math.max(6, spacingPx);
        const radius = Math.max(0.5, radiusPx);

        const key = `${spacing}|${radius}|${colorRgba}|${dpr}`;
        // const cached = cache.get(key);
        // if (cached) return cached;

        // тайл у device px для чіткості
        const tileSize = spacing;

        const tile =
            typeof OffscreenCanvas !== "undefined"
                ? new OffscreenCanvas(tileSize, tileSize)
                : Object.assign(document.createElement("canvas"), { width: tileSize, height: tileSize });

        const tctx = tile.getContext("2d");
        tctx.clearRect(0, 0, tileSize, tileSize);
        tctx.fillStyle = colorRgba;

        // крапка по центру
        tctx.beginPath();
        tctx.arc(tileSize / 2, tileSize / 2, radius * dpr, 0, Math.PI * 2);
        tctx.fill();

        const pattern = ctx.createPattern(tile, "repeat");
        cache.set(key, { pattern, spacing }); // spacing в CSS px
        return { pattern, spacing };
    }




    function drawGrid2(ctx, scale) {
        const [radius, spacing] = getGridSpacing(scale);

        const color = `rgba(252,252,252,0.3)`;
        const { pattern, spacing: spacingPx } = getGridPattern(ctx, radius, spacing, color);

        const dpr = window.devicePixelRatio || 1;

        // Розміри в CSS px (якщо canvas = css*dpr)
        const w = ctx.canvas.width / dpr;
        const h = ctx.canvas.height / dpr;

        // Офсет, щоб grid "приклеївся" до світу через твій origin
        // Якщо в тебе Y інвертований — поміняй знак у oy (див. коментар нижче)
        // let ox = _mod(originXYRef.current.x, spacingPx);
        // let oy = _mod(originXYRef.current.y, spacingPx); // або _mod(-originXYRef.current.y, spacingPx)
        let ox = 0;
        let oy = 0;


        // const startX = (0 - originXYRef.current.x);
        // const startY = (0 - originXYRef.current.y);
        // const newX = lowerNumber(startX, spacing);
        // const newY = lowerNumber(startY, spacing);
        // ox = newX;
        // oy = newY;

        ctx.save();

        // Малюємо в CSS px (screen-space)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        // if (pattern.setTransform) {
        pattern.setTransform(new DOMMatrix().translate(ox, oy));
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, w, h);
        // } else {
        //     // fallback, якщо setTransform нема
        //     ctx.translate(ox, oy);
        //     ctx.fillStyle = pattern;
        //     ctx.fillRect(-ox, -oy, w, h);
        // }

        ctx.restore();
    }

    const draw = useCallback(({ isWindowForceResize, isFromMouseMove, exportImgCanvas = null } = {}) => {
        const isResizingArea = resizeAreaPropsRef.current?.areaId;

        if (isFromMouseMove) {
            // console.log('mouse-draw', new Date().getTime())
        } else {
            // console.log('draw', new Date().getTime())
        }
        if (
            isBtnMinimapEnabled &&
            minimapRef.current &&
            minimapRef.current.staticCanvas
        ) {
            const staticCanvas = minimapRef.current.staticCanvas;
            const staticContext = staticCanvas.getContext('2d');

            drawMinimapStatic(staticCanvas, staticContext);
        }

        const canvas = ref.current;
        const context = contextRef.current;

        if (!canvas || !context) {
            return;
        }

        // ===================== COLLAB AWARENESS | GEOMETRY (clear) =====================
        // Cleared at the start of every draw pass and populated below as elements,
        // areas, lines, and columns are drawn. Read by drawRemoteHalos at the end of
        // the local-content draw passes.
        elementsGeomRef.current = {};
        areasGeomRef.current = {};
        linesGeomRef.current = {};
        columnsGeomRef.current = {};

        // =========================================
        const dpr = window.devicePixelRatio || 1;

        canvas.style.width = window.innerWidth + 'px';
        canvas.style.height = window.innerHeight + 'px';

        canvas.width = window.innerWidth * dpr;
        canvas.height = window.innerHeight * dpr;

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        // =========================================

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.save();
        context.setTransform(
            scaleRef.current * dpr,
            0,
            0,
            scaleRef.current * dpr,
            originXYRef.current.x * dpr,
            originXYRef.current.y * dpr
        );

        const movingRelated = isMovingElementId ? getElementsRelatedProps([isMovingElementId]) : null;
        const movingElementsRelatedLines = movingRelated?.linesIds || [];
        const movingElementsRelatedElements = movingRelated?.elementsIds || [];

        const relatedLinesIds = [
            ...(focusedRelatedPropsRef.current?.linesIds || []),
            ...movingElementsRelatedLines,
        ];

        const relatedElementsIds = [
            ...(focusedRelatedPropsRef.current?.elementsIds || []),
            ...movingElementsRelatedElements,
        ].filter(onlyUnique);

        const focusedLineRelatedElementsIds = focusedLineRelatedPropsRef.current?.elementsIds || [];
        const hoveredLineRelatedElementsIds = hoveredLineRelatedPropsRef.current?.elementsIds || [];

        const focusedElementsIds = focusedIdsRef.current.filter(
            (sid) => elementsRef.current[sid] && elementsRef.current[sid].type !== 'area'
        );
        const focusedAndMovingElementsIds = isMovingElementId
            ? [...focusedElementsIds, isMovingElementId]
            : focusedElementsIds;


        if (exportImgCanvas && gridStyle) {
            const contextGrid = exportImgCanvas.getContext('2d');
            if (gridStyle === 'lines') {
                drawGridLines(contextGrid, scaleRef.current, exportImgCanvas);
            } else if (gridStyle === 'dots') {
                drawGridPattern(contextGrid, scaleRef.current, exportImgCanvas);
                // drawGrid(contextGrid, scaleRef.current, null);
            }
        }

        if (canvasGridRef.current) {
            const canvasGrid = canvasGridRef.current;
            const contextGrid = canvasGrid.getContext('2d');

            if (gridStyle) {
                if (isWindowForceResize ||
                    !lastGridProps.current ||
                    lastGridProps.current?.scale !== scaleRef.current ||
                    lastGridProps.current?.originX !== originXYRef.current.x ||
                    lastGridProps.current?.originY !== originXYRef.current.y ||
                    lastGridProps.current?.style !== gridStyle
                ) {
                    lastGridProps.current = {
                        scale: scaleRef.current,
                        originX: originXYRef.current.x,
                        originY: originXYRef.current.y,
                        style: gridStyle,
                    };

                    // =========================================
                    const dpr = window.devicePixelRatio || 1;

                    canvasGrid.style.width = window.innerWidth + 'px';
                    canvasGrid.style.height = window.innerHeight + 'px';

                    canvasGrid.width = window.innerWidth * dpr;
                    canvasGrid.height = window.innerHeight * dpr;

                    contextGrid.imageSmoothingEnabled = true;
                    contextGrid.imageSmoothingQuality = 'high';

                    // =========================================

                    contextGrid.clearRect(0, 0, canvasGrid.width, canvasGrid.height);
                    contextGrid.save();
                    contextGrid.setTransform(
                        scaleRef.current * dpr,
                        0,
                        0,
                        scaleRef.current * dpr,
                        originXYRef.current.x * dpr,
                        originXYRef.current.y * dpr
                    );
                    if (gridStyle === 'lines') {
                        drawGridLines(contextGrid, scaleRef.current, null);
                    } else if (gridStyle === 'dots') {
                        drawGridPattern(contextGrid, scaleRef.current, null);
                        // drawGrid(contextGrid, scaleRef.current, null);
                    }
                    contextGrid.restore();
                }
            } else {
                if (lastGridProps.current) {
                    lastGridProps.current = null;
                }
                contextGrid.resetTransform();
                contextGrid.clearRect(0, 0, canvasGrid.width, canvasGrid.height);
            }
        }


        // 0. RED / GREEN VIEW
        if (isBtnRedGreenViewEnabled) {
            // 2. ELEMENTS CHANGES |==| ALL
            for (const key in elementsChangesRef.current) {
                const element = elementsChangesRef.current[key];

                if (!element) {
                    continue;
                }

                if (
                    !focusedElementsIds?.includes(element.id) &&
                    !relatedElementsIds?.includes(element.id) &&
                    !focusedAndMovingElementsIds?.includes(element.id)
                ) {
                    switch (element.type) {
                        case 'table': {
                            drawTableRef.current(element, exportImgCanvas);
                            break;
                        }
                        default:
                            break;
                    }
                }
            }

            // 5. ELEMENTS |==| FOCUSED ELEMENTS AND MOVING ELEMENT
            for (let i = 0; i < focusedAndMovingElementsIds.length; i++) {
                const id = focusedAndMovingElementsIds[i];
                const element =
                    elementsChangesRef.current &&
                    elementsChangesRef.current[id];

                if (!element) {
                    continue;
                }

                switch (element.type) {
                    case 'table': {
                        drawTableRef.current(element, exportImgCanvas);
                        break;
                    }
                    default:
                        break;
                }
            }

            context.restore();

            return;
        }

        // 1. AREAS |==| ALL
        for (const key in areasRef.current) {
            const area = areasRef.current[key];
            if (
                area.id !== hoveredAreaIdRef.current &&
                !focusedIdsRef.current.includes(area.id)
            ) {
                drawArea(area, exportImgCanvas);
            }
        }

        // 1.1. AREAS |==| HOVERED AREA
        if (
            hoveredAreaIdRef.current &&
            !focusedIdsRef.current.includes(hoveredAreaIdRef.current)
        ) {
            const area = areasRef.current[hoveredAreaIdRef.current];

            if (area) {
                drawArea(area, exportImgCanvas);
            }
        }

        // 1.2. AREAS |==| FOCUSED AREAS (from focusedIds)
        for (const sid of focusedIdsRef.current) {
            const area = areasRef.current[sid];
            if (area) {
                drawArea(area, exportImgCanvas);
            }
        }

        // 1. LINES |==| ALL
        for (const key in linesRef.current) {
            const line = linesRef.current[key];

            if (!line || !visibleLinesIdsRef.current.has(line.id)) {
                // if (!line) {
                continue;
            }

            if (
                !focusedIdsRef.current.includes(line.id) &&
                hoveredLineIdRef.current !== line.id &&
                !relatedLinesIds?.includes(line.id)
            ) {
                drawCreatedLine(line, exportImgCanvas);
            }
        }

        // 2. ELEMENTS |==| ALL
        for (const id of visibleElementsIdsRef.current) {
            const element = elementsRef.current[id];

            if (!element) {
                continue;
            }

            if (
                !focusedElementsIds?.includes(element.id) &&
                !relatedElementsIds?.includes(element.id) &&
                !focusedLineRelatedElementsIds?.includes(element.id) &&
                !hoveredLineRelatedElementsIds?.includes(element.id) &&
                !focusedAndMovingElementsIds?.includes(element.id) &&
                (!isBtnNewAreaEnabled ||
                    !newAreaPropsRef?.current?.selectedElements?.includes(
                        element.id
                    ))
            ) {
                switch (element.type) {
                    case 'table': {
                        drawTableRef.current(element, exportImgCanvas);
                        break;
                    }
                    case 'note': {
                        drawNote(element, exportImgCanvas);
                        break;
                    }
                    // case 'pen-segment': {
                    // 	drawPenSegment(element);
                    // 	break;
                    // }
                    default:
                        break;
                }
            }
        }

        // 3. LINES |==| RELATED LINES AND MOVING ELEMENT'S LINES
        for (let i = 0; i < relatedLinesIds.length; i++) {
            const id = relatedLinesIds[i];
            const line = linesRef.current[id];

            if (!line || !visibleLinesIdsRef.current.has(line.id)) {
                // if (!line) {
                continue;
            }

            drawCreatedLine(line, exportImgCanvas);
        }

        // if (
        //     hoveredLineIdRef.current &&
        //     hoveredLineIdRef.current !== selectedLineIdLegacyRef.current &&
        //     !isBtnExportImageEnabled &&
        //     !isBtnPenEnabled &&
        //     !isResizingArea
        // ) {
        //     const line = linesRef.current[hoveredLineIdRef.current];

        //     if (line) {
        //         drawCreatedLine(line, exportImgCanvas);
        //     }
        // }
        // 4. ELEMENTS |==| RELATED ELEMENTS
        for (let i = 0; i < relatedElementsIds.length; i++) {
            const id = relatedElementsIds[i];
            const element = elementsRef.current[id];

            if (!element) {
                continue;
            }

            switch (element.type) {
                case 'table': {
                    drawTableRef.current(element, exportImgCanvas);
                    break;
                }
                case 'note': {
                    drawNote(element, exportImgCanvas);
                    break;
                }
                // case 'pen-segment': {
                // 	drawPenSegment(element, exportImgCanvas);
                // 	break;
                // }
                default:
                    break;
            }
        }

        // 7. LINES |==| HOVERED LINE
        // if (hoveredLineIdRef.current) {
        // 	const line = linesRef.current[hoveredLineIdRef.current];

        // 	if (line) {
        // 		drawCreatedLine(line, exportImgCanvas);
        // 	}
        // }

        // 7. ELEMENTS |==| HOVERED ELEMENT
        if (hoveredElementIdRef.current &&
            !focusedAndMovingElementsIds.includes(hoveredElementIdRef.current) &&
            (!isBtnNewAreaEnabled ||
                !newAreaPropsRef?.current?.selectedElements?.includes(hoveredElementIdRef.current)
            ) &&
            !isResizingArea &&
            !isBtnExportImageEnabled &&
            !isBtnPenEnabled
        ) {
            const element = elementsRef.current[hoveredElementIdRef.current];

            switch (element?.type) {
                case 'table': {
                    drawTableRef.current(element, exportImgCanvas);
                    break;
                }
                case 'note': {
                    drawNote(element, exportImgCanvas);
                    break;
                }
                // case 'pen-segment': {
                // 	drawPenSegment(element, exportImgCanvas);
                // 	break;
                // }
                default:
                    break;
            }

            // 7. LINES |==| HOVERED LINE
        }

        if (
            hoveredLineIdRef.current &&
            !focusedIdsRef.current.includes(hoveredLineIdRef.current) &&
            !isBtnExportImageEnabled &&
            !isBtnPenEnabled &&
            !isResizingArea
        ) {
            const line = linesRef.current[hoveredLineIdRef.current];

            if (line) {
                drawCreatedLine(line, exportImgCanvas);
            }

            // 4. ELEMENTS |==| HOVERED LINE => RELATED ELEMENTS
            for (let i = 0; i < hoveredLineRelatedElementsIds.length; i++) {
                const id = hoveredLineRelatedElementsIds[i];
                const element = elementsRef.current[id];

                if (!element) {
                    continue;
                }

                switch (element.type) {
                    case 'table': {
                        drawTableRef.current(element, exportImgCanvas);
                        break;
                    }
                    case 'note': {
                        drawNote(element, exportImgCanvas);
                        break;
                    }
                    // case 'pen-segment': {
                    // 	drawPenSegment(element, exportImgCanvas);
                    // 	break;
                    // }
                    default:
                        break;
                }
            }
        }

        // 5. ELEMENTS |==| FOCUSED ELEMENTS AND MOVING ELEMENT
        for (let i = 0; i < focusedAndMovingElementsIds.length; i++) {
            const id = focusedAndMovingElementsIds[i];
            const element = elementsRef.current[id];

            if (
                !element ||
                (isBtnNewAreaEnabled &&
                    newAreaPropsRef?.current?.selectedElements?.includes(
                        element.id
                    ))
            ) {
                continue;
            }

            switch (element.type) {
                case 'table': {
                    drawTableRef.current(element, exportImgCanvas);
                    break;
                }
                case 'note': {
                    drawNote(element, exportImgCanvas);
                    break;
                }
                // case 'pen-segment': {
                // 	drawPenSegment(element, exportImgCanvas);
                // 	break;
                // }
                default:
                    break;
            }
        }

        if (
            isBtnNewAreaEnabled &&
            newAreaPropsRef?.current?.selectedElements?.length
        ) {
            // 5. ELEMENTS |==| FOCUSED ELEMENTS AND MOVING ELEMENT
            for (
                let i = 0;
                i < newAreaPropsRef?.current?.selectedElements.length;
                i++
            ) {
                const id = newAreaPropsRef?.current?.selectedElements[i];
                const element = elementsRef.current[id];

                if (!element) {
                    continue;
                }

                switch (element.type) {
                    case 'table': {
                        drawTableRef.current(element, exportImgCanvas);
                        break;
                    }
                    case 'note': {
                        drawNote(element, exportImgCanvas);
                        break;
                    }
                    // case 'pen-segment': {
                    // 	drawPenSegment(element, exportImgCanvas);
                    // 	break;
                    // }
                    default:
                        break;
                }
            }
        }

        // 6. LINES |==| FOCUSED LINE (from focusedIds)
        const focusedLineId = focusedIdsRef.current.find(
            (sid) => linesRef.current[sid]
        ) || null;
        if (focusedLineId) {
            const line = linesRef.current[focusedLineId];

            if (line) {
                drawCreatedLine(line, exportImgCanvas);
            }

            // 4. ELEMENTS |==| FOCUSED LINE => RELATED ELEMENTS
            for (let i = 0; i < focusedLineRelatedElementsIds.length; i++) {
                const id = focusedLineRelatedElementsIds[i];
                const element = elementsRef.current[id];

                if (!element) {
                    continue;
                }

                switch (element.type) {
                    case 'table': {
                        drawTableRef.current(element, exportImgCanvas);
                        break;
                    }
                    case 'note': {
                        drawNote(element, exportImgCanvas);
                        break;
                    }
                    // case 'pen-segment': {
                    // 	drawPenSegment(element, exportImgCanvas);
                    // 	break;
                    // }
                    default:
                        break;
                }
            }
        }

        // 7. PEN SEGMENTS
        for (const id of visibleElementsIdsRef.current) {
            const element = elementsRef.current[id];

            if (!element) {
                continue;
            }

            // if (
            // 	!focusedElementsIds?.includes(element.id) &&
            // 	!relatedElementsIds?.includes(element.id) &&
            // 	!focusedAndMovingElementsIds?.includes(element.id) &&
            // 	(!isBtnNewAreaEnabled || !newAreaPropsRef?.current?.selectedElements?.includes(element.id))
            // ) {
            switch (element.type) {
                // case 'table': {
                // 	drawTableRef.current(element, exportImgCanvas);
                // 	break;
                // }
                // case 'note': {
                // 	drawNote(element, exportImgCanvas);
                // 	break;
                // }
                case 'pen-segment': {
                    drawPenSegment(element, exportImgCanvas);
                    break;
                }
                default:
                    break;
            }
            // }
        }

        // ===================== COLLAB AWARENESS | GEOMETRY (populate) + HALOS =====================
        // Captured AFTER all local element/area/line/pen-segment draw passes (the
        // same x/y/width/height the existing code passes to ctx.strokeRect/fillRect),
        // BEFORE in-progress overlays, selection box, tooltips, and local cursors.
        for (const id in elementsRef.current) {
            const el = elementsRef.current[id];
            if (!el) continue;
            elementsGeomRef.current[el.id] = {
                x: el.x,
                y: el.y,
                width: el.width,
                height: el.height,
            };
            if (el.type === 'table' && el.columns) {
                const colKeys = orderedColumnKeys(el.columns, el.columnsOrder);
                const perCol = {};
                for (let i = 0; i < colKeys.length; i++) {
                    const colId = colKeys[i];
                    const columnY =
                        el.y +
                        TABLE_HEADER_HEIGHT +
                        i * TABLE_ROW_HEIGHT +
                        HEADER_BODY_MARGIN;
                    perCol[colId] = {
                        x: el.x,
                        y: columnY,
                        width: el.width,
                        height: TABLE_ROW_HEIGHT,
                    };
                }
                columnsGeomRef.current[el.id] = perCol;
            }
        }
        for (const id in areasRef.current) {
            const ar = areasRef.current[id];
            if (!ar) continue;
            areasGeomRef.current[ar.id] = {
                x: ar.x,
                y: ar.y,
                width: ar.width,
                height: ar.height,
            };
        }
        for (const id in linesRef.current) {
            const ln = linesRef.current[id];
            if (!ln) continue;
            // Skip lines whose path hasn't been built yet — the normal draw pass
            // populates `path` and the next frame's geometry capture picks it up.
            // Avoids paying _buildLinePath per frame for transient lines.
            const polyline = ln.path?.[1];
            if (polyline && polyline.length > 0) {
                linesGeomRef.current[ln.id] = polyline;
            }
        }

        const aw = getDiagramAwareness();
        if (aw) {
            const members = Array.from(aw.getStates().entries())
                .filter(([clientID]) => clientID !== aw.clientID)
                .map(([, state]) => state);
            drawRemoteHalos(context, members, {
                elements: elementsGeomRef.current,
                areas: areasGeomRef.current,
                lines: linesGeomRef.current,
                columns: columnsGeomRef.current,
            });
        }

        // 8. NEW TEMPORARY AREA
        if (isBtnNewAreaEnabled && newAreaPropsRef.current) {
            const area = {
                id: 'temporary_area_id',
                x: newAreaPropsRef.current.startX,
                y: newAreaPropsRef.current.startY,
                width:
                    newAreaPropsRef.current.endX -
                    newAreaPropsRef.current.startX,
                height:
                    newAreaPropsRef.current.endY -
                    newAreaPropsRef.current.startY,
                // border: 'dashes',
                border: 'solid',
                color: 'blue',
            };

            drawArea(area, exportImgCanvas);
        }

        // 8. NEW TEMPORARY LINE
        if (lineFromRef.current?.elementId && lineToRef.current) {
            drawNewLine();
        }

        const isFocusedAreaMoving =
            clickedAreaIdRef.current &&
            focusedIdsRef.current.includes(clickedAreaIdRef.current);
        // 9. MOUSE SELECTION BOX
        if (
            !isCursorHand &&
            !isBtnNewLineEnabled &&
            !isBtnNewAreaEnabled &&
            isSelectionClickedRef.current &&
            !isFocusedAreaMoving
        ) {
            drawSelectionBoxRef.current();
        }

        // 10. EXPORT IMAGE BOX
        if (isBtnExportImageEnabled && !isExportImageClickedRef.current) {
            drawExportImageBox();
        }

        // 10. PEN
        if (
            newPenSegmentRef.current.isPressed &&
            newPenSegmentRef.current.points.length
        ) {
            drawNewPenSegment();
        }
        // 10. PEN CURSOR
        if (isBtnPenEnabled && newPenCursorPointRef.current) {
            drawNewPenCursor();
        }

        if (alignmentLinesRef.current && viewMode === 'full') {
            const ctx = contextRef.current;

            // Малюємо лінії вирівнювання
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';

            ctx.strokeStyle = 'rgba(130, 167, 255, 1)';
            ctx.lineWidth = 0.9 / scaleRef.current;

            const dashLine = 5.5 / scaleRef.current;
            const dashSpace = 5 / scaleRef.current;
            ctx.setLineDash([dashLine, dashSpace]); // Маленький штрихпунктир

            alignmentLinesRef.current.forEach((line) => {
                ctx.beginPath();
                ctx.moveTo(line.x1, line.y1);
                ctx.lineTo(line.x2, line.y2);
                ctx.stroke();
            });

            ctx.setLineDash([]); // Скидаємо налаштування лінії
        }

        // if (isBtnSearchEnabled) {
        //     const ctx = contextRef.current;
        //     const startX = (0 - originXYRef.current.x) / scaleRef.current;
        //     const startY = (0 - originXYRef.current.y) / scaleRef.current;

        //     const width = canvas.width / scaleRef.current;
        //     const height = canvas.height / scaleRef.current;

        //     ctx.fillStyle = 'rgba(255, 255, 255, 0.03)'; // Біле затемнення з прозорістю
        //     ctx.fillRect(startX, startY, width, height);
        // }




        if (hoveredElementIdHeaderRef.current && !isMovingElementId && !isBtnExportImageEnabled) {
            const elementId = hoveredElementIdHeaderRef.current;
            const element = elementsRef.current[elementId];

            if (element && (element.description || Object.values(element.columns ?? {}).some(c => !!c.indexType))) {
                drawTableTooltip(element);
            }
        }

        if (hoveredElementIdRef.current && hoveredColumnIdRef.current && !isMovingElementId && !isBtnExportImageEnabled) {
            const element = elementsRef.current[hoveredElementIdRef.current];

            if (element && element.type === 'table') {
                const column = element.columns[hoveredColumnIdRef.current];

                if (column && (
                    column.description || column.defaultValue || column.enumValues || column.generatedValue || column.checkValue
                )) {
                    drawColumnTooltip(element, column, hoveredColumnIndexRef.current);
                }
            }

        }



        // CanvasScrollbars: sole publish call site, on the normal (non
        // export-image) draw path only — every scheduleDraw()-coalesced
        // animation frame republishes the live viewport (cheap O(1), so
        // pan/zoom track the scrollbar thumbs every frame) alongside the
        // last content bbox the elements-observer computed (see
        // computeContentBoundsFromElements call sites above). The store's
        // own equality check no-ops the vast majority of these calls.
        publishScrollGeometry();

        context.restore();
    }, [
        focusedIds,
        isCursorHand,
        isBtnRedGreenViewEnabled,
        isBtnSearchEnabled,
        gridStyle,
        // These selector flags are read directly in the draw closure (not via refs),
        // so they must be deps — otherwise the memoized draw keeps a stale value and
        // their overlays (new-area preview, export-image box, pen cursor) never render
        // while the mode is active.
        isBtnNewAreaEnabled,
        isBtnExportImageEnabled,
        isBtnPenEnabled,
        // viewMode switches the line renderer (drawCreatedLine ↔ drawLineForViewKeys),
        // which is called directly in this closure. Without it as a dep the memoized
        // draw keeps the old renderer and lines stay at the previous view's column
        // positions after toggling keys/full.
        viewMode,
        // Read directly in this closure to decide whether to draw the minimap. Without
        // it the memoized draw keeps the page-load value, so enabling the minimap later
        // never calls drawMinimapStatic (the minimap stays blank).
        isBtnMinimapEnabled,
    ]); // drawTable/drawSelectionBox/focusedInsideElementState accessed via refs — intentionally omitted

    // Keep refs pointing at the latest versions so stable callbacks always
    // invoke current closures without being in dep arrays.
    useEffect(() => {
        drawRef.current = draw;
        drawTableRef.current = drawTable;
        drawSelectionBoxRef.current = drawSelectionBox;
        handlerStateRef.current = {
            draw,
            isScaling,
            isBoardMoving,
            isBoardMovingStarted,
            isMovingElementId,
            isElementResizing,
            isCursorHand,
            isBtnNewTableClickEnabled,
            isBtnNewTableGrabEnabled,
            isBtnNewNoteClickEnabled,
            isBtnNewNoteGrabEnabled,
            isBtnNewLineEnabled,
            isBtnNewAreaEnabled,
            isBtnExportImageEnabled,
            isBtnPenEnabled,
            isBtnRedGreenViewEnabled,
        };
    });

    // When the view mode (keys/full) changes, hidden columns shift the others, so any
    // cached line paths were built for the other mode and are now stale. Drop them and
    // redraw so every line reconnects at the current view's column positions.
    useEffect(() => {
        for (const id in linesRef.current) {
            if (linesRef.current[id]) linesRef.current[id].path = null;
        }
        // CanvasScrollbars: clipped tables' EFFECTIVE height (what
        // computeContentBoundsFromElements measures) only applies in
        // 'full' view — toggling keys/full changes every clipped table's
        // contribution to the content bbox with no element data changing
        // at all, so the cached bbox needs a fresh walk here too, not just
        // on the next unrelated element mutation.
        computeContentBoundsFromElements();
        scheduleDraw();
    }, [viewMode]);

    // Toggling the minimap on after load must trigger a redraw so drawMinimapStatic runs
    // with the now-current isBtnMinimapEnabled (the memoized draw was just rebuilt).
    useEffect(() => {
        scheduleDraw();
    }, [isBtnMinimapEnabled]);

    // Direct Yjs subscription — bypasses React re-renders for element/line/area
    // data changes. observeDeep callbacks update the working refs and schedule a
    // draw; only the focused element getting changed triggers a React re-render
    // (via setFocusedElementVersion) so the column-editor JSX stays fresh.
    useEffect(() => {
        const doc = getDiagramDoc();
        if (!doc) return undefined;

        const elMap = yElements(doc);
        const lnMap = yLines(doc);

        // Full clone on diagram load (initial state from Yjs).
        const initEl = yToJS(elMap);
        const initLn = yToJS(lnMap);
        elementsRef.current = structuredClone(initEl);
        for (const penId in elementsRef.current) ensurePenSegmentPath(elementsRef.current[penId]);
        areasRef.current = Object.fromEntries(
            Object.entries(elementsRef.current).filter(([, v]) => v.type === 'area')
        );
        linesRef.current = structuredClone(initLn);
        prevElementsReduxRef.current = initEl;
        prevLinesReduxRef.current = initLn;
        deriveIsRelated();
        calculateVisibleElementsAndLines();
        computeContentBoundsFromElements();
        scheduleDraw();

        // queueMicrotask flag so multiple maps changed in one Yjs transaction
        // coalesce into a single recalc + draw call.
        // Flags accumulate until flush; both reset to false in flushRecalc.
        let recalcPending = false;
        let recalcNeedsRelated = false;
        let recalcNeedsGeom = false;

        const flushRecalc = () => {
            recalcPending = false;
            if (recalcNeedsRelated) { deriveIsRelated(); recalcNeedsRelated = false; }
            if (recalcNeedsGeom) { calculateVisibleElementsAndLines(); recalcNeedsGeom = false; }
            scheduleDraw();
        };

        // related=true  → run deriveIsRelated  (line topology changed)
        // geom=true     → run calculateVisible (element geometry/count changed)
        const scheduleRecalc = (related = false, geom = false) => {
            recalcNeedsRelated ||= related;
            recalcNeedsGeom ||= geom;
            if (!recalcPending) {
                recalcPending = true;
                queueMicrotask(flushRecalc);
            }
        };

        // Geometry fields whose change requires a visibility recalc.
        const GEOM_FIELDS = new Set(['x', 'y', 'width', 'height', 'type']);

        // CanvasScrollbars: a SEPARATE gate from GEOM_FIELDS above —
        // `displayHeight` changes a clipped table's EFFECTIVE height
        // (getTableEffectiveHeight), which changes the content bbox, but
        // must NOT join GEOM_FIELDS itself: that set also gates the
        // viewport-culling recalc below, which — per the comment on the
        // clip-changed block further down — deliberately treats
        // `displayHeight` as a redraw-only change, not a visibility recalc.
        // Widening GEOM_FIELDS would silently change that culling
        // semantics; BBOX_FIELDS is its own independent condition instead.
        const BBOX_FIELDS = new Set([...GEOM_FIELDS, 'displayHeight']);

        const handleElChange = (events) => {
            for (const e of events) invalidateAlong(elMap, e.path);
            const curr = yToJS(elMap);
            const prev = prevElementsReduxRef.current || {};
            const working = elementsRef.current;
            const changedIds = [];
            for (const id in curr) {
                if (curr[id] !== prev[id]) {
                    working[id] = ensurePenSegmentPath(structuredClone(curr[id]));
                    changedIds.push(id);
                }
            }
            for (const id in working) {
                if (!(id in curr)) delete working[id];
            }
            prevElementsReduxRef.current = curr;
            areasRef.current = Object.fromEntries(
                Object.entries(working).filter(([, v]) => v.type === 'area')
            );
            const fState = focusedInsideElementStateRef.current;
            if (fState?.elementId && curr[fState.elementId] !== prev[fState.elementId]) {
                setFocusedElementVersion(v => v + 1);
            }
            // Only recompute visibility when element geometry or element count changed.
            // Content-only edits (column names, colors, etc.) just need a redraw.
            const geomChanged = events.some(e =>
                e.path.length === 0 ||
                (e.path.length === 1 && e.keysChanged &&
                    [...e.keysChanged].some(k => GEOM_FIELDS.has(k)))
            );
            // CanvasScrollbars: independent of geomChanged above — a
            // displayHeight-only change (table clip/expand) takes the
            // `else` branch below (no visibility recalc needed) but STILL
            // changes the content bbox, so this is evaluated and acted on
            // unconditionally rather than nested inside `if (geomChanged)`.
            const bboxChanged = events.some(e =>
                e.path.length === 0 ||
                (e.path.length === 1 && e.keysChanged &&
                    [...e.keysChanged].some(k => BBOX_FIELDS.has(k)))
            );
            if (bboxChanged) computeContentBoundsFromElements();
            if (geomChanged) {
                // Refresh cached paths of lines touching the moved elements. The cache
                // is set during a drag; changes from elsewhere (undo/redo, remote peers)
                // don't go through the drag handler, so without this a line keeps its
                // old path — e.g. on undo the tables move back but the connecting line
                // stays at the previous position.
                if (changedIds.length) updateLinesByElementsIds(changedIds);
                scheduleRecalc(false, true);
            } else {
                scheduleDraw();
            }

            // A clipped table's `displayHeight` (or its content-derived `height`
            // while still clipped, e.g. a column add/remove) changes the
            // EFFECTIVE height its connected lines route from/to
            // (getTableEffectiveHeight) — same idea as an x/y/width move above,
            // but `displayHeight` is deliberately NOT in GEOM_FIELDS (it doesn't
            // need a visibility recalc — calculateVisibleElementsAndLines is
            // about viewport culling, not clip state), so a displayHeight-only
            // change takes the `else` branch above and would otherwise never
            // re-clamp lines. Local writes (resize-drag commit, chevron
            // click-to-expand, the auto-reveal saga) all go through
            // withLocalTransaction/setTableDisplayHeight/resizeElements and
            // already call updateLinesByElementsIds at their own call sites —
            // skip those here so this doesn't double-recompute on every local
            // drag frame. What's left after that skip — undo/redo (Y.UndoManager
            // transacts under its own origin, not ORIGIN_LOCAL) and a remote
            // peer's change (applied under the socket's origin — see
            // socket-sync.js) — never goes through a local call site, so this
            // observer is the only place that can catch them. Cheap origin
            // check: every event in one observeDeep callback belongs to the
            // same transaction, so events[0] speaks for the whole batch.
            if (events[0]?.transaction?.origin !== ORIGIN_LOCAL) {
                const clipChangedIds = [];
                for (const e of events) {
                    if (e.path.length === 1 && e.keysChanged) {
                        const id = e.path[0];
                        if (
                            curr[id]?.type === 'table' &&
                            (e.keysChanged.has('displayHeight') || e.keysChanged.has('height'))
                        ) {
                            clipChangedIds.push(id);
                        }
                    }
                }
                if (clipChangedIds.length) updateLinesByElementsIds(clipChangedIds);
            }
        };

        const handleLnChange = (events) => {
            for (const e of events) invalidateAlong(lnMap, e.path);
            const curr = yToJS(lnMap);
            const prev = prevLinesReduxRef.current || {};
            const working = linesRef.current;
            for (const id in curr) {
                if (curr[id] !== prev[id]) working[id] = structuredClone(curr[id]);
            }
            for (const id in working) {
                if (!(id in curr)) delete working[id];
            }
            prevLinesReduxRef.current = curr;
            // Line changes always need both: isRelated flags and line path visibility.
            scheduleRecalc(true, true);
        };

        elMap.observeDeep(handleElChange);
        lnMap.observeDeep(handleLnChange);

        return () => {
            elMap.unobserveDeep(handleElChange);
            lnMap.unobserveDeep(handleLnChange);
        };
    }, []);

    // Redraw the base canvas when a remote peer's SELECTION changes (halos
    // depend on selection). Cursor-only changes are rendered via the
    // RemoteCursors DOM overlay and don't need a canvas redraw — skipping
    // them here keeps the receiver's main thread free so the cursor's CSS
    // tween renders smoothly.
    useEffect(() => {
        if (!diagramAwareness) return undefined;
        const sigsByClient = new Map(); // clientId -> JSON.stringify(selection)
        const handler = ({ added, updated, removed }) => {
            const localId = diagramAwareness.clientID;
            const states = diagramAwareness.getStates();
            let selectionChanged = false;
            for (const id of added) {
                if (id === localId) continue;
                const sig = JSON.stringify(states.get(id)?.selection || null);
                sigsByClient.set(id, sig);
                selectionChanged = true; // new peer — refresh halos
            }
            for (const id of updated) {
                if (id === localId) continue;
                const sig = JSON.stringify(states.get(id)?.selection || null);
                if (sig !== sigsByClient.get(id)) {
                    sigsByClient.set(id, sig);
                    selectionChanged = true;
                }
            }
            for (const id of removed) {
                if (id === localId) continue;
                if (sigsByClient.delete(id)) selectionChanged = true;
            }
            if (selectionChanged) scheduleDraw();
        };
        diagramAwareness.on('change', handler);
        return () => diagramAwareness.off('change', handler);
    }, [diagramAwareness, scheduleDraw]);

    useEffect(() => {
        const handleMinimapWheel = (event) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            event.preventDefault();

            calculateMinimapZoomNumbers();

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // // previous zoom
            // const wheel = event.deltaY < 0 ? 1 : -1;
            // const ZOOM_FACTOR = 0.15;
            // const zoom = Math.exp(wheel * ZOOM_FACTOR);
            // const newScale = scale * zoom;

            let delta = Math.max(-600, Math.min(600, event.deltaY));

            // const zoomIntensity = 0.001;
            let zoomIntensity = 0.001;

            if (scaleRef.current <= 1.1 && scaleRef.current > 0.65) {
                zoomIntensity = 0.001;
                delta = Math.max(-500, Math.min(500, event.deltaY));
            } else if (scaleRef.current <= 0.65 && scaleRef.current > 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-600, Math.min(600, event.deltaY));
                    zoomIntensity = 0.0012;

                    // zoom out
                } else {
                    delta = Math.max(-450, Math.min(450, event.deltaY));
                    zoomIntensity = 0.0012;
                }
            } else if (scaleRef.current <= 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0025;

                    // zoom out
                } else {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0015;
                }
            }

            // Same trackpad boost as the canvas wheel handler (see its
            // comment) — pinch deltas are small, keep both zoom surfaces
            // consistent.
            if (inputModeRef.current === 'trackpad') {
                zoomIntensity *= 1.5;
            }

            const newScale = scaleRef.current * (1 - delta * zoomIntensity);
            // const newScale = scaleRef.current + event.deltaY * -0.001; // COEF should be dinamic changed on edge values(on min and max values) 			// const COEF = -0.002 or -0.0025 or -0.001;

            const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

            const zoomFactor = clampedScale / scaleRef.current;

            // const newOriginX = (originXYRef.current.x - mouseX) * zoomFactor + mouseX;
            // const newOriginY = (originXYRef.current.y - mouseY) * zoomFactor + mouseY;

            let newOriginX = 0;
            let newOriginY = 0;

            const zoom = minimapZoomRef.current?.zoom || 1;
            // minimapZoomRef.current = {
            // 	zoom: newZoom,
            // 	originX: newOriginX,
            // 	originY: newOriginY,
            // };

            const mainCanvas = ref.current;
            const minimap = minimapRef.current.dynamicCanvas;

            const minimapWidth = minimap.width;
            const minimapHeight = minimap.height;
            let viewportWidth = (mainCanvas.width * zoom) / scaleRef.current;
            let viewportHeight = (mainCanvas.height * zoom) / scaleRef.current;

            // Отримуємо розташування мінімапи на екрані
            const minimapRect = minimap.getBoundingClientRect();

            // Вираховуємо координати кліку відносно мінімапи
            let relativeX = mouseX - minimapRect.left;
            let relativeY = mouseY - minimapRect.top;

            relativeX = relativeX - viewportWidth / 2;
            relativeY = relativeY - viewportHeight / 2;

            relativeX = Math.min(
                Math.max(1, relativeX),
                minimapWidth - viewportWidth - 1
            );
            relativeY = Math.min(
                Math.max(2, relativeY),
                minimapHeight - viewportHeight - 1
            );

            if (viewportWidth >= minimapWidth) {
                const diff = (viewportWidth - minimapWidth) / 2;
                relativeX = 1 - diff;
            }

            if (viewportHeight >= minimapHeight) {
                const diff = (viewportHeight - minimapHeight) / 2;
                relativeY = 2 - diff;
            }

            function minimapToCanvasCoordinates(minimapX, minimapY) {
                const realOriginX =
                    minimapZoomRef.current.originX /
                    minimapZoomRef.current.zoom;
                const realX = minimapX / minimapZoomRef.current.zoom;

                const realOriginY =
                    minimapZoomRef.current.originY /
                    minimapZoomRef.current.zoom;
                const realY = minimapY / minimapZoomRef.current.zoom;

                return {
                    x: (realOriginX - realX) * scaleRef.current,
                    y: (realOriginY - realY) * scaleRef.current,
                };
            }

            const canvasPoint = minimapToCanvasCoordinates(
                relativeX,
                relativeY
            );

            newOriginX = canvasPoint.x;
            newOriginY = canvasPoint.y;

            // ---------------------------------------------------------------
            // ---------------------------------------------------------------
            let newRelativeX = mouseX - minimapRect.left;
            let newRelativeY = mouseY - minimapRect.top;

            const screenZoomFactorWidth = window.innerWidth / minimap.width;
            const screenZoomFactorHeight = window.innerHeight / minimap.height;

            const newMouseX = newRelativeX * screenZoomFactorWidth;
            const newMouseY = newRelativeY * screenZoomFactorHeight;

            newOriginX =
                (originXYRef.current.x - newMouseX) * zoomFactor + newMouseX;
            newOriginY =
                (originXYRef.current.y - newMouseY) * zoomFactor + newMouseY;

            // ---------------------------------------------------------------
            // ---------------------------------------------------------------

            scaleRef.current = clampedScale;

            originXYRef.current = { x: newOriginX, y: newOriginY };
            calculateAndSetScaleNumbers(clampedScale);

            syncScaleToReduxRef.current(clampedScale, newOriginX, newOriginY);

            if (!isScaling) {
                setIsScaling(true);
            }
            clearTimeout(scalingTimerRef.current);
            scalingTimerRef.current = setTimeout(() => {
                setIsScaling(false);
            }, 200);

            calculateVisibleElementsAndLines();

            scheduleDraw();
        };

        const handleMinimapMouseDown = (event) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            if (!minimapRef.current || !minimapRef.current.dynamicCanvas) {
                return;
            }
            event.preventDefault();

            // const minimap = minimapRef.current.dynamicCanvas;

            // // Отримуємо координати кліку миші відносно документу
            // const mouseX = event.clientX;
            // const mouseY = event.clientY;

            // Отримуємо розташування мінімапи на екрані
            // const minimapRect = minimap.getBoundingClientRect();

            // // Вираховуємо координати кліку відносно мінімапи
            // const relativeX = mouseX - minimapRect.left;
            // const relativeY = mouseY - minimapRect.top;

            // minimapClickedXYRef.current = { x: relativeX, y: relativeY };

            // draw();

            const zoom = minimapZoomRef.current?.zoom || 1;
            // minimapZoomRef.current = {
            // 	zoom: newZoom,
            // 	originX: newOriginX,
            // 	originY: newOriginY,
            // };

            const mainCanvas = ref.current;
            const minimap = minimapRef.current.dynamicCanvas;

            const minimapWidth = minimap.width;
            const minimapHeight = minimap.height;
            let viewportWidth = (mainCanvas.width * zoom) / scaleRef.current;
            let viewportHeight = (mainCanvas.height * zoom) / scaleRef.current;

            event.preventDefault();

            // Отримуємо координати кліку миші відносно документу
            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // Отримуємо розташування мінімапи на екрані
            const minimapRect = minimap.getBoundingClientRect();

            // Вираховуємо координати кліку відносно мінімапи
            let relativeX = mouseX - minimapRect.left;
            let relativeY = mouseY - minimapRect.top;

            relativeX = relativeX - viewportWidth / 2;
            relativeY = relativeY - viewportHeight / 2;

            relativeX = Math.min(
                Math.max(1, relativeX),
                minimapWidth - viewportWidth - 1
            );
            relativeY = Math.min(
                Math.max(2, relativeY),
                minimapHeight - viewportHeight - 1
            );

            if (viewportWidth >= minimapWidth) {
                const diff = (viewportWidth - minimapWidth) / 2;
                relativeX = 1 - diff;
            }

            if (viewportHeight >= minimapHeight) {
                const diff = (viewportHeight - minimapHeight) / 2;
                relativeY = 2 - diff;
            }

            minimapClickedXYRef.current = { x: relativeX, y: relativeY };

            function minimapToCanvasCoordinates(minimapX, minimapY) {
                const realOriginX =
                    minimapZoomRef.current.originX /
                    minimapZoomRef.current.zoom;
                const realX = minimapX / minimapZoomRef.current.zoom;

                const realOriginY =
                    minimapZoomRef.current.originY /
                    minimapZoomRef.current.zoom;
                const realY = minimapY / minimapZoomRef.current.zoom;

                return {
                    x: (realOriginX - realX) * scaleRef.current,
                    y: (realOriginY - realY) * scaleRef.current,
                };
            }

            const canvasPoint = minimapToCanvasCoordinates(
                relativeX,
                relativeY
            );

            const newOriginX = canvasPoint.x; //originXYRef.current.x + dx;
            const newOriginY = canvasPoint.y; //originXYRef.current.y + dy;

            // update board originXY positions
            originXYRef.current = { x: newOriginX, y: newOriginY };
            syncOriginXYToReduxRef.current(newOriginX, newOriginY);

            calculateVisibleElementsAndLines();

            // set is board moving
            if (!isBoardMoving) {
                setIsBoardMoving(true);
                setIsBoardMovingStarted(true);
            }

            // set timeout on board moving
            clearTimeout(boardMovingTimerRef.current);
            boardMovingTimerRef.current = setTimeout(() => {
                setIsBoardMoving(false);
            }, 200);

            scheduleDraw();
        };

        const handleMinimapMouseMove = (event) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            if (
                !ref.current ||
                !minimapRef.current ||
                !minimapRef.current.dynamicCanvas ||
                !minimapClickedXYRef.current ||
                !minimapZoomRef.current
            ) {
                return;
            }

            const zoom = minimapZoomRef.current?.zoom || 1;
            // minimapZoomRef.current = {
            // 	zoom: newZoom,
            // 	originX: newOriginX,
            // 	originY: newOriginY,
            // };

            const mainCanvas = ref.current;
            const minimap = minimapRef.current.dynamicCanvas;

            const minimapWidth = minimap.width;
            const minimapHeight = minimap.height;
            let viewportWidth = (mainCanvas.width * zoom) / scaleRef.current;
            let viewportHeight = (mainCanvas.height * zoom) / scaleRef.current;

            event.preventDefault();

            // Отримуємо координати кліку миші відносно документу
            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // Отримуємо розташування мінімапи на екрані
            const minimapRect = minimap.getBoundingClientRect();

            // Вираховуємо координати кліку відносно мінімапи
            let relativeX = mouseX - minimapRect.left;
            let relativeY = mouseY - minimapRect.top;

            relativeX = relativeX - viewportWidth / 2;
            relativeY = relativeY - viewportHeight / 2;

            relativeX = Math.min(
                Math.max(1, relativeX),
                minimapWidth - viewportWidth - 1
            );
            relativeY = Math.min(
                Math.max(2, relativeY),
                minimapHeight - viewportHeight - 1
            );

            if (viewportWidth >= minimapWidth) {
                const diff = (viewportWidth - minimapWidth) / 2;
                relativeX = 1 - diff;
            }

            if (viewportHeight >= minimapHeight) {
                const diff = (viewportHeight - minimapHeight) / 2;
                relativeY = 2 - diff;
            }

            function minimapToCanvasCoordinates(minimapX, minimapY) {
                const realOriginX =
                    minimapZoomRef.current.originX /
                    minimapZoomRef.current.zoom;
                const realX = minimapX / minimapZoomRef.current.zoom;

                const realOriginY =
                    minimapZoomRef.current.originY /
                    minimapZoomRef.current.zoom;
                const realY = minimapY / minimapZoomRef.current.zoom;

                return {
                    x: (realOriginX - realX) * scaleRef.current,
                    y: (realOriginY - realY) * scaleRef.current,
                };
            }

            const canvasPoint = minimapToCanvasCoordinates(
                relativeX,
                relativeY
            );

            const newOriginX = canvasPoint.x; //originXYRef.current.x + dx;
            const newOriginY = canvasPoint.y; //originXYRef.current.y + dy;

            // update board originXY positions
            originXYRef.current = { x: newOriginX, y: newOriginY };
            syncOriginXYToReduxRef.current(newOriginX, newOriginY);

            calculateVisibleElementsAndLines();

            // set is board moving
            if (!isBoardMoving) {
                setIsBoardMoving(true);
                setIsBoardMovingStarted(true);
            }

            // set timeout on board moving
            clearTimeout(boardMovingTimerRef.current);
            boardMovingTimerRef.current = setTimeout(() => {
                setIsBoardMoving(false);
            }, 200);

            scheduleDraw();
        };

        const handleDocumnetMouseUp = () => {
            if (minimapClickedXYRef.current) {
                minimapClickedXYRef.current = null;
                scheduleDraw();
            }
        };

        if (minimapRef.current && minimapRef.current.dynamicCanvas) {
            minimapRef.current.dynamicCanvas.addEventListener(
                'wheel',
                handleMinimapWheel
            );
            minimapRef.current.dynamicCanvas.addEventListener(
                'mousedown',
                handleMinimapMouseDown
            );
            document.addEventListener('mousemove', handleMinimapMouseMove);
            // minimapRef.current.dynamicCanvas.addEventListener('pointerdown', handleMinimapMouseDown);
            // document.addEventListener('pointermove', handleMinimapMouseMove);

            document.addEventListener('mouseup', handleDocumnetMouseUp);
        }

        return () => {
            if (minimapRef.current && minimapRef.current.dynamicCanvas) {
                minimapRef.current.dynamicCanvas.removeEventListener(
                    'wheel',
                    handleMinimapWheel
                );
                minimapRef.current.dynamicCanvas.removeEventListener(
                    'mousedown',
                    handleMinimapMouseDown
                );
                document.removeEventListener(
                    'mousemove',
                    handleMinimapMouseMove
                );
                document.removeEventListener('mouseup', handleDocumnetMouseUp);
            }
        };
    }, [minimapRef]);

    function drawMinimapViewPort(ctx, x, y, newZoom) {
        const mainCanvas = ref.current;

        // const scaleValue = canvasRef.current.width / scaleRef.current / staticCanvasRef.current.width;
        // const scaleValue = 20;
        // const scaleValue = 60;

        ctx.strokeStyle = 'white';

        const width = (mainCanvas.width * newZoom) / scaleRef.current;
        const height = (mainCanvas.height * newZoom) / scaleRef.current;

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 1);
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }

    function drawMinimapTable(ctx, element) {
        const { x, y, width, height } = element;

        ctx.strokeStyle = '#a2adb6';
        ctx.fillStyle = '#a2adb6';
        ctx.fillStyle = 'rgba(74, 79, 86, 1)';

        ctx.beginPath();
        ctx.roundRect(x, y, width * 0.8, height, 1);
        ctx.stroke();
        ctx.fill();
    }

    function drawMinimapArea(ctx, area) {
        const {
            x,
            y,
            width,
            height,
            color,
        } = area;

        // ctx.strokeStyle = area.color;
        ctx.fillStyle = area.color;






        // COLOR

        // default (grey)
        ctx.fillStyle = 'rgba(168, 170, 174, 0.1)';
        ctx.strokeStyle = '#a8aaae';

        ctx.fillStyle = 'rgba(175, 197, 240, 0.12)';

        if (color) {
            switch (color) {
                case 'blue': {
                    ctx.fillStyle = 'rgba(28, 134, 255, 0.15)';
                    ctx.strokeStyle = '#1c86ff';
                    break;
                }
                case 'green': {
                    // ctx.fillStyle = 'rgba(59, 173, 132, 0.1)';
                    ctx.strokeStyle = '#3bad84';

                    ctx.fillStyle = 'rgba(59, 173, 132, 0.75)';

                    ctx.fillStyle = 'rgba(6, 255, 151, 0.15)';
                    break;
                }
                case 'orange': {
                    ctx.fillStyle = 'rgba(255, 129, 88, 0.11)'; // 0.07 // 0.12
                    ctx.strokeStyle = '#ff8158';
                    break;
                }
                case 'yellow': {
                    ctx.fillStyle = 'rgba(225, 179, 93, 0.11)';
                    ctx.strokeStyle = '#e1b35d';
                    break;
                }
                case 'pink': {
                    ctx.fillStyle = 'rgba(225, 93, 137, 0.11)';
                    ctx.strokeStyle = '#e15d89';
                    break;
                }
                case 'purple': {
                    // ctx.fillStyle = 'rgba(139, 125, 235, 0.1)';
                    ctx.strokeStyle = '#8b7deb';

                    ctx.fillStyle = 'rgba(116, 84, 232, 0.15)';
                    break;
                }
                case 'violet': {
                    // ctx.fillStyle = 'rgba(235, 3, 235, 0.075)';
                    ctx.strokeStyle = '#eb03eb';

                    ctx.fillStyle = 'rgba(220, 97, 220, 0.15)';
                    break;
                }
                case 'red': {
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.175)'; // 0.06 // 0.08
                    ctx.strokeStyle = '#e80362';
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.12)'; // 0.06 // 0.08

                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 1);
        // ctx.stroke();
        ctx.fill();
        // ctx.shadowColor = 'transparent';

        // ctx.fillStyle = '#39424c'; // canvas background
        // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
        // ctx.stroke();
    }

    function calculateMinimapZoomNumbers() {
        const staticCanvas = minimapRef.current?.staticCanvas;
        if (!staticCanvas) return;

        // Bounding box of all elements + areas. The redux `canvasLimits` is no longer
        // maintained (elements live in the Y.Doc now) — it stayed {} → maxX-minX = NaN,
        // which is why the minimap drew nothing and clicks mapped to bad coordinates.
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        let hasContent = false;
        const collect = (obj) => {
            for (const key in obj) {
                const el = obj[key];
                if (!el || typeof el.x !== 'number') continue;
                hasContent = true;
                const w = el.width || 0;
                const h = el.height || 0;
                if (el.x < minX) minX = el.x;
                if (el.y < minY) minY = el.y;
                if (el.x + w > maxX) maxX = el.x + w;
                if (el.y + h > maxY) maxY = el.y + h;
            }
        };
        collect(elementsRef.current);
        collect(areasRef.current);
        if (!hasContent) {
            minX = 0; minY = 0; maxX = staticCanvas.width; maxY = staticCanvas.height;
        }

        const offset = 15;
        const elementsWidth = Math.max(maxX - minX, 1);
        const elementsHeight = Math.max(maxY - minY, 1);
        const canvasWidth = staticCanvas.width - 2 * offset;
        const canvasHeight = staticCanvas.height - 2 * offset;

        const newZoom = Math.min(canvasWidth / elementsWidth, canvasHeight / elementsHeight);

        const originX = (canvasWidth - elementsWidth * newZoom) / 2 - minX * newZoom + offset;
        const originY = (canvasHeight - elementsHeight * newZoom) / 2 - minY * newZoom + offset;

        minimapZoomRef.current = { zoom: newZoom, originX, originY };
    }

    useEffect(() => {
        canvasLimitsRef.current = canvasLimits;
        calculateMinimapZoomNumbers();
    }, [canvasLimits]);

    const drawMinimapStatic = useCallback(
        (staticCanvas, staticContext) => {
            // Re-fit to the current elements each draw (they live in refs and change
            // without a canvasLimits dispatch).
            calculateMinimapZoomNumbers();

            staticContext.clearRect(
                0,
                0,
                staticCanvas.width,
                staticCanvas.height
            );
            staticContext.save();
            staticContext.setTransform(
                minimapZoomRef.current.zoom,
                0,
                0,
                minimapZoomRef.current.zoom,
                minimapZoomRef.current.originX,
                minimapZoomRef.current.originY
            );

            // 1. AREAS |==| ALL
            for (const key in areasRef.current) {
                const area = areasRef.current[key];
                drawMinimapArea(staticContext, area);
            }

            // 2. ELEMENTS |==| ALL
            for (const key in elementsRef.current) {
                const element = elementsRef.current[key];

                switch (element.type) {
                    case 'table': {
                        drawMinimapTable(staticContext, element);
                        break;
                    }
                    case 'note': {
                        drawMinimapTable(staticContext, element);
                        break;
                    }
                    default:
                        break;
                }
            }

            staticContext.restore();

            // =================== START DYNAMIC DRAW ===================
            const viewportX =
                0 +
                minimapZoomRef.current.originX -
                (originXYRef.current.x / scaleRef.current) *
                minimapZoomRef.current.zoom;
            const viewportY =
                0 +
                minimapZoomRef.current.originY -
                (originXYRef.current.y / scaleRef.current) *
                minimapZoomRef.current.zoom;

            drawMinimapViewPort(
                staticContext,
                viewportX,
                viewportY,
                minimapZoomRef.current.zoom
            );
            // =================== END DYNAMIC DRAW ===================
        },
        [isBtnMinimapEnabled]
    );

    function unsetAllVariablesOnClick() {
        // unset all variables which was clicked on 'mousedown'
        if (alignmentLinesRef.current) {
            alignmentLinesRef.current = null;
        }

        if (clickedPointRef.current) {
            clickedPointRef.current = null;
        }

        if (isStartedLineState) {
            setIsStartedLineState(false);
        }

        // if (lineFromRef.current && (!isBtnNewLineEnabled || isLineCreated)) {
        // 	lineFromRef.current = null;
        // }

        // if (lineToRef.current && (!isBtnNewLineEnabled || isLineCreated)) {
        // 	lineToRef.current = null;
        // }

        if (clickedCircleElementIdRef.current) {
            clickedCircleElementIdRef.current = null;
        }

        if (clickedLineIdRef.current) {
            clickedLineIdRef.current = null;
        }

        if (clickedAreaIdRef.current) {
            clickedAreaIdRef.current = null;
        }

        if (clickedElementIdRef.current) {
            clickedElementIdRef.current = null;
        }
        if (clickedInsideElementRef.current) {
            clickedInsideElementRef.current = null;
        }

        if (isBoardClickedRef.current) {
            isBoardClickedRef.current = false;
        }
        if (isSelectionClickedRef.current) {
            isSelectionClickedRef.current = false;
        }
        if (isItemResizeClickedRef.current) {
            isItemResizeClickedRef.current = false;
        }

        if (handlerStateRef.current.isMovingElementId) {
            setIsMovingElementId(null);
            handlerStateRef.current.isMovingElementId = null;
        }

        if (handlerStateRef.current.isElementResizing) {
            setIsElementResizing(false);
            handlerStateRef.current.isElementResizing = false;
        }

        if (handlerStateRef.current.isBoardMoving) {
            setIsBoardMoving(false);
        }
        if (handlerStateRef.current.isBoardMovingStarted) {
            setIsBoardMovingStarted(false);
            handlerStateRef.current.isBoardMovingStarted = false;
        }

        if (exportImageBoxPropsRef.current.isPressed) {
            exportImageBoxPropsRef.current.isPressed = false;
        }

        if (exportImageBoxPropsRef.current.isOutsidePressed) {
            exportImageBoxPropsRef.current.isOutsidePressed = false;
        }

        if (exportImageBoxPropsRef.current.clickedResizeKey) {
            exportImageBoxPropsRef.current.clickedResizeKey = false;
        }

        if (selectedElementsIdsRef.current.length) {
            selectedElementsIdsRef.current = [];
        }
        if (selectedAreasIdsRef.current.length) {
            selectedAreasIdsRef.current = [];
        }


        if (shouldUnfocusElementsOnClickOnAreaRef.current) {
            shouldUnfocusElementsOnClickOnAreaRef.current = false;
        }
    }

    useEffect(() => {
        const canvas = ref.current;
        const context = canvas.getContext('2d');
        contextRef.current = context;
        let lastCallTime = 0;

        // const exportImgCanvas = exportImgCanvasRef.current;
        // const exportImgContext = exportImgCanvas.getContext('2d');
        // exportImgContextRef.current = exportImgContext;


        // if (isCursorHand) {
        // 	canvas.style.cursor = 'grab';
        // } else {
        // 	canvas.style.cursor = 'default';
        // }

        const handleWindowResize = () => {
            const { draw } = handlerStateRef.current; // eslint-disable-line
            // const dpr = window.devicePixelRatio || 1;

            // // Отримуємо розміри контейнера канвасу
            // const rect = canvas.parentNode.getBoundingClientRect();

            // // Встановлюємо CSS-розміри
            // // canvas.style.width = rect.width + 'px';
            // // canvas.style.height = rect.height + 'px';
            // canvas.style.width = rect.width * dpr + 'px';
            // canvas.style.height = rect.height * dpr + 'px';

            // // Встановлюємо атрибути розмірів з урахуванням DPR
            // canvas.width = rect.width * dpr;
            // canvas.height = rect.height * dpr;

            // console.log('=================||||||||||||||||', window.innerWidth, window.innerHeight, dpr, rect.width, rect.height, rect.width * dpr, rect.height * dpr)
            // // Масштабуємо контекст
            // context.scale(dpr, dpr);
            // canvas.width = window.innerWidth;
            // canvas.height = window.innerHeight;

            calculateVisibleElementsAndLines();

            draw({ isWindowForceResize: true }); // Redraw all canvas after size change
        };
        const handleCanvasMouseDown = async (e) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            // e.preventDefault();
            const event = e.type === 'touchstart' ? e.changedTouches[0] : e;

            // spec 8b: any mousedown on the canvas hides the note-link hover
            // preview instantly (pan/resize/selection-drag/tool-click are all
            // ABOUT to invalidate its anchor) — no grace period here, unlike
            // the ordinary pointer-left-the-zone case.
            hoveredNoteLinkKeyRef.current = null;
            hideNoteLinkPreviewImmediately();

            // console.log('e.type', e.type)
            if (isBtnNewTableClickEnabled || isBtnNewNoteClickEnabled) {
                scheduleDraw();
                return;
            }

            if (isBtnPenEnabled && canEditRef.current) {
                const mouseX =
                    (event.clientX - originXYRef.current.x) / scaleRef.current;
                const mouseY =
                    (event.clientY - originXYRef.current.y) / scaleRef.current;

                newPenSegmentRef.current.isPressed = true;
                newPenSegmentRef.current.points = [[mouseX, mouseY]];

                // newPenSegmentRef.current.points.push([mouseX, mouseY]);
                // newPenSegmentRef.current.points.push([event.clientX, event.clientY]);
                scheduleDraw();

                return;
            }

            // get mouse coordinates
            const mouseX =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseY =
                (event.clientY - originXYRef.current.y) / scaleRef.current;

            // set initial coordinates for moving startXY & start(first) point of selection box
            startXYRef.current = { x: event.clientX, y: event.clientY };
            selectionStartXYRef.current = { x: mouseX, y: mouseY };
            selectionCurrentXYRef.current = { x: mouseX, y: mouseY };

            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                // right button click
                if (event.button === 2) {
                    isBoardClickedRef.current = true;
                    canvas.style.cursor = 'grabbing';
                }

                return;
            }

            if (isBtnNewAreaEnabled && canEditRef.current) {
                newAreaPropsRef.current = {
                    startX: mouseX,
                    startY: mouseY,
                    endX: mouseX,
                    endY: mouseY,
                };
                // draw();

                return;
            }

            if (isCursorHand) {
                // Read-only diagrams can't grab-pan; swallow the click without panning.
                if (canEditRef.current) {
                    isBoardClickedRef.current = true;
                    canvas.style.cursor = 'grabbing';
                }
                return;
            }

            // get values for business logic
            const [
                newClickedElementId,
                newClickedInsideElement,
                newClickedCircleElementId,
                newClickedPointElementId,
                newClickedPointInsideElementId,
                newClickedPointInsideElementIndex,
                newClickedPointDirection,
                newClickedLineId,
                isClickedInsideExportImageBox,
                newClickedAreaId,
            ] = getNewClickedElements(
                elementsRef.current,
                mouseX,
                mouseY,
                event.clientX,
                event.clientY
            );

            let [isItemResizeClicked, resizeKey] = isHoverOnResizeHandle(mouseX, mouseY);

            // click on a clipped table's overflow chevron expands it.
            // Precedence: INSIDE the chevron's own hit circle (r=13/scale at
            // (x+width/2, y+effectiveHeight)) the chevron wins even over the
            // table's own 's' resize handle — same pattern as the note
            // chevron block below (which solved this identical problem
            // first). Since Fix 3, isHoverOnResizeHandle positions a focused
            // clipped table's 's'/'se'/'sw' EDGE-LINE hit zone at that same
            // effective height, so on a FOCUSED clipped table the two zones
            // coincide exactly — this check therefore runs UNCONDITIONALLY on
            // `isItemResizeClicked` (no longer gated on `!isItemResizeClicked`)
            // and `return`s before the `else if (isItemResizeClicked)` branch
            // further down is ever reached, so isItemResizeClickedRef.current/
            // resizeKeyRef.current never get armed in the first place —
            // nothing to neutralize beyond the same unsetAllVariablesOnClick()
            // every other chevron branch already runs. Everywhere OUTSIDE the
            // circle the resize handle still wins exactly as before —
            // `isItemResizeClicked`/`resizeKey` (computed above) are untouched
            // by this block and still drive the normal chain. Unfocused
            // clipped tables have no armed resize handle to begin with
            // (isHoverOnResizeHandle bails out unless exactly one element is
            // focused), so this was already the only path there.
            //
            // Full view only and hidden below OVERFLOW_INDICATOR_MIN_SCALE,
            // same threshold the draw block and the hover-cursor block below
            // gate on — no invisible click target; and tool interactions win —
            // export-image box drag and starting a new relation line take
            // precedence; pen drawing and area creation already returned
            // early above this point.
            if (
                viewMode === 'full' &&
                scaleRef.current > OVERFLOW_INDICATOR_MIN_SCALE &&
                canEditRef.current &&
                !isBtnExportImageEnabled &&
                !newClickedPointElementId
            ) {
                for (const elId in elementsRef.current) {
                    const el = elementsRef.current[elId];
                    if (el?.type !== 'table' || el.displayHeight == null) continue;
                    const keysCount = orderedColumnKeys(el.columns, el.columnsOrder).length;
                    if (!isTableClipped(el, keysCount)) continue;
                    const effH = getTableEffectiveHeight(el);
                    const cx = el.x + el.width / 2;
                    const cy = el.y + effH;
                    // radius kept in lockstep with the draw block's chevronR
                    // (drawTable's "TABLE OVERFLOW INDICATOR" — matches the
                    // note indicator's r=13/scale exactly, see Fix 1)
                    const r = 13 / scaleRef.current;
                    if ((mouseX - cx) ** 2 + (mouseY - cy) ** 2 <= r * r) {
                        delete elementsRef.current[elId].displayHeight;
                        setTableDisplayHeight(getDiagramDoc(), elId, null);
                        updateLinesByElementsIds([elId]);
                        scheduleDraw();
                        isBoardClickedRef.current = false;
                        isSelectionClickedRef.current = false;
                        unsetAllVariablesOnClick();
                        return;
                    }
                }
            }

            // click on an overflowing note's chevron expands it to fit all
            // content. Precedence: INSIDE the chevron's own hit circle
            // (r=13/scale at (x+width/2, y+height)) the chevron wins even
            // over the note's own 's' resize handle — for a focused,
            // single-selected, overflowing note, isHoverOnResizeHandle's 's'
            // EDGE-LINE hit zone (calculateItemResizeCoordinates, keyed off
            // the note's real `height`) sits at that exact same point. So
            // this check runs UNCONDITIONALLY on `isItemResizeClicked` — a
            // chevron hit `return`s before the `else if (isItemResizeClicked)`
            // branch further down is ever reached, so
            // isItemResizeClickedRef.current/resizeKeyRef.current never get
            // armed in the first place; nothing to neutralize beyond the same
            // unsetAllVariablesOnClick() every other chevron branch already
            // runs. Everywhere OUTSIDE the circle the resize handle still
            // wins exactly as before — `isItemResizeClicked`/`resizeKey`
            // (computed above) are untouched by this block and still drive
            // the normal chain. TABLE's chevron block above now mirrors this
            // exact same precedence pattern (its 's'/'se'/'sw' handle sits at
            // the same CLIPPED effective height as its chevron since Fix 3,
            // so the two zones DO coincide on a focused clipped table).
            //
            // NOT gated on this note's own editor being open —
            // NOTE_EDITOR_BOTTOM_REVEAL (EditableNote.jsx) exists precisely
            // so this chevron stays visible AND usable while editing.
            //
            // Full view only and hidden below OVERFLOW_INDICATOR_MIN_SCALE
            // like drawNote's own gate on this same constant; export-image
            // box drag and starting a new relation line from a connection
            // point take precedence; pen drawing and area creation already
            // returned early above this point.
            if (
                viewMode === 'full' &&
                scaleRef.current > OVERFLOW_INDICATOR_MIN_SCALE &&
                canEditRef.current &&
                !isBtnExportImageEnabled &&
                !newClickedPointElementId
            ) {
                for (const elId in elementsRef.current) {
                    const el = elementsRef.current[elId];
                    if (el?.type !== 'note') continue;
                    // cheap gate first: only overflowing notes carry a chevron
                    const cachedLayout = noteLayoutCacheRef.current.get(elId);
                    if (!cachedLayout) continue;
                    const innerHeight = el.height - NOTE_PADDING_Y - NOTE_PADDING_Y_BOTTOM;
                    if (cachedLayout.lines.length * NOTE_LINE_HEIGHT <= innerHeight) continue;
                    const cx = el.x + el.width / 2;
                    const cy = el.y + el.height;
                    const r = 13 / scaleRef.current;
                    if ((mouseX - cx) ** 2 + (mouseY - cy) ** 2 <= r * r) {
                        // inverts drawNote's own overflow math: the smallest
                        // height whose innerHeight (height - NOTE_PADDING_Y -
                        // NOTE_PADDING_Y_BOTTOM) no longer falls short of
                        // lines.length * NOTE_LINE_HEIGHT — i.e. the exact
                        // fit, indicator gone, normal bottom padding kept.
                        const fitHeight = cachedLayout.lines.length * NOTE_LINE_HEIGHT + NOTE_PADDING_Y + NOTE_PADDING_Y_BOTTOM;
                        // same 60 floor the manual note resize-drag clamps to
                        const newHeight = Math.max(fitHeight, 60);
                        elementsRef.current[elId].height = newHeight;
                        resizeElements(getDiagramDoc(), [{ id: elId, height: newHeight }]);
                        updateLinesByElementsIds([elId]);
                        scheduleDraw();
                        isBoardClickedRef.current = false;
                        isSelectionClickedRef.current = false;
                        unsetAllVariablesOnClick();
                        return;
                    }
                }
            }

            // BUSINESS LOGIC | CLICK MOUSE DOWN

            if (isBtnExportImageEnabled) {
                let clickedResizeKey = getHoveredResizeKeyOnExportImageBox(
                    mouseX,
                    mouseY
                );

                // is clicked on resize key
                if (clickedResizeKey) {
                    exportImageBoxPropsRef.current.clickedResizeKey =
                        clickedResizeKey;
                    // left button moves the export frame (resize handles take
                    // priority). The right button pans the canvas via
                    // isBoardClickedRef — previously a left-drag outside the frame
                    // also panned, so left and right both moved the whole canvas.
                } else {
                    exportImageBoxPropsRef.current.isPressed = true;
                }

                // draw();
            } else if (newClickedPointElementId && canEditRef.current) {
                clickedPointRef.current = {
                    elementId: newClickedPointElementId,
                    insideElementId: newClickedPointInsideElementId,
                    insideElementIndex: newClickedPointInsideElementIndex,
                    direction: newClickedPointDirection,
                };

                // ------------
                setIsStartedLineState(true);

                // ------------
                const element = elementsRef.current[newClickedPointElementId];
                const elementType = element?.type;
                const elementWidth = element?.width;

                const insideElement = newClickedPointInsideElementId
                    ? elementsRef.current[newClickedPointInsideElementId]
                    : null;
                const insideElementIndex =
                    newClickedPointInsideElementIndex || null;
                const hasInsideElement = !!insideElement;

                lineFromRef.current = {
                    elementId: newClickedPointElementId,
                    insideElementId: newClickedPointInsideElementId,
                    // insideElementIndex: newClickedPointInsideElementIndex,
                    x: mouseX,
                    y: mouseY,

                    elementType,
                    elementWidth,
                    elementX: element.x,
                    elementY: element.y,
                    hasInsideElement,
                    insideElementIndex,
                };
                lineToRef.current = {
                    elementId: null,
                    insideElementId: null,
                    x: mouseX,
                    y: mouseY,
                };

                // click on circle to add new row
            } else if (newClickedCircleElementId) {
                clickedCircleElementIdRef.current = newClickedCircleElementId;

                // click on element resize points — deliberately ABOVE the
                // element-body branch (it used to sit below it, after the
                // whole element/line chain): the handle zones straddle the
                // focused element's outline, so their inner halves (and the
                // edge-line bands) overlap the element's own body — with the
                // body winning, that overlap showed the resize cursor (the
                // hover chain checks resize FIRST for a focused single
                // element) but the click dragged/selected instead of
                // resizing. Small deliberate targets stay ABOVE this branch
                // (connection points, add-row circle — mirrored in the hover
                // chain, which shows their pointer cursor over these zones
                // instead of the resize arrow), same precedence family as
                // the chevron blocks near the top of this handler.
            } else if (isItemResizeClicked) {
                isItemResizeClickedRef.current = true;
                resizeKeyRef.current = resizeKey;

                // click on element
            } else if (newClickedElementId) {
                clickedElementIdRef.current = newClickedElementId;

                if (newClickedInsideElement) {
                    clickedInsideElementRef.current = {
                        ...newClickedInsideElement,
                    };
                }
                // click on line
            } else if (newClickedLineId) {
                clickedLineIdRef.current = newClickedLineId;

                // } else if (newClickedAreaId) {
                // 	clickedAreaIdRef.current = newClickedAreaId;
            } else if (newClickedAreaId) {
                if (!isCursorHand) {
                    clickedAreaIdRef.current = newClickedAreaId;

                    if (!focusedIdsRef.current.includes(newClickedAreaId)) {
                        isSelectionClickedRef.current = true;
                        selectedElementsIdsRef.current = [];
                        selectedAreasIdsRef.current = [];

                        shouldUnfocusElementsOnClickOnAreaRef.current = focusedIdsRef.current.length > 0;

                        if (focusedInsideElementState) {
                            setFocusedInsideElementState(null);
                            syncFocusedInsideElementToReduxRef.current(null);
                        }
                        if (focusedIdsRef.current.length) {
                            focusedIdsRef.current = [];
                            focusedRelatedPropsRef.current = null;
                            focusedLineRelatedPropsRef.current = null;
                            dispatch(updateFocusedIds([]));
                        }
                    }
                }
                // click on board
            } else {
                if (!isCursorHand) {
                    // Box-select only when editing — read-only diagrams can still clear
                    // the selection by clicking the board, but not drag a selection box.
                    if (canEditRef.current) isSelectionClickedRef.current = true;
                    selectedElementsIdsRef.current = [];
                    selectedAreasIdsRef.current = [];

                    // ----------------------------- (clear selection)
                    const hasFocusedInsideElementChanges = !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {
                        setFocusedInsideElementState(null);
                        syncFocusedInsideElementToReduxRef.current(null);
                    }
                    if (focusedIdsRef.current.length) {
                        focusedIdsRef.current = [];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([]));
                    }
                    // -----------------------------
                } else {
                    isBoardClickedRef.current = true;
                }
            }


            // BUSINESS LOGIC | CURSOR
            if (
                (isCursorHand && isBoardClickedRef.current) ||
                (isBtnExportImageEnabled &&
                    (exportImageBoxPropsRef.current.isPressed ||
                        exportImageBoxPropsRef.current.isOutsidePressed))
            ) {
                canvas.style.cursor = 'grabbing';
                // canvas.style.cursor = 'grab';
            } else if (clickedPointRef.current) {
                canvas.style.cursor = 'crosshair';
            }

            scheduleDraw();
        };
        const handleDocumentMouseMove = (e) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            // e.preventDefault();
            const event = e.type === 'touchmove' ? e.changedTouches[0] : e;

            // console.log('e.type', e.type)

            // inside mouse move in canvas root elements tree
            const isMouseInCanvas =
                ref.current && ref.current.contains(event.target);

            // TODO:
            // if (isBtnPenEnabled || Math.random() !== 12) {
            const mouseXForPen =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseYForPen =
                (event.clientY - originXYRef.current.y) / scaleRef.current;
            newPenCursorPointRef.current = { x: mouseXForPen, y: mouseYForPen };
            // }

            if (isBtnPenEnabled && newPenSegmentRef.current.isPressed) {
                const mouseX =
                    (event.clientX - originXYRef.current.x) / scaleRef.current;
                const mouseY =
                    (event.clientY - originXYRef.current.y) / scaleRef.current;

                newPenSegmentRef.current.points.push([mouseX, mouseY]);

                scheduleDraw();
                return;
            }

            // is clicked on resize key
            if (exportImageBoxPropsRef.current.clickedResizeKey) {
                const clientDX = event.clientX - startXYRef.current.x;
                const clientDY = event.clientY - startXYRef.current.y;

                // get new sizes for resizing box
                let {
                    x: newClientDX,
                    y: newClientDY,
                    width: newClientWidth,
                    height: newClientHeight,
                } = handleResize(
                    exportImageBoxPropsRef.current.clickedResizeKey,
                    clientDX,
                    clientDY
                );

                // update new-clicked box position

                const { clientBoxWidth, clientBoxHeight } =
                    exportImageBoxPropsRef.current;

                const MIN_BOX_SIZE = 15;

                if (
                    clientBoxWidth + newClientWidth >= MIN_BOX_SIZE &&
                    clientBoxHeight + newClientHeight >= MIN_BOX_SIZE
                ) {
                    exportImageBoxPropsRef.current.clientX += newClientDX;
                    exportImageBoxPropsRef.current.clientY += newClientDY;
                    exportImageBoxPropsRef.current.clientBoxWidth +=
                        newClientWidth;
                    exportImageBoxPropsRef.current.clientBoxHeight +=
                        newClientHeight;

                    // draw();
                }

                // is clicked on 'inside box'
            } else if (exportImageBoxPropsRef.current.isPressed) {
                // move the export image box
                const dx = event.clientX - startXYRef.current.x;
                const dy = event.clientY - startXYRef.current.y;

                exportImageBoxPropsRef.current.clientX += dx;
                exportImageBoxPropsRef.current.clientY += dy;

                // draw();

                // is clicked on 'outside the box'
            } else if (exportImageBoxPropsRef.current.isOutsidePressed) {
                // just board moving
                const dx = event.clientX - startXYRef.current.x;
                const dy = event.clientY - startXYRef.current.y;

                // Hard pan limits (review follow-up, found while locating
                // "the" mouse drag-pan path — this is a SECOND incremental
                // drag-pan site, gated behind export-image mode: panning the
                // board while positioning the export selection box, via the
                // same "board moving" mechanism as the primary drag-pan
                // branch below). Same clamp-the-result reasoning applies.
                const newOriginX = clampOriginForAxis('x', originXYRef.current.x + dx);
                const newOriginY = clampOriginForAxis('y', originXYRef.current.y + dy);

                // update board originXY positions
                originXYRef.current = { x: newOriginX, y: newOriginY };
                syncOriginXYToReduxRef.current(newOriginX, newOriginY);

                calculateVisibleElementsAndLines();

                // update canvas
                // draw();
            }

            if (isBtnNewTableClickEnabled) {
                // change cursor to 'new_table';
            }
            if (isBtnNewNoteClickEnabled) {
                // change cursor to 'new_note';
            }

            if (
                isBtnNewTableGrabEnabled &&
                isNewTableGrabStartedRef.current === false
            ) {
                isNewTableGrabStartedRef.current = true;
            }

            if (
                isBtnNewNoteGrabEnabled &&
                isNewNoteGrabStartedRef.current === false
            ) {
                isNewNoteGrabStartedRef.current = true;
            }

            // get mouse coordinates
            const mouseX =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseY =
                (event.clientY - originXYRef.current.y) / scaleRef.current;

            // set new value for current(second) point of selection box
            selectionCurrentXYRef.current = { x: mouseX, y: mouseY };

            // set new value for temporary new area
            if (isBtnNewAreaEnabled && newAreaPropsRef.current) {
                newAreaPropsRef.current.endX = mouseX;
                newAreaPropsRef.current.endY = mouseY;

                // draw();
            }

            // get values for business logic
            const [
                newHoveredElementId,
                newHoveredInsideElementId,
                newHoveredInsideElementIndex,
                newHoveredCircleAreaElementsIds,
                newHoveredCircleElementId,
                newHoveredPointInsideElementId,
                newHoveredPointDirection,
                newHoveredPointAreaInsideElementsIds,
                newHoveredLineId,
                newHoveredElementIdEmptyColumn,
                newHoveredAreaId,
                newHoveredElementIdHeader,
            ] = getNewHoveredElements(
                elementsRef.current,
                mouseX,
                mouseY,
                event.clientX,
                event.clientY
            );

            // BUSINESS LOGIC | CLICKING AND MOVING

            if (isBtnNewAreaEnabled) {
                if (newAreaPropsRef.current) {
                    const point1 = {
                        x: newAreaPropsRef.current.startX,
                        y: newAreaPropsRef.current.startY,
                    };
                    const point2 = {
                        x: newAreaPropsRef.current.endX,
                        y: newAreaPropsRef.current.endY,
                    };

                    // get selected elements by creating area
                    const selectedElements = getAreaSelectedElements(
                        point1,
                        point2,
                        elementsRef.current,
                        areasRef.current,
                        viewMode
                    );

                    newAreaPropsRef.current.selectedElements = selectedElements;

                    // check if mouse moving away on viewport / window sizes
                    // scrollIfNeeded(e);

                    const hasFocusedInsideElementChanges = !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {
                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    // clear selection
                    if (focusedIdsRef.current.length) {
                        focusedIdsRef.current = [];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([]));
                    }

                    // update canvas
                    // draw();
                }
            } else if (isBtnNewLineEnabled) {
                if (!isStartedLineState) {
                    setIsStartedLineState(true);
                }

                // 1. line has no 'from' point
                if (!lineFromRef.current || !lineFromRef.current.isClicked) {
                    if (newHoveredElementId && newHoveredInsideElementId) {
                        const element =
                            elementsRef.current[newHoveredElementId];
                        const elementType = element?.type;
                        const elementWidth = element?.width;

                        lineFromRef.current = {
                            elementId: newHoveredElementId,
                            insideElementId: newHoveredInsideElementId,

                            hasInsideElement: !!newHoveredInsideElementId,
                            insideElementIndex: newHoveredInsideElementIndex,

                            x: mouseX,
                            y: mouseY,

                            elementType,
                            elementWidth,
                            elementX: element.x,
                            elementY: element.y,
                        };
                    } else {
                        lineFromRef.current = null;
                    }

                    // 2. line has 'from' point
                } else {
                    if (newHoveredElementId) {
                        const element =
                            elementsRef.current[newHoveredElementId];
                        const elementType = element?.type;
                        const elementWidth = element?.width;

                        lineToRef.current = {
                            elementId: newHoveredElementId,
                            insideElementId: newHoveredInsideElementId,

                            hasInsideElement: !!newHoveredInsideElementId,
                            insideElementIndex: newHoveredInsideElementIndex,

                            x: mouseX,
                            y: mouseY,

                            elementType,
                            elementWidth,
                            elementX: element.x,
                            elementY: element.y,
                        };
                    } else {
                        lineToRef.current = {
                            elementId: null,
                            insideElementId: null,
                            x: mouseX,
                            y: mouseY,
                        };
                    }
                }

                // draw();

                // 0. click on column's relation point
            } else if (clickedPointRef.current?.elementId) {
                const element = elementsRef.current[newHoveredElementId];
                const elementType = element?.type;
                const elementWidth = element?.width;

                const insideElement = newHoveredInsideElementId
                    ? elementsRef.current[newHoveredInsideElementId]
                    : null;
                const insideElementIndex = newHoveredInsideElementIndex || null;
                const hasInsideElement = !!insideElement;

                lineToRef.current = {
                    elementId: newHoveredElementId,
                    insideElementId: newHoveredInsideElementId,
                    // insideElementIndex: newHoveredInsideElementIndex,
                    x: mouseX,
                    y: mouseY,

                    elementType,
                    elementWidth,
                    elementX: element?.x,
                    elementY: element?.y,
                    hasInsideElement,
                    insideElementIndex,
                };

                // draw();

                // move with element (only when editing — read-only diagrams can't move)
            } else if (clickedElementIdRef.current && canEditRef.current) {
                const dx =
                    (event.clientX - startXYRef.current.x) / scaleRef.current;
                const dy =
                    (event.clientY - startXYRef.current.y) / scaleRef.current;

                // ALIGNMENT LINES SECTION

                const isMultiElementsMove =
                    focusedIdsRef.current.includes(
                        clickedElementIdRef.current
                    ) && focusedIdsRef.current.length > 1;
                if (!isMultiElementsMove && scaleRef.current >= 0.26) {
                    const [alignmentLines, movedElementNewXY] =
                        getAlignmentLines(clickedElementIdRef.current);

                    if (movedElementNewXY) {
                        // if closer point x is the same as selected before
                        if (
                            typeof movedElementNewXY.x === 'number' &&
                            typeof alignmentLinePropsRef.current.x ===
                            'number' &&
                            movedElementNewXY.x ===
                            alignmentLinePropsRef.current.x
                        ) {
                            const moveDX = Math.abs(
                                e.clientX - alignmentLinePropsRef.current.mouseX
                            );

                            if (moveDX < 4) {
                                elementsRef.current[
                                    clickedElementIdRef.current
                                ].x = movedElementNewXY.x;
                                alignmentLinePropsRef.current.x =
                                    movedElementNewXY.x;
                                alignmentLinePropsRef.current.elementIdByX =
                                    clickedElementIdRef.current;
                            } else {
                                alignmentLinePropsRef.current.elementIdByX =
                                    null;
                            }
                        } else if (typeof movedElementNewXY.x === 'number') {
                            elementsRef.current[clickedElementIdRef.current].x =
                                movedElementNewXY.x;
                            alignmentLinePropsRef.current.x =
                                movedElementNewXY.x;
                            alignmentLinePropsRef.current.mouseX =
                                event.clientX;
                            alignmentLinePropsRef.current.elementIdByX =
                                clickedElementIdRef.current;
                        }

                        // if closer point y is the same as selected before
                        if (
                            typeof movedElementNewXY.y === 'number' &&
                            typeof alignmentLinePropsRef.current.y ===
                            'number' &&
                            movedElementNewXY.y ===
                            alignmentLinePropsRef.current.y
                        ) {
                            const moveDY = Math.abs(
                                e.clientY - alignmentLinePropsRef.current.mouseY
                            );

                            if (moveDY < 4) {
                                elementsRef.current[
                                    clickedElementIdRef.current
                                ].y = movedElementNewXY.y;
                                alignmentLinePropsRef.current.y =
                                    movedElementNewXY.y;
                                alignmentLinePropsRef.current.elementIdByY =
                                    clickedElementIdRef.current;
                            } else {
                                alignmentLinePropsRef.current.elementIdByY =
                                    null;
                            }
                        } else if (typeof movedElementNewXY.y === 'number') {
                            elementsRef.current[clickedElementIdRef.current].y =
                                movedElementNewXY.y;
                            alignmentLinePropsRef.current.y =
                                movedElementNewXY.y;
                            alignmentLinePropsRef.current.mouseY =
                                event.clientY;
                            alignmentLinePropsRef.current.elementIdByY =
                                clickedElementIdRef.current;
                        }
                    }
                    alignmentLinesRef.current = alignmentLines;
                }

                // set is element moving
                setIsMovingElementId(clickedElementIdRef.current);

                // move with focused element
                if (
                    focusedIdsRef.current.includes(
                        clickedElementIdRef.current
                    )
                ) {
                    // update focused elements positions
                    const _selectedElementIdsForMove = focusedIdsRef.current.filter(
                        (sid) => elementsRef.current[sid] && elementsRef.current[sid].type !== 'area'
                    );
                    _selectedElementIdsForMove.forEach((elementId) => {
                        if (
                            elementId !==
                            alignmentLinePropsRef.current.elementIdByX
                        ) {
                            elementsRef.current[elementId].x += dx;
                        }
                        if (
                            elementId !==
                            alignmentLinePropsRef.current.elementIdByY
                        ) {
                            elementsRef.current[elementId].y += dy;
                        }

                        elementsRef.current[elementId].isChangedXY = true;

                        // ------------------------- check element's areaId

                        const element = elementsRef.current[elementId];
                        const newAreaIdForElement = getNewAreaIdForElement(
                            areasRef.current,
                            elementId
                        );

                        if (newAreaIdForElement) {
                            if (element.areaId !== newAreaIdForElement) {
                                // 1. if element.areaId is not null, remove nestedElementId from area
                                // 2. set element's areaId to new areaId
                                // 3. set newArea's nestedElementsIds with new element.id

                                elementsRef.current[elementId].areaId =
                                    newAreaIdForElement;
                                elementsRef.current[
                                    elementId
                                ].isChangedAreaId = true;
                                // dispatch(setNewAreaIdForElement(element.id, newAreaIdForElement));

                                resizeElementPropsRef.current = { elementId, areaId: newAreaIdForElement };
                            }
                        } else {
                            if (
                                element.areaId &&
                                areasRef.current[element.areaId]
                            ) {
                                // 1. remove areaId from element (nestedElementsIds is now derived)

                                elementsRef.current[elementId].areaId =
                                    null;
                                elementsRef.current[
                                    elementId
                                ].isChangedAreaId = true;

                                // dispatch(setNewAreaIdForElement(element.id, null));
                                resizeElementPropsRef.current = null;
                            }
                        }

                        // -------------------------
                    });

                    updateLinesByElementsIds(_selectedElementIdsForMove);

                    // move with new-clicked element
                } else {
                    // update new-clicked element position
                    // elementsRef.current[clickedElementIdRef.current].x += dx;
                    // elementsRef.current[clickedElementIdRef.current].y += dy;
                    if (
                        clickedElementIdRef.current !==
                        alignmentLinePropsRef.current.elementIdByX
                    ) {
                        elementsRef.current[clickedElementIdRef.current].x +=
                            dx;
                    }
                    if (
                        clickedElementIdRef.current !==
                        alignmentLinePropsRef.current.elementIdByY
                    ) {
                        elementsRef.current[clickedElementIdRef.current].y +=
                            dy;
                    }


                    elementsRef.current[
                        clickedElementIdRef.current
                    ].isChangedXY = true;

                    updateLinesByElementsIds([clickedElementIdRef.current]);

                    // ------------------------- check element's areaId

                    const element =
                        elementsRef.current[clickedElementIdRef.current];
                    const newAreaIdForElement = getNewAreaIdForElement(
                        areasRef.current,
                        clickedElementIdRef.current
                    );

                    if (newAreaIdForElement) {
                        if (element.areaId !== newAreaIdForElement) {
                            // 1. if element.areaId is not null, remove nestedElementId from area
                            // 2. set element's areaId to new areaId
                            // 3. set newArea's nestedElementsIds with new element.id

                            elementsRef.current[
                                clickedElementIdRef.current
                            ].areaId = newAreaIdForElement;
                            elementsRef.current[
                                clickedElementIdRef.current
                            ].isChangedAreaId = true;
                            // dispatch(setNewAreaIdForElement(element.id, newAreaIdForElement));

                            resizeElementPropsRef.current = { elementId: clickedElementIdRef.current, areaId: newAreaIdForElement };
                        }
                    } else {
                        if (
                            element.areaId &&
                            areasRef.current[element.areaId]
                        ) {
                            // 1. remove areaId from element (nestedElementsIds is now derived)

                            elementsRef.current[
                                clickedElementIdRef.current
                            ].areaId = null;
                            elementsRef.current[
                                clickedElementIdRef.current
                            ].isChangedAreaId = true;
                            // dispatch(setNewAreaIdForElement(element.id, null));
                            resizeElementPropsRef.current = null;
                        }
                    }

                    // -------------------------

                    // set focused elements to []
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    if (focusedIdsRef.current.length) {
                        focusedIdsRef.current = [];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([]));
                    }
                }

                // draw();

                // move with board (only when editing — read-only diagrams can't move)
            } else if (clickedAreaIdRef.current && canEditRef.current) {
                if (focusedIdsRef.current.includes(clickedAreaIdRef.current)) {
                    const dx = (event.clientX - startXYRef.current.x) / scaleRef.current;
                    const dy = (event.clientY - startXYRef.current.y) / scaleRef.current;

                    // set is area moving
                    setIsMovingElementId(clickedAreaIdRef.current);
                    // set is moving

                    // move with focused area
                    if (focusedIdsRef.current.includes(clickedAreaIdRef.current)) {
                        // update focused area position
                        areasRef.current[clickedAreaIdRef.current].x += dx;
                        areasRef.current[clickedAreaIdRef.current].y += dy;
                        areasRef.current[clickedAreaIdRef.current].isChangedXY = true;

                        const area = areasRef.current[clickedAreaIdRef.current];


                        /// resize area props
                        const point1 = { x: area.x, y: area.y };
                        const point2 = { x: area.x + area.width, y: area.y + area.height };

                        // get selected elements by creating area
                        const selectedElements = getAreaSelectedElementsForResize(
                            point1,
                            point2,
                            elementsRef.current,
                            areasRef.current,
                            viewMode,
                            area.id
                        );

                        resizeAreaPropsRef.current = { areaId: area.id, selectedElements };

                        if (!isElementResizing) {
                            setIsElementResizing(true);
                        }

                        {
                            // update nested elements positions (derived from element.areaId)
                            const nestedElementsIds = Object.values(elementsRef.current)
                                .filter(el => el.areaId === area.id)
                                .map(el => el.id);
                            if (nestedElementsIds.length) {
                                nestedElementsIds.forEach(
                                    (elementId) => {
                                        elementsRef.current[elementId].x += dx;
                                        elementsRef.current[elementId].y += dy;

                                        elementsRef.current[elementId].isChangedXY = true;
                                    }
                                );

                                updateLinesByElementsIds(nestedElementsIds);
                            }
                        }

                        // draw();
                        // setElementsHasChanges(true);

                        // move with new-clicked area
                    } else {
                        // update new-clicked area position
                        areasRef.current[clickedAreaIdRef.current].x += dx;
                        areasRef.current[clickedAreaIdRef.current].y += dy;
                        areasRef.current[clickedAreaIdRef.current].isChangedXY = true;

                        const area = areasRef.current[clickedAreaIdRef.current];

                        {
                            // update nested elements positions (derived from element.areaId)
                            const nestedElementsIds = Object.values(elementsRef.current)
                                .filter(el => el.areaId === area.id)
                                .map(el => el.id);
                            if (nestedElementsIds.length) {
                                nestedElementsIds.forEach(
                                    (elementId) => {
                                        elementsRef.current[elementId].x += dx;
                                        elementsRef.current[elementId].y += dy;

                                        elementsRef.current[
                                            elementId
                                        ].isChangedXY = true;
                                    }
                                );

                                updateLinesByElementsIds(nestedElementsIds);
                            }
                        }

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);

                        if (focusedIdsRef.current.length) {
                            focusedIdsRef.current = [];
                            focusedRelatedPropsRef.current = null;
                            focusedLineRelatedPropsRef.current = null;
                            dispatch(updateFocusedIds([]));
                        }
                        // draw();
                    }

                    // const isMultiElementsMove = focusedIdsRef.current.includes(clickedElementIdRef.current) && selectedElementIdsLegacyRef.current.length > 1;

                    // if (!isMultiElementsMove) {
                    // const [alignmentLines, movedElementNewXY] = getAlignmentLines(clickedElementIdRef.current);
                    // alignmentLinesRef.current = alignmentLines;
                    // }
                } else {

                    // move with selected focused group (areas and elements)
                    if (
                        focusedIdsRef.current.includes(clickedAreaIdRef.current) ||
                        focusedIdsRef.current.includes(clickedElementIdRef.current)
                    ) {
                        const dx = (event.clientX - startXYRef.current.x) / scaleRef.current;
                        const dy = (event.clientY - startXYRef.current.y) / scaleRef.current;

                        const selectedAreaIds = focusedIdsRef.current.filter(
                            (sid) => areasRef.current[sid]
                        );
                        const selectedElementIds = focusedIdsRef.current.filter(
                            (sid) =>
                                elementsRef.current[sid] &&
                                elementsRef.current[sid].type !== 'area'
                        );

                        selectedAreaIds.forEach((areaId) => {
                            areasRef.current[areaId].x += dx;
                            areasRef.current[areaId].y += dy;
                            areasRef.current[areaId].isChangedXY = true;
                        });

                        selectedElementIds.forEach((elementId) => {
                            elementsRef.current[elementId].x += dx;
                            elementsRef.current[elementId].y += dy;
                            elementsRef.current[elementId].isChangedXY = true;
                        });





                        // if (!focusedIdsRef.current.includes(clickedAreaIdRef.current)) {
                        //     if (selectedGroupAreasLegacyRef.current.length) {
                        //         selectedGroupAreasLegacyRef.current = [];
                        //         selectedGroupElementsLegacyRef.current = [];
                        //         syncSelectedGroupLegacyRef.current([], []);
                        //     }
                        // }

                        // if (!focusedIdsRef.current.includes(clickedElementIdRef.current)) {
                        //     if (selectedGroupAreasLegacyRef.current.length) {
                        //         selectedGroupAreasLegacyRef.current = [];
                        //         selectedGroupElementsLegacyRef.current = [];
                        //         syncSelectedGroupLegacyRef.current([], []);
                        //     }
                        // }



                    } else {
                        // get selected areas by mouse selectionBox
                        const selectedAreasIds = getSelectedAreas(selectionStartXYRef.current, selectionCurrentXYRef.current, areasRef.current);
                        selectedAreasIdsRef.current = selectedAreasIds;

                        // get selected elements by mouse selectionBox
                        const selectedElementsIds = getSelectedElements(selectionStartXYRef.current, selectionCurrentXYRef.current, elementsRef.current, viewMode);
                        selectedElementsIdsRef.current = selectedElementsIds;

                        if (!selectedAreasIds?.length) {
                            focusedRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);
                        }

                        // set is board moving | for context menu and etc.
                        if (!isBoardMoving) {
                            setIsBoardMoving(true);
                            setIsBoardMovingStarted(true);
                        }
                    }

                }
            } else if (isBoardClickedRef.current) {
                const dx = event.clientX - startXYRef.current.x;
                const dy = event.clientY - startXYRef.current.y;

                // Hard pan limits: clamp the RESULT, not the delta — dx/dy
                // here is already a per-tick increment (startXYRef.current
                // is reset to the current mouse position at the end of
                // every mousemove tick, see "set new value to startXY for
                // next 'mousemove' event" further down in this handler), so
                // once pinned, continuing to drag past the limit keeps
                // recomputing a newOrigin just beyond it and re-clamping
                // back to the same pinned value — a wall, not a rubber-band.
                const newOriginX = clampOriginForAxis('x', originXYRef.current.x + dx);
                const newOriginY = clampOriginForAxis('y', originXYRef.current.y + dy);

                // update board originXY positions
                originXYRef.current = { x: newOriginX, y: newOriginY };
                syncOriginXYToReduxRef.current(newOriginX, newOriginY);

                sumOriginDxyRef.current = {
                    dx: sumOriginDxyRef.current.dx + dx,
                    dy: sumOriginDxyRef.current.dy + dy,
                };

                if (
                    Math.abs(sumOriginDxyRef.current.dx) > 250 ||
                    Math.abs(sumOriginDxyRef.current.dy) > 250
                ) {
                    sumOriginDxyRef.current = { dx: 0, dy: 0 };
                    calculateVisibleElementsAndLines();
                }
                // update canvas
                // draw();

                // set is board moving
                if (!isBoardMoving) {
                    setIsBoardMoving(true);
                    setIsBoardMovingStarted(true);
                }

                // set timeout on board moving
                clearTimeout(boardMovingTimerRef.current);
                boardMovingTimerRef.current = setTimeout(() => {
                    setIsBoardMoving(false);
                }, 200);

                // move with mouse selection (box-select disabled on read-only diagrams)
            } else if (isSelectionClickedRef.current && canEditRef.current) {
                // get selected areas by mouse selectionBox
                const selectedAreasIds = getSelectedAreas(
                    selectionStartXYRef.current,
                    selectionCurrentXYRef.current,
                    areasRef.current
                );
                selectedAreasIdsRef.current = selectedAreasIds;

                // get selected elements by mouse selectionBox
                const selectedElementsIds = getSelectedElements(
                    selectionStartXYRef.current,
                    selectionCurrentXYRef.current,
                    elementsRef.current,
                    viewMode
                );
                selectedElementsIdsRef.current = selectedElementsIds;
                // focusedRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);
                if (!selectedAreasIds?.length) {
                    focusedRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);
                }

                // check if mouse moving away on viewport / window sizes
                // scrollIfNeeded(e);

                // ----------------------------- (disabled by selectedElementsIdsRef.current)
                // const hasFocusedInsideElementChanges = !!focusedInsideElementState;
                // if (hasFocusedInsideElementChanges) {

                // 	setFocusedInsideElementState(null);
                // 	// setColumnNameState('');
                // 	// setColumnTypeState('');

                // 	syncFocusedInsideElementToReduxRef.current(null);
                // 	// draw();
                // }
                // // set selected elements to focused elements
                // const hasFocusedElementsChanges = !arraysAreEqual(selectedElementIdsLegacyRef.current, selectedElementsIds);
                // if (hasFocusedElementsChanges) {
                // 	selectedElementIdsLegacyRef.current = selectedElementsIds;
                // 	focusedRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);

                // 	syncSelectedElementsLegacyRef.current(selectedElementsIds);
                // }

                // if (selectedAreaIdLegacyRef.current) {
                // 	selectedAreaIdLegacyRef.current = null;
                // 	syncSelectedAreaLegacyRef.current(null);
                // }
                // -----------------------------

                // update canvas
                // draw();

                // set is board moving | for context menu and etc.
                if (!isBoardMoving) {
                    setIsBoardMoving(true);
                    setIsBoardMovingStarted(true);
                }

                // move with resizing item
            } else if (isItemResizeClickedRef.current) {
                const dx =
                    (event.clientX - startXYRef.current.x) / scaleRef.current;
                const dy =
                    (event.clientY - startXYRef.current.y) / scaleRef.current;

                // get new sizes for resizing item
                const {
                    x: newX,
                    y: newY,
                    width: newWidth,
                    height: newHeight,
                } = handleResize(resizeKeyRef.current, dx, dy);

                // 1. resizing focused area (single area selected)
                const _resizingAreaId = focusedIdsRef.current.length === 1 &&
                    areasRef.current[focusedIdsRef.current[0]]
                    ? focusedIdsRef.current[0]
                    : null;
                if (_resizingAreaId) {

                    // now here
                    const areaId = _resizingAreaId;

                    // update new-clicked area position
                    areasRef.current[areaId].x += newX;
                    areasRef.current[areaId].y += newY;

                    areasRef.current[areaId].width += newWidth;
                    areasRef.current[areaId].height += newHeight;
                    areasRef.current[areaId].isChangedXY = true;

                    /// resize area props

                    const area = areasRef.current[areaId];
                    const point1 = { x: area.x, y: area.y };
                    const point2 = { x: area.x + area.width, y: area.y + area.height };

                    // get selected elements by creating area
                    const selectedElements = getAreaSelectedElementsForResize(
                        point1,
                        point2,
                        elementsRef.current,
                        areasRef.current,
                        viewMode,
                        areaId
                    );

                    resizeAreaPropsRef.current = { areaId, selectedElements };

                    if (!isElementResizing) {
                        setIsElementResizing(true);
                    }

                    // 2. resizing focused element
                } else {
                    const elementId = focusedIdsRef.current[0];
                    const element = elementsRef.current[elementId];

                    if (elementsRef.current[elementId].type !== 'pen-segment') {

                        if (element.type === 'table' && (element.width + newWidth) < 320) {
                            if (newX > 0) {
                                // const newValueX = (element.width + newWidth) - 320;
                                // elementsRef.current[elementId].x += newValueX;
                            } else {
                                elementsRef.current[elementId].x += newX;
                            }

                            // update new-clicked element position
                            // elementsRef.current[elementId].x += newX;


                            // elementsRef.current[elementId].y += newY;

                            elementsRef.current[elementId].width = 320;
                            // elementsRef.current[elementId].height += newHeight;
                            elementsRef.current[elementId].isChangedXY = true;

                        } else {
                            // update new-clicked element position
                            elementsRef.current[elementId].x += newX;
                            if (element.type !== 'table') {
                                elementsRef.current[elementId].y += newY;
                            }

                            // if (elementsRef.current[elementId].type === 'pen-segment') {
                            //     elementsRef.current[elementId].translateX += dx;
                            //     elementsRef.current[elementId].translateY += dy;
                            // }

                            elementsRef.current[elementId].width += newWidth;
                            if (element.type !== 'table') {
                                elementsRef.current[elementId].height += newHeight;
                            }

                            if (element.type === 'note') {
                                if (elementsRef.current[elementId].width < 120) {
                                    const overshootX = 120 - elementsRef.current[elementId].width;
                                    // left-edge handles move x while shrinking — pull x back by the clamped overshoot
                                    if (newX > 0) {
                                        elementsRef.current[elementId].x -= overshootX;
                                    }
                                    elementsRef.current[elementId].width = 120;
                                }
                                if (elementsRef.current[elementId].height < 60) {
                                    const overshootY = 60 - elementsRef.current[elementId].height;
                                    if (newY > 0) {
                                        elementsRef.current[elementId].y -= overshootY;
                                    }
                                    elementsRef.current[elementId].height = 60;
                                }
                            }

                            elementsRef.current[elementId].isChangedXY = true;
                        }

                        // clip (displayHeight) update applies on 's'/'se'/'sw' drags
                        // regardless of whether the width branch above hit the 320
                        // min-width clamp — a se/sw diagonal drag must keep
                        // shrinking/growing the visible height even while width is pinned
                        if (element.type === 'table' && ['s', 'se', 'sw'].includes(resizeKeyRef.current)) {
                            const current = elementsRef.current[elementId].displayHeight
                                ?? elementsRef.current[elementId].height;
                            const next = current + newHeight;
                            const min = heightForRows(1);
                            const max = elementsRef.current[elementId].height;
                            elementsRef.current[elementId].displayHeight = Math.min(Math.max(next, min), max);
                        }

                        if (elementsRef.current[elementId].type === 'table') {
                            // updateElementColumnsTextsOnResize(elementId);
                        }

                        updateLinesByElementsIds([elementId]);

                        if (!isElementResizing) {
                            setIsElementResizing(true);
                        }

                    }
                }

            }

            // BUSINESS LOGIC | HOVERING

            if (isMouseInCanvas) {
                if (hoveredElementIdHeaderRef.current !== newHoveredElementIdHeader) {
                    hoveredElementIdHeaderRef.current = newHoveredElementIdHeader;
                }
                else if (hoveredColumnIdRef.current !== newHoveredInsideElementId) {
                    hoveredColumnIdRef.current = newHoveredInsideElementId;
                    hoveredColumnIndexRef.current = newHoveredInsideElementIndex;
                }
                // hover on Table Circle Area OR direct on Table Circle
                else if (
                    !arraysAreEqual(
                        hoveredCircleAreaRef.current?.elementsIds,
                        newHoveredCircleAreaElementsIds
                    ) ||
                    hoveredCircleRef.current?.elementId !==
                    newHoveredCircleElementId
                ) {
                    hoveredCircleAreaRef.current = {
                        elementsIds: newHoveredCircleAreaElementsIds,
                    };
                    hoveredCircleRef.current = {
                        elementId: newHoveredCircleElementId,
                    };
                    // draw();
                }

                // hover on Table Point Area OR direct on Table Point
                else if (
                    !setsAreEqual(
                        hoveredPointAreaRef.current?.insideElementsIds,
                        newHoveredPointAreaInsideElementsIds
                    ) ||
                    hoveredPointRef.current?.insideElementId !==
                    newHoveredPointInsideElementId ||
                    hoveredPointRef.current?.direction !==
                    newHoveredPointDirection
                ) {
                    hoveredPointAreaRef.current = {
                        insideElementsIds: newHoveredPointAreaInsideElementsIds,
                    };
                    hoveredPointRef.current = {
                        insideElementId: newHoveredPointInsideElementId,
                        direction: newHoveredPointDirection,
                    };
                    // draw();
                }

                // hover on Line
                else if (hoveredLineIdRef.current !== newHoveredLineId) {
                    hoveredLineIdRef.current = newHoveredLineId;
                    hoveredLineRelatedPropsRef.current =
                        getLineRelatedProps(newHoveredLineId);
                    // draw();
                }

                // hover on Element OR Element and Inside Element
                else if (
                    hoveredElementIdRef.current !== newHoveredElementId ||
                    hoveredInsideElementRef.current?.id !== newHoveredInsideElementId ||
                    hoveredInsideElementRef.current?.index !== newHoveredInsideElementIndex ||
                    hoveredElementIdEmptyColumnRef.current !== newHoveredElementIdEmptyColumn
                ) {
                    hoveredElementIdRef.current = newHoveredElementId;
                    hoveredInsideElementRef.current = {
                        id: newHoveredInsideElementId,
                        index: newHoveredInsideElementIndex,
                    };
                    hoveredElementRelatedPropsRef.current =
                        getElementsRelatedProps([newHoveredElementId]);

                    hoveredElementIdEmptyColumnRef.current =
                        newHoveredElementIdEmptyColumn;
                    // draw();
                } else if (hoveredAreaIdRef.current !== newHoveredAreaId) {
                    hoveredAreaIdRef.current = newHoveredAreaId;
                    // draw();
                }
            } else {
                // table circle
                hoveredCircleAreaRef.current = null;
                hoveredCircleRef.current = null;

                // table points
                hoveredPointAreaRef.current = null;
                hoveredPointRef.current = null;

                // line
                hoveredLineIdRef.current = null;
                hoveredLineRelatedPropsRef.current = null;

                // element
                hoveredElementIdRef.current = null;
                hoveredInsideElementRef.current = null;

                hoveredColumnIdRef.current = null;
                hoveredColumnIndexRef.current = null;

                hoveredElementRelatedPropsRef.current = null;

                hoveredElementIdEmptyColumnRef.current = null;

                hoveredElementIdHeaderRef.current = null;

                hoveredAreaIdRef.current = null;
                // draw();
            }

            // BUSINESS LOGIC | CURSOR

            if (isBtnExportImageEnabled) {
                let hoveredExportImageResizeKey =
                    getHoveredResizeKeyOnExportImageBox(mouseX, mouseY);

                // hover on resize points on export image box: style cursor to resize
                if (hoveredExportImageResizeKey) {
                    const cursorMap = {
                        ne: 'nesw',
                        sw: 'nesw',
                        nw: 'nwse',
                        se: 'nwse',
                    };
                    canvas.style.cursor = `${cursorMap[hoveredExportImageResizeKey]}-resize`;
                } else {
                    if (
                        isBoardClickedRef.current ||
                        (isBtnExportImageEnabled &&
                            (exportImageBoxPropsRef.current.isPressed ||
                                exportImageBoxPropsRef.current
                                    .isOutsidePressed))
                    ) {
                        canvas.style.cursor = 'grabbing';
                    } else {
                        canvas.style.cursor = 'grab';
                    }
                }
            } else if (isBtnPenEnabled) {
                // canvas.style.cursor = 'pointer';
                canvas.style.cursor = 'none';

                // hover on resize points if focused only one element: style cursor to resize
            } else if (focusedIdsRef.current.length === 1) {
                let [isHoverOnResize, resizeKey] = isHoverOnResizeHandle(
                    mouseX,
                    mouseY
                );
                const cursorMap = {
                    e: 'ew',
                    w: 'ew',
                    n: 'ns',
                    s: 'ns',
                    ne: 'nesw',
                    sw: 'nesw',
                    nw: 'nwse',
                    se: 'nwse',
                };
                if (isItemResizeClickedRef.current && resizeKeyRef.current) {
                    const cursorMap = {
                        ne: 'nesw',
                        sw: 'nesw',
                        nw: 'nwse',
                        se: 'nwse',
                    };
                    canvas.style.cursor = `${cursorMap[resizeKeyRef.current]}-resize`;
                } else if (
                    // Small deliberate targets keep their own affordance: a
                    // connection point or add-row circle DIRECTLY under the
                    // pointer takes the CLICK over the broad resize bands
                    // (they sit above the resize branch in the mousedown
                    // chain), so the cursor must not promise a resize there —
                    // same hover-matches-click rule the chevron blocks below
                    // apply. Checked via the fresh per-event hit values, NOT
                    // hoveredPointRef/hoveredCircleRef — those are wrapper
                    // objects that stay truthy (with null ids inside) for the
                    // whole hover-display area, i.e. most of the element.
                    newHoveredCircleElementId ||
                    newHoveredPointInsideElementId ||
                    newHoveredPointDirection
                ) {
                    canvas.style.cursor = 'pointer';
                } else if (isHoverOnResize) {
                    canvas.style.cursor = `${cursorMap[resizeKey]}-resize`;
                } else {
                    canvas.style.cursor = 'default';
                }
            } else {
                // if (isCursorHand) {
                // 	if (isBoardClickedRef.current) {
                // 		canvas.style.cursor = 'grabbing';
                // 	} else {
                // 		canvas.style.cursor = 'grab';
                // 	}
                // } else {
                // 	canvas.style.cursor = 'default';
                // }
                if (isBoardClickedRef.current) {
                    canvas.style.cursor = 'grabbing';
                } else if (isCursorHand) {
                    canvas.style.cursor = 'grab';
                } else {
                    canvas.style.cursor = 'default';
                }
            }

            // hover on a clipped table's overflow chevron: pointer cursor
            // (cheap — only tables carrying a displayHeight are checked).
            // Precedence over a focused clipped table's 's'/'se'/'sw'
            // resize-cursor (set by the `focusedIdsRef.current.length === 1`
            // branch above, which — since Fix 3 — positions that handle at
            // the same effective height as the chevron) comes for free from
            // execution order: this block runs AFTER that branch in this same
            // synchronous mousemove pass, so a chevron hit's unconditional
            // `canvas.style.cursor = 'pointer'` always overwrites it — same
            // ordering the note chevron hover block below relies on for its
            // own identical precedence problem; verified, no reordering
            // needed here either.
            // Only when no interaction is in progress — otherwise this would
            // clobber the resize/pan/box-select cursor set above mid-drag
            // (e.g. an 's'-handle drag on a clipped table moves the chevron
            // itself under the pointer, which would stomp the ns-resize cursor).
            // Clipping is full-view only and hidden below
            // OVERFLOW_INDICATOR_MIN_SCALE — same threshold as the draw block
            // and the click block above, so there's never a hoverable/
            // clickable chevron with nothing drawn there.
            if (
                viewMode === 'full' &&
                scaleRef.current > OVERFLOW_INDICATOR_MIN_SCALE &&
                canEditRef.current &&
                !isBoardClickedRef.current &&
                !isSelectionClickedRef.current &&
                !isItemResizeClickedRef.current &&
                !isMovingElementId
            ) {
                for (const elId in elementsRef.current) {
                    const el = elementsRef.current[elId];
                    if (el?.type !== 'table' || el.displayHeight == null) continue;
                    // a stale displayHeight can outlive a collab column delete
                    // that shrank the table below the clip — re-check clipped-ness
                    const keysCount = orderedColumnKeys(el.columns, el.columnsOrder).length;
                    if (!isTableClipped(el, keysCount)) continue;
                    const effH = getTableEffectiveHeight(el);
                    const cx = el.x + el.width / 2;
                    const cy = el.y + effH;
                    // radius kept in lockstep with the draw block's chevronR
                    // (drawTable's "TABLE OVERFLOW INDICATOR" — matches the
                    // note indicator's r=13/scale exactly, see Fix 1)
                    const r = 13 / scaleRef.current;
                    if ((mouseX - cx) ** 2 + (mouseY - cy) ** 2 <= r * r) {
                        canvas.style.cursor = 'pointer';
                        break;
                    }
                }
            }

            // hover on an overflowing note's chevron: pointer cursor (same
            // interaction-state gating family as the table chevron block
            // above; cheap gate first — only notes whose cached layout
            // overflows are checked). NOT gated on this note's own editor
            // being open — see the click handler's own comment on
            // NOTE_EDITOR_BOTTOM_REVEAL. Precedence over the focused note's
            // 's' resize-handle cursor comes for free from execution order:
            // this block runs AFTER the earlier `focusedIdsRef.current.length
            // === 1` branch above (which sets the ns-resize cursor for that
            // handle) in this same synchronous mousemove pass, so a chevron
            // hit's unconditional `canvas.style.cursor = 'pointer'` always
            // overwrites it — verified, no ordering change needed. The
            // `!isItemResizeClickedRef.current` guard here only matters
            // mid-DRAG (mouse held down), where the resize cursor must keep
            // winning instead — same reasoning as the table chevron block's
            // own comment above; a chevron hit can no longer arm that drag
            // in the first place per the click handler's precedence fix.
            // Same OVERFLOW_INDICATOR_MIN_SCALE threshold as the note's draw
            // block and click block, and the table chevron blocks above.
            if (
                viewMode === 'full' &&
                scaleRef.current > OVERFLOW_INDICATOR_MIN_SCALE &&
                canEditRef.current &&
                !isBoardClickedRef.current &&
                !isSelectionClickedRef.current &&
                !isItemResizeClickedRef.current &&
                !isMovingElementId
            ) {
                for (const elId in elementsRef.current) {
                    const el = elementsRef.current[elId];
                    if (el?.type !== 'note') continue;
                    const cachedLayout = noteLayoutCacheRef.current.get(elId);
                    if (!cachedLayout) continue;
                    const innerHeight = el.height - NOTE_PADDING_Y - NOTE_PADDING_Y_BOTTOM;
                    if (cachedLayout.lines.length * NOTE_LINE_HEIGHT <= innerHeight) continue;
                    const cx = el.x + el.width / 2;
                    const cy = el.y + el.height;
                    const r = 13 / scaleRef.current;
                    if ((mouseX - cx) ** 2 + (mouseY - cy) ** 2 <= r * r) {
                        canvas.style.cursor = 'pointer';
                        break;
                    }
                }
            }

            // hover on a note's link run (spec 8b): shows NoteLinkPreview
            // below the zone. Full view only, never mid-interaction/pan/
            // resize/selection-drag or with a drawing tool armed (same
            // interaction-state gating family as the chevron block above,
            // plus the drawing-tool flags), and inert for whichever note is
            // currently open for editing (that note's canvas text sits under
            // the EditableNote overlay's opaque background — nothing of it
            // is actually visible under the pointer there). Change-gated via
            // hoveredNoteLinkKeyRef like every other hoveredXRef in this
            // file — the pub/sub store only gets poked on an actual zone
            // transition, not once per mousemove tick.
            //
            // This block runs off `document`'s mousemove (handleDocumentMouseMove
            // is registered on `document`, not the canvas element — see this
            // effect's own addEventListener call), so it still receives the
            // mousemove that follows the pointer entering NoteLinkPreview's
            // own DOM node (a `position: fixed` sibling rendered by
            // Diagram.jsx, NOT inside the canvas subtree). Left ungated, that
            // event's clientX/clientY translate to a world position the zone
            // hit-test below would almost always MISS (the popover sits
            // BELOW its zone) — arming a grace-hide moments after the
            // popover's own onMouseEnter (noteLinkPopoverEntered) already
            // cleared it, hiding the popover under the user's cursor. Skip
            // this block entirely for such events; the popover's own
            // onMouseEnter/onMouseLeave already own that side of the
            // grace/persistence state machine (noteLinkHoverStore.js).
            //
            // Deliberately NOT gated on `isMouseInCanvas` (the flag the
            // "BUSINESS LOGIC | HOVERING" block above uses) instead: that
            // flag is `ref.current.contains(event.target)` against the bare
            // `<canvas>` element, which — since a `<canvas>` can never have
            // DOM children — is only ever true when `event.target` IS that
            // exact node. It's false for EVERY non-canvas target, not just
            // the popover: the sidebar, a modal, an EditableNote overlay, or
            // simply empty page chrome. Gating this block on it would also
            // suppress the grace-arm this block SHOULD still run when the
            // pointer leaves a link zone for one of THOSE areas (a real
            // "left the zone" that isn't "entered the popover") — swapping
            // one bug (stuck-open-under-cursor) for another (stuck-open
            // indefinitely). A targeted `.closest('.note-link-preview')`
            // check on the event's own target is the precise fix: it
            // excludes only the one case that's actually wrong (the popover
            // itself) and leaves every other miss path — including "left to
            // the sidebar" — working exactly as before.
            if (event.target?.closest?.('.note-link-preview')) {
                // no-op: see the popover's own onMouseEnter/onMouseLeave.
            } else if (
                viewMode === 'full' &&
                scaleRef.current > 0.15 &&
                !isBoardClickedRef.current &&
                !isSelectionClickedRef.current &&
                !isItemResizeClickedRef.current &&
                !isMovingElementId &&
                !isScaling &&
                !isBoardMoving &&
                !isBoardMovingStarted &&
                !isBtnNewTableClickEnabled &&
                !isBtnNewTableGrabEnabled &&
                !isBtnNewNoteClickEnabled &&
                !isBtnNewNoteGrabEnabled &&
                !isBtnNewLineEnabled &&
                !isBtnNewAreaEnabled &&
                !isBtnPenEnabled &&
                !isBtnExportImageEnabled &&
                !isBtnRedGreenViewEnabled
            ) {
                const editingInsideEl = focusedInsideElementStateRef.current;
                let hitZone = null;
                let hitNoteId = null;
                for (const elId in elementsRef.current) {
                    const el = elementsRef.current[elId];
                    if (el?.type !== 'note') continue;
                    if (
                        editingInsideEl?.elementId === elId &&
                        editingInsideEl?.insideElementType === 'note-text'
                    ) continue;
                    const zones = noteLinkZonesRef.current.get(elId);
                    if (!zones || !zones.length) continue;
                    if (
                        mouseX < el.x || mouseX > el.x + el.width ||
                        mouseY < el.y || mouseY > el.y + el.height
                    ) continue;
                    const zone = zones.find((z) =>
                        mouseX >= z.x && mouseX <= z.x + z.w &&
                        mouseY >= z.y && mouseY <= z.y + z.h
                    );
                    if (zone) { hitZone = zone; hitNoteId = elId; break; }
                }

                const hitKey = hitZone ? `${hitNoteId}|${hitZone.href}|${hitZone.x}|${hitZone.y}` : null;
                if (hoveredNoteLinkKeyRef.current !== hitKey) {
                    hoveredNoteLinkKeyRef.current = hitKey;
                    if (hitZone) {
                        // zone's bottom-left -> screen coords
                        const screenX = hitZone.x * scaleRef.current + originXYRef.current.x;
                        const screenY = (hitZone.y + hitZone.h) * scaleRef.current + originXYRef.current.y;
                        noteLinkZoneEntered({ href: hitZone.href, x: screenX, y: screenY });
                    } else {
                        noteLinkZoneLeft();
                    }
                }
            } else if (hoveredNoteLinkKeyRef.current !== null) {
                hoveredNoteLinkKeyRef.current = null;
                hideNoteLinkPreviewImmediately();
            }

            if (clickedPointRef.current || isBtnNewLineEnabled) {
                canvas.style.cursor = 'crosshair';
            }

            if (isBtnNewAreaEnabled) {
                canvas.style.cursor = 'crosshair';
            }

            if (isBtnNewTableClickEnabled) {
                // canvas.style.cursor = 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'20\' height=\'20\'><rect width=\'20\' height=\'20\' fill=\'white\'/></svg>") 10 10, auto';
                // canvas.style.cursor = 'url("data:image/svg+xml,<svg fill=\'#ffffff\' width=\'31\' height=\'31\' viewBox=\'0 0 32 32\' xmlns=\'http://www.w3.org/2000/svg\'><path stroke-width=\'0px\' stroke=\'#ffffff\' d=\'M 27 3 H 5 A 2 2 0 0 0 3 5 V 27 a 2 2 0 0 0 2 2 H 27 a 2 2 0 0 0 2 -2 V 5 A 2 2 0 0 0 27 3 Z m 0 4 V 13 H 5 V 7 Z M 17 15 H 27 v 5 H 17 Z m -2 5 H 5 V 15 H 15 Z M 5 22 H 15 v 5 H 5 Z m 12 5 V 22 H 27 v 5 Z\'></path></svg>" 10 10, auto';
                // document.body.style.cursor = 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'150\' height=\'100\' viewBox=\'0 0 150 100\'><rect x=\'5\' y=\'5\' width=\'140\' height=\'90\' fill=\'white\' stroke=\'black\' stroke-width=\'2\'/><text x=\'10\' y=\'20\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>ID</text><text x=\'50\' y=\'20\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Name</text><text x=\'100\' y=\'20\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Age</text><text x=\'10\' y=\'40\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>1</text><text x=\'50\' y=\'40\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>John</text><text x=\'100\' y=\'40\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>30</text><text x=\'10\' y=\'60\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>2</text><text x=\'50\' y=\'60\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Jane</text><text x=\'100\' y=\'60\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>25</text><text x=\'10\' y=\'80\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>3</text><text x=\'50\' y=\'80\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Alice</text><text x=\'100\' y=\'80\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>28</text></svg>") 10 10, auto';
                // canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAcCAYAAAB/E6/TAAAAAXNSR0IArs4c6QAABHZJREFUSA2VVl1Im1cYzo/5szXTON1We6FOL2REnBuudtVNVxV64ypDxYrMi2GQgiAMtwsZFLwq65X0SreqI4gbJYZljd28EOffcOq0TOYfrKJumRloXRrTxOx5Pvw+E2O+xBce3vP+nPN+73vOe86nUJyfPsGUQeAukBwyPQljdYgcNoxqCPM6EUpyc3M/Wlpaura3t7c/Pz+vh2ke+Fyn07UGg8GbwB+QXSdTYo8S4PIZ8B1wDzABt7u7ux9hseDs7OzCsf5KZmamNRAI+Pr7+x9D9wUQQaoIzYmiqrS01Hx4eHijpaUlA+oPgQsmk8lAl/T09FSwFKAA9osqlUqTl5f3KmSWUwmEkVygrLq6OpNWqzV0dnZexawyQOX3+4U5SUlJRsgXgZySkpLL4Irt7e3/wA4AMZCOelK0QHR87nK5juiUkZFxOTU1lftxyefzBagzGo0Mkghk5efn51C3urrKQG4gH3igVqut4LcA2UB/z8zMHNKJVFlZSZbl9XqF4CiVCgfgKC0tLYjseOIUy8vLXjA/TJ9OTk6+gQzfgXyTtmgZ0bY+NzcnncrGxkaWR31wcCBkRIfExER/YWGhEJjy2traC7Bii8WiLS4ufnt3d/cZZEIdLVAQxqconWofhLGioqLiTeyXH6KfMiklJeWovLxcyIbyxsaGIiEhwdjV1fU+5YGBgQ2w3wBVtED044LrExMT6xQ0Go2uurrah0BSRjU1NbqGhgYz7aSdnR19a2trIDk5+WXKPT09zHacYzliH1na2tqEvmHvuN3uf7AP6xyfJvTRi+zs7Ico7T5t09PTy5g/APB0SluAcQTR+F5BQYHt9KLxyE1NTQ7MtwA8mUp+NYlReQwvAX8CTmAbWFtZWdGAn5tsNhtbZALgFgTFPbqDEpWNjo6WdXR0VBkMhi9h/BjY93g8CvSOdMyhi0mbm5t/YS/ZU/xoaU8Z+asnILEkuDDd6JtvoO8GHLzHRFs8fHx8/Anm3QeEsoELfcSsvi4qKlqdmppapBJdbxoZGbnV29ubU19f7+U9Rn28tLi4+C98WXpmw1YRiPvEPbqOK8NmtVon4/lqOZ/m5mYehAYg7AOZEW/kdOBdpVI5PDQ09IvcQrFsuBvtWOsKEHGsqbgAvAKUIrPvnU7nQqwFz7LjYn2KNb4FXgLEw4bhCTEYN4+ZVeEqeTQ2Nvb7WYvJ6Wpra1m2tuO1eNDOJDEYM6vG3ea02+1xZzY8PMxn/SHwOqAFZCm0jDfgaW9vb/8Jz4NHLpPBwcFZPBts9OsADxfXiUmhwd6C9z29Xv8YF+aPDodjYWtry4Um9pH39fXNmM3mH+DDfakAWHo+kmFlCxNgDCUG41PMQ8LX9DXgAyAT4P8Cv5rdz83/FfgZ4JPC94cPoHQjYBwelYpTxFPDWvML2QLk7DuxLGxGLugDPMBzICIIdDEDiT5cnGADEgzEajAQL00G4utKSC8uxhLJlU5yOh7QlxmKEO1cWLxqpOtGNIr8PIHEOeSh86IuHjrhf75uE+FNCrMEAAAAAElFTkSuQmCC") 14 16, auto`;
                canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAVFBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////////////////////////////8wXzyWAAAAG3RSTlMACAkRGFVmdXZ3eIiZpcDJzNfd5Ofs7e/3+vucN3j2AAAAAWJLR0QbAmDUpAAAAH5JREFUKM/NkzcOgEAMBM2Rcw53+/9/UoBdmSABElOtNIW9kk30Ap6v4bFOHTRcsmmja8AZIiIKcESw+6XRWMS36tbtb/wQaQzir/qPOVOhkjwq8zNkp/t94+eCqVFLnm/360OmRCm5/0k/42zHTJgkW7vdLyX6gdv45v88YQXHUR7h7PdO0gAAAABJRU5ErkJggg==") 14 16, auto`;
            }
            if (isBtnNewNoteClickEnabled) {
                canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAYAAADE6YVjAAACHElEQVR4AeyUu0srQRTGzcMNuUWyCQS0udfrH3DrCxcsLoQVLdVYWIgWViqC4qtRS7GxUBALtY4otqZS0P9AbKzUImIgjyKYd/ydRYfEx8pG1MblfHtmZ+d838yZmeNs+oTna0Si0aiWSqV+JRKJVruQOIl/mpy6lSSTyZ+GYawwaM/tdh/ahcSFw+Fl4aGtTIlUq1UN0iWHw9EHjpxO56ZdwHpMTD9YED6+TVMiLLWlUqn8A1u6rk/5/f41uwgEApNMcBuRv/F4vNVU4KVEPB5PEXU3fdegYSuVStflctnDBAuPJErkseMj/LeIrax+p+td6aoSLcC9bVzgtkwm051Op2dpTwq4iB0SyaVsFi9oeE8gbOPyrkNyAPEgpIP4IXwnfVderzeHN61hEQQMSP9Thvqy2WxHsVjs5LbvI/ID5h38Ld60hkUQaEfoPJfLnbpcrjtN04bx04guUPt2TfaH11MRB/0C3JtWQaRA5W7mmYB8nu95atYqq7irjVYi+XxeNqpEcWupHWDRFhEXhXUM0lnGzem6vk47T7vOlAhl+oYUXLDkHjZ1kVMzagXI/jC2nUmN055GYAP/TEDUlAgDCgTM0HlLCnpZ/ogVGPeb9JzgB3w+3zbxLwrwv0mJyEcwGDyLxWJdnJJuYFiBCXUx+xEycIBA3R4IVy3qRORHJBIpEHgZCoXiVmBCV5CrYyqxr+GZyGsD39N/DwAA//9XLutcAAAABklEQVQDAAwjVEKAupSDAAAAAElFTkSuQmCC") 14 16, auto`;
            }
            // set new value to startXY for next 'mousemove' event
            startXYRef.current = { x: event.clientX, y: event.clientY };

            if (isMouseInCanvas || scaleRef.current > 0.5 || isSelectionClickedRef.current ||
                exportImageBoxPropsRef.current.clickedResizeKey ||
                exportImageBoxPropsRef.current.isPressed ||
                exportImageBoxPropsRef.current.isOutsidePressed
            ) {
                scheduleDraw();
            }
        };

        const handleDocumentClick = (e) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            // e.preventDefault();
            const event = e.type === 'touchstart' ? e.changedTouches[0] : e;

            if (isHistoryClickedState) {
                setIsHistoryClickedState(false);
                dispatch(setIsHistoryClicked(false));
            }

            if (!canEditRef.current) return;

            if (isBtnPenEnabled && newPenSegmentRef.current.isPressed) {
                const newPenSegment = buildNewPenSegment(
                    newPenSegmentRef.current.points
                );
                // addElement(getDiagramDoc(), newPenSegment);
                if (checkPens(elementsRef.current)) {
                    addElement(getDiagramDoc(), newPenSegment);
                } else {
                    dispatch(enableCanvasPricingPlanLimit('pens'));
                }

                newPenSegmentRef.current.isPressed = false;
                unsetAllVariablesOnClick();

                return;
            }

            // inside click in canvas root elements tree
            if (ref.current && ref.current.contains(event.target)) {
                if (isBtnNewTableClickEnabled) {
                    // const mouseX = (event.clientX - originXYRef.current.x) / scaleRef.current;
                    // const mouseY = (event.clientY - originXYRef.current.y) / scaleRef.current;
                    const mouseX =
                        (event.clientX -
                            originXYRef.current.x -
                            160 * scaleRef.current) /
                        scaleRef.current;
                    const mouseY =
                        (event.clientY -
                            originXYRef.current.y -
                            40 * scaleRef.current) /
                        scaleRef.current;

                    const { newTable: newElement, firstColumn } = buildNewTable(mouseX, mouseY);

                    // addElement(getDiagramDoc(), newElement); addColumn(...)
                    if (checkTables(elementsRef.current)) {
                        const doc = getDiagramDoc();
                        addElement(doc, newElement);
                        addColumn(doc, { tableId: newElement.id, mode: 'end', column: firstColumn });
                        focusedIdsRef.current = [newElement.id];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([newElement.id]));
                        dispatch(updateFocusedInsideElement(newElement.id, null, null, 'header'));
                    } else {
                        dispatch(enableCanvasPricingPlanLimit('tables'));
                    }
                    dispatch(disableBtnNewTableClick());

                    isBoardClickedRef.current = false;
                    isSelectionClickedRef.current = false;

                    unsetAllVariablesOnClick();

                    return;
                } else if (isBtnNewNoteClickEnabled) {
                    // const mouseX = (event.clientX - originXYRef.current.x) / scaleRef.current;
                    // const mouseY = (event.clientY - originXYRef.current.y) / scaleRef.current;
                    const mouseX =
                        (event.clientX -
                            originXYRef.current.x -
                            160 * scaleRef.current) /
                        scaleRef.current;
                    const mouseY =
                        (event.clientY -
                            originXYRef.current.y -
                            40 * scaleRef.current) /
                        scaleRef.current;

                    const newElement = buildNewNote(mouseX, mouseY);

                    if (checkNotes(elementsRef.current)) {
                        addElement(getDiagramDoc(), newElement);
                        focusedIdsRef.current = [newElement.id];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([newElement.id]));
                        // note-text with no noteOpenClickPointRef set — safe
                        // (end-of-text on a brand-new empty note) only
                        // because ENABLE_BTN_NEW_NOTE_CLICK's reducer already
                        // called unfocusFocusedItems() before this ran. See
                        // noteOpenClickPointRef's own comment above.
                        dispatch(updateFocusedInsideElement(newElement.id, null, null, 'note-text'));
                    } else {
                        dispatch(enableCanvasPricingPlanLimit('notes'));
                    }

                    dispatch(disableBtnNewNoteClick());

                    isBoardClickedRef.current = false;
                    isSelectionClickedRef.current = false;

                    unsetAllVariablesOnClick();

                    return;
                }
            }

            // if (isNewTableGrabStartedRef.current) {
            // 	const mouseX = (event.clientX - originXYRef.current.x - 160 * scaleRef.current) / scaleRef.current;
            // 	const mouseY = (event.clientY - originXYRef.current.y - 40 * scaleRef.current) / scaleRef.current;

            // 	const newElement = buildNewTable(mouseX, mouseY);
            // 	dispatch(addNewElement(newElement));
            // 	// dispatch(disableBtnNewTableClick());
            // 	// cursor = default;
            // 	// open Context Menu; (isEditElement = true)
            // 	// focusedElementsIds = [new_table_id];

            // 	isNewTableGrabStartedRef.current = false;
            // 	return;

            // } else if (isNewNoteGrabStartedRef.current) {
            // 	const mouseX = (event.clientX - originXYRef.current.x - 160 * scaleRef.current) / scaleRef.current;
            // 	const mouseY = (event.clientY - originXYRef.current.y - 40 * scaleRef.current) / scaleRef.current;

            // 	const newElement = buildNewNote(mouseX, mouseY);
            // 	dispatch(addNewElement(newElement));

            // 	// dispatch(disableBtnNewNoteClick());
            // 	// cursor = default;
            // 	// open Context Menu; (isEditElement = true)
            // 	// focusedElementsIds = [new_table_id];

            // 	isNewNoteGrabStartedRef.current = false;
            // 	return;
            // }

            // business logic | CLICK MOUSE UP
            let isLineCreated = false;
            if (isBtnNewLineEnabled) {
                // 1. line has no points 'from'/'to'
                if (!lineFromRef.current) {
                    // nothing
                    // 2. line has 'from' point
                } else if (lineFromRef.current && !lineToRef.current) {
                    lineFromRef.current.isClicked = true;

                    // 3. line has 'from' and 'to' points
                } else if (
                    lineFromRef.current?.elementId &&
                    lineToRef.current?.elementId
                ) {
                    if (
                        lineToRef.current.insideElementId &&
                        lineFromRef.current.insideElementId !==
                        lineToRef.current.insideElementId
                    ) {
                        const newLine = buildNewLine();
                        addLine(getDiagramDoc(), newLine);
                        isLineCreated = true;

                        // Exit new-line creation mode and focus the freshly created
                        // line. Without this the button stays toggled on, so the flow
                        // never closes and the user keeps drawing more lines.
                        dispatch(toggleBtnNewLine());
                        dispatch(updateFocusedIds([newLine.id]));

                        const hasFocusedInsideElementChanges =
                            !!focusedInsideElementState;
                        if (hasFocusedInsideElementChanges) {

                            setFocusedInsideElementState(null);
                            // setColumnNameState('');
                            // setColumnTypeState('');

                            syncFocusedInsideElementToReduxRef.current(null);
                            // draw();
                        }
                    }
                }
                //
            } else if (isBtnNewAreaEnabled) {
                if (newAreaPropsRef.current) {
                    if (
                        Math.abs(
                            newAreaPropsRef.current.endX -
                            newAreaPropsRef.current.startX
                        ) < 10 ||
                        Math.abs(
                            newAreaPropsRef.current.endY -
                            newAreaPropsRef.current.startY
                        ) < 10
                    ) {
                        // nothing;
                    } else {
                        if (newAreaPropsRef.current) {
                            const newArea = buildNewArea();
                            // addArea(getDiagramDoc(), newArea);
                            if (checkAreas(areasRef.current)) {
                                addArea(getDiagramDoc(), newArea);
                            } else {
                                dispatch(enableCanvasPricingPlanLimit('areas'));
                            }

                            dispatch(disableBtnNewArea());
                        }
                    }
                }

                newAreaPropsRef.current = null;

                // draw();

                // 0. click on column's point
            } else if (clickedPointRef.current) {
                if (
                    // 'elementTo' is valid
                    lineToRef.current?.elementId &&
                    // 'insideElementTo' is valid
                    lineToRef.current?.insideElementId &&
                    // line is between two different columns, not one
                    lineFromRef.current?.insideElementId !==
                    lineToRef.current?.insideElementId
                ) {
                    const newLine = buildNewLine();
                    addLine(getDiagramDoc(), newLine);

                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                }

                // 0. click on circle to add new row
            } else if (clickedCircleElementIdRef.current) {
                const tableId = clickedCircleElementIdRef.current;
                if (checkColumns(elementsRef.current, tableId)) {
                    const newColumnId = nanoid();
                    const colOrder = elementsRef.current[tableId]?.columnsOrder ?? [];
                    const newColumnIndex = colOrder.length;
                    addColumn(getDiagramDoc(), { tableId, mode: 'end', column: { id: newColumnId, name: 'column_new', type: 'int' } });
                    // Focus the freshly added column so its name is selected and ready to edit.
                    dispatch(updateFocusedInsideElement(tableId, newColumnId, newColumnIndex, 'column'));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('columns'));
                }


                // 1. click on a element => static click on an element |OR| move and click on an element
            } else if (clickedElementIdRef.current) {
                // static click on an element
                if (!isMovingElementId) {
                    // static click on a new element => focus on new clicked element
                    if (
                        !focusedIdsRef.current.includes(
                            clickedElementIdRef.current
                        )
                    ) {
                        const hasFocusedInsideElementChanges =
                            !!focusedInsideElementState;
                        if (hasFocusedInsideElementChanges) {

                            setFocusedInsideElementState(null);
                            // setColumnNameState('');
                            // setColumnTypeState('');

                            syncFocusedInsideElementToReduxRef.current(null);
                            // draw();
                        }

                        // const hasFocusedElementsChanges = true;
                        focusedIdsRef.current = [clickedElementIdRef.current];
                        focusedRelatedPropsRef.current =
                            getElementsRelatedProps([
                                clickedElementIdRef.current,
                            ]);
                        focusedLineRelatedPropsRef.current = null;

                        dispatch(updateFocusedIds([clickedElementIdRef.current]));

                        // static click on an one focused element => open column context menu
                    } else if (
                        focusedIdsRef.current.length === 1 &&
                        focusedIdsRef.current[0] ===
                        clickedElementIdRef.current
                    ) {
                        if (clickedInsideElementRef.current) {
                            const hasFocusedInsideElementChanges =
                                !focusedInsideElementState ||
                                focusedInsideElementState.elementId !==
                                clickedInsideElementRef.current.elementId ||
                                focusedInsideElementState.insideElementId !==
                                clickedInsideElementRef.current
                                    .insideElementId;
                            if (hasFocusedInsideElementChanges) {

                                if (columnState && columnNameState) {
                                    const _el = elementsRef.current[columnState.elementId];
                                    if (_el) {
                                        if (columnState?.insideElementType === 'header') {
                                            _el.name = columnNameState;
                                        } else if (_el.columns?.[columnState.insideElementId]) {
                                            _el.columns[columnState.insideElementId].name = columnNameState;
                                            _el.columns[columnState.insideElementId].type = columnTypeState;
                                        }
                                    }
                                }

                                // click on an already-focused note opens its
                                // text editor (insideElementType 'note-text',
                                // set above in getNewClickedElements) — stash
                                // where the user clicked so EditableNote's
                                // mount effect can land the caret there
                                // instead of end-of-text.
                                if (clickedInsideElementRef.current.insideElementType === 'note-text') {
                                    noteOpenClickPointRef.current = {
                                        x: event.clientX,
                                        y: event.clientY,
                                    };
                                }

                                setFocusedInsideElementState({
                                    ...clickedInsideElementRef.current,
                                });
                                syncFocusedInsideElementToReduxRef.current(
                                    clickedInsideElementRef.current.elementId,
                                    clickedInsideElementRef.current
                                        .insideElementId,
                                    clickedInsideElementRef.current.index,
                                    clickedInsideElementRef.current
                                        .insideElementType
                                );

                                setColumnNameState(
                                    clickedInsideElementRef.current
                                        ?.insideElement?.name || ''
                                );
                                setColumnTypeState(
                                    clickedInsideElementRef.current
                                        ?.insideElement?.type || ''
                                );
                                setColumnState({
                                    ...clickedInsideElementRef.current,
                                });

                                // draw();
                            }
                        } else {
                            const hasFocusedInsideElementChanges =
                                !!focusedInsideElementState;
                            if (hasFocusedInsideElementChanges) {

                                setFocusedInsideElementState(null);
                                // setColumnNameState('');
                                // setColumnTypeState('');

                                syncFocusedInsideElementToReduxRef.current(
                                    null
                                );
                                // draw();
                            }
                        }

                        // static click on an one of the many focused elements => focus on one clicked element
                    } else {
                        const hasFocusedInsideElementChanges =
                            !!focusedInsideElementState;
                        if (hasFocusedInsideElementChanges) {

                            setFocusedInsideElementState(null);
                            // setColumnNameState('');
                            // setColumnTypeState('');


                            syncFocusedInsideElementToReduxRef.current(null);
                            // draw();
                        }

                        focusedIdsRef.current = [clickedElementIdRef.current];
                        focusedRelatedPropsRef.current =
                            getElementsRelatedProps([
                                clickedElementIdRef.current,
                            ]);
                        focusedLineRelatedPropsRef.current = null;

                        dispatch(updateFocusedIds([
                            clickedElementIdRef.current,
                        ]));
                    }

                    // move and click on an element
                } else {
                    // nothing
                    // if move and click on new-clicked element => unfocus and move element on 'mousemove'
                    // if move and click on focused element => move focused elements on 'mousemove'
                }

                // 2. click on item resize points
            } else if (isItemResizeClickedRef.current) {
                // resize of a table's bottom handle ends on a transient
                // `displayHeight` (see the resize-drag branch above) — snap it
                // to a whole-row height and persist, or drop it entirely if
                // the drag reached natural height (no more clipping).
                const resizedElementId = focusedIdsRef.current[0];
                const resizedEl = elementsRef.current[resizedElementId];
                if (
                    resizedEl?.type === 'table' &&
                    resizedEl.displayHeight != null &&
                    ['s', 'se', 'sw'].includes(resizeKeyRef.current)
                ) {
                    const totalColumns = orderedColumnKeys(resizedEl.columns, resizedEl.columnsOrder).length;
                    const snapped = snapTableDisplayHeight(resizedEl.displayHeight, resizedEl, totalColumns);
                    if (snapped == null) {
                        delete resizedEl.displayHeight;
                    } else {
                        resizedEl.displayHeight = snapped;
                    }
                    setTableDisplayHeight(getDiagramDoc(), resizedElementId, snapped);
                    updateLinesByElementsIds([resizedElementId]);
                    scheduleDraw();
                }
            } else if (clickedLineIdRef.current) {
                focusedIdsRef.current = [clickedLineIdRef.current];
                focusedRelatedPropsRef.current = null;
                focusedLineRelatedPropsRef.current = getLineRelatedProps(
                    clickedLineIdRef.current
                );
                dispatch(updateFocusedIds([clickedLineIdRef.current]));
            } else if (clickedAreaIdRef.current) {
                const staticClickWithSelection =
                    // static click on the board with mouse/selectionBox
                    isSelectionClickedRef.current &&
                    selectionStartXYRef.current.x ===
                    selectionCurrentXYRef.current.x &&
                    selectionStartXYRef.current.y ===
                    selectionCurrentXYRef.current.y;

                // static click on an element
                if (
                    staticClickWithSelection ||
                    (!isSelectionClickedRef.current &&
                        isMovingElementId !== clickedAreaIdRef.current)
                ) {
                    if (focusedIdsRef.current.includes(clickedAreaIdRef.current)) {
                        // toggle off — clear selection
                        if (focusedIdsRef.current.length) {
                            focusedIdsRef.current = [];
                            focusedRelatedPropsRef.current = null;
                            focusedLineRelatedPropsRef.current = null;
                            dispatch(updateFocusedIds([]));
                        }
                    } else {
                        if (!shouldUnfocusElementsOnClickOnAreaRef.current) {
                            focusedIdsRef.current = [clickedAreaIdRef.current];
                            focusedRelatedPropsRef.current = null;
                            focusedLineRelatedPropsRef.current = null;
                            dispatch(updateFocusedIds([clickedAreaIdRef.current]));
                        }
                    }
                } else {
                    if (focusedIdsRef.current.includes(clickedAreaIdRef.current)) {
                        // nothing. stay with focus on focused elements which selected with selectionBox on 'mousemove'
                    } else {
                        if (focusedInsideElementState) {
                            setFocusedInsideElementState(null);
                            syncFocusedInsideElementToReduxRef.current(null);
                        }

                        // box-select completes — areas selected (with contained elements)
                        if (selectedAreasIdsRef.current?.length) {
                            const areaIds = [...selectedAreasIdsRef.current];
                            const elementIds = [...selectedElementsIdsRef.current];
                            const containedIds = areaIds
                                .flatMap(aId =>
                                    Object.values(elementsRef.current)
                                        .filter(el => el.areaId === aId && el.type !== 'area')
                                        .map(el => el.id)
                                )
                                .filter(id => !elementIds.includes(id));
                            focusedIdsRef.current = [...areaIds, ...elementIds, ...containedIds];
                            focusedRelatedPropsRef.current = getElementsRelatedProps(
                                [...elementIds, ...containedIds]
                            );
                            focusedLineRelatedPropsRef.current = null;
                            dispatch(updateFocusedIds(focusedIdsRef.current));
                        } else {
                            // box-select completes — elements only
                            focusedIdsRef.current = [...selectedElementsIdsRef.current];
                            focusedRelatedPropsRef.current = getElementsRelatedProps(
                                selectedElementsIdsRef.current
                            );
                            focusedLineRelatedPropsRef.current = null;
                            dispatch(updateFocusedIds(focusedIdsRef.current));
                        }
                        // -----------------------------
                    }
                }

                // 3. click on the board => static click on the board |OR| move and click on the board (hand => drag) (mouse => selectionBox)
            } else if (isBoardClickedRef.current) {
                // 3.1 static click on the board
                if (!isBoardMovingStarted) {
                    // unfocus focused elements
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    if (focusedIdsRef.current.length) {
                        focusedIdsRef.current = [];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([]));
                    }

                    handleCursorClick();

                    // 3.2 move and click on the board (hand => drag)
                } else {
                    // nothing. stay with focus on focused elements before dragging board
                }

                // 3.3 move and click on the board (mouse => selectionBox)
            } else if (isSelectionClickedRef.current) {
                // 3.3.1 static click on the board with mouse/selectionBox
                if (
                    selectionStartXYRef.current.x ===
                    selectionCurrentXYRef.current.x &&
                    selectionStartXYRef.current.y ===
                    selectionCurrentXYRef.current.y
                ) {
                    // unfocus focused elements
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    if (focusedIdsRef.current.length) {
                        focusedIdsRef.current = [];
                        focusedRelatedPropsRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds([]));
                    }

                    handleCursorClick();
                } else {
                    // box-select completes — set selection from box

                    // ----------------------------- (added by )
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {
                        setFocusedInsideElementState(null);
                        syncFocusedInsideElementToReduxRef.current(null);
                    }

                    // box-select completes — areas selected (with contained elements)
                    if (selectedAreasIdsRef.current?.length) {
                        const areaIds = [...selectedAreasIdsRef.current];
                        const elementIds = [...selectedElementsIdsRef.current];
                        const containedIds = areaIds
                            .flatMap(aId =>
                                Object.values(elementsRef.current)
                                    .filter(el => el.areaId === aId && el.type !== 'area')
                                    .map(el => el.id)
                            )
                            .filter(id => !elementIds.includes(id));
                        focusedIdsRef.current = [...areaIds, ...elementIds, ...containedIds];
                        focusedRelatedPropsRef.current = getElementsRelatedProps(
                            [...elementIds, ...containedIds]
                        );
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds(focusedIdsRef.current));
                    } else {
                        // box-select completes — elements only
                        focusedIdsRef.current = [...selectedElementsIdsRef.current];
                        focusedRelatedPropsRef.current = getElementsRelatedProps(
                            selectedElementsIdsRef.current
                        );
                        focusedLineRelatedPropsRef.current = null;
                        dispatch(updateFocusedIds(focusedIdsRef.current));
                    }
                    // -----------------------------
                }
            }

            unsetAllVariablesOnClick();

            // // unset all variables which was clicked on 'mousedown'
            // if (alignmentLinesRef.current) {
            // 	alignmentLinesRef.current = null;
            // }

            // if (clickedPointRef.current) {
            // 	clickedPointRef.current = null;
            // }

            // Line drawing ends on mouse-up. Reset so the inline column/header editor
            // (gated on !isStartedLineState) can open on the next click — otherwise after
            // creating a line, clicking a column focuses it but the editor never shows.
            // Unconditional so it doesn't depend on a possibly-stale closure value.
            setIsStartedLineState(false);

            if (
                lineFromRef.current &&
                (!isBtnNewLineEnabled || isLineCreated)
            ) {
                lineFromRef.current = null;
            }

            if (lineToRef.current && (!isBtnNewLineEnabled || isLineCreated)) {
                lineToRef.current = null;
            }

            // if (clickedCircleElementIdRef.current) {
            // 	clickedCircleElementIdRef.current = null;
            // }

            // if (clickedLineIdRef.current) {
            // 	clickedLineIdRef.current = null;
            // }

            // if (clickedAreaIdRef.current) {
            // 	clickedAreaIdRef.current = null;
            // }

            // if (clickedElementIdRef.current) {
            // 	clickedElementIdRef.current = null;
            // }
            // if (clickedInsideElementRef.current) {
            // 	clickedInsideElementRef.current = null;
            // }

            // if (isBoardClickedRef.current) {
            // 	isBoardClickedRef.current = false;
            // }
            // if (isSelectionClickedRef.current) {
            // 	isSelectionClickedRef.current = false;
            // }
            // if (isItemResizeClickedRef.current) {
            // 	isItemResizeClickedRef.current = false;
            // }

            // if (isMovingElementId) {
            // 	setIsMovingElementId(null);
            // }
            // if (isBoardMoving) {
            // 	setIsBoardMoving(false);
            // }
            // if (isBoardMovingStarted) {
            // 	setIsBoardMovingStarted(false);
            // }

            // if (exportImageBoxPropsRef.current.isPressed) {
            // 	exportImageBoxPropsRef.current.isPressed = false;
            // }

            // if (exportImageBoxPropsRef.current.isOutsidePressed) {
            // 	exportImageBoxPropsRef.current.isOutsidePressed = false;
            // }

            // if (exportImageBoxPropsRef.current.clickedResizeKey) {
            // 	exportImageBoxPropsRef.current.clickedResizeKey = false;
            // }

            ///////////
            // Recompute which elements belong to which area based on final positions.
            // Done here (commit time) so mid-drag the nested-element set stays frozen.
            recomputeElementAreaMembership();

            ///////////
            // update stored elements positions
            const updatedElements = {};
            const changedAreaIdElements = [];
            for (const key in elementsRef.current) {
                const element = elementsRef.current[key];
                if (element.isChangedXY) {
                    updatedElements[element.id] = {
                        x: element.x,
                        y: element.y,

                        translateX: element.translateX,
                        translateY: element.translateY,

                        width: element.width,
                        height: element.height,
                    };
                }

                if (element.isChangedAreaId) {
                    changedAreaIdElements.push({ id: element.id, areaId: element.areaId });
                }
            }

            const updatedLines = getUpdatedLinesByElementsIds(
                Object.keys(updatedElements)
            );

            ///////////
            // update stored areas positions
            const updatedAreas = {};
            for (const key in areasRef.current) {
                const area = areasRef.current[key];
                if (area.isChangedXY) {
                    let updatedX = area.x;
                    let updatedY = area.y;
                    let updatedWidth = area.width;
                    let updatedHeight = area.height;

                    if (area.width < 0) {
                        updatedX = area.x + area.width;
                        updatedWidth = Math.abs(area.width);
                    }
                    if (area.height < 0) {
                        updatedY = area.y + area.height;
                        updatedHeight = Math.abs(area.height);
                    }

                    updatedAreas[area.id] = {
                        x: updatedX,
                        y: updatedY,
                        width: updatedWidth,
                        height: updatedHeight,
                        nestedElementsIds: resizeAreaPropsRef.current?.selectedElements || [],
                        // now here 2
                    };
                }
            }

            {
                // Single atomic transaction: areas (position + size), element positions,
                // and areaId membership changes — remote peers never see intermediate state.
                const areaMoves = Object.entries(updatedAreas).map(([id, a]) => ({
                    id, x: a.x, y: a.y, width: a.width, height: a.height,
                }));
                // NOTE: previously diffed `el.width`/`el.height` against
                // `elementsRef.current[id]` to decide whether to include them, but `el`
                // was built FROM `elementsRef.current[id]` moments earlier in this same
                // synchronous pass (see `updatedElements` above) with no mutation in
                // between — the comparison always evaluated false, so width/height were
                // never sent to the doc on drag-resize. Sending them unconditionally
                // (like x/y already were) is a no-op for pure moves (unchanged value)
                // and fixes persistence for resizes.
                const elementUpdates = Object.entries(updatedElements).map(([id, el]) => ({
                    id, x: el.x, y: el.y, width: el.width, height: el.height,
                }));
                moveAreasAndElements(getDiagramDoc(), {
                    areaMoves,
                    elementUpdates,
                    areaIdUpdates: changedAreaIdElements,
                });
            }



            resizeAreaPropsRef.current = null;
            resizeElementPropsRef.current = null;

            scheduleDraw();
        };

        const handleCanvasWheel = (event) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            event.preventDefault();

            // spec 8b: zoom AND wheel-pan both flow through this one handler
            // — either invalidates the popover's screen-space anchor, so hide
            // it instantly rather than waiting for the mousemove miss/grace.
            hoveredNoteLinkKeyRef.current = null;
            hideNoteLinkPreviewImmediately();

            // "Mouse or Trackpad" input-mode preference (inputModeRef, see its
            // declaration above for the ref-mirror rationale). Trackpad mode
            // reroutes a PLAIN wheel (no ctrl/meta) to PAN instead of zoom — a
            // two-finger trackpad scroll is delivered as an ordinary wheel
            // event, so without this branch trackpad users could never pan
            // without the space+drag / hand-tool workaround. ctrl/meta+wheel is
            // how browsers report a trackpad PINCH gesture (also how a mouse
            // wheel spells "zoom" if the user holds ctrl) — that always falls
            // through to the existing zoom logic below, byte-for-byte
            // unchanged. Mouse mode (and any unrecognized future input_mode
            // value) never takes this branch, so mouse users keep exactly
            // today's wheel-always-zooms behavior no matter what.
            if (inputModeRef.current === 'trackpad' && !event.ctrlKey && !event.metaKey) {
                // Pan in screen pixels, sign/accumulation matched to the
                // mousedown-drag pan path further down in this same effect
                // (`dx = event.clientX - startXYRef.current.x;
                // originXYRef.current.x += dx`) — same "origin += delta"
                // update, just fed a wheel-derived delta instead of a
                // pointer-position delta. Negated because a two-finger swipe
                // up (content follows the fingers, like every other trackpad
                // app) reports a positive deltaY, and the content must move up
                // on screen in response — i.e. origin.y decreases, so
                // dy = -deltaY.
                // Damping below 1 slows the pan relative to the raw wheel
                // deltas (user-tuned; raise toward 1 for faster).
                const TRACKPAD_PAN_SPEED = 0.75;
                const dx = -event.deltaX * TRACKPAD_PAN_SPEED;
                const dy = -event.deltaY * TRACKPAD_PAN_SPEED;

                // Hard pan limits: clamp the RESULT before committing it —
                // NOT the delta. Each wheel tick's dx/dy is a small
                // increment on top of the ALREADY-clamped previous
                // originXYRef.current, so once pinned, further wheel
                // events in the blocked direction keep recomputing a
                // newOrigin just past the limit and get re-clamped back to
                // the same pinned value — it feels like hitting a wall,
                // not a rubber-band, and never drifts/accumulates.
                const newOriginX = clampOriginForAxis('x', originXYRef.current.x + dx);
                const newOriginY = clampOriginForAxis('y', originXYRef.current.y + dy);

                originXYRef.current = { x: newOriginX, y: newOriginY };
                syncOriginXYToReduxRef.current(newOriginX, newOriginY);

                // Hide the DOM overlays (EditableNote/EditableTableHeader)
                // while wheel-panning, via the SAME transient isScaling gate +
                // trailing timer the wheel-zoom path below uses — user
                // decision: pan and zoom must treat an open editor
                // identically (an earlier version kept the overlays mounted
                // and origin-synced through the pan instead). isScaling is
                // used rather than the mousedown-drag pan flags
                // (isBoardMoving/isBoardMovingStarted) because those belong
                // to the pointer-drag gesture state machine — setting them
                // from a wheel stream with no mouseup to close the gesture
                // risks confusing that logic, while isScaling is only ever
                // set here and cleared by its own timer. Known accepted
                // quirk: momentum/inertial scrolling delivers trailing
                // events with growing gaps, so near the end of a swipe the
                // overlay can remount and hide again — the same flap a slow
                // notched-wheel zoom already produces in mouse mode.
                // EditableNote's caret consume-and-clear logic explicitly
                // handles these isScaling remounts (caret lands at
                // end-of-text, same as wheel-zoom).
                if (!isScaling) {
                    setIsScaling(true);
                }
                clearTimeout(scalingTimerRef.current);
                scalingTimerRef.current = setTimeout(() => {
                    setIsScaling(false);
                }, 200);

                sumOriginDxyRef.current = {
                    dx: sumOriginDxyRef.current.dx + dx,
                    dy: sumOriginDxyRef.current.dy + dy,
                };
                if (
                    Math.abs(sumOriginDxyRef.current.dx) > 250 ||
                    Math.abs(sumOriginDxyRef.current.dy) > 250
                ) {
                    sumOriginDxyRef.current = { dx: 0, dy: 0 };
                    calculateVisibleElementsAndLines();
                }

                scheduleDraw();
                return;
            }

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // // previous zoom
            // const wheel = event.deltaY < 0 ? 1 : -1;
            // const ZOOM_FACTOR = 0.15;
            // const zoom = Math.exp(wheel * ZOOM_FACTOR);
            // const newScale = scale * zoom;

            let delta = Math.max(-600, Math.min(600, event.deltaY));

            // const zoomIntensity = 0.001;
            let zoomIntensity = 0.001;

            if (scaleRef.current <= 1.1 && scaleRef.current > 0.65) {
                zoomIntensity = 0.001;
                delta = Math.max(-500, Math.min(500, event.deltaY));
            } else if (scaleRef.current <= 0.65 && scaleRef.current > 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-600, Math.min(600, event.deltaY));
                    zoomIntensity = 0.0012;

                    // zoom out
                } else {
                    delta = Math.max(-450, Math.min(450, event.deltaY));
                    zoomIntensity = 0.0012;
                }
            } else if (scaleRef.current <= 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0025;

                    // zoom out
                } else {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0015;
                }
            }

            // Trackpad pinch deltas are much smaller than mouse-wheel ticks,
            // so the shared band intensities feel sluggish there — boost only
            // in trackpad mode (user-tuned; mouse mode stays byte-identical).
            if (inputModeRef.current === 'trackpad') {
                zoomIntensity *= 1.5;
            }

            const newScale = scaleRef.current * (1 - delta * zoomIntensity);
            // const newScale = scaleRef.current + event.deltaY * -0.001; // COEF should be dinamic changed on edge values(on min and max values) 			// const COEF = -0.002 or -0.0025 or -0.001;

            const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

            const zoomFactor = clampedScale / scaleRef.current;

            const newOriginX =
                (originXYRef.current.x - mouseX) * zoomFactor + mouseX;
            const newOriginY =
                (originXYRef.current.y - mouseY) * zoomFactor + mouseY;


            scaleRef.current = clampedScale;
            originXYRef.current = { x: newOriginX, y: newOriginY };
            calculateAndSetScaleNumbers(clampedScale);

            syncScaleToReduxRef.current(clampedScale, newOriginX, newOriginY);

            if (!isScaling) {
                setIsScaling(true);
            }
            clearTimeout(scalingTimerRef.current);
            scalingTimerRef.current = setTimeout(() => {
                setIsScaling(false);
            }, 200);

            calculateVisibleElementsAndLines();



            // const currentTime = Date.now();
            // const diffTime = currentTime - (scalingRedrawCounterRef.current || 0);

            // console.log('diffTime', diffTime, !!scalingRedrawTimerRef.current)
            // if (diffTime > 20) {
            //     draw();
            // } else if (!scalingRedrawTimerRef.current) {
            //     scalingRedrawTimerRef.current = setTimeout(() => {
            //         draw();
            //     }, diffTime);
            // }
            // scalingRedrawCounterRef.current = currentTime;  // Оновлюємо час останнього виклику

            scheduleDraw();

            // drawUpdateZoom(clampedScale, newOriginX, newOriginY);
        };

        const handleDocumentContextMenuClick = (event) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            console.log('adadfa')
            event.preventDefault();

            // inside click in canvas root elements tree
            // if (ref.current && ref.current.contains(event.target)) {

            // }

            // // business logic | CLICK MOUSE UP
            // if (isBoardClickedRef.current) {

            // 	// 3.1 static click on the board
            // 	if (!isBoardMovingStarted) {
            // 		// unfocus focused elements
            // 		const hasFocusedInsideElementChanges = !!focusedInsideElementState;
            // 		if (hasFocusedInsideElementChanges) {
            // setFocusedInsideElementState(null);

            // 			syncFocusedInsideElementToReduxRef.current(null);
            // 		}
            // 		const hasFocusedElementsChanges = !!(selectedElementIdsLegacyRef.current.length);
            // 		if (hasFocusedElementsChanges) {
            // 			selectedElementIdsLegacyRef.current = [];
            // 			focusedRelatedPropsRef.current = null;
            // 			syncSelectedElementsLegacyRef.current([]);
            // 		}

            // 		const hasFocusedLineChanges = !!selectedLineIdLegacyRef.current;
            // 		if (hasFocusedLineChanges) {
            // 			selectedLineIdLegacyRef.current = null;
            // 			focusedLineRelatedPropsRef.current = null;
            // 			syncSelectedLineLegacyRef.current(null);
            // 		}

            // 		handleCursorClick();

            // 		// 3.2 move and click on the board (hand => drag)
            // 	} else {
            // 		// nothing. stay with focus on focused elements before dragging board
            // 	}

            // 	// 3.3 move and click on the board (mouse => selectionBox)
            // } else if (isSelectionClickedRef.current) {

            // 	// 3.3.1 static click on the board with mouse/selectionBox
            // 	if (selectionStartXYRef.current.x === selectionCurrentXYRef.current.x && selectionStartXYRef.current.y === selectionCurrentXYRef.current.y) {
            // 		// unfocus focused elements
            // 		const hasFocusedInsideElementChanges = !!focusedInsideElementState;
            // 		if (hasFocusedInsideElementChanges) {

            // 			syncFocusedInsideElementToReduxRef.current(null);
            // 		}
            // 		const hasFocusedElementsChanges = !!(selectedElementIdsLegacyRef.current.length);
            // 		if (hasFocusedElementsChanges) {
            // 			selectedElementIdsLegacyRef.current = [];
            // 			focusedRelatedPropsRef.current = null;
            // 			syncSelectedElementsLegacyRef.current([]);
            // 		}

            // 		const hasFocusedLineChanges = !!selectedLineIdLegacyRef.current;
            // 		if (hasFocusedLineChanges) {
            // 			selectedLineIdLegacyRef.current = null;
            // 			focusedLineRelatedPropsRef.current = null;
            // 			syncSelectedLineLegacyRef.current(null);
            // 		}

            // 		handleCursorClick();

            // 	} else {
            // 		// nothing. stay with focus on focused elements which selected with selectionBox on 'mousemove'
            // 		// dispatch(changeCursor());
            // 	}

            // }

            // if (isBoardClickedRef.current) {
            // 	isBoardClickedRef.current = false;
            // }
            // if (isSelectionClickedRef.current) {
            // 	isSelectionClickedRef.current = false;
            // }

            // if (isMovingElementId) {
            // 	setIsMovingElementId(null);
            // }
            // if (isBoardMoving) {
            // 	setIsBoardMoving(false);
            // }
            // if (isBoardMovingStarted) {
            // 	setIsBoardMovingStarted(false);
            // }

            // ///////////
            // // update stored elements positions
            // const updatedElements = {};
            // for (const key in elementsRef.current) {
            // 	const element = elementsRef.current[key];
            // 	if (element.isChangedXY) {
            // 		updatedElements[element.id] = {
            // 			x: element.x,
            // 			y: element.y,

            // 			translateX: element.translateX,
            // 			translateY: element.translateY,

            // 			width: element.width,
            // 			height: element.height,
            // 		}
            // 	}
            // }

            // draw();
        };

        const handleDocumentMouseUp = async (e) => {
            const { draw, isScaling, isBoardMoving, isBoardMovingStarted, isMovingElementId, isCursorHand, isBtnNewTableClickEnabled, isBtnNewTableGrabEnabled, isBtnNewNoteClickEnabled, isBtnNewNoteGrabEnabled, isBtnNewLineEnabled, isBtnNewAreaEnabled, isBtnExportImageEnabled, isBtnPenEnabled, isBtnRedGreenViewEnabled } = handlerStateRef.current; // eslint-disable-line
            if (!canEditRef.current) return;

            // e.preventDefault();
            const event = e.type === 'touchstart' ? e.changedTouches[0] : e;

            sumOriginDxyRef.current = { dx: 0, dy: 0 };

            alignmentLinePropsRef.current = {};

            const mouseX =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseY =
                (event.clientY - originXYRef.current.y) / scaleRef.current;

            if (isNewTableGrabStartedRef.current) {
                const mouseX =
                    (event.clientX -
                        originXYRef.current.x -
                        160 * scaleRef.current) /
                    scaleRef.current;
                const mouseY =
                    (event.clientY -
                        originXYRef.current.y -
                        40 * scaleRef.current) /
                    scaleRef.current;

                const { newTable: newElement, firstColumn } = buildNewTable(mouseX, mouseY);
                // addElement(getDiagramDoc(), newElement); addColumn(...)
                if (checkTables(elementsRef.current)) {
                    const doc = getDiagramDoc();
                    addElement(doc, newElement);
                    addColumn(doc, { tableId: newElement.id, mode: 'end', column: firstColumn });
                    focusedIdsRef.current = [newElement.id];
                    focusedRelatedPropsRef.current = null;
                    focusedLineRelatedPropsRef.current = null;
                    dispatch(updateFocusedIds([newElement.id]));
                    dispatch(updateFocusedInsideElement(newElement.id, null, null, 'header'));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('tables'));
                }

                isNewTableGrabStartedRef.current = false;
                return;
            } else if (isNewNoteGrabStartedRef.current) {
                const mouseX =
                    (event.clientX -
                        originXYRef.current.x -
                        160 * scaleRef.current) /
                    scaleRef.current;
                const mouseY =
                    (event.clientY -
                        originXYRef.current.y -
                        40 * scaleRef.current) /
                    scaleRef.current;

                const newElement = buildNewNote(mouseX, mouseY);
                if (checkNotes(elementsRef.current)) {
                    addElement(getDiagramDoc(), newElement);
                    focusedIdsRef.current = [newElement.id];
                    focusedRelatedPropsRef.current = null;
                    focusedLineRelatedPropsRef.current = null;
                    dispatch(updateFocusedIds([newElement.id]));
                    // note-text with no noteOpenClickPointRef set — safe
                    // (end-of-text on a brand-new empty note) only because
                    // ENABLE_BTN_NEW_NOTE_GRAB's reducer already called
                    // unfocusFocusedItems() before this ran. See
                    // noteOpenClickPointRef's own comment above.
                    dispatch(updateFocusedInsideElement(newElement.id, null, null, 'note-text'));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('notes'));
                }

                isNewNoteGrabStartedRef.current = false;
                return;
            }

            // BUSINESS LOGIC | CURSOR

            if (isBtnExportImageEnabled) {
                let hoveredExportImageResizeKey =
                    getHoveredResizeKeyOnExportImageBox(mouseX, mouseY);

                // hover on resize points on export image box: style cursor to resize
                if (hoveredExportImageResizeKey) {
                    const cursorMap = {
                        ne: 'nesw',
                        sw: 'nesw',
                        nw: 'nwse',
                        se: 'nwse',
                    };
                    canvas.style.cursor = `${cursorMap[hoveredExportImageResizeKey]}-resize`;
                } else {
                    canvas.style.cursor = 'grab';
                }
            } else if (isBtnPenEnabled) {
                // canvas.style.cursor = 'pointer';
                canvas.style.cursor = 'none';

                // hover on resize points if focused only one element: style cursor to resize
            } else if (focusedIdsRef.current.length === 1) {
                let [isHoverOnResize, resizeKey] = isHoverOnResizeHandle(
                    mouseX,
                    mouseY
                );
                const cursorMap = {
                    e: 'ew',
                    w: 'ew',
                    n: 'ns',
                    s: 'ns',
                    ne: 'nesw',
                    sw: 'nesw',
                    nw: 'nwse',
                    se: 'nwse',
                };
                if (
                    // Same hover-matches-click rule as the mousemove chain:
                    // points/circles DIRECTLY under the pointer take the
                    // click over the resize bands. Inner fields, not the
                    // refs themselves — the refs are wrapper objects that
                    // stay truthy (null ids inside) across the whole
                    // hover-display area.
                    hoveredCircleRef.current?.elementId ||
                    hoveredPointRef.current?.insideElementId ||
                    hoveredPointRef.current?.direction
                ) {
                    canvas.style.cursor = 'pointer';
                } else if (isHoverOnResize) {
                    canvas.style.cursor = `${cursorMap[resizeKey]}-resize`;
                } else {
                    canvas.style.cursor = 'default';
                }
            } else {
                if (isBtnNewLineEnabled) {
                    canvas.style.cursor = 'crosshair';
                } else if (isBtnNewAreaEnabled) {
                    canvas.style.cursor = 'crosshair';
                } else if (isCursorHand) {
                    canvas.style.cursor = 'grab';
                } else {
                    canvas.style.cursor = 'default';
                }
            }


            // console.log('mouse-up up')
            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                // right button click
                if (event.button === 2) {
                    if (isBoardClickedRef.current) {
                        isBoardClickedRef.current = false;
                    }
                    if (isSelectionClickedRef.current) {
                        isSelectionClickedRef.current = false;
                    }

                    scheduleDraw();
                }

                return;
            }
        };

        // Clears hover state when the cursor leaves the canvas without
        // an intermediate mousemove — the !isMouseInCanvas branch in
        // handleDocumentMouseMove only fires while moving, so without this
        // listener the last-painted hover would persist.
        const handleCanvasPointerLeave = () => {
            hoveredCircleAreaRef.current = null;
            hoveredCircleRef.current = null;
            hoveredPointAreaRef.current = null;
            hoveredPointRef.current = null;
            hoveredLineIdRef.current = null;
            hoveredLineRelatedPropsRef.current = null;
            hoveredElementIdRef.current = null;
            hoveredInsideElementRef.current = null;
            hoveredColumnIdRef.current = null;
            hoveredColumnIndexRef.current = null;
            hoveredElementRelatedPropsRef.current = null;
            hoveredElementIdEmptyColumnRef.current = null;
            hoveredElementIdHeaderRef.current = null;
            hoveredAreaIdRef.current = null;
            scheduleDraw();
        };

        // Set up initial window size
        // handleWindowResize();

        window.addEventListener('resize', handleWindowResize);
        canvasWrapRef.current.addEventListener('wheel', handleCanvasWheel);
        canvasWrapRef.current.addEventListener('pointerleave', handleCanvasPointerLeave);
        canvas.addEventListener('mousedown', handleCanvasMouseDown);
        document.addEventListener('mousemove', handleDocumentMouseMove);
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('contextmenu', handleDocumentContextMenuClick);
        document.addEventListener('mouseup', handleDocumentMouseUp);

        // // FOR MOUSE DEVICES:
        // circle.addEventListener('mousedown', onDown);
        // container.addEventListener('mousemove', onMove);
        // container.addEventListener('mouseup', onUp);

        // FOR TOUCH DEVICES:
        // canvas.addEventListener('touchstart', handleCanvasMouseDown);
        // document.addEventListener('touchmove', handleDocumentMouseMove);
        // document.addEventListener('touchend', handleDocumentClick);
        // // document.addEventListener('contextmenu', handleDocumentContextMenuClick);
        // document.addEventListener('touchend', handleDocumentMouseUp);

        // canvas.addEventListener('pointerdown', handleCanvasMouseDown);
        // document.addEventListener('pointermove', handleDocumentMouseMove);
        // document.addEventListener('pointerup', handleDocumentClick);
        // document.addEventListener('contextmenu', handleDocumentContextMenuClick);
        // document.addEventListener('pointerup', handleDocumentMouseUp);


        // circle.addEventListener('touchstart', onDown);
        // container.addEventListener('touchmove', onMove);
        // container.addEventListener('touchend', onUp);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
            if (canvasWrapRef.current) {
                canvasWrapRef.current.removeEventListener(
                    'wheel',
                    handleCanvasWheel
                );
                canvasWrapRef.current.removeEventListener(
                    'pointerleave',
                    handleCanvasPointerLeave
                );
            }
            if (canvas) {
                canvas.removeEventListener('mousedown', handleCanvasMouseDown);
            }
            document.removeEventListener('mousemove', handleDocumentMouseMove);
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener(
                'contextmenu',
                handleDocumentContextMenuClick
            );
            document.removeEventListener('mouseup', handleDocumentMouseUp);

            // spec 8b: noteLinkHoverStore is a MODULE-level singleton, so it
            // outlives this Canvas instance (NoteLinkPreview, mounted by
            // Diagram.jsx with no `key`, is never remounted when Canvas is —
            // e.g. navigating between diagrams remounts Canvas via its own
            // `key={diagramId}`). Without this, a link hovered right before
            // unmount leaves `{href, screenX, screenY}` sitting in the store,
            // and the next-mounted Canvas's freshly mounted preview would
            // render it at the OLD diagram's screen coordinates before any
            // new hover ever occurs. hideNoteLinkPreviewImmediately() already
            // clears both the store's value AND any pending grace timer (see
            // its own doc comment) — a full reset, not just a value clear.
            hoveredNoteLinkKeyRef.current = null;
            hideNoteLinkPreviewImmediately();
        };
    }, []);

    // useEffect without any dependencies, so just exucute only once at the start of the component
    useEffect(() => {
        calculateAndSetScaleNumbers(scaleRef.current);
    }, []);

    // ===================== COLLAB AWARENESS | PRODUCER ATTACH =====================
    useEffect(() => {
        let cancelled = false;
        let attached = false;
        const tryAttach = () => {
            if (cancelled || attached) return;
            const el = canvasWrapRef.current;
            const producer = getAwarenessProducer();
            if (!el || !producer) {
                // Parent's getOrCreateDiagramDoc effect hasn't run yet — try
                // again next frame. Stops once attached, so this is bounded by
                // the parent's first effect tick (typically 1–2 frames).
                requestAnimationFrame(tryAttach);
                return;
            }
            const viewportToDiagram = (clientX, clientY) => {
                const rect = el.getBoundingClientRect();
                return {
                    x: (clientX - rect.left - originXYRef.current.x) / scaleRef.current,
                    y: (clientY - rect.top - originXYRef.current.y) / scaleRef.current,
                };
            };
            producer.attachCanvas(el, viewportToDiagram);
            attached = true;
            setDiagramAwareness(getDiagramAwareness());
        };
        tryAttach();
        return () => {
            cancelled = true;
            const producer = getAwarenessProducer();
            if (producer && attached) producer.detachCanvas();
        };
    }, []);

    useEffect(() => {
        scheduleDraw();
    }, [draw]);

    // TODO: fix theme switch: on switch theme tables are not updated. couse used variable 'const theme', instead should be used ref/state/etc
    useEffect(() => {
        scheduleDraw();
    }, [resolvedTheme]);

    // useEffect(() => {
    //     elementsChangesRef.current = elementsChangesRedux;

    //     const fState = focusedInsideElementStateRef.current;
    //     if (
    //         elementsChangesRedux &&
    //         fState?.elementId &&
    //         fState?.insideElementId
    //     ) {
    //         if (
    //             !elementsChangesRedux[fState.elementId] ||
    //             !elementsChangesRedux[fState.elementId].columns[fState.insideElementId]
    //         ) {
    //             setFocusedInsideElementState(null);
    //             syncFocusedInsideElementToReduxRef.current(null);
    //         }
    //     }

    //     scheduleDraw();
    //     // eslint-disable-next-line
    // }, [elementsChangesRedux]);

    useEffect(() => {
        if (!arraysAreEqual(focusedIdsRef.current, focusedIds)) {
            focusedIdsRef.current = focusedIds;

            // Derive element-only ids and line-only ids for related-prop refs.
            const elementOnlyIds = (focusedIds || []).filter(
                (sid) => elementsRef.current[sid] && elementsRef.current[sid].type !== 'area'
            );
            focusedRelatedPropsRef.current = getElementsRelatedProps(elementOnlyIds);

            const firstLineId = (focusedIds || []).find((sid) => linesRef.current[sid]);
            focusedLineRelatedPropsRef.current = firstLineId
                ? getLineRelatedProps(firstLineId)
                : { elementsIds: [], insideElementsIds: new Set() };

            scheduleDraw();
            // The ref just changed from a non-handler path (e.g. Ctrl+A select-all),
            // which — unlike mouse selection — doesn't pre-sync focusedIdsRef before the
            // render. Force a re-render so the ref-driven context-menu JSX evaluates now
            // instead of only on the next movement/zoom.
            setFocusedElementVersion((v) => v + 1);
        }
    }, [focusedIds]);

    // Search spotlight column hover: redux-driven mirror into a ref the draw loop reads.
    // Never touches focusedInsideElement / the mousemove-owned hover refs.
    useEffect(() => {
        const prev = searchHoveredColumnRef.current;
        const same = prev?.elementId === searchHoveredColumn?.elementId && prev?.columnId === searchHoveredColumn?.columnId;
        if (!same) {
            searchHoveredColumnRef.current = searchHoveredColumn;
            scheduleDraw();
        }
    }, [searchHoveredColumn]);

    // TODO: get selector for focusedLine
    // + update state on focusedLine changes
    // + use focusedLine instead focusedLineId

    // useEffect(() => {
    // 	if (selectedLineIdLegacyRef.current !== focusedLineId) {
    // 		selectedLineIdLegacyRef.current = focusedLineId;
    // 		focusedLineRelatedPropsRef.current = getLineRelatedProps(focusedLineId);

    // 		draw();
    // 		// calculateContextMenuXY();

    // 		// if (focusedLineId) {
    // 		// 	setIsStateUpdated(isStateUpdated);
    // 		// }
    // 		setIsStateUpdated(isStateUpdated);

    // 	}
    // }, [focusedLine]);

    useEffect(() => {
        setIsFocusedInsideElementChanged(!isFocusedInsideElementChanged);
        setFocusedInsideElementState(focusedInsideElementRedux);
        focusedInsideElementStateRef.current = focusedInsideElementRedux;
        setColumnState(focusedInsideElementRedux);

        // spec 8b: the note entering edit mode hides the hover preview
        // instantly — its canvas text (and this ref's own hit-testing) goes
        // inert for that note the moment insideElementType becomes
        // 'note-text', per the mousemove gate above.
        if (focusedInsideElementRedux?.insideElementType === 'note-text') {
            hoveredNoteLinkKeyRef.current = null;
            hideNoteLinkPreviewImmediately();
        }

        if (!focusedInsideElementRedux) {
            setColumnNameState('');
            setColumnTypeState('');
        } else if (focusedInsideElementRedux.insideElementType === 'header') {
            const el = elementsRef.current[focusedInsideElementRedux.elementId];
            setColumnNameState(el?.name || '');
            setColumnTypeState('');
        } else if (focusedInsideElementRedux.insideElementId) {
            const el = elementsRef.current[focusedInsideElementRedux.elementId];
            const col = el?.columns?.[focusedInsideElementRedux.insideElementId];
            setColumnNameState(col?.name || '');
            setColumnTypeState(col?.type || '');
        }

        scheduleDraw();
    }, [focusedInsideElementRedux]);

    // spec 8b: the note-link hover preview is full-view-only (its zones and
    // hit-test both gate on viewMode === 'full') — a mode change instantly
    // invalidates any shown popover rather than leaving it stranded until
    // the next mousemove miss.
    useEffect(() => {
        hoveredNoteLinkKeyRef.current = null;
        hideNoteLinkPreviewImmediately();
    }, [viewMode]);

    const updateLiveElementsWithRedux = () => {
        const updatedElements = {};
        for (const key in elementsRef.current) {
            const element = elementsRef.current[key];

            if (element.isChangedXY) {
                updatedElements[element.id] = {
                    x: element.x,
                    y: element.y,

                    translateX: element.translateX,
                    translateY: element.translateY,
                };
            }
        }
        dispatch(updateLiveElementsPositions(updatedElements));
    };


    // const handleOriginXYorScaleChange = (scaleValue, originXYValue) => {

    // 	scaleValue,
    // 	if (scaleValue !== scaleRef.current) {
    // 		scaleRef.current = scaleValue;
    // 		calculateAndSetScaleNumbers(scaleValue);

    // 		setIsScaling(true);
    // 		clearTimeout(scalingTimerRef.current);
    // 		scalingTimerRef.current = setTimeout(() => { setIsScaling(false); }, 200);

    // 		draw();
    // 	}
    // };

    const handleScaleChange = (scaleValue) => {
        if (scaleValue !== scaleRef.current) {
            scaleRef.current = scaleValue;
            calculateAndSetScaleNumbers(scaleValue);

            setIsScaling(true);
            clearTimeout(scalingTimerRef.current);
            scalingTimerRef.current = setTimeout(() => {
                setIsScaling(false);
            }, 200);

            scheduleDraw();
        }
    };
    useEffect(() => {
        handleScaleChange(scale);
    }, [scale]);

    const handleOriginXYChange = (originXYValue) => {
        if (
            originXYValue.x !== originXYRef.current.x ||
            originXYValue.y !== originXYRef.current.y
        ) {
            originXYRef.current = { x: originXYValue.x, y: originXYValue.y };

            calculateVisibleElementsAndLines();
            scheduleDraw();
        }
    };
    useEffect(() => {
        handleOriginXYChange(originXY);
    }, [originXY]);

    // CanvasScrollbars' imperative write side (see canvasScrollbarsStore.js
    // for why this crosses the component boundary via a module singleton:
    // CanvasScrollbars is mounted as Canvas's SIBLING in Diagram.jsx, not
    // its child). applyDelta adds a delta to originXY on a single axis
    // exactly like the mousedown board-drag branch elsewhere in this file
    // (`originXYRef.current += delta`, debounced redux sync, scheduleDraw).
    //
    // Flags: a scrollbar-thumb drag is one continuous mousedown -> document
    // mousemove -> document mouseup gesture — the SAME shape as that
    // mousedown board-drag branch (which DOES set isBoardMoving/
    // isBoardMovingStarted), not the bursty trackpad wheel-pan branch
    // (which deliberately doesn't, to avoid flapping the EditableNote/
    // EditableTableHeader overlay's mount state across gaps between wheel
    // events within one swipe). So applyDelta reuses that same flag +
    // 250-world-unit-threshold recalc + 200ms-inactivity-clear machinery
    // (sumOriginDxyRef / boardMovingTimerRef), shared with — not
    // reimplemented from — that branch. endGesture (review IMPORTANT 4)
    // clears the flags/timer IMMEDIATELY instead, mirroring the immediate
    // clear the native board-drag path gets on its own mouseup via
    // unsetAllVariablesOnClick, rather than making every scrollbar drag
    // wait out the 200ms inactivity timer before editor overlays return.
    //
    // scrollbarPanImplRef holds the LATEST {applyDelta, endGesture} pair,
    // refreshed every render (no-dep effect below, mirroring the
    // handlerStateRef "latest ref" effect above) so neither closes over a
    // stale calculateVisibleElementsAndLines/syncOriginXYToReduxRef. The
    // object actually REGISTERED into the store (in the mount-once effect
    // further below) is a stable trampoline that just delegates to this
    // ref — registration itself only happens once per real mount/unmount,
    // not every render, which matters because the store bumps its token on
    // every registration (review CRITICAL 1): re-registering every render
    // would make an in-flight drag look "stale" mid-drag.
    const scrollbarPanImplRef = useRef(null);
    useEffect(() => {
        scrollbarPanImplRef.current = {
            applyDelta: (axis, deltaOrigin) => {
                if (!deltaOrigin) return;

                hoveredNoteLinkKeyRef.current = null;
                hideNoteLinkPreviewImmediately();

                // Hard pan limits: route through the same clamp for safety,
                // even though CanvasScrollbars' own range (PAD_FRACTION =
                // 0.125 of viewportSize per side, see CanvasScrollbars.jsx)
                // is narrower than this clamp's PAN_LIMIT_FRACTION = 1 and so
                // normally never triggers it — belt-and-braces, not the
                // primary defense.
                const rawX = axis === 'x' ? originXYRef.current.x + deltaOrigin : originXYRef.current.x;
                const rawY = axis === 'y' ? originXYRef.current.y + deltaOrigin : originXYRef.current.y;
                const newOriginX = clampOriginForAxis('x', rawX);
                const newOriginY = clampOriginForAxis('y', rawY);
                originXYRef.current = { x: newOriginX, y: newOriginY };
                syncOriginXYToReduxRef.current(newOriginX, newOriginY);

                sumOriginDxyRef.current = {
                    dx: sumOriginDxyRef.current.dx + (axis === 'x' ? deltaOrigin : 0),
                    dy: sumOriginDxyRef.current.dy + (axis === 'y' ? deltaOrigin : 0),
                };
                if (
                    Math.abs(sumOriginDxyRef.current.dx) > 250 ||
                    Math.abs(sumOriginDxyRef.current.dy) > 250
                ) {
                    sumOriginDxyRef.current = { dx: 0, dy: 0 };
                    calculateVisibleElementsAndLines();
                }

                if (!handlerStateRef.current.isBoardMoving) {
                    setIsBoardMoving(true);
                    setIsBoardMovingStarted(true);
                }
                clearTimeout(boardMovingTimerRef.current);
                boardMovingTimerRef.current = setTimeout(() => {
                    setIsBoardMoving(false);
                }, 200);

                scheduleDraw();
            },
            endGesture: () => {
                clearTimeout(boardMovingTimerRef.current);
                sumOriginDxyRef.current = { dx: 0, dy: 0 };
                if (handlerStateRef.current.isBoardMoving) {
                    setIsBoardMoving(false);
                }
                if (handlerStateRef.current.isBoardMovingStarted) {
                    setIsBoardMovingStarted(false);
                    handlerStateRef.current.isBoardMovingStarted = false;
                }
                calculateVisibleElementsAndLines();
                scheduleDraw();
            },
        };
    });

    // The one-time registration/unregistration pair (review CRITICAL 1):
    // a stable trampoline registered on mount, unregistered with the exact
    // token registerCanvasScrollPanApi returned on unmount — so a stale
    // handler (this Canvas instance after it's gone) can never receive
    // anything, even from a drag gesture CanvasScrollbars started against
    // it before the unmount (CanvasScrollbars itself isn't remounted on a
    // diagramId change, only <Canvas key={diagramId}> is — see
    // canvasScrollbarsStore.js's module comment).
    useEffect(() => {
        const token = registerCanvasScrollPanApi({
            applyDelta: (axis, deltaOrigin) => scrollbarPanImplRef.current?.applyDelta(axis, deltaOrigin),
            endGesture: () => scrollbarPanImplRef.current?.endGesture(),
        });
        return () => unregisterCanvasScrollPanApi(token);
    }, []);

    useEffect(() => {
        if (isBtnExportImageEnabled) {
            // calculate initial props for export image box
            // exportImageBoxPropsRef.current = getInitialValuesForExportImageBox();
            setInitialValuesForExportImageBox();
        } else {
            // clear props for export image box
        }

        scheduleDraw();
    }, [isBtnExportImageEnabled]);

    useEffect(() => {
        if (isHistoryClicked && !isHistoryClickedState) {
            setIsHistoryClickedState(true);
        }

        if (!isHistoryClicked && isHistoryClickedState) {
            setIsHistoryClickedState(false);
        }
    }, [isHistoryClicked]);

    // const handleExportByCoordinates = (exportCoordinates) => {
    // 	if (exportCoordinates) {
    // 		//
    // 	}
    // };

    // useEffect(() => {
    // 	handleExportByCoordinates(exportCoordinates);
    // }, [exportCoordinates]);

    useEffect(() => {
        // Перевіряємо, чи шрифти вже завантажені
        if (document.fonts.status === 'loaded') {
            scheduleDraw();
        } else {
            document.fonts.ready.then(() => {
                scheduleDraw();
            });
        }
    }, []);
    useEffect(() => {
        if (!isBtnNewLineEnabled) {
            lineFromRef.current = null;
            lineToRef.current = null;
        }
        scheduleDraw();
    }, [isBtnNewLineEnabled]);

    useEffect(() => {
        const canvas = ref.current;

        // BUSINESS LOGIC | CURSOR
        if (clickedPointRef.current || isBtnNewLineEnabled) {
            canvas.style.cursor = 'crosshair';
        } else if (isBtnNewAreaEnabled) {
            canvas.style.cursor = 'crosshair';
        } else if (isBtnExportImageEnabled) {
            canvas.style.cursor = 'grab';
        } else if (isBtnPenEnabled) {
            // canvas.style.cursor = 'pointer';
            canvas.style.cursor = 'none';
        } else if (isBtnNewTableClickEnabled) {
            canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAVFBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////////////////////////////8wXzyWAAAAG3RSTlMACAkRGFVmdXZ3eIiZpcDJzNfd5Ofs7e/3+vucN3j2AAAAAWJLR0QbAmDUpAAAAH5JREFUKM/NkzcOgEAMBM2Rcw53+/9/UoBdmSABElOtNIW9kk30Ap6v4bFOHTRcsmmja8AZIiIKcESw+6XRWMS36tbtb/wQaQzir/qPOVOhkjwq8zNkp/t94+eCqVFLnm/360OmRCm5/0k/42zHTJgkW7vdLyX6gdv45v88YQXHUR7h7PdO0gAAAABJRU5ErkJggg==") 14 16, auto`;
        } else if (isBtnNewNoteClickEnabled) {
            canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAYAAADE6YVjAAACHElEQVR4AeyUu0srQRTGzcMNuUWyCQS0udfrH3DrCxcsLoQVLdVYWIgWViqC4qtRS7GxUBALtY4otqZS0P9AbKzUImIgjyKYd/ydRYfEx8pG1MblfHtmZ+d838yZmeNs+oTna0Si0aiWSqV+JRKJVruQOIl/mpy6lSSTyZ+GYawwaM/tdh/ahcSFw+Fl4aGtTIlUq1UN0iWHw9EHjpxO56ZdwHpMTD9YED6+TVMiLLWlUqn8A1u6rk/5/f41uwgEApNMcBuRv/F4vNVU4KVEPB5PEXU3fdegYSuVStflctnDBAuPJErkseMj/LeIrax+p+td6aoSLcC9bVzgtkwm051Op2dpTwq4iB0SyaVsFi9oeE8gbOPyrkNyAPEgpIP4IXwnfVderzeHN61hEQQMSP9Thvqy2WxHsVjs5LbvI/ID5h38Ld60hkUQaEfoPJfLnbpcrjtN04bx04guUPt2TfaH11MRB/0C3JtWQaRA5W7mmYB8nu95atYqq7irjVYi+XxeNqpEcWupHWDRFhEXhXUM0lnGzem6vk47T7vOlAhl+oYUXLDkHjZ1kVMzagXI/jC2nUmN055GYAP/TEDUlAgDCgTM0HlLCnpZ/ogVGPeb9JzgB3w+3zbxLwrwv0mJyEcwGDyLxWJdnJJuYFiBCXUx+xEycIBA3R4IVy3qRORHJBIpEHgZCoXiVmBCV5CrYyqxr+GZyGsD39N/DwAA//9XLutcAAAABklEQVQDAAwjVEKAupSDAAAAAElFTkSuQmCC") 14 16, auto`;
        } else if (isCursorHand) {
            canvas.style.cursor = 'grab';
        } else {
            canvas.style.cursor = 'default';
        }

        scheduleDraw();
    }, [
        isCursorHand,
        isBtnNewLineEnabled,
        isBtnPenEnabled,
        isBtnNewAreaEnabled,
        isBtnExportImageEnabled,
        isBtnNewTableClickEnabled,
        isBtnNewNoteClickEnabled,
    ]);

    useEffect(() => {
        if (isBtnExportImageClickEnabled && isBtnExportImageEnabled) {
            setTimeout(() => {
                // downloadCanvasImage();
                downloadCanvasImageNew();
                dispatch(disableExportImageClick());

                exportImageBoxPropsRef.current = {
                    isPressed: false,
                    x: 0,
                    y: 0,
                    boxWidth: 0,
                    boxHeight: 0,
                };
                isExportImageClickedRef.current = false;

            }, 150);
        }

    }, [isBtnExportImageClickEnabled, isBtnExportImageEnabled]);

    async function downloadCanvasImage() {
        const canvas = ref.current;

        const prevScale = scaleRef.current;
        const prevOriginXY = { x: originXYRef.current.x, y: originXYRef.current.y };
        const prevCanvasWH = { w: canvas.width, h: canvas.height };

        const imageX = exportImageBoxPropsRef.current.clientX;
        const imageY = exportImageBoxPropsRef.current.clientY;
        const imageWidth = exportImageBoxPropsRef.current.clientBoxWidth / scaleRef.current;
        const imageHeight = exportImageBoxPropsRef.current.clientBoxHeight / scaleRef.current;

        const newScale = 1;
        const zoomFactor = newScale / scaleRef.current;
        let newOriginX = (originXYRef.current.x - imageX) * zoomFactor;
        let newOriginY = (originXYRef.current.y - imageY) * zoomFactor;

        scaleRef.current = newScale;
        originXYRef.current = { x: newOriginX, y: newOriginY };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(newScale, newOriginX, newOriginY);

        canvas.width = imageWidth * newScale;
        canvas.height = imageHeight * newScale;

        isExportImageClickedRef.current = true;
        draw();

        const targetCanvas = document.createElement('canvas');
        const targetContext = targetCanvas.getContext('2d');

        targetCanvas.width = imageWidth * newScale;
        targetCanvas.height = imageHeight * newScale;
        targetContext.fillStyle = '#3a495a'; // '#39424c'
        targetContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

        targetContext.imageSmoothingEnabled = true;
        targetContext.imageSmoothingQuality = 'high';
        // targetContext.drawImage(ref.current, 0, 0, imageWidth, imageHeight, 0, 0, imageWidth * scaleFactor, imageHeight * scaleFactor);
        targetContext.setTransform(
            scaleRef.current,
            0,
            0,
            scaleRef.current,
            originXYRef.current.x,
            originXYRef.current.y
        );
        // targetContext.drawImage(ref.current, imageX, imageY, imageWidth, imageHeight, -350, -300, imageWidth, imageHeight);

        // targetContext.drawImage(canvas, imageX, imageY, imageWidth, imageHeight, newOriginX, newOriginY, imageWidth, imageHeight);
        targetContext.drawImage(
            canvas,
            -newOriginX / scaleRef.current,
            -newOriginY / scaleRef.current
        );
        // targetContext.drawImage(canvas, -newOriginX, -newOriginY);

        // targetContext.drawImage(canvas, imageX, imageY, imageWidth, imageHeight, newOriginX, newOriginY, imageWidth, imageHeight);

        // targetContext.setTransform(scale, 0, 0, scale, -originX * scale, -originY * scale);
        // targetContext.drawImage(ref.current, offsetX, offsetY, imageWidth / scale, imageHeight / scale, 0, 0, imageWidth, imageHeight);

        const blob = await new Promise((resolve) =>
            targetCanvas.toBlob((blob) => resolve(blob), 'image/png', 1)
        );

        const fileName = 'dbdesign_screen.png';
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);



        // go to back
        scaleRef.current = prevScale;
        originXYRef.current = { x: prevOriginXY.x, y: prevOriginXY.y };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(prevScale, prevOriginXY.x, prevOriginXY.y);

        canvas.width = prevCanvasWH.w;
        canvas.height = prevCanvasWH.h;

        exportImageBoxPropsRef.current = {
            isPressed: false,
            x: 0,
            y: 0,
            boxWidth: 0,
            boxHeight: 0,
        };
        isExportImageClickedRef.current = false;
        draw();
    }

    async function downloadCanvasImageNew() {
        const canvas = ref.current;

        const prevScale = scaleRef.current;
        const prevOriginXY = { x: originXYRef.current.x, y: originXYRef.current.y };
        const prevCanvasWH = { w: canvas.width, h: canvas.height };

        const imageX = exportImageBoxPropsRef.current.clientX;
        const imageY = exportImageBoxPropsRef.current.clientY;
        let imageWidth = exportImageBoxPropsRef.current.clientBoxWidth / scaleRef.current;
        let imageHeight = exportImageBoxPropsRef.current.clientBoxHeight / scaleRef.current;

        let newScale = 1;
        const maxImageWidth = 4800;
        const maxImageHeight = 2500;
        if (imageWidth * newScale > maxImageWidth) {
            newScale = maxImageWidth / (imageWidth);
        }
        if (imageHeight * newScale > maxImageHeight) {
            newScale = maxImageHeight / (imageHeight);
        }

        const zoomFactor = newScale / scaleRef.current;
        let newOriginX = (originXYRef.current.x - imageX) * zoomFactor;
        let newOriginY = (originXYRef.current.y - imageY) * zoomFactor;

        scaleRef.current = newScale;
        originXYRef.current = { x: newOriginX, y: newOriginY };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(newScale, newOriginX, newOriginY);

        canvas.width = imageWidth * newScale;
        canvas.height = imageHeight * newScale;


        let targetCanvas = document.createElement('canvas');
        let targetContext = targetCanvas.getContext('2d');

        targetCanvas.width = imageWidth * newScale;
        targetCanvas.height = imageHeight * newScale;
        // targetContext.fillStyle = '#3a495a'; // '#39424c'

        // targetContext.fillStyle = 'rgb(17, 18, 20)'; // rgba(0, 63, 141, 0.02)
        targetContext.fillStyle = 'rgb(15, 16, 18)'; // rgba(0, 63, 141, 0.02)
        // targetContext.fillStyle = 'rgb(8, 9, 13)'; // rgba(0, 63, 141, 0.02)
        // targetContext.fillStyle = 'rgb(8, 8, 9)'; // rgba(0, 63, 141, 0.02)
        // targetContext.fillStyle = '#fff'; // rgba(0, 63, 141, 0.02)


        targetContext.fillStyle = 'rgb(15, 16, 18)'; // rgba(0, 63, 141, 0.02)

        // targetContext.fillStyle = 'transparent'; // rgba(0, 63, 141, 0.02)

        // Export now carries the same 'Background color' preference as the
        // live canvas (canvasBackgroundColor from canvasBackgrounds.js) —
        // overrides the hardcoded fillStyle above, kept for history.
        targetContext.fillStyle = canvasBackgroundColor(diagramPrefs?.background);

        targetContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

        targetContext.imageSmoothingEnabled = true;
        targetContext.imageSmoothingQuality = 'high';
        targetContext.setTransform(
            scaleRef.current,
            0,
            0,
            scaleRef.current,
            originXYRef.current.x,
            originXYRef.current.y
        );

        isExportImageClickedRef.current = true;
        draw({ exportImgCanvas: targetCanvas });

        const blob = await new Promise((resolve) =>
            targetCanvas.toBlob((blob) => resolve(blob), 'image/png', 1)
        );

        const fileName = 'dbdesign_screen.png';
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);


        // go to back
        scaleRef.current = prevScale;
        originXYRef.current = { x: prevOriginXY.x, y: prevOriginXY.y };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(prevScale, prevOriginXY.x, prevOriginXY.y);

        canvas.width = prevCanvasWH.w;
        canvas.height = prevCanvasWH.h;

        // delete target canvas + context
        targetContext.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
        targetCanvas.width = 0;
        targetCanvas.height = 0;
        targetCanvas = null;

        exportImageBoxPropsRef.current = {
            isPressed: false,
            x: 0,
            y: 0,
            boxWidth: 0,
            boxHeight: 0,
        };
        isExportImageClickedRef.current = false;
        draw();
    }

    // useEffect(() => {
    //     const canvas = ref.current;

    //     if (isBtnNewTableClickEnabled) {
    //         canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAVFBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////////////////////////////8wXzyWAAAAG3RSTlMACAkRGFVmdXZ3eIiZpcDJzNfd5Ofs7e/3+vucN3j2AAAAAWJLR0QbAmDUpAAAAH5JREFUKM/NkzcOgEAMBM2Rcw53+/9/UoBdmSABElOtNIW9kk30Ap6v4bFOHTRcsmmja8AZIiIKcESw+6XRWMS36tbtb/wQaQzir/qPOVOhkjwq8zNkp/t94+eCqVFLnm/360OmRCm5/0k/42zHTJgkW7vdLyX6gdv45v88YQXHUR7h7PdO0gAAAABJRU5ErkJggg==") 14 16, auto`;
    //     }
    // }, [isBtnNewTableClickEnabled]);


    function getContextMenuXY() {
        // Derive selection breakdown from unified focusedIdsRef.
        const _focusedIds = focusedIdsRef.current || [];
        const _focusedLineIds = _focusedIds.filter((sid) => linesRef.current?.[sid]);
        const _focusedAreaIds = _focusedIds.filter(
            (sid) => areasRef.current?.[sid]
        );
        const _focusedElementIds = _focusedIds.filter(
            (sid) => elementsRef.current?.[sid] && elementsRef.current[sid].type !== 'area'
        );

        // 1. no focused elements
        if (!_focusedElementIds.length) {
            // 1.1. focused line
            if (_focusedLineIds.length === 1 && linesRef.current && linesRef.current[_focusedLineIds[0]]) {
                // const lineObject = linesRef.current[selectedLineIdLegacyRef.current];
                // let { value: { path } } = lineObject;

                // if (!path) {
                //     const { value: { fromElementId, fromInsideElementId, fromRelationType, toElementId, toInsideElementId, toRelationType } } = lineObject;

                //     const fromElement = elementsRef.current[fromElementId];
                //     const toElement = elementsRef.current[toElementId];

                //     if (!fromElement || !toElement) {
                //         return { elementCenterX: 0, elementY: 0 };
                //     }

                //     const fromInsideElement = fromElement.columns[fromInsideElementId];
                //     const toInsideElement = toElement.columns[toInsideElementId];

                //     if (!fromInsideElement || !toInsideElement) {
                //         return { elementCenterX: 0, elementY: 0 };
                //     }

                //     const fromElementColumnsKeys = Object.keys(fromElement.columns);
                //     let fromInsideElementIndex = -1;
                //     fromElementColumnsKeys.forEach((key, index) => {
                //         if (fromInsideElementId === key) {
                //             fromInsideElementIndex = index;
                //         }
                //     });

                //     const toElementColumnsKeys = Object.keys(toElement.columns);
                //     let toInsideElementIndex = -1;
                //     toElementColumnsKeys.forEach((key, index) => {
                //         if (toInsideElementId === key) {
                //             toInsideElementIndex = index;
                //         }
                //     });

                //     const lineData = {
                //         mouseX: null,
                //         mouseY: null,

                //         fromElementType: fromElement.type,
                //         fromElementWidth: fromElement.width,
                //         fromElementX: fromElement.x,
                //         fromElementY: fromElement.y,
                //         fromInsideElementIndex,
                //         fromRelationType,

                //         toElementType: toElement.type,
                //         toElementWidth: toElement.width,
                //         toElementX: toElement.x,
                //         toElementY: toElement.y,
                //         toInsideElementIndex,
                //         toRelationType,
                //     };

                //     path = _buildLinePath(lineData);
                // }

                // const [lines, points, bounds] = path;

                // const elementX = bounds.minX * scaleRef.current + originXYRef.current.x;
                // const elementWidth = (bounds.maxX - bounds.minX) * scaleRef.current;
                // const elementHeight = (bounds.maxY - bounds.minY) * scaleRef.current;

                // const elementCenterX = ((bounds.minX + bounds.maxX) / 2) * scaleRef.current + originXYRef.current.x;
                // const elementY = (bounds.minY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                // return {
                //     elementCenterX,
                //     elementX,
                //     elementY,
                //     elementWidth,
                //     elementHeight,
                // };

                const lineObject = linesRef.current[_focusedLineIds[0]];
                const fromElementId = lineObject.sourceElementId ?? lineObject.fromElementId;
                const toElementId = lineObject.targetElementId ?? lineObject.toElementId;

                const fromElement = elementsRef.current[fromElementId];
                const toElement = elementsRef.current[toElementId];

                if (!fromElement || !toElement) {
                    return { elementCenterX: 0, elementY: 0 };
                }

                let minElementsX = Math.min(fromElement.x, toElement.x);
                let maxElementsX = Math.max(fromElement.x + fromElement.width, toElement.x + toElement.width);
                let minElementsY = Math.min(fromElement.y, toElement.y);
                let maxElementsY = Math.max(fromElement.y + fromElement.height, toElement.y + toElement.height);

                const elementX = minElementsX * scaleRef.current + originXYRef.current.x;
                const elementWidth = (maxElementsX - minElementsX) * scaleRef.current;
                const elementHeight = (maxElementsY - minElementsY) * scaleRef.current;

                const elementCenterX = ((minElementsX + maxElementsX) / 2) * scaleRef.current + originXYRef.current.x;
                const elementY = (minElementsY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                return {
                    elementCenterX,
                    elementX,
                    elementY,
                    elementWidth,
                    elementHeight,
                };

                // 1.2. focused area
            } else if (_focusedAreaIds.length === 1 && areasRef.current && areasRef.current[_focusedAreaIds[0]]) {
                const area = areasRef.current[_focusedAreaIds[0]];

                const elementX = area.x * scaleRef.current + originXYRef.current.x;
                const elementWidth = area.width * scaleRef.current;
                const elementHeight = area.height * scaleRef.current;

                const elementCenterX = (area.x + area.width / 2) * scaleRef.current + originXYRef.current.x;
                const elementY = (area.y - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                return {
                    elementCenterX,
                    elementX,
                    elementY,
                    elementWidth,
                    elementHeight,
                };

                // 1.3 focused group (multiple areas, possibly with elements)
            } else if (_focusedAreaIds.length) {
                let minElementsX = areasRef.current[_focusedAreaIds[0]]?.x;
                let maxElementsX = areasRef.current[_focusedAreaIds[0]].x + areasRef.current[_focusedAreaIds[0]].width;
                let minElementsY = areasRef.current[_focusedAreaIds[0]].y;
                let maxElementsY = areasRef.current[_focusedAreaIds[0]].y + areasRef.current[_focusedAreaIds[0]].height;

                _focusedAreaIds.forEach((areaId) => {
                    const minX = areasRef.current[areaId].x;
                    const maxX = areasRef.current[areaId].x + areasRef.current[areaId].width;
                    const minY = areasRef.current[areaId].y;
                    const maxY = areasRef.current[areaId].y + areasRef.current[areaId].height;

                    if (minX < minElementsX) {
                        minElementsX = minX;
                    }
                    if (maxX > maxElementsX) {
                        maxElementsX = maxX;
                    }
                    if (minY < minElementsY) {
                        minElementsY = minY;
                    }
                    if (maxY > maxElementsY) {
                        maxElementsY = maxY;
                    }
                });

                if (_focusedElementIds.length) {
                    _focusedElementIds.forEach((elementId) => {
                        const minX = elementsRef.current[elementId].x;
                        const maxX = elementsRef.current[elementId].x + elementsRef.current[elementId].width;
                        const minY = elementsRef.current[elementId].y;
                        const maxY = elementsRef.current[elementId].y + elementsRef.current[elementId].height;

                        if (minX < minElementsX) {
                            minElementsX = minX;
                        }
                        if (maxX > maxElementsX) {
                            maxElementsX = maxX;
                        }
                        if (minY < minElementsY) {
                            minElementsY = minY;
                        }
                        if (maxY > maxElementsY) {
                            maxElementsY = maxY;
                        }
                    });
                }




                const elementX = minElementsX * scaleRef.current + originXYRef.current.x;
                const elementWidth = (maxElementsX - minElementsX) * scaleRef.current;
                const elementHeight = (maxElementsY - minElementsY) * scaleRef.current;

                const elementCenterX = ((minElementsX + maxElementsX) / 2) * scaleRef.current + originXYRef.current.x;
                const elementY = (minElementsY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                return {
                    elementCenterX,
                    elementX,
                    elementY,
                    elementWidth,
                    elementHeight,
                };

                // 1.4. no focused line and no focused area and focused elements
            } else {
                return { elementCenterX: 0, elementY: 0 };
            }

            // 2. one focused element
        } else if (_focusedElementIds.length === 1) {
            const element = elementsRef.current[_focusedElementIds[0]];
            if (!element) return { elementCenterX: 0, elementY: 0 };

            const elementX = element.x * scaleRef.current + originXYRef.current.x;
            const elementWidth = element.width * scaleRef.current;
            const elementHeight = element.height * scaleRef.current;

            const elementCenterX = (element.x + element.width / 2) * scaleRef.current + originXYRef.current.x;
            const elementY = (element.y - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

            return {
                elementCenterX,
                elementX,
                elementY,
                elementWidth,
                elementHeight,
            };

            // 3. many focused elements
        } else {
            const firstEl = elementsRef.current[_focusedElementIds[0]];
            if (!firstEl) return { elementCenterX: 0, elementY: 0 };
            let minElementsX = firstEl.x;
            let maxElementsX = firstEl.x + firstEl.width;
            let minElementsY = firstEl.y;
            let maxElementsY = firstEl.y + firstEl.height;

            _focusedElementIds.forEach((elementId) => {
                const el = elementsRef.current[elementId];
                if (!el) return;
                const minX = el.x;
                const maxX = el.x + el.width;
                const minY = el.y;
                const maxY = el.y + el.height;

                if (minX < minElementsX) {
                    minElementsX = minX;
                }
                if (maxX > maxElementsX) {
                    maxElementsX = maxX;
                }
                if (minY < minElementsY) {
                    minElementsY = minY;
                }
                if (maxY > maxElementsY) {
                    maxElementsY = maxY;
                }
            });

            const elementX = minElementsX * scaleRef.current + originXYRef.current.x;
            const elementWidth = (maxElementsX - minElementsX) * scaleRef.current;
            const elementHeight = (maxElementsY - minElementsY) * scaleRef.current;

            const elementCenterX = ((minElementsX + maxElementsX) / 2) * scaleRef.current + originXYRef.current.x;
            const elementY = (minElementsY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

            return {
                elementCenterX,
                elementX,
                elementY,
                elementWidth,
                elementHeight,
            };
        }
    }
    const { elementCenterX, elementY, elementX, elementWidth, elementHeight } = getContextMenuXY();


    const [isLoader, setIsLoader] = useState(false);

    useEffect(() => {
        setIsLoader(true);
        // setTimeout(() => { setIsLoader(false) }, 1800);
        setTimeout(() => { setIsLoader(false) }, 2200);
    }, [])

    // 'Background color' preference (redux/preferences → editor.background,
    // 'default' | 'gray' | 'blue' | 'purple' | 'violet' | 'green' | 'red' |
    // 'yellow', see ControlsTopLeft's View flyout + canvasBackgrounds.js).
    // Plain reactive read (not a ref mirror): unlike inputModeRef/
    // showAlignLinesRef above, this only feeds a CSS background on this JSX
    // render — it's not read from inside once-bound draw()/mouse-handler
    // closures — so the normal re-render on diagramPrefs.background change is
    // enough to repaint it immediately, no scheduleDraw needed. Painted on
    // canvasWrapRef (not #canvas/#canvas-g themselves) so it sits behind
    // BOTH the grid canvas (#canvas-g) and the content canvas (#canvas)
    // regardless of their own z-index/resize quirks — see downloadCanvasImageNew
    // for the export-side twin of this value.
    const canvasBackgroundValue = canvasBackgroundColor(diagramPrefs?.background);

    return (
        <div className="CanvasWrapper">
            <div
                ref={canvasWrapRef}
                style={{ position: 'absolute', inset: 0, backgroundColor: canvasBackgroundValue }}
            >
                {isLoader ? <LoaderBar /> : ''}
                <canvas
                    className={`${isLoader ? 'hide-canvas' : ''}`}
                    id="canvas"
                    ref={ref}
                    width={window.innerWidth}
                    height={window.innerHeight}
                />
                <div style={{ position: 'absolute', inset: 0, zIndex: 1002, pointerEvents: 'none' }}>
                    <RemoteCursors
                        awareness={diagramAwareness}
                        viewportToScreen={(diagX, diagY) => ({
                            x: diagX * scaleRef.current + originXYRef.current.x,
                            y: diagY * scaleRef.current + originXYRef.current.y,
                        })}
                    />
                </div>
                <canvas
                    className={`${isLoader ? 'hide-canvas' : ''}`}
                    id="canvas-g"
                    ref={canvasGridRef}
                    width={window.innerWidth}
                    height={window.innerHeight}
                />
                {/* <canvas
                    className={`${isLoader ? 'my-class' : ''}`}
                    id="canvas-i" // export-image-box-canvas
                    ref={exportImgCanvasRef}
                    width={window.innerWidth}
                    height={window.innerHeight}
                />
 */}
                {
                    // isDesktop &&
                    scaleRef.current > 0.15 &&
                        !isStartedLineState &&
                        !isBoardMovingStarted &&
                        !isBoardMoving &&
                        !isScaling &&
                        !isMovingElementId &&
                        !isElementResizing &&
                        !isBtnNewAreaEnabled &&

                        // !hoveredElementIdHeaderRef.current &&
                        // selectedElementIdsLegacyRef.current.length &&
                        focusedInsideElementState &&
                        focusedInsideElementState.insideElementType === 'column' &&
                        viewMode === 'full' ?
                        // isOpenEditableInsideElement &&
                        <EditableTableColumn
                            tableX={elementsRef.current[focusedInsideElementState?.elementId]?.x}
                            tableY={elementsRef.current[focusedInsideElementState?.elementId]?.y}
                            tableWidth={elementsRef.current[focusedInsideElementState?.elementId]?.width}
                            scale={scaleRef.current}
                            originXY={originXYRef.current}
                            elementId={focusedInsideElementState?.elementId}
                            insideElementId={focusedInsideElementState?.insideElementId}
                            insideElementIndex={focusedInsideElementState?.insideElementIndex || 0}
                            name={columnNameState}
                            type={columnTypeState}

                            isPK={elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.isPK}
                            isFK={elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.isFK}
                            isAutoIncrement={elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.isAutoIncrement}
                            isEnum={elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.isEnum}
                            isNN={elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.isNotNull}
                            isDefaultValue={!!elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.defaultValue}
                            isGeneratedValue={!!elementsRef.current[focusedInsideElementState?.elementId]?.columns?.[focusedInsideElementState?.insideElementId]?.generatedValue}
                            handleNameChange={(value, newWidth) => {
                                setColumnNameState(value);
                                // syncEditableTableColumnNameToReduxRef.current(columnState.elementId, columnState.insideElementId, value, elementsRef.current[columnState.elementId].width);
                                const width = newWidth || elementsRef.current[columnState.elementId].width;

                                if (newWidth && columnState.elementId && elementsRef.current[columnState.elementId]) {
                                    elementsRef.current[columnState.elementId].width = newWidth;
                                }

                                // TODO: lines should be updated in redux too
                                const updatedLinesIds = updateLinesByElementId(columnState.elementId);
                                syncEditableTableColumnNameToReduxRef.current(columnState.elementId, columnState.insideElementId, value, width);
                            }}
                            handleTypeChange={(value, newWidth) => {
                                setColumnTypeState(value);

                                const width = newWidth || elementsRef.current[columnState.elementId].width;

                                if (newWidth && columnState.elementId && elementsRef.current[columnState.elementId]) {
                                    elementsRef.current[columnState.elementId].width = newWidth;
                                }

                                // TODO: lines should be updated in redux too
                                const updatedLinesIds = updateLinesByElementId(columnState.elementId);
                                syncEditableTableColumnTypeToReduxRef.current(columnState.elementId, columnState.insideElementId, value, width);
                            }}
                            handleFocusOnColumnBelow={(elementId, insideElementId) => {
                                dispatch(focusOnColumnBelow(elementId, insideElementId));
                            }}
                            handleFocusOnColumnAbove={(elementId, insideElementId) => {
                                dispatch(focusOnColumnAbove(elementId, insideElementId));
                            }}
                            handleAddNewColumnBelow={(elementId, insideElementId, isMoveToNextInsideElement) => {
                                if (!canEditRef.current) return;
                                const tableEl = elementsRef.current[elementId];
                                const colOrder = orderedColumnKeys(tableEl?.columns, tableEl?.columnsOrder);
                                if (isMoveToNextInsideElement) {
                                    const curIdx = colOrder.indexOf(insideElementId);
                                    if (curIdx >= 0 && curIdx < colOrder.length - 1) {
                                        const nextId = colOrder[curIdx + 1];
                                        dispatch(updateFocusedInsideElement(elementId, nextId, curIdx + 1, 'column'));
                                        return;
                                    }
                                }
                                if (checkColumns(elementsRef.current, elementId)) {
                                    const newColumnId = nanoid();
                                    const curIdx = colOrder.indexOf(insideElementId);
                                    const newColumnIndex = curIdx >= 0 ? curIdx + 1 : colOrder.length;
                                    addColumn(getDiagramDoc(), { tableId: elementId, mode: 'below', refColumnId: insideElementId, column: { id: newColumnId, name: 'column_new', type: 'int' } });
                                    dispatch(updateFocusedInsideElement(elementId, newColumnId, newColumnIndex, 'column'));
                                } else {
                                    dispatch(enableCanvasPricingPlanLimit('columns'));
                                }
                            }}
                            handleMoveColumnBelow={(elementId, insideElementId) => {
                                if (!canEditRef.current) return;
                                const tableEl = elementsRef.current[elementId];
                                const colOrder = orderedColumnKeys(tableEl?.columns, tableEl?.columnsOrder);
                                const curIdx = colOrder.indexOf(insideElementId);
                                yjsMoveColumnBelow(getDiagramDoc(), elementId, insideElementId);
                                if (curIdx >= 0 && curIdx < colOrder.length - 1) {
                                    dispatch(updateFocusedInsideElement(elementId, insideElementId, curIdx + 1, 'column'));
                                }
                            }}
                            handleMoveColumnAbove={(elementId, insideElementId) => {
                                if (!canEditRef.current) return;
                                const tableEl = elementsRef.current[elementId];
                                const colOrder = orderedColumnKeys(tableEl?.columns, tableEl?.columnsOrder);
                                const curIdx = colOrder.indexOf(insideElementId);
                                yjsMoveColumnAbove(getDiagramDoc(), elementId, insideElementId);
                                if (curIdx > 0) {
                                    dispatch(updateFocusedInsideElement(elementId, insideElementId, curIdx - 1, 'column'));
                                }
                            }}
                            handleDeleteColumn={(elementId, insideElementId) => {
                                if (!canEditRef.current) return;
                                const tableEl = elementsRef.current[elementId];
                                const colOrder = orderedColumnKeys(tableEl?.columns, tableEl?.columnsOrder);
                                const curIdx = colOrder.indexOf(insideElementId);
                                yjsDeleteColumn(getDiagramDoc(), elementId, insideElementId);
                                if (colOrder.length <= 1) {
                                    dispatch(updateFocusedInsideElement(elementId, null, null, 'header'));
                                } else if (curIdx < colOrder.length - 1) {
                                    const nextId = colOrder[curIdx + 1];
                                    dispatch(updateFocusedInsideElement(elementId, nextId, curIdx, 'column'));
                                } else {
                                    const prevId = colOrder[curIdx - 1];
                                    dispatch(updateFocusedInsideElement(elementId, prevId, curIdx - 1, 'column'));
                                }
                            }}
                            onNeedResize={(newWidth) => {

                                // if (focusedInsideElementState?.elementId && elementsRef.current[focusedInsideElementState?.elementId]?.width) {
                                //     elementsRef.current[focusedInsideElementState.elementId].width = newWidth;
                                // }


                                // dispatch(updateElementsPositions({
                                //     [focusedInsideElementState?.elementId]: {
                                //         width: newWidth,
                                //     }
                                // }));
                            }}

                        />
                        : null}

                {isDesktop &&
                    scaleRef.current > 0.15 &&
                    !isStartedLineState &&
                    !isBoardMovingStarted &&
                    !isBoardMoving &&
                    !isScaling &&
                    !isMovingElementId &&
                    !isElementResizing &&
                    !isBtnNewAreaEnabled &&

                    // selectedElementIdsLegacyRef.current.length &&
                    focusedInsideElementState &&
                    focusedInsideElementState.insideElementType === 'header' &&
                    viewMode === 'full' ?
                    // Math.random() === 12 &&
                    // isOpenEditableInsideElement &&
                    <EditableTableHeader
                        tableX={elementsRef.current[focusedInsideElementState?.elementId]?.x}
                        tableY={elementsRef.current[focusedInsideElementState?.elementId]?.y}
                        tableWidth={elementsRef.current[focusedInsideElementState?.elementId]?.width}
                        scale={scaleRef.current}
                        originXY={originXYRef.current}
                        elementId={focusedInsideElementState?.elementId}
                        insideElementId={focusedInsideElementState?.insideElementId}
                        name={columnNameState}
                        handleHeaderNameChange={(value) => {
                            setColumnNameState(value);
                            syncEditableTableHeaderNameToReduxRef.current(columnState.elementId, value);
                        }}
                        handleAddNewFirstColumn={(elementId, isMoveToNextInsideElement) => {
                            if (!canEditRef.current) return;
                            // this handle is not emits
                            // addColumn(getDiagramDoc(), { tableId: elementId, mode: 'first', column: { id: nanoid(), name: 'column_new', type: 'int' } });
                            if (checkColumns(elementsRef.current, elementId)) {
                                addColumn(getDiagramDoc(), { tableId: elementId, mode: 'first', column: { id: nanoid(), name: 'column_new', type: 'int' } });
                            } else {
                                dispatch(enableCanvasPricingPlanLimit('columns'));
                            }
                        }}
                        handleFocusOnFirstColumn={(elementId) => {
                            dispatch(focusOnFirstColumn(elementId));
                        }}
                        handleDeleteElements={() => {
                            if (!canEditRef.current) return;
                            dispatch(deleteElements());
                        }}
                    />
                    : null}

                {
                    scaleRef.current > 0.15 &&
                        !isStartedLineState &&
                        !isBoardMovingStarted &&
                        !isBoardMoving &&
                        !isScaling &&
                        !isMovingElementId &&
                        !isElementResizing &&
                        !isBtnNewAreaEnabled &&
                        focusedInsideElementState &&
                        focusedInsideElementState.insideElementType === 'note-text' &&
                        elementsRef.current[focusedInsideElementState?.elementId] ?
                        <EditableNote
                            noteX={elementsRef.current[focusedInsideElementState.elementId].x}
                            noteY={elementsRef.current[focusedInsideElementState.elementId].y}
                            noteWidth={elementsRef.current[focusedInsideElementState.elementId].width}
                            noteHeight={elementsRef.current[focusedInsideElementState.elementId].height}
                            scale={scaleRef.current}
                            originXY={originXYRef.current}
                            text={elementsRef.current[focusedInsideElementState.elementId].text}
                            color={elementsRef.current[focusedInsideElementState.elementId].color}
                            alignmentHorizontal={elementsRef.current[focusedInsideElementState.elementId].alignmentHorizontal}
                            alignmentVertical={elementsRef.current[focusedInsideElementState.elementId].alignmentVertical}
                            isFontMonospace={elementsRef.current[focusedInsideElementState.elementId].isFontMonospace}
                            noteId={focusedInsideElementState.elementId}
                            key={focusedInsideElementState.elementId}
                            initialCaretPoint={noteOpenClickPointRef.current}
                            onCaretPointConsumed={() => {
                                // consume-and-clear: EditableNote's mount
                                // effect calls this right after using (or not
                                // using) the point above, exactly once per
                                // mount — guarantees a REMOUNT of this same
                                // note (e.g. a transient isScaling/
                                // isBoardMoving/isElementResizing gate below
                                // unmounting then remounting this component
                                // while focusedInsideElementState never left
                                // 'note-text') never replays a stale point.
                                noteOpenClickPointRef.current = null;
                            }}
                            onTextChange={(value) => {
                                syncNoteTextToYjsRef.current(focusedInsideElementState.elementId, value);
                                scheduleDraw();
                            }}
                        />
                        : null}
            </div>
            {isDesktop &&
                // scaleRef.current > 0.15 &&
                // is not export enabled
                !isBtnRedGreenViewEnabled &&
                !isBtnExportImageEnabled &&
                !isBoardMoving &&
                !isScaling &&
                !isMovingElementId &&
                focusedIdsRef.current.length > 0 &&
                viewMode === 'full' &&
                !isHistoryClickedState &&
                !isElementResizing ?
                <ContextMenu
                    // contextMenu width 472, so divide on 2 = 236
                    // contextMenu width 659, so divide on 2 = 327
                    // left={elementCenterX - 410}
                    // left={elementCenterX - 327}
                    left={elementCenterX - 195}
                    // top={elementY - 120}
                    top={elementY - 115 + 15}

                    elementCenterX={elementCenterX}
                    elementX={elementX}
                    elementY={elementY}
                    elementWidth={elementWidth}
                    elementHeight={elementHeight}
                    isFocusedInsideElementEmpty={!focusedInsideElementState}
                // top={elementY - 95}
                />
                : null}
            {/* <div
			style={{

				position: 'absolute',
				left: '10px',
				top: '10px',
				zIndex: '100',
				fontSize: '60px'
			}}
			>{scaleRef.current}</div> */}
        </div>
    );
});

export default Canvas;
